import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

// ── Firebase ─────────────────────────────────────────────────────────────────
import { initializeApp } from "firebase/app";
import {
  getFirestore, doc, collection, increment,
  getDoc as _fbGetDoc, setDoc as _fbSetDoc, onSnapshot as _fbOnSnapshot, runTransaction as _fbRunTransaction,
} from "firebase/firestore";
import { getAuth, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult, signOut, onAuthStateChanged } from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyAWvhdOgVc8MZ_Aot_X52fYt6sOG9nOfv4",
  authDomain: "gaveti-gantt.firebaseapp.com",
  projectId: "gaveti-gantt",
  storageBucket: "gaveti-gantt.firebasestorage.app",
  messagingSenderId: "419780354538",
  appId: "1:419780354538:web:dfe482c14234745bcbd293",
  measurementId: "G-FJ977DXJTJ"
};

const fbApp  = initializeApp(firebaseConfig);
const db     = getFirestore(fbApp);
const auth   = getAuth(fbApp);
const googleProvider = new GoogleAuthProvider();

// ── Envoltorio de Firestore para modo demo ────────────────────────────────
// En demo.partecpg.com (ver IS_DEMO, definido más abajo junto a DEMO_SEED),
// ninguna lectura ni escritura toca la red: todo sale de/regresa a memoria.
// Se sobreescriben aquí los mismos nombres (onSnapshot/getDoc/setDoc/
// runTransaction) para que ninguno de los ~40 puntos de la app que ya los
// usan tenga que enterarse de que existe un modo demo.
function demoSnapFor(path) {
  const data = DEMO_SEED[path];
  return { exists: () => data !== undefined, data: () => data, id: path.split("/").pop() };
}
function onSnapshot(ref, onNext, onError) {
  if (IS_DEMO) {
    try { onNext(demoSnapFor(ref.path)); } catch (e) { if (onError) onError(e); }
    return () => {};
  }
  return _fbOnSnapshot(ref, onNext, onError);
}
function getDoc(ref) {
  if (IS_DEMO) return Promise.resolve(demoSnapFor(ref.path));
  return _fbGetDoc(ref);
}
function setDoc(ref, data, opts) {
  if (IS_DEMO) return Promise.resolve();
  return _fbSetDoc(ref, data, opts);
}
function runTransaction(dbArg, fn) {
  if (IS_DEMO) return Promise.resolve();
  return _fbRunTransaction(dbArg, fn);
}

// Firestore document paths
const DOC_PROJECTS     = () => doc(db, "gantt", "projects");
const DOC_OWNER_COLORS = () => doc(db, "gantt", "ownerColors");
const DOC_OBJECTIVES   = () => doc(db, "gantt", "objectives");
const DOC_META         = () => doc(db, "gantt", "meta");
const DOC_TIME         = (uid) => doc(db, "gantt", `time_${uid}`);
const DOC_TIME_INDEX   = () => doc(db, "gantt", "time_index"); // { users: [{uid, name, email}] }
const DOC_PIZARRA      = () => doc(db, "gantt", "pizarra");    // { avisos: [{id, texto, fecha, done}] }
const DOC_TIME_ARCHIVE = () => doc(db, "gantt", "time_archive"); // { archives: [{monthKey:"2026-03", users:[{uid,name,email,entries:[]}]}] }
const DOC_CLIENT_TASKS = () => doc(db, "gantt", "client_tasks"); // { areas: [{id, name, rows:[{id, paso, responsable, fecha, estatus, urgente}]}] }
const DOC_CRM          = () => doc(db, "gantt", "crm_deals");    // { deals: [{id, nombre, contacto, fecha, etapa, notas}] }
const DOC_BLOQUEOS     = () => doc(db, "gantt", "bloqueos");   // { ventanas: [...], bloqueos: [...] }
const DOC_TIMELINE     = () => doc(db, "gantt", "timeline");    // { milestones: [{id, frente, label, estado, fecha, notas}] }
const DOC_RESULTADOS   = () => doc(db, "gantt", "resultados");  // { fichas: [{id, codigo, frente, titulo, resumen, hallazgos[], kpis[], version, fecha, link, tareas, publicado}] }
const TIME_ADMINS      = new Set(["rodrigo@partecpg.com", "miguel@partecpg.com"]);
const METRICS_VIEWER    = "miguel@partecpg.com";

// ── Métricas de uso — solo módulo de precios/FX, contador propio (sin GA4/terceros) ──
// Un doc por mes: gantt/analytics_YYYY-MM = { client: {accion: n}, team: {accion: n} }.
// "Ver" (views) vs "usar" (drag/matrix/edit_pnl/copy/session60s) — separa abrir la
// pestaña de tocar algo real. Nunca se registra mientras modo presentación está activo,
// para no contar las demos de Miguel como uso autónomo del cliente.
const DOC_ANALYTICS = (monthKey) => doc(db, "gantt", "analytics_" + monthKey);
function analyticsMonthKey(d = new Date()) { return d.toISOString().slice(0, 7); }
function logMetric(role, action) {
  if (!role || !action) return;
  setDoc(DOC_ANALYTICS(analyticsMonthKey()), { [role]: { [action]: increment(1) } }, { merge: true })
    .catch((e) => console.error("analytics", e));
}
const METRIC_LABELS = {
  views: "Abrió la pestaña", session60s: "Sesión > 60s", drag: "Movió TC/arancel",
  matrix: "Clic en matriz", edit_pnl: "Editó un supuesto", copy: "Copió el resumen",
};

// ── Serialize / Deserialize dates (Firestore stores plain objects) ────────────
function serializeProjects(projects) {
  return projects.map(space => ({
    ...space,
    tasks: space.tasks.map(t => ({
      ...t,
      start: t.start.toISOString(),
      end:   t.end.toISOString(),
      subtasks: (t.subtasks || []).map(s => ({
        ...s,
        start: s.start.toISOString(),
        end:   s.end.toISOString(),
      }))
    }))
  }));
}

function deserializeProjects(raw) {
  return raw.map(space => ({
    ...space,
    tasks: space.tasks.map(t => ({
      ...t,
      start: new Date(t.start),
      end:   new Date(t.end),
      subtasks: (t.subtasks || []).map(s => ({
        ...s,
        start: new Date(s.start),
        end:   new Date(s.end),
      }))
    }))
  }));
}


// ── Iconos SVG minimalistas (estilo Lucide, sin dependencias) ────────────────
const Ic = ({ size = 14, color = "currentColor", style, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ flexShrink: 0, verticalAlign: "middle", display: "inline-block", ...style }}>{children}</svg>
);
const IcList = (p) => <Ic {...p}><rect x="8" y="2" width="8" height="4" rx="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="M12 11h4"/><path d="M12 16h4"/><path d="M8 11h.01"/><path d="M8 16h.01"/></Ic>;
const IcAlert = (p) => <Ic {...p}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 20h16a2 2 0 0 0 1.73-2Z"/><path d="M12 9v4"/><path d="M12 17h.01"/></Ic>;
const IcTrend = (p) => <Ic {...p}><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></Ic>;
const IcGantt = (p) => <Ic {...p}><path d="M3 6h8"/><path d="M7 12h10"/><path d="M11 18h10"/></Ic>;
const IcLock = (p) => <Ic {...p}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Ic>;
const IcKey = (p) => <Ic {...p}><circle cx="7.5" cy="15.5" r="5.5"/><path d="m21 2-9.6 9.6"/><path d="m15.5 7.5 3 3L22 7l-3-3"/></Ic>;
const IcPencil = (p) => <Ic {...p}><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></Ic>;
const IcUser = (p) => <Ic {...p}><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></Ic>;
const IcCal = (p) => <Ic {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4"/><path d="M8 2v4"/><path d="M3 10h18"/></Ic>;
const IcCheckCircle = (p) => <Ic {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Ic>;
const IcClock = (p) => <Ic {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Ic>;
const IcMegaphone = (p) => <Ic {...p}><path d="m3 11 18-5v12L3 14v-3z"/><path d="M11.6 16.8a3 3 0 1 1-5.8-1.6"/></Ic>;
const IcWhatsApp = (p) => <Ic {...p}><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></Ic>;
const IcChevronUp = (p) => <Ic {...p}><polyline points="18 15 12 9 6 15"/></Ic>;
const IcChevronDown = (p) => <Ic {...p}><polyline points="6 9 12 15 18 9"/></Ic>;
const IcFlag = (p) => <Ic {...p}><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" y1="22" x2="4" y2="15"/></Ic>;
const IcLink = (p) => <Ic {...p}><path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/></Ic>;
const IcChart = (p) => <Ic {...p}><path d="M3 3v18h18"/><circle cx="8.5" cy="8.5" r="2"/><circle cx="15" cy="6" r="1.4"/><circle cx="16" cy="14.5" r="2.6"/></Ic>;
const IcBlock = (p) => <Ic {...p}><circle cx="12" cy="12" r="10"/><line x1="4.9" y1="4.9" x2="19.1" y2="19.1"/></Ic>;
const IcAward = (p) => <Ic {...p}><path d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z"/><path d="M8.2 13.9 7 22l5-3 5 3-1.2-8.1"/></Ic>;
const IcArchive = (p) => <Ic {...p}><rect x="2" y="3" width="20" height="5" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/></Ic>;
const IcScatter = (p) => <Ic {...p}><circle cx="7" cy="17" r="2"/><circle cx="17" cy="7" r="2.6"/><circle cx="14" cy="15" r="1.6"/><circle cx="6" cy="6" r="1.4"/></Ic>;
const IcHourglass = (p) => <Ic {...p}><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.2a2 2 0 0 0-.6-1.4L12 12l-4.4 4.4a2 2 0 0 0-.6 1.4V22"/><path d="M7 2v4.2a2 2 0 0 0 .6 1.4L12 12l4.4-4.4a2 2 0 0 0 .6-1.4V2"/></Ic>;
const IcPlus = (p) => <Ic {...p}><path d="M12 5v14"/><path d="M5 12h14"/></Ic>;

// ── Campo de fecha reutilizable ──────────────────────────────────────────────
// Se puede TECLEAR la fecha en formato dd/mm/aaaa (o pegarla), o elegirla del
// calendario con el ícono. Internamente SIEMPRE guarda ISO (AAAA-MM-DD) para no
// romper la lógica de "Vencido", ordenamiento ni el resto de la app.
const isoToDisplay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const displayToIso = (str) => {
  if (!str || !str.trim()) return "";
  const m = /^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2,4})$/.exec(str.trim());
  if (!m) return null; // formato inválido
  let d = parseInt(m[1], 10), mo = parseInt(m[2], 10), y = parseInt(m[3], 10);
  if (y < 100) y += 2000;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null; // fecha inexistente (ej. 31/02)
  const pad = (n) => String(n).padStart(2, "0");
  return `${y}-${pad(mo)}-${pad(d)}`;
};
function DateField({ value, onChange, style, inputStyle }) {
  const [text, setText] = useState(isoToDisplay(value));
  const nativeRef = useRef(null);
  useEffect(() => { setText(isoToDisplay(value)); }, [value]);
  const commit = () => {
    if (!text.trim()) { onChange(""); return; }
    const iso = displayToIso(text);
    if (iso) { onChange(iso); setText(isoToDisplay(iso)); }
    else { setText(isoToDisplay(value)); } // revertir si no es válida
  };
  const openPicker = () => {
    const el = nativeRef.current; if (!el) return;
    try { if (el.showPicker) el.showPicker(); else el.focus(); } catch { el.focus(); }
  };
  const base = { width: "100%", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 32px 8px 10px", outline: "none", boxSizing: "border-box", color: text ? "#334155" : "#94a3b8", ...inputStyle };
  return (
    <div style={{ position: "relative", width: "100%", ...style }}>
      <input type="text" inputMode="numeric" value={text} placeholder="dd/mm/aaaa"
        onChange={e => setText(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === "Enter") { commit(); e.currentTarget.blur(); } }}
        style={base} />
      <button type="button" onClick={openPicker} title="Elegir del calendario"
        style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", cursor: "pointer", padding: 2, display: "flex", alignItems: "center" }}>
        <IcCal size={15} color="#94a3b8" />
      </button>
      <input ref={nativeRef} type="date" value={value || ""} tabIndex={-1} aria-hidden="true"
        onChange={e => { onChange(e.target.value); setText(isoToDisplay(e.target.value)); }}
        style={{ position: "absolute", right: 4, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }} />
    </div>
  );
}

const TODAY = (() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); })();
const PROJECT_START = new Date(2026, 0, 1);
const PROJECT_END   = new Date(2026, 11, 31);
const TOTAL_MS = PROJECT_END - PROJECT_START;

// STATUS colors — NEVER assign to users
const STATUS_COLORS_FORBIDDEN = new Set(["#22C55E","#F59E0B","#EF4444","#94A3B8"]);

// Soft pastel user palette — chosen to be pleasant & distinct from status colors
const USER_PALETTE = [
  "#6366F1", // indigo
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#14B8A6", // teal
  "#0EA5E9", // sky blue
  "#F97316", // orange
  "#A855F7", // violet
  "#06B6D4", // cyan
  "#F43F5E", // rose
  "#3B82F6", // blue
  "#10B981", // emerald   ← distinct from #22C55E (brighter green)
  "#D946EF", // fuchsia
  "#FB7185", // light rose
  "#818CF8", // light indigo
  "#34D399", // mint
  "#60A5FA", // light blue
];

const OWNER_COLORS_INIT = {
  MPG: { bg: "#6366F1", label: "Miguel P." },
  RZ:  { bg: "#14B8A6", label: "Rodrigo Z." },
  MR:  { bg: "#8B5CF6", label: "Marco R." },
  KC:  { bg: "#EC4899", label: "Karen C." },
  BL:  { bg: "#F97316", label: "BL" },
  AV:  { bg: "#0EA5E9", label: "AV" },
  PN:  { bg: "#A855F7", label: "PN" },
  JZ:  { bg: "#06B6D4", label: "JZ" },
};

// Used outside component for static references
const SPACE_COLORS = {
  "MARKET ANALYSIS":      "#6C63FF",
  "SALES":                "#0EA5E9",
  "REGULATORY COMPLIANCE":"#F59E0B",
  "OPERATIONS":           "#10B981",
  "MARKETING":            "#EF4444",
  "R&D":                  "#8B5CF6",
};

const INITIAL_PROJECTS = [
  {
    id: "s1", space: "MARKET ANALYSIS", color: SPACE_COLORS["MARKET ANALYSIS"],
    tasks: [
      { id: "t1", name: "Acquire & Analyze Category Data", owners: [], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 0,
        subtasks: [
          { id: "st1a", name: "Obtain 52-week sales data (SPINS/Nielsen/IRI)", owners: [], start: new Date(2026,1,6), end: new Date(2026,1,14), progress: 0 },
          { id: "st1b", name: "Analyze category trends (flavors, pack sizes, pricing)", owners: [], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 0 },
          { id: "st1c", name: "Identify key competitors and market share", owners: [], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 0 },
        ]
      },
      { id: "t2", name: "Conduct Competitive Assessment", owners: ["MR","RZ"], start: new Date(2026,1,6), end: new Date(2026,2,20), progress: 30,
        subtasks: [
          { id: "st2a", name: "Benchmark packaging and shelf presence", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,24), progress: 20 },
          { id: "st2b", name: "Analyze ingredient and nutritional claims", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,24), progress: 20 },
          { id: "st2c", name: "Identify differentiation points", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,24), progress: 10 },
          { id: "st2d", name: "Conduct sensory evaluation & consumer insights", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,24), progress: 0 },
        ]
      },
      { id: "t3", name: "Store Visits – California (Anaheim / LA)", owners: ["RZ"], start: new Date(2026,2,3), end: new Date(2026,2,7), progress: 0,
        subtasks: [
          { id: "st3a", name: "Plan store visits by anchor priority accounts", owners: ["RZ"], start: new Date(2026,1,23), end: new Date(2026,2,2), progress: 70 },
          { id: "st3b", name: "Travel Booking: Flight, Hotel, Car Rental", owners: ["RZ"], start: new Date(2026,1,22), end: new Date(2026,1,22), progress: 100 },
        ]
      },
      { id: "t4", name: "Positioning vs. Competition Summary", owners: ["RZ"], start: new Date(2026,2,1), end: new Date(2026,2,31), progress: 0, subtasks: [] },
      { id: "t5", name: "Sample Request & Delivery – Core Team", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 0,
        subtasks: [
          { id: "st5a", name: "Sample Request", owners: [], start: new Date(2026,1,6), end: new Date(2026,1,14), progress: 0 },
          { id: "st5b", name: "Sample Delivery", owners: [], start: new Date(2026,1,14), end: new Date(2026,1,20), progress: 0 },
        ]
      },
      { id: "t6", name: "Shelf Insight: Packaging Dimensions", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 40,
        subtasks: [
          { id: "st6a", name: "Research standard shelf dimensions in retail", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,24), progress: 30 },
          { id: "st6b", name: "Analyze competitor product packaging sizes", owners: ["MR"], start: new Date(2026,1,6), end: new Date(2026,1,24), progress: 30 },
          { id: "st6c", name: "Propose packaging dimensions for shelf efficiency", owners: ["MR"], start: new Date(2026,1,20), end: new Date(2026,2,10), progress: 0 },
        ]
      },
    ]
  },
  {
    id: "s2", space: "REGULATORY COMPLIANCE", color: SPACE_COLORS["REGULATORY COMPLIANCE"],
    tasks: [
      { id: "t7", name: "Product Compliance", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,6), progress: 15,
        subtasks: [
          { id: "st7a", name: "Identify relevant regulatory requirements", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 50 },
          { id: "st7b", name: "Review packaging for regulatory requirements", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 30 },
          { id: "st7c", name: "Prepare documentation for FDA/FSMA", owners: ["MPG"], start: new Date(2026,1,20), end: new Date(2026,2,6), progress: 0 },
          { id: "st7d", name: "Dieline design", owners: ["MPG"], start: new Date(2026,1,20), end: new Date(2026,3,20), progress: 0 },
          { id: "st7e", name: "Dieline Approval", owners: ["MPG"], start: new Date(2026,3,20), end: new Date(2026,3,24), progress: 0 },
          { id: "st7f", name: "PO to packaging supplier", owners: ["MPG"], start: new Date(2026,4,1), end: new Date(2026,4,15), progress: 0 },
          { id: "st7g", name: "Pre-press", owners: ["MPG"], start: new Date(2026,4,15), end: new Date(2026,4,30), progress: 0 },
          { id: "st7h", name: "Packaging printing", owners: ["MPG"], start: new Date(2026,4,30), end: new Date(2026,5,15), progress: 0 },
        ]
      },
      { id: "t8", name: "Preliminary Product Assessment", owners: ["RZ"], start: new Date(2026,1,12), end: new Date(2026,1,26), progress: 10,
        subtasks: [
          { id: "st8a", name: "Gather Finished Product Spec Sheets & Nutritionals", owners: ["MPG"], start: new Date(2026,1,12), end: new Date(2026,1,19), progress: 20 },
          { id: "st8b", name: "Gather Spec Sheet of Hero Ingredient", owners: ["MPG"], start: new Date(2026,1,12), end: new Date(2026,1,19), progress: 20 },
          { id: "st8c", name: "Research Hero Ingredient", owners: ["RZ"], start: new Date(2026,1,12), end: new Date(2026,1,26), progress: 10 },
          { id: "st8d", name: "Assess Product Spec & Scientific Evidence", owners: ["RZ"], start: new Date(2026,1,19), end: new Date(2026,2,5), progress: 0 },
        ]
      },
      { id: "t9", name: "Shelf Life Review", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,1,14), progress: 0,
        subtasks: [
          { id: "st9a", name: "Review shelf life and current records", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,1,14), progress: 0 },
        ]
      },
      { id: "t10", name: "Manage US Subsidiary Setup", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,6), progress: 0,
        subtasks: [
          { id: "st10a", name: "Complete legal setup for US subsidiary", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,6), progress: 0 },
          { id: "st10b", name: "Complete tax setup for US subsidiary", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,6), progress: 0 },
          { id: "st10c", name: "Align transfer pricing policies", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,6), progress: 0 },
        ]
      },
    ]
  },
  {
    id: "s3", space: "OPERATIONS", color: SPACE_COLORS["OPERATIONS"],
    tasks: [
      { id: "t11", name: "Export & Logistics Costs", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,6), progress: 20,
        subtasks: [
          { id: "st11a", name: "Weight and Pallet Dimensions", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,1,14), progress: 50 },
          { id: "st11b", name: "Define freight service requirements", owners: ["MPG"], start: new Date(2026,1,14), end: new Date(2026,2,6), progress: 0 },
          { id: "st11c", name: "Identify potential vendors", owners: ["MPG"], start: new Date(2026,1,20), end: new Date(2026,2,6), progress: 0 },
        ]
      },
      { id: "t12", name: "Unit Financials", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,6), progress: 0,
        subtasks: [
          { id: "st12a", name: "Gather financial data for the unit", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,1,20), progress: 0 },
          { id: "st12b", name: "Organize data into a spreadsheet", owners: ["MPG"], start: new Date(2026,1,20), end: new Date(2026,2,1), progress: 0 },
          { id: "st12c", name: "Review report for accuracy", owners: ["MPG"], start: new Date(2026,2,1), end: new Date(2026,2,6), progress: 0 },
        ]
      },
      { id: "t13", name: "Logistics & Custom Agents", owners: [], start: new Date(2026,1,6), end: new Date(2026,3,30), progress: 0,
        subtasks: [
          { id: "st13a", name: "Identify logistics requirements", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,2,28), progress: 0 },
          { id: "st13b", name: "Research Logistics Vendors", owners: ["MPG"], start: new Date(2026,2,1), end: new Date(2026,3,15), progress: 0 },
          { id: "st13c", name: "Evaluate Insurance Providers", owners: ["MPG"], start: new Date(2026,3,1), end: new Date(2026,3,30), progress: 0 },
        ]
      },
      { id: "t14", name: "3PL Setup", owners: [], start: new Date(2026,4,1), end: new Date(2026,7,30), progress: 0,
        subtasks: [
          { id: "st14a", name: "Negotiate contracts with 3PL provider", owners: ["MPG"], start: new Date(2026,4,1), end: new Date(2026,6,1), progress: 0 },
          { id: "st14b", name: "Set up logistics processes", owners: ["MPG"], start: new Date(2026,6,1), end: new Date(2026,7,1), progress: 0 },
          { id: "st14c", name: "Conduct trial run of 3PL operations", owners: ["MPG"], start: new Date(2026,7,1), end: new Date(2026,7,30), progress: 0 },
        ]
      },
      { id: "t15", name: "Assess US Subsidiary Formation", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,7,30), progress: 0,
        subtasks: [
          { id: "st15a", name: "Obtain legal documents for subsidiary", owners: ["MPG"], start: new Date(2026,1,6), end: new Date(2026,3,1), progress: 0 },
          { id: "st15b", name: "Register US subsidiary with government agencies", owners: ["MPG"], start: new Date(2026,3,1), end: new Date(2026,5,1), progress: 0 },
          { id: "st15c", name: "Set up bank account for US subsidiary", owners: ["MPG"], start: new Date(2026,5,1), end: new Date(2026,7,30), progress: 0 },
        ]
      },
      { id: "t16", name: "2027 Financial Plan", owners: ["MPG"], start: new Date(2026,8,1), end: new Date(2026,10,30), progress: 0,
        subtasks: [
          { id: "st16a", name: "Analyze current revenue streams", owners: ["MPG"], start: new Date(2026,8,1), end: new Date(2026,9,1), progress: 0 },
          { id: "st16b", name: "Identify areas for cost reduction", owners: ["MPG"], start: new Date(2026,9,1), end: new Date(2026,10,1), progress: 0 },
          { id: "st16c", name: "Evaluate investment opportunities", owners: ["MPG"], start: new Date(2026,10,1), end: new Date(2026,10,30), progress: 0 },
        ]
      },
    ]
  },
  {
    id: "s4", space: "SALES", color: SPACE_COLORS["SALES"],
    tasks: [
      { id: "t17", name: "Build Preliminary Sales Strategy (CA & TX)", owners: [], start: new Date(2026,2,1), end: new Date(2026,5,30), progress: 0,
        subtasks: [
          { id: "st17a", name: "Define target retailers and channels", owners: [], start: new Date(2026,2,1), end: new Date(2026,3,1), progress: 0 },
          { id: "st17b", name: "Prepare sales deck and selling story", owners: [], start: new Date(2026,3,1), end: new Date(2026,4,1), progress: 0 },
          { id: "st17c", name: "Plan distributor outreach and onboarding", owners: ["KC"], start: new Date(2026,4,1), end: new Date(2026,5,30), progress: 0 },
        ]
      },
      { id: "t18", name: "Develop Trade Spend Strategy", owners: [], start: new Date(2026,2,1), end: new Date(2026,5,1), progress: 0,
        subtasks: [
          { id: "st18a", name: "Gather pricing benchmarks and promo norms", owners: [], start: new Date(2026,2,1), end: new Date(2026,3,1), progress: 0 },
          { id: "st18b", name: "Recommend promotional budget and tactics", owners: [], start: new Date(2026,3,1), end: new Date(2026,4,1), progress: 0 },
          { id: "st18c", name: "Align trade spend with sales targets", owners: [], start: new Date(2026,4,1), end: new Date(2026,5,1), progress: 0 },
        ]
      },
      { id: "t19", name: "Sales Forecasting and Planning", owners: [], start: new Date(2026,2,1), end: new Date(2026,5,30), progress: 0,
        subtasks: [
          { id: "st19a", name: "Develop sales forecast for 2026–2027", owners: [], start: new Date(2026,2,1), end: new Date(2026,3,15), progress: 0 },
          { id: "st19b", name: "Align unit economics and cost targets", owners: [], start: new Date(2026,3,15), end: new Date(2026,4,15), progress: 0 },
          { id: "st19c", name: "Plan for Amazon US launch and retail launches", owners: [], start: new Date(2026,4,15), end: new Date(2026,5,30), progress: 0 },
        ]
      },
      { id: "t20", name: "Execute Sales Launch", owners: ["KC"], start: new Date(2026,6,1), end: new Date(2026,8,30), progress: 0,
        subtasks: [
          { id: "st20a", name: "Coordinate sample shipments and kits", owners: ["KC"], start: new Date(2026,6,1), end: new Date(2026,6,20), progress: 0 },
          { id: "st20b", name: "Schedule retailer meetings and submissions", owners: ["KC"], start: new Date(2026,6,20), end: new Date(2026,7,15), progress: 0 },
          { id: "st20c", name: "Support in-store execution and follow-up", owners: ["KC"], start: new Date(2026,7,15), end: new Date(2026,8,30), progress: 0 },
        ]
      },
    ]
  },
  {
    id: "s5", space: "MARKETING", color: SPACE_COLORS["MARKETING"],
    tasks: [
      { id: "t21", name: "Develop Brand Storytelling", owners: [], start: new Date(2026,2,1), end: new Date(2026,4,30), progress: 0, subtasks: [] },
      { id: "t22", name: "Produce and Test Content", owners: [], start: new Date(2026,3,1), end: new Date(2026,5,30), progress: 0, subtasks: [] },
      { id: "t23", name: "Launch Ad Campaigns", owners: [], start: new Date(2026,5,1), end: new Date(2026,7,31), progress: 0, subtasks: [] },
      { id: "t24", name: "Plan and Execute Field Marketing", owners: [], start: new Date(2026,6,1), end: new Date(2026,8,30), progress: 0, subtasks: [] },
    ]
  },
  {
    id: "s6", space: "R&D", color: SPACE_COLORS["R&D"],
    tasks: [
      { id: "t25", name: "Optimize Product Formulation", owners: [], start: new Date(2026,2,1), end: new Date(2026,5,30), progress: 0, subtasks: [] },
      { id: "t26", name: "Develop Packaging Solutions", owners: [], start: new Date(2026,2,1), end: new Date(2026,5,30), progress: 0, subtasks: [] },
      { id: "t27", name: "Identify Innovation Opportunities", owners: [], start: new Date(2026,3,1), end: new Date(2026,7,30), progress: 0, subtasks: [] },
    ]
  },
];

function pct(date) {
  return Math.max(0, Math.min(100, ((date - PROJECT_START) / TOTAL_MS) * 100));
}

const MONTHS = [];
let _cur = new Date(PROJECT_START);
while (_cur <= PROJECT_END) {
  MONTHS.push(new Date(_cur));
  _cur = new Date(_cur.getFullYear(), _cur.getMonth() + 1, 1);
}

function taskColor(task) {
  if (task.progress === 100) return "#22C55E";
  if (TODAY > task.end && task.progress < 100) return "#EF4444";
  if (TODAY >= task.start && TODAY <= task.end) return "#F59E0B";
  return "#94A3B8";
}

const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#64748b", marginBottom: 4 };
const inp = { display: "block", width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, marginBottom: 14, boxSizing: "border-box", outline: "none" };

// ── Owner picker popover ──────────────────────────────────────────────────────
function OwnerPicker({ currentOwners, ownerColors, onSave, onClose, onEditUser }) {
  const [selected, setSelected] = useState(currentOwners || []);
  const toggle = (o) => setSelected(prev => prev.includes(o) ? prev.filter(x => x !== o) : [...prev, o]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "#00000044" }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, boxShadow: "0 8px 40px #0003", width: 340 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: "#1e293b", display: "flex", alignItems: "center", gap: 6 }}><IcUser size={14} color="#1e293b" /> Responsables</div>

        {/* User list — click row to toggle, ⋯ to edit */}
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16, maxHeight: 300, overflowY: "auto" }}>
          {Object.entries(ownerColors).map(([o, c]) => {
            const active = selected.includes(o);
            return (
              <div key={o} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 10, cursor: "pointer",
                background: active ? `${c.bg}18` : "#f8fafc",
                border: `1.5px solid ${active ? c.bg : "#e2e8f0"}`,
                transition: "all 0.12s" }}
                onClick={() => toggle(o)}>
                {/* Avatar */}
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: c.bg, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, fontWeight: 800, color: "#fff" }}>{o.slice(0,2)}</div>
                {/* Name */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: active ? c.bg : "#334155" }}>{c.label}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>{o}</div>
                </div>
                {/* Checkmark */}
                <div style={{ width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                  background: active ? c.bg : "#e2e8f0",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, color: "#fff", fontWeight: 900, transition: "all 0.12s" }}>
                  {active ? "✓" : ""}
                </div>
                {/* ⋯ edit button */}
                {onEditUser && (
                  <div onClick={e => { e.stopPropagation(); onEditUser(o, c); }}
                    title="Editar usuario"
                    style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 14, color: "#94a3b8", cursor: "pointer", fontWeight: 900,
                      background: "transparent", transition: "all 0.12s" }}
                    onMouseEnter={e => { e.currentTarget.style.background="#f1f5f9"; e.currentTarget.style.color="#334155"; }}
                    onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.color="#94a3b8"; }}>
                    ⋯
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 12 }}>Cancelar</button>
          <button onClick={() => { onSave(selected); onClose(); }}
            style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: "#6366F1", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 12 }}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// ── RACI Picker popover ───────────────────────────────────────────────────────
const RACI_META = {
  R: { label: "Responsible", desc: "Quien ejecuta la tarea", color: "#6366F1" },
  A: { label: "Accountable", desc: "Quien responde por el resultado", color: "#F59E0B" },
  C: { label: "Consulted",   desc: "Quien debe ser consultado", color: "#10B981" },
  I: { label: "Informed",    desc: "Quien debe ser informado", color: "#94A3B8" },
};
function RaciPicker({ role, current, ownerColors, onSave, onClose }) {
  const meta = RACI_META[role];
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", background: "#00000044" }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, boxShadow: "0 8px 40px #0003", width: 300 }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div style={{ width: 28, height: 28, borderRadius: 8, background: meta.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 900, color: "#fff" }}>{role}</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{meta.label}</div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>{meta.desc}</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 14, maxHeight: 260, overflowY: "auto" }}>
          {/* None option */}
          <div onClick={() => { onSave(null); onClose(); }}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 10, cursor: "pointer",
              background: current === null ? "#f1f5f9" : "#fafafa",
              border: `1.5px solid ${current === null ? "#94a3b8" : "#e2e8f0"}` }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#94a3b8" }}>—</div>
            <div style={{ fontSize: 12, color: "#94a3b8", fontWeight: 500 }}>Sin asignar</div>
            {current === null && <div style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>✓</div>}
          </div>
          {Object.entries(ownerColors).map(([o, c]) => {
            const active = current === o;
            return (
              <div key={o} onClick={() => { onSave(o); onClose(); }}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", borderRadius: 10, cursor: "pointer",
                  background: active ? `${meta.color}12` : "#fafafa",
                  border: `1.5px solid ${active ? meta.color : "#e2e8f0"}`,
                  transition: "all 0.12s" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", background: c.bg, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, fontWeight: 800, color: "#fff" }}>{o.slice(0,2)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: active ? meta.color : "#334155" }}>{c.label}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>{o}</div>
                </div>
                {active && <div style={{ fontSize: 12, color: meta.color, fontWeight: 900 }}>✓</div>}
              </div>
            );
          })}
        </div>
        <button onClick={onClose} style={{ width: "100%", padding: "7px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#64748b" }}>Cerrar</button>
      </div>
    </div>
  );
}

// ── Confirm Delete Modal ─────────────────────────────────────────────────────
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000044", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onCancel}>
      <div style={{ background: "#fff", borderRadius: 12, padding: "24px 28px", width: 340, boxShadow: "0 12px 40px #0003", textAlign: "center" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 28, marginBottom: 10 }}>🗑️</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>¿Eliminar?</div>
        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>{message}</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={onCancel}
            style={{ padding: "8px 20px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            Cancelar
          </button>
          <button onClick={onConfirm}
            style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#EF4444", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700 }}>
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}


// ── Add User Modal ───────────────────────────────────────────────────────────
function AddUserModal({ usedColors, onAdd, onClose }) {
  const [initials, setInitials] = useState("");
  const [name, setName]         = useState("");
  const [color, setColor]       = useState(null);

  // Colors explicitly forbidden (status colors + already-used user colors)
  const STATUS_COLORS = new Set(["#22C55E","#f59e0b","#F59E0B","#EF4444","#ef4444","#94A3B8","#94a3b8"]);
  const available = USER_PALETTE.filter(c => !STATUS_COLORS.has(c) && !usedColors.has(c));
  // Auto-pick first available if none selected
  const pickedColor = color || available[0] || USER_PALETTE[0];

  const submit = () => {
    const key = initials.trim().toUpperCase().slice(0, 4);
    if (!key || !name.trim()) return;
    onAdd(key, { bg: pickedColor, label: name.trim() });
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000055", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 26, width: 380, boxShadow: "0 20px 60px #0003" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 18, display: "flex", alignItems: "center", gap: 6 }}><IcUser size={15} color="#1e293b" /> Agregar Usuario</div>

        <label style={lbl}>Iniciales (máx. 4 letras)</label>
        <input style={inp} value={initials} onChange={e => setInitials(e.target.value.toUpperCase().slice(0,4))}
          placeholder="ej. MPG" autoFocus onKeyDown={e => e.key === "Enter" && submit()} />

        <label style={lbl}>Nombre completo</label>
        <input style={inp} value={name} onChange={e => setName(e.target.value)}
          placeholder="ej. María González" onKeyDown={e => e.key === "Enter" && submit()} />

        <label style={lbl}>Color</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
          {USER_PALETTE.map(c => {
            const isForbidden = STATUS_COLORS.has(c) || usedColors.has(c);
            const isSelected = pickedColor === c;
            return (
              <div key={c} onClick={() => !isForbidden && setColor(c)}
                title={isForbidden ? "Ya en uso" : c}
                style={{ width: 28, height: 28, borderRadius: "50%", background: isForbidden ? "#f1f5f9" : c,
                  cursor: isForbidden ? "not-allowed" : "pointer", opacity: isForbidden ? 0.3 : 1,
                  border: isSelected ? `3px solid #0f172a` : "3px solid transparent",
                  boxShadow: isSelected ? "0 0 0 2px #fff inset" : "none",
                  transition: "all 0.15s" }} />
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
          <button onClick={submit} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#1D4ED8", color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Agregar</button>
        </div>
      </div>
    </div>
  );
}


// ── Edit User Modal ───────────────────────────────────────────────────────────
function EditUserModal({ userKey, userData, ownerColors, onSave, onClose }) {
  const [initials, setInitials] = useState(userKey);
  const [name, setName]         = useState(userData.label);
  const [color, setColor]       = useState(userData.bg);

  const usedColors = new Set(
    Object.entries(ownerColors)
      .filter(([k]) => k !== userKey)
      .map(([, v]) => v.bg)
  );

  const submit = () => {
    const newKey = initials.trim().toUpperCase().slice(0, 4);
    if (!newKey || !name.trim()) return;
    onSave(userKey, newKey, { bg: color, label: name.trim() });
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000055", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 26, width: 380, boxShadow: "0 20px 60px #0003" }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <div style={{ width: 40, height: 40, borderRadius: "50%", background: color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "#fff" }}>
            {initials.slice(0,2) || "?"}
          </div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b" }}>Editar Usuario</div>
        </div>

        <label style={lbl}>Iniciales (máx. 4 letras)</label>
        <input style={inp} value={initials} onChange={e => setInitials(e.target.value.toUpperCase().slice(0,4))}
          autoFocus onKeyDown={e => e.key === "Enter" && submit()} />

        <label style={lbl}>Nombre completo</label>
        <input style={inp} value={name} onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submit()} />

        <label style={lbl}>Color</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
          {USER_PALETTE.map(c => {
            const isTaken = usedColors.has(c);
            const isSelected = color === c;
            return (
              <div key={c} onClick={() => !isTaken && setColor(c)}
                title={isTaken ? "En uso por otro usuario" : ""}
                style={{ width: 30, height: 30, borderRadius: "50%", background: c,
                  cursor: isTaken ? "not-allowed" : "pointer", opacity: isTaken ? 0.25 : 1,
                  border: isSelected ? "3px solid #0f172a" : "3px solid transparent",
                  boxShadow: isSelected ? "0 0 0 2px #fff inset" : "none",
                  transition: "all 0.15s" }} />
            );
          })}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
          <button onClick={submit} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: color, color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

// ── Context Menu (3-dot) ──────────────────────────────────────────────────────
function ContextMenu({ menu, space, task, sub, onAddSubtask, onDelete, onClose }) {
  return (
    <>
      <div style={{ position: "fixed", inset: 0, zIndex: 400 }} onClick={onClose} />
      <div style={{
        position: "fixed", left: menu.x, top: menu.y, zIndex: 401,
        background: "#fff", borderRadius: 10, boxShadow: "0 8px 32px #0002",
        border: "1px solid #e2e8f0", minWidth: 180, overflow: "hidden"
      }}>
        {!sub && (
          <div onClick={() => { onAddSubtask(); onClose(); }}
            style={{ padding: "10px 16px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: "#334155" }}
            onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
            onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
            <span style={{ fontSize: 14 }}>↳</span> Agregar subtarea
          </div>
        )}
        <div onClick={() => { onDelete(); onClose(); }}
          style={{ padding: "10px 16px", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, color: "#EF4444" }}
          onMouseEnter={e => e.currentTarget.style.background = "#FEF2F2"}
          onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
          <span style={{ fontSize: 14 }}>✕</span> {sub ? "Eliminar subtarea" : "Eliminar tarea"}
        </div>
      </div>
    </>
  );
}

// ── Unified Add Modal (task or subtask) ──────────────────────────────────────
function AddModal({ config, onAdd, onClose, ownerColors }) {
  // config: { spaceId, taskId? (if adding subtask), type: "task"|"subtask", spaceColor }
  const [name, setName] = useState("");
  const [start, setStart] = useState("2026-03-01");
  const [end, setEnd] = useState("2026-04-01");
  const [owners, setOwners] = useState([]);
  const isSubtask = config.type === "subtask";

  const submit = () => {
    if (!name.trim()) return;
    onAdd({ ...config, item: { id: (isSubtask ? "st_" : "t_") + Date.now(), name: name.trim(), owners, start: new Date(start), end: new Date(end), progress: 0, ...(isSubtask ? {} : { subtasks: [] }) } });
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "#00000055", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#fff", borderRadius: 14, padding: 26, width: 420, boxShadow: "0 20px 60px #0003" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: config.spaceColor, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}>
            {isSubtask ? "↳" : "＋"}
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b" }}>{isSubtask ? "Nueva Subtarea" : "Nueva Tarea"}</div>
            {isSubtask && <div style={{ fontSize: 11, color: "#94a3b8" }}>Se agregará dentro de la tarea seleccionada</div>}
          </div>
        </div>
        <label style={lbl}>Nombre</label>
        <input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder={isSubtask ? "Nombre de la subtarea..." : "Nombre de la tarea..."} autoFocus
          onKeyDown={e => e.key === "Enter" && submit()} />
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}><label style={lbl}>Inicio</label><input style={inp} type="date" value={start} onChange={e => setStart(e.target.value)} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>Fin</label><input style={inp} type="date" value={end} onChange={e => setEnd(e.target.value)} /></div>
        </div>
        <label style={lbl}>Responsables</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 20 }}>
          {Object.entries(ownerColors).map(([o, c]) => (
            <div key={o} onClick={() => setOwners(p => p.includes(o) ? p.filter(x => x !== o) : [...p, o])}
              style={{ display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", userSelect: "none",
                background: owners.includes(o) ? c.bg : "#f1f5f9", color: owners.includes(o) ? "#fff" : "#64748b",
                border: `2px solid ${owners.includes(o) ? c.bg : "#e2e8f0"}` }}>
              <div style={{ width: 14, height: 14, borderRadius: "50%", background: owners.includes(o) ? "rgba(255,255,255,0.3)" : c.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:7, color:"#fff", fontWeight:800 }}>{o.slice(0,2)}</div>
              {o}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "8px 18px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", fontSize: 13 }}>Cancelar</button>
          <button onClick={submit} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: config.spaceColor, color: "#fff", cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
            {isSubtask ? "Agregar Subtarea" : "Agregar Tarea"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// Análisis de precios — burbujas comparativas Gaveti vs. retail (Costco / Sam's)
// Datos tomados de "Comparativa_Gaveti_vs_Costco_Sams2.xlsx".
// El PRIMER elemento de cada arreglo es Gaveti (objeto de estudio).
//   n  = nombre · g = gramaje total (g) · gp = g por empaque · p = precio total (USD)
//   comp = competidor directo
// ══════════════════════════════════════════════════════════════════════════
const PA_COSTCO = [
  { n: "Galletas de Gaveti Minis de chispas de chocolate", g: 840, gp: 30, p: 10.89, comp: false },
  { n: "Loacker Tortina Original", g: 504, gp: 21, p: 13.49, comp: false },
  { n: "Oreo Cookies Variety (60 ct, mix)", g: 1460, gp: 24.3, p: 14.99, comp: false },
  { n: "Mother's Circus Animal Cookies", g: 1100, gp: 27.5, p: 16.49, comp: true },
  { n: "Barnum's Animals Crackers", g: 840, gp: 28, p: 10.89, comp: true },
  { n: "Nabisco Variety Pack (40 ct)", g: 1120, gp: 28, p: 13.59, comp: false },
  { n: "Oreo Reese's / Double Stuf (40 ct)", g: 1160, gp: 29, p: 13.99, comp: false },
  { n: "Oreo Mini Variety (30 ct)", g: 1260, gp: 42, p: 11.99, comp: true },
  { n: "Lorna Doone Shortbread", g: 1290, gp: 43, p: 11.49, comp: false },
  { n: "Chips Ahoy! Original", g: 1050, gp: 43.8, p: 10.99, comp: false },
  { n: "Loacker Classic Hazelnut", g: 540, gp: 45, p: 8.79, comp: false },
  { n: "Keebler Chips Deluxe Minis (M&M's)", g: 1360, gp: 45.3, p: 12.49, comp: true },
  { n: "Nutter Butter Sandwich Cookies", g: 1340, gp: 55.8, p: 11.69, comp: false },
  { n: "Knott's Berry Farm Strawberry Shortbread", g: 2040, gp: 56.7, p: 11.49, comp: false },
  { n: "Knott's Berry Farm Raspberry Shortbread", g: 2040, gp: 56.7, p: 11.49, comp: false },
  { n: "Sweet Serenity Chocolate Chip", g: 1700, gp: 56.7, p: 13.79, comp: false },
  { n: "Famous Amos Chocolate Chip", g: 2380, gp: 56.7, p: 18.59, comp: false },
  { n: "Oreo Sandwich Cookies (classic, 30 ct)", g: 2040, gp: 68, p: 13.79, comp: false },
  { n: "Grandma's Variety Mix", g: 2300, gp: 69.7, p: 19.69, comp: false },
  { n: "Grandma's Vanilla Crème", g: 2200, gp: 91.7, p: 16.69, comp: false },
  { n: "Grandma's Mini Vanilla Crème", g: 2500, gp: 104.2, p: 19.99, comp: false },
  { n: "Nilla Wafers Nabisco", g: 850, gp: 425, p: 10.99, comp: false },
];
const PA_SAMS = [
  { n: "Galletas de Gaveti Blueberry en Sam's Club", g: 840, gp: 30, p: 10.78, comp: false },
  { n: "Nabisco Variety Pack (60 ct, mini)", g: 1320, gp: 22, p: 13.98, comp: false },
  { n: "Nabisco Classic Mix (40 ct)", g: 1120, gp: 28, p: 12.98, comp: false },
  { n: "Oreo Reese's / Double Stuf (40 ct)*", g: 1160, gp: 29, p: 9.72, comp: false },
  { n: "Oreo BTS / Double Stuf (40 ct)", g: 1160, gp: 29, p: 14.72, comp: false },
  { n: "Bauducco Wafer (20 ct)", g: 800, gp: 40, p: 6.48, comp: false },
  { n: "Oreo Mini Variety (30 ct)", g: 1260, gp: 42, p: 10.78, comp: true },
  { n: "Pillsbury Mini Chocolate Chip (28 ct)", g: 1190, gp: 42.5, p: 11.82, comp: true },
  { n: "Nutter Butter Sandwich (24 ct)", g: 1340, gp: 55.8, p: 10.84, comp: false },
  { n: "Keebler Fudge Stripes Minis (36 ct)", g: 2040, gp: 56.7, p: 13.98, comp: true },
  { n: "Member's Mark Animal Cookies (36 ct)", g: 2040, gp: 56.7, p: 9.98, comp: false },
  { n: "Knott's Berry Farm Strawberry Shortbread (36 ct)", g: 2040, gp: 56.7, p: 11.58, comp: false },
  { n: "Famous Amos Chocolate Chip (42 ct)", g: 2380, gp: 56.7, p: 18.38, comp: false },
  { n: "Nabisco Variety Pack (30 ct)*", g: 1740, gp: 58, p: 10.98, comp: false },
  { n: "Oreo Sandwich Cookies (30 ct, 6-cookie)", g: 2040, gp: 68, p: 13.48, comp: false },
  { n: "Oreo (12 Stay Fresh Packs / sleeves)", g: 1770, gp: 147.5, p: 10.98, comp: false },
  { n: "Nilla Wafers Nabisco (2 ct)", g: 850, gp: 425, p: 8.12, comp: false },
  { n: "Member's Mark Mini Choc Chip Cookie Thins (bolsa)", g: 454, gp: 454, p: 4.98, comp: true },
  { n: "Member's Mark Mini Strawberry Shortcake (bolsa)", g: 454, gp: 454, p: 6.48, comp: true },
  { n: "Member's Mark Animal Parade Mix (bolsa)", g: 566, gp: 566, p: 7.98, comp: false },
];

// Diámetro del círculo = K px por gramo, lineal y proporcional dentro de cada modo:
//   modo "empaque": un empaque de 100 g mide 30 px de diámetro; 200 g -> 60 px; etc.
//   modo "total":   escala más chica porque el gramaje total llega a miles de gramos.
const PA_BUBBLE_K = 0.30;    // px de diámetro por gramo de EMPAQUE
const PA_BUBBLE_K_G = 0.05;  // px de diámetro por gramo de GRAMAJE TOTAL

// ── Modelo comercial/financiero por caja (P&L). Todo se recalcula con el
//    precio arrastrable = "Suggested Retail". Todos los supuestos son
//    EDITABLES en la interfaz e INDEPENDIENTES por gráfico (cada club/canal
//    aparte) — Retailer Margin y Trade Spend varían mucho entre clubes
//    (Costco/Sam's, ~15%, salvo mejor condición negociada) y retail
//    tradicional (~40% margen, ~25% trade spend). Todos % del Net Sales
//    salvo Retailer Margin (% del Suggested Retail), Trade Spend (% del
//    List Price) y EXW/Freight-Import (monto fijo / % del EXW). ──
const PA_ASSUMPTIONS_DEFAULT = {
  exw: 4.05, freightImport: 0.09, broker: 0.03, marketing: 0.09, warehouseFreightOut: 0.13,
  retailerMargin: 0.15, tradeSpend: 0.15, sgaParte: 0.05, sgaGaveti: 0.05,
};

// Nombre corto de marca para etiquetar cada burbuja (identificable de un vistazo).
function paBrand(n) {
  const ov = ["Member's", "Nutter Butter", "Famous Amos", "Lorna Doone", "Sweet Serenity",
    "Chips Ahoy!", "Knott's", "Grandma's", "Mother's", "Barnum's", "Nilla", "Gaveti"];
  for (const k of ov) { if (n.includes(k)) return k; }
  return n.split(" ")[0];
}

// Campo numérico robusto para el P&L: conserva EXACTAMENTE lo que se teclea
// (permite decimales como 4.05 y borrar para reescribir) y va confirmando el
// número al modelo conforme se escribe. Corrige el bug de que "no se guardaban".
function PnlNumInput({ value, isPct, onChange, accent, width = 56 }) {
  const toDisp = (v) => (isPct ? +(v * 100).toFixed(2) : v);
  const [txt, setTxt] = useState(() => String(toDisp(value)));
  const lastModel = useRef(value);
  useEffect(() => {
    if (value !== lastModel.current) { lastModel.current = value; setTxt(String(toDisp(value))); }
  }, [value]); // sincroniza solo si el valor cambia desde fuera (p. ej. reset)
  const commit = (s) => {
    const n = parseFloat(s.replace(",", "."));
    if (isNaN(n)) return;
    let clean = Math.max(0, n); if (isPct) clean = Math.min(100, clean);
    const model = isPct ? clean / 100 : clean;
    lastModel.current = model; onChange(model);
  };
  const handleChange = (e) => {
    const s = e.target.value;
    setTxt(s);                                   // muestra lo tecleado tal cual
    if (s === "" || s === "." || s === "," || s === "-") return; // entrada parcial: espera
    commit(s);
  };
  const handleBlur = () => {
    let n = parseFloat(txt.replace(",", ".")); if (isNaN(n)) n = 0;
    let clean = Math.max(0, n); if (isPct) clean = Math.min(100, clean);
    setTxt(String(clean));
    const model = isPct ? clean / 100 : clean;
    lastModel.current = model; onChange(model);
  };
  return (
    <input type="text" inputMode="decimal" value={txt}
      onChange={handleChange} onBlur={handleBlur} onFocus={(e) => e.target.select()}
      style={{ width, fontSize: 12, fontWeight: 700, textAlign: "right", padding: "2px 5px",
        borderRadius: 6, border: `1.5px solid ${accent}55`, outline: "none",
        color: "#0f172a", fontVariantNumeric: "tabular-nums", background: "#fff" }} />
  );
}

// Monto USD editable para una línea del P&L cuyo modelo de fondo es un % (de
// Net Sales). Editar el % ya existía; esto permite lo inverso — teclear el
// monto absoluto y que el % se despeje contra `base` (Net Sales). Ambos lados
// escriben al mismo campo de `assump`, no hay estado paralelo que desincronizar.
function PnlUsdSyncInput({ pct, base, onChangePct, accent, width = 64, prefix = "$" }) {
  const usdValue = base ? pct * base : 0;
  const [txt, setTxt] = useState(() => usdValue.toFixed(2));
  const lastModel = useRef(usdValue);
  useEffect(() => {
    if (Math.abs(usdValue - lastModel.current) > 0.005) { lastModel.current = usdValue; setTxt(usdValue.toFixed(2)); }
  }, [usdValue]);
  const apply = (n) => {
    if (!base) return;
    const clean = Math.max(0, n);
    lastModel.current = clean;
    onChangePct(clean / base);
  };
  const handleChange = (e) => {
    const s = e.target.value;
    setTxt(s);
    if (s === "" || s === "." || s === "," || s === "-") return;
    const n = parseFloat(s.replace(",", "."));
    if (!isNaN(n)) apply(n);
  };
  const handleBlur = () => {
    let n = parseFloat(txt.replace(",", ".")); if (isNaN(n)) n = 0;
    const clean = Math.max(0, n);
    setTxt(clean.toFixed(2));
    apply(clean);
  };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 2, justifySelf: "end" }}>
      <span style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>{prefix}</span>
      <input type="text" inputMode="decimal" value={txt} disabled={!base}
        onChange={handleChange} onBlur={handleBlur} onFocus={(e) => e.target.select()}
        style={{ width, fontSize: 12.5, fontWeight: 700, textAlign: "right", padding: "2px 5px",
          borderRadius: 6, border: `1.5px solid ${accent}55`, outline: "none",
          color: "#0f172a", fontVariantNumeric: "tabular-nums", background: base ? "#fff" : "#f1f5f9" }} />
    </span>
  );
}

function PriceBubbleChart({ store, rows, accent, assump, setAssump }) {
  const base = rows[0]; // Gaveti
  const others = rows.slice(1);
  const [gav, setGav] = useState({ p: base.p, g: base.g });
  const [hover, setHover] = useState(null); // { i, cx, cy }  (i = -1 => Gaveti)
  const [sizeBy, setSizeBy] = useState("empaque"); // "empaque" | "total"
  // `assump` (supuestos del P&L) llega por props desde PriceAnalysisTab y se
  // persiste en Firebase; cada club conserva su propio objeto independiente.
  const svgRef = useRef(null);
  const dragRef = useRef(false);

  // Lienzo (coordenadas internas; el SVG escala al contenedor vía viewBox)
  const W = 820, H = 580, M = { l: 68, r: 24, t: 22, b: 60 };
  const pw = W - M.l - M.r, ph = H - M.t - M.b;

  // Dominios fijos a partir de los datos base (estables al arrastrar)
  const xmax = Math.max(Math.ceil(Math.max(...rows.map(r => r.p)) * 1.15), 1);
  const ymax = Math.max(Math.ceil((Math.max(...rows.map(r => r.g)) * 1.12) / 100) * 100, 1);
  const sx = (p) => M.l + (p / xmax) * pw;
  const sy = (g) => M.t + ph - (g / ymax) * ph;

  // Radio según el modo de tamaño de burbuja
  const kSize = sizeBy === "empaque" ? PA_BUBBLE_K : PA_BUBBLE_K_G;
  const rVal = (v) => (v * kSize) / 2;
  const rOther = (o) => rVal(sizeBy === "empaque" ? o.gp : o.g);
  const rGav = () => rVal(sizeBy === "empaque" ? base.gp : gav.g);

  // Relación de valor de Gaveti (g por dólar) -> recta de "igual $/kg"
  const ratio = gav.g / gav.p;                 // g por USD
  const gavPk = (gav.p * 1000) / gav.g;        // USD por kg
  const cheaper = others.filter(o => (o.p * 1000) / o.g < gavPk).length; // rinden más por kg
  const pricier = others.length - cheaper;

  // ── P&L por caja, alimentado por el precio arrastrable (gav.p) ──
  const sr = gav.p;                                   // Suggested Retail (arrastrable)
  const retailerMargin = sr * assump.retailerMargin;
  const listPrice = sr - retailerMargin;
  const tradeSpend = listPrice * assump.tradeSpend;
  const netSales = listPrice - tradeSpend;
  const freightCost = netSales * assump.freightImport;  // % del Net Sales
  const deliveredCogs = assump.exw + freightCost;       // EXW (monto fijo) + Freight/Import
  const grossMargin = netSales - deliveredCogs;
  const broker = netSales * assump.broker;
  const marketing = netSales * assump.marketing;
  const whFreightOut = netSales * assump.warehouseFreightOut;
  const contribution = grossMargin - broker - marketing - whFreightOut;
  const sgaParte = netSales * assump.sgaParte;
  const sgaGaveti = netSales * assump.sgaGaveti;
  const ebitda = contribution - sgaParte - sgaGaveti;
  const pctNet = (v) => (netSales ? (v / netSales) * 100 : 0);
  const money = (v) => (v < 0 ? "−$" : "$") + Math.abs(v).toFixed(2);
  const pnlRows = [
    { label: "Suggested Retail (arrastrable)", val: sr, kind: "input" },
    { label: "Retailer Margin", val: -retailerMargin, kind: "ded", editPct: "retailerMargin", base: sr },
    { label: "List Price", val: listPrice, kind: "sub" },
    { label: "Trade Spend", val: -tradeSpend, kind: "ded", editPct: "tradeSpend", base: listPrice },
    { label: "Net Sales", val: netSales, kind: "sub", pct: 100 },
    { label: "Delivered COGS (est.)", val: -deliveredCogs, kind: "ded" },
    { label: "Target Price (EXW)", val: assump.exw, kind: "detail", editUsd: true },
    { label: "Freight in & Import Cost", val: freightCost, kind: "detail", editPct: "freightImport", base: netSales },
    { label: "Gross Margin", val: grossMargin, kind: "sub", pct: pctNet(grossMargin) },
    { label: "Broker clubes", val: -broker, kind: "ded", editPct: "broker", base: netSales },
    { label: "Marketing", val: -marketing, kind: "ded", editPct: "marketing", base: netSales },
    { label: "Warehouse & Freight Out", val: -whFreightOut, kind: "ded", editPct: "warehouseFreightOut", base: netSales },
    { label: "Contribution Margin", val: contribution, kind: "sub", pct: pctNet(contribution) },
    { label: "SG&A Parte", val: -sgaParte, kind: "ded", editPct: "sgaParte", base: netSales },
    { label: "SG&A Gaveti", val: -sgaGaveti, kind: "ded", editPct: "sgaGaveti", base: netSales },
    { label: "EBITDA", val: ebitda, kind: "total", pct: pctNet(ebitda) },
  ];

  // Ticks
  const xTicks = 6, yTicks = 6;
  const xt = Array.from({ length: xTicks + 1 }, (_, i) => (xmax * i) / xTicks);
  const yt = Array.from({ length: yTicks + 1 }, (_, i) => (ymax * i) / yTicks);

  // Orden de dibujo: burbujas grandes atrás (según el modo activo)
  const drawList = others
    .map((o, i) => ({ ...o, i }))
    .sort((a, b) => (sizeBy === "empaque" ? b.gp - a.gp : b.g - a.g));

  // Punto donde la recta de valor corta el borde del área de graficado
  const rayEndG = Math.min(ymax, ratio * xmax);
  const rayEndP = rayEndG < ymax ? xmax : ymax / ratio;

  // ── Arrastre del punto Gaveti (funciona con mouse y touch) ──
  const toData = (clientX, clientY) => {
    const rect = svgRef.current.getBoundingClientRect();
    const vx = ((clientX - rect.left) / rect.width) * W;
    const vy = ((clientY - rect.top) / rect.height) * H;
    const p = Math.max(0, Math.min(xmax, ((vx - M.l) / pw) * xmax));
    const g = Math.max(0, Math.min(ymax, ((M.t + ph - vy) / ph) * ymax));
    return { p: Math.round(p * 100) / 100, g: Math.round(g / 5) * 5 };
  };
  const onDown = (e) => { dragRef.current = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch {} };
  const onMove = (e) => { if (dragRef.current) { const d = toData(e.clientX, e.clientY); setGav(d); } };
  const onUp = () => { dragRef.current = false; };

  const gx = sx(gav.p), gy = sy(gav.g);
  const bubbleFill = "#64748B";
  const compColor = "#F59E0B";
  const SALMON = "#E07856";
  const fmtG = (v) => (v >= 1000 ? (v / 1000).toFixed(v % 1000 === 0 ? 0 : 1) + " kg" : Math.round(v) + " g");

  return (
    <div style={{ marginBottom: 34 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 2 }}>
        <div style={{ width: 10, height: 10, borderRadius: 3, background: accent }} />
        <div style={{ fontSize: 15.5, fontWeight: 800, color: "#0f172a" }}>{store}</div>
      </div>
      <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>
        Cada burbuja es un producto · eje X = precio · eje Y = gramaje total · <b>diámetro ∝ {sizeBy === "empaque" ? "gramos por empaque" : "gramaje total"}</b>. Arrastra el punto de {BRAND_NAME} o usa los deslizadores.
      </div>

      {/* Toggle: tamaño de burbuja por */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 12px", flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>Tamaño de burbuja por:</span>
        <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 9, padding: 3 }}>
          {[["empaque", "Tamaño de empaque"], ["total", "Gramaje total"]].map(([k, lab]) => (
            <button key={k} onClick={() => setSizeBy(k)}
              style={{
                padding: "5px 12px", borderRadius: 6, border: "none", cursor: "pointer",
                fontSize: 11.5, fontWeight: 700, transition: "all .12s",
                background: sizeBy === k ? "#fff" : "transparent",
                color: sizeBy === k ? accent : "#94a3b8",
                boxShadow: sizeBy === k ? "0 1px 2px rgba(0,0,0,.10)" : "none"
              }}>{lab}</button>
          ))}
        </div>
      </div>

      {/* Lienzo del chart */}
      <div style={{ position: "relative", width: "100%", border: "1px solid #e2e8f0", borderRadius: 14, background: "#fff", overflow: "hidden" }}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", touchAction: "none" }}
          onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
          {/* Rejilla */}
          {xt.map((t, i) => (
            <line key={"gx" + i} x1={sx(t)} y1={M.t} x2={sx(t)} y2={M.t + ph} stroke="#f1f5f9" strokeWidth="1" />
          ))}
          {yt.map((t, i) => (
            <line key={"gy" + i} x1={M.l} y1={sy(t)} x2={M.l + pw} y2={sy(t)} stroke="#f1f5f9" strokeWidth="1" />
          ))}
          {/* Ejes */}
          <line x1={M.l} y1={M.t} x2={M.l} y2={M.t + ph} stroke="#cbd5e1" strokeWidth="1.5" />
          <line x1={M.l} y1={M.t + ph} x2={M.l + pw} y2={M.t + ph} stroke="#cbd5e1" strokeWidth="1.5" />
          {/* Ticks X */}
          {xt.map((t, i) => (
            <text key={"tx" + i} x={sx(t)} y={M.t + ph + 20} textAnchor="middle" fontSize="11" fill="#94a3b8">${t.toFixed(0)}</text>
          ))}
          {/* Ticks Y */}
          {yt.map((t, i) => (
            <text key={"ty" + i} x={M.l - 10} y={sy(t) + 4} textAnchor="end" fontSize="11" fill="#94a3b8">{fmtG(t)}</text>
          ))}
          {/* Títulos de ejes */}
          <text x={M.l + pw / 2} y={H - 14} textAnchor="middle" fontSize="12.5" fontWeight="700" fill="#475569">Precio del paquete (USD)</text>
          <text x={16} y={M.t + ph / 2} textAnchor="middle" fontSize="12.5" fontWeight="700" fill="#475569" transform={`rotate(-90 16 ${M.t + ph / 2})`}>Gramaje total (g)</text>

          {/* Recta de igual valor ($/kg) que Gaveti */}
          <line x1={sx(0)} y1={sy(0)} x2={sx(rayEndP)} y2={sy(rayEndG)} stroke={accent} strokeWidth="1.4" strokeDasharray="6 5" opacity="0.55" />

          {/* Burbujas (grandes atrás) + etiqueta de marca */}
          {drawList.map((o) => {
            const cx = sx(o.p), cy = sy(o.g), r = rOther(o);
            const col = o.comp ? compColor : bubbleFill;
            const isHover = hover && hover.i === o.i;
            const inside = r >= 20;
            let ly = inside ? cy : cy + r + 8;
            if (!inside && ly > M.t + ph - 2) ly = cy - r - 4;
            return (
              <g key={o.i} style={{ cursor: "pointer" }}
                onMouseEnter={() => setHover({ i: o.i, cx, cy })}
                onMouseLeave={() => setHover(null)}>
                <circle cx={cx} cy={cy} r={r} fill={col} fillOpacity={o.comp ? 0.22 : 0.16} stroke={col} strokeWidth={o.comp ? 2 : 1.3} strokeOpacity={o.comp ? 0.9 : 0.6} />
                <circle cx={cx} cy={cy} r={2.2} fill={col} />
                {/* Nombre de marca: se oculta cuando esta burbuja tiene el cursor (aparece el tooltip) */}
                {!isHover && (
                  <text x={cx} y={ly} textAnchor="middle" dominantBaseline={inside ? "central" : "auto"}
                    fontSize={inside ? 9.5 : 8.5} fontWeight={o.comp ? 700 : 600}
                    fill={inside ? (o.comp ? "#7c2d12" : "#334155") : (o.comp ? "#b45309" : "#94a3b8")}
                    opacity={inside ? 0.92 : 0.82} pointerEvents="none" style={{ userSelect: "none" }}>
                    {paBrand(o.n)}
                  </text>
                )}
              </g>
            );
          })}

          {/* Guías punteadas de Gaveti a los ejes */}
          <line x1={gx} y1={gy} x2={gx} y2={M.t + ph} stroke={SALMON} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />
          <line x1={gx} y1={gy} x2={M.l} y2={gy} stroke={SALMON} strokeWidth="1" strokeDasharray="3 3" opacity="0.5" />

          {/* Burbuja proporcional de Gaveti (según el modo) */}
          <circle cx={gx} cy={gy} r={rGav()} fill={SALMON} fillOpacity="0.18" stroke={SALMON} strokeWidth="1.2" />

          {/* Marcador arrastrable de Gaveti (siempre visible) */}
          <g style={{ cursor: "grab" }} onPointerDown={onDown}
            onMouseEnter={() => setHover({ i: -1, cx: gx, cy: gy })} onMouseLeave={() => setHover(null)}>
            <circle cx={gx} cy={gy} r={13} fill={SALMON} fillOpacity="0.15" />
            <circle cx={gx} cy={gy} r={7} fill={SALMON} stroke="#fff" strokeWidth="2.5" />
          </g>
          {/* Etiqueta de Gaveti */}
          <g transform={`translate(${Math.min(gx + 12, W - 150)}, ${Math.max(gy - 30, M.t + 4)})`} pointerEvents="none">
            <rect x="0" y="0" width="138" height="24" rx="6" fill={SALMON} />
            <text x="9" y="16" fontSize="11.5" fontWeight="800" fill="#fff">{BRAND_NAME} · ${gav.p.toFixed(2)} · {fmtG(gav.g)}</text>
          </g>
        </svg>

        {/* Tooltip */}
        {hover && (() => {
          const d = hover.i === -1 ? { n: `Galletas de ${BRAND_NAME}`, p: gav.p, g: gav.g, gp: base.gp, comp: false } : others[hover.i];
          const pk = (d.p * 1000) / d.g;
          return (
            <div style={{ position: "absolute", left: `${(hover.cx / W) * 100}%`, top: `${(hover.cy / H) * 100}%`, transform: "translate(-50%, calc(-100% - 14px))", pointerEvents: "none", zIndex: 5 }}>
              <div style={{ background: "#0f172a", color: "#fff", borderRadius: 8, padding: "8px 10px", fontSize: 11.5, lineHeight: 1.45, minWidth: 150, maxWidth: 230, boxShadow: "0 6px 18px rgba(0,0,0,.18)" }}>
                <div style={{ fontWeight: 800, marginBottom: 3, color: hover.i === -1 ? "#FCA98A" : (d.comp ? "#FBBF24" : "#e2e8f0") }}>{d.n}{d.comp ? " · competidor" : ""}</div>
                <div>Precio: <b>${d.p.toFixed(2)}</b></div>
                <div>Gramaje: <b>{Math.round(d.g)} g</b> · empaque <b>{d.gp} g</b></div>
                <div>Precio/kg: <b>${pk.toFixed(2)}</b></div>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Leyenda */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", margin: "10px 2px 12px", fontSize: 11.5, color: "#64748b" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: SALMON }} />{BRAND_NAME} (arrastrable)</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: "#F59E0B", opacity: 0.5, border: "2px solid #F59E0B" }} />Competidor directo</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 11, height: 11, borderRadius: "50%", background: "#64748B", opacity: 0.4 }} />Otro producto</span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 18, height: 0, borderTop: `2px dashed ${accent}` }} />Igual $/kg que {BRAND_NAME}</span>
      </div>

      {/* Controles de marca */}
      <div style={{ background: "#FDF3EE", border: `1px solid ${SALMON}44`, borderRadius: 12, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#9A3412" }}>Ajusta la galleta {BRAND_NAME}</div>
          <button onClick={() => setGav({ p: base.p, g: base.g })}
            style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${SALMON}`, background: "#fff", color: SALMON, fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
            Restablecer (${base.p.toFixed(2)} · {base.g} g)
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Precio (USD)</span>
              <span style={{ color: "#9A3412", fontWeight: 800 }}>${gav.p.toFixed(2)}</span>
            </div>
            <input type="range" min={0} max={xmax} step={0.05} value={gav.p}
              onChange={(e) => setGav(v => ({ ...v, p: parseFloat(e.target.value) }))}
              style={{ width: "100%", accentColor: SALMON }} />
          </div>
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Gramaje total (g)</span>
              <span style={{ color: "#9A3412", fontWeight: 800 }}>{Math.round(gav.g)} g</span>
            </div>
            <input type="range" min={0} max={ymax} step={5} value={gav.g}
              onChange={(e) => setGav(v => ({ ...v, g: parseFloat(e.target.value) }))}
              style={{ width: "100%", accentColor: SALMON }} />
          </div>
        </div>

        {/* Veredicto de valor en vivo */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 }}>
          <div style={{ flex: "1 1 150px", background: "#fff", borderRadius: 10, padding: "10px 12px", border: "1px solid #eadbd2" }}>
            <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Precio por kg de {BRAND_NAME}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#9A3412" }}>${gavPk.toFixed(2)}<span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}> /kg</span></div>
          </div>
          <div style={{ flex: "1 1 150px", background: "#fff", borderRadius: 10, padding: "10px 12px", border: "1px solid #eadbd2" }}>
            <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Rinden más/kg</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: cheaper > pricier ? "#DC2626" : "#16A34A" }}>{cheaper}<span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}> de {others.length}</span></div>
          </div>
          <div style={{ flex: "1 1 150px", background: "#fff", borderRadius: 10, padding: "10px 12px", border: "1px solid #eadbd2" }}>
            <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>Más caros/kg que {BRAND_NAME}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#16A34A" }}>{pricier}<span style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8" }}> de {others.length}</span></div>
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: "#78716c", marginTop: 10 }}>
          Los productos <b>por encima</b> de la línea punteada rinden más gramos por dólar que {BRAND_NAME} (mejor valor para el cliente); los que están <b>por debajo</b>, menos.
        </div>
      </div>

      {/* Panorama comercial y financiero (se recalcula con el precio arrastrable) */}
      <div style={{ marginTop: 14, border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ background: "#0f172a", color: "#fff", padding: "9px 14px", display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 6 }}>
          <span style={{ fontSize: 12.5, fontWeight: 800 }}>Panorama comercial y financiero</span>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>se recalcula con el precio arrastrable</span>
        </div>
        <div>
          {pnlRows.map((r, i) => {
            const isSub = r.kind === "sub", isTot = r.kind === "total", isInput = r.kind === "input";
            const isDed = r.kind === "ded", isDetail = r.kind === "detail";
            const bg = isTot ? (r.val >= 0 ? "#ECFDF5" : "#FEF2F2") : isSub ? "#f8fafc" : isInput ? "#FDF3EE" : "#fff";
            const labelColor = isDed ? "#94a3b8" : isDetail ? "#64748b" : "#0f172a";
            const valColor = isTot ? (r.val >= 0 ? "#047857" : "#b91c1c") : isDed ? "#b91c1c" : isDetail ? "#64748b" : "#0f172a";
            const weight = isTot ? 900 : (isSub || isInput) ? 800 : isDetail ? 500 : 600;
            return (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "1fr auto 56px", alignItems: "center", gap: 8,
                padding: isTot ? "11px 14px" : isDetail ? "5px 14px" : "7px 14px", background: bg,
                borderTop: (isSub || isTot) ? "1px solid #e2e8f0" : "1px solid #f1f5f9",
              }}>
                {/* Etiqueta (con input % editable para SG&A) */}
                <span style={{ fontSize: isTot ? 13 : isDetail ? 11.5 : 12, fontWeight: weight, color: labelColor, paddingLeft: isDed ? 10 : isDetail ? 22 : 0, display: "inline-flex", alignItems: "center", gap: 5 }}>
                  {r.label}
                  {r.editPct && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                      (<PnlNumInput value={assump[r.editPct]} isPct accent={accent} width={46}
                        onChange={(m) => setAssump(a => ({ ...a, [r.editPct]: m }))} />%)
                    </span>
                  )}
                </span>
                {/* Valor: input $ para EXW (sin contraparte %), input $↔% sincronizado
                    para las líneas con editPct, texto plano para el resto */}
                {r.editUsd ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 2, justifySelf: "end", color: "#64748b", fontSize: 12, fontWeight: 700 }}>
                    $<PnlNumInput value={assump.exw} accent={accent} width={58}
                      onChange={(m) => setAssump(a => ({ ...a, exw: m }))} />
                  </span>
                ) : r.editPct ? (
                  <PnlUsdSyncInput pct={assump[r.editPct]} base={r.base} accent={accent} width={58}
                    prefix={isDed ? "−$" : "$"}
                    onChangePct={(p) => setAssump(a => ({ ...a, [r.editPct]: p }))} />
                ) : (
                  <span style={{ fontSize: isTot ? 15 : 12.5, fontWeight: weight, color: valColor, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{money(r.val)}</span>
                )}
                <span style={{ fontSize: 11.5, fontWeight: 700, color: r.pct == null ? "transparent" : "#64748b", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.pct == null ? "" : r.pct.toFixed(1) + "%"}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
        Bases de cálculo: Retailer Margin = % del Suggested Retail · Trade Spend = % del List Price · <b>todo lo demás (Freight/Import, Broker, Marketing, Warehouse, SG&amp;A) = % del Net Sales</b> (Net Sales = 100%). EXW es un monto fijo en USD. Editables por club/canal (independientes) y <b>se guardan automáticamente</b>: Retailer Margin, Trade Spend, EXW, Freight/Import, Broker, Marketing, Warehouse &amp; Freight Out, SG&amp;A Parte y {BRAND_NAME}. En todos salvo EXW puedes teclear el % o el monto en USD — el otro se recalcula solo. Retailer Margin y Trade Spend varían mucho por canal: clubes suele rondar 15% de margen salvo mejor condición negociada, retail tradicional ronda 40%. El 10% de SG&amp;A total hoy se reparte 5%/5% entre Parte y {BRAND_NAME} de forma provisional — todavía no se define si corre completo a cargo de {BRAND_NAME} o se sigue dividiendo.
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// EXPOSICIÓN CAMBIARIA Y ARANCELARIA
//
// Modelo, por caja:
//   Base FOB      = EXW + 50% del flete/importación (proxy de valor en aduana)
//   Arancel       = Base FOB × tasa
//   P&L en USD    = ... − EXW − Arancel − ...  →  EBITDA operación EE.UU. (USD)
//   Planta Gaveti = EXW × TC − costo de producción (MXN)  →  Utilidad Gaveti (MXN)
//   Consolidado   = (EBITDA + EXW) × TC − costo de producción (MXN)
//
// EXW, flete, broker y SG&A se leen de gantt/price_pnl (el P&L de arriba).
// Escenario y supuestos propios: gantt/fx_exposure — compartidos con el equipo.
// ══════════════════════════════════════════════════════════════════════════

const FX_MIN = 15.0, FX_MAX = 20.0;

// Proporción del flete/importación que se considera parte del valor en aduana.
const FX_FOB_FREIGHT_SHARE = 0.5;

const FX_DEFAULT = {
  fxRef: 16.92,       // tipo de cambio de referencia
  fxBudget: 17.30,    // tipo de cambio con el que se presupuestó el proyecto
  fx: 16.92,          // escenario activo (compartido con el equipo)
  tariff: 0,          // escenario activo
  club: "costco",
  costco: { retail: 10.89, costMxn: 58.0, cajasAnio: 20000 },
  sams:   { retail: 10.78, costMxn: 58.0, cajasAnio: 20000 },
};

const FX_TARIFF_PRESETS = [
  { v: 0,    lab: "0%",  sub: "T-MEC" },
  { v: 0.10, lab: "10%", sub: "Sec. 301" },
  { v: 0.15, lab: "15%", sub: "escenario" },
  { v: 0.25, lab: "25%", sub: "adverso" },
];

const FX_CLUBS = [
  { key: "costco", store: "Costco — Texas",     accent: "#1D4ED8" },
  { key: "sams",   store: "Sam's Club — Texas", accent: "#7C3AED" },
];

function fxMergeCfg(d) {
  return {
    fxRef:    typeof d.fxRef === "number" ? d.fxRef : FX_DEFAULT.fxRef,
    fxBudget: typeof d.fxBudget === "number" ? d.fxBudget : FX_DEFAULT.fxBudget,
    fx:       typeof d.fx === "number" ? d.fx : FX_DEFAULT.fx,
    tariff:   typeof d.tariff === "number" ? d.tariff : FX_DEFAULT.tariff,
    club:     d.club === "sams" ? "sams" : "costco",
    costco:   { ...FX_DEFAULT.costco, ...(d.costco || {}) },
    sams:     { ...FX_DEFAULT.sams, ...(d.sams || {}) },
  };
}

// ── Modelo puro: una sola función alimenta tarjetas, gráficas, matriz y P&L ──
function fxModel({ retail, assump, tariff, fx, costMxn }) {
  const exw            = assump.exw;
  const retailerMargin = retail * assump.retailerMargin;
  const listPrice      = retail - retailerMargin;
  const tradeSpend     = listPrice * assump.tradeSpend;
  const netSales       = listPrice - tradeSpend;
  const freightCost    = netSales * assump.freightImport;
  const fobBase        = exw + freightCost * FX_FOB_FREIGHT_SHARE;   // valor en aduana estimado
  const arancel        = fobBase * tariff;
  const deliveredCogs  = exw + arancel + freightCost;
  const grossMargin    = netSales - deliveredCogs;
  const broker         = netSales * assump.broker;
  const marketing      = netSales * assump.marketing;
  const whOut          = netSales * assump.warehouseFreightOut;
  const contribution   = grossMargin - broker - marketing - whOut;
  const sgaParte       = netSales * assump.sgaParte;
  const sgaGaveti      = netSales * assump.sgaGaveti;
  const ebitda         = contribution - sgaParte - sgaGaveti;   // USD/caja — operación EE.UU.

  // Excedente en USD antes de pagarle a la planta y antes de arancel
  const usdPreExw      = ebitda + exw;            // después de arancel
  const usdPreArancel  = usdPreExw + arancel;     // antes de arancel

  const ingresoGavMxn  = exw * fx;
  const utilGavMxn     = ingresoGavMxn - costMxn;      // MXN/caja — planta
  const consolidadoMxn = usdPreExw * fx - costMxn;     // MXN/caja — consolidado

  // Equilibrios
  const fxBeGaveti  = exw > 0 ? costMxn / exw : null;
  const fxBeConsol  = usdPreExw > 0 ? costMxn / usdPreExw : null;
  const ebitdaSinAr = ebitda + arancel;
  const tariffBeUsd = fobBase > 0 ? ebitdaSinAr / fobBase : null;                       // EBITDA USD = 0
  const tariffBeCon = fobBase > 0 && fx > 0
    ? (usdPreArancel - costMxn / fx) / fobBase : null;                                  // consolidado MXN = 0

  return {
    exw, fobBase, retailerMargin, listPrice, tradeSpend, netSales, freightCost, arancel,
    deliveredCogs, grossMargin, broker, marketing, whOut, contribution,
    sgaParte, sgaGaveti, ebitda, usdPreExw, usdPreArancel,
    ingresoGavMxn, utilGavMxn, consolidadoMxn,
    fxBeGaveti, fxBeConsol, tariffBeUsd, tariffBeCon, ebitdaSinAr,
    utilGavPct: ingresoGavMxn ? (utilGavMxn / ingresoGavMxn) * 100 : 0,
    ebitdaPct: netSales ? (ebitda / netSales) * 100 : 0,
  };
}

const fxUsd  = (v) => (v < 0 ? "−$" : "$") + Math.abs(v).toFixed(2);
const fxMxn  = (v) => (v < 0 ? "−$" : "$") + Math.abs(v).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fxMxn0 = (v) => (v < 0 ? "−$" : "$") + Math.abs(Math.round(v)).toLocaleString("es-MX");
const fxPctT = (v) => (v == null || !isFinite(v) ? "—" : (v * 100).toFixed(1) + "%");

function FxSlider({ label, value, min, max, step, onChange, accent, format, hint }) {
  return (
    <div style={{ flex: "1 1 260px", minWidth: 240 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 2 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#475569" }}>{label}</span>
        <span style={{ fontSize: 20, fontWeight: 800, color: accent, fontVariantNumeric: "tabular-nums" }}>{format(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        style={{ width: "100%", accentColor: accent, cursor: "pointer", height: 22 }} />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10.5, color: "#cbd5e1", marginTop: -2 }}>
        <span>{format(min)}</span><span>{format(max)}</span>
      </div>
      {hint && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

function FxStat({ label, value, sub, color, bg, wide, big }) {
  return (
    <div style={{
      flex: wide ? "1 1 210px" : "1 1 150px", minWidth: 140,
      background: bg || "#f8fafc", borderRadius: 12, padding: big ? "16px 18px" : "11px 13px",
      border: `${big ? 2 : 1}px solid ${color}${big ? "55" : "22"}`,
    }}>
      <div style={{ fontSize: big ? 12 : 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: big ? 5 : 3 }}>{label}</div>
      <div style={{ fontSize: big ? 34 : 21, fontWeight: 800, color, fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: big ? 13 : 11, color: "#94a3b8", marginTop: big ? 5 : 3 }}>{sub}</div>}
    </div>
  );
}

// ── Utilidad en MXN por caja según el tipo de cambio. Arrastrable. ──
function FxMarginChart({ m, fx, fxBudget, accent, narrow, onFx }) {
  const W = 760, H = narrow ? 300 : 320, M = { l: 66, r: 16, t: 16, b: 42 };
  const pw = W - M.l - M.r, ph = H - M.t - M.b;
  const svgRef = useRef(null);
  const dragRef = useRef(false);
  const [hoverFx, setHoverFx] = useState(null);

  const costMxn = m.exw * fx - m.utilGavMxn;
  const gavAt = (x) => m.exw * x - costMxn;
  const conAt = (x) => m.usdPreExw * x - costMxn;

  const cands = [FX_MIN, FX_MAX, fx, fxBudget];
  [m.fxBeGaveti, m.fxBeConsol].forEach((v) => { if (v != null && isFinite(v) && v > 0) cands.push(v); });
  const x0 = Math.max(6, Math.floor((Math.min(...cands) - 0.4) * 2) / 2);
  const x1 = Math.min(30, Math.ceil((Math.max(...cands) + 0.4) * 2) / 2);

  const ys = [gavAt(x0), gavAt(x1), conAt(x0), conAt(x1), 0];
  let ymin = Math.min(...ys), ymax = Math.max(...ys);
  const pad = Math.max((ymax - ymin) * 0.12, 5);
  ymin -= pad; ymax += pad;

  const sx = (x) => M.l + ((x - x0) / (x1 - x0)) * pw;
  const sy = (y) => M.t + ph - ((y - ymin) / (ymax - ymin)) * ph;
  const line = (fn, color, dash) => (
    <line x1={sx(x0)} y1={sy(fn(x0))} x2={sx(x1)} y2={sy(fn(x1))}
      stroke={color} strokeWidth={2.6} strokeDasharray={dash} strokeLinecap="round" />
  );

  const nxt = narrow ? 5 : 7;
  const xt = Array.from({ length: nxt }, (_, i) => x0 + ((x1 - x0) * i) / (nxt - 1));
  const yt = Array.from({ length: 5 }, (_, i) => ymin + ((ymax - ymin) * i) / 4);

  const marker = (x, color, label, up) => {
    if (x == null || !isFinite(x) || x < x0 || x > x1) return null;
    return (
      <g>
        <line x1={sx(x)} y1={M.t} x2={sx(x)} y2={M.t + ph} stroke={color} strokeWidth={1.4} strokeDasharray="3 3" opacity={0.8} />
        <text x={sx(x)} y={up ? M.t + 12 : M.t + ph - 20} textAnchor="middle" fontSize={10.5} fontWeight={800} fill={color}>{label}</text>
      </g>
    );
  };

  // ── Interacción: arrastrar en cualquier punto del lienzo mueve el escenario ──
  const toFx = (clientX) => {
    const rect = svgRef.current.getBoundingClientRect();
    const vx = ((clientX - rect.left) / rect.width) * W;
    const raw = x0 + ((vx - M.l) / pw) * (x1 - x0);
    return Math.max(FX_MIN, Math.min(FX_MAX, Math.round(raw / 0.05) * 0.05));
  };
  const toFxRaw = (clientX) => {
    const rect = svgRef.current.getBoundingClientRect();
    const vx = ((clientX - rect.left) / rect.width) * W;
    return Math.max(x0, Math.min(x1, x0 + ((vx - M.l) / pw) * (x1 - x0)));
  };
  const onDown = (e) => {
    dragRef.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    if (onFx) onFx(toFx(e.clientX));
  };
  const onMove = (e) => {
    setHoverFx(toFxRaw(e.clientX));
    if (dragRef.current && onFx) onFx(toFx(e.clientX));
  };
  const onUp = () => { dragRef.current = false; };

  // ── Tooltip dentro del SVG (escala junto con la gráfica) ──
  const hv = hoverFx == null ? null : hoverFx;
  const tip = hv == null ? null : (() => {
    const lines = [
      ["Tipo de cambio", hv.toFixed(2)],
      [`Utilidad ${BRAND_NAME}`, fxMxn(gavAt(hv)) + " MXN"],
      ["Consolidado", fxMxn(conAt(hv)) + " MXN"],
      ["EBITDA EUA", fxUsd(m.ebitda) + " USD"],
    ];
    const bw = 176, bh = 20 + lines.length * 16;
    let bx = sx(hv) + 14;
    if (bx + bw > M.l + pw) bx = sx(hv) - bw - 14;
    const by = Math.max(M.t + 4, Math.min(M.t + ph - bh - 4, M.t + 30));
    return { lines, bw, bh, bx, by };
  })();

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      onPointerLeave={() => { dragRef.current = false; setHoverFx(null); }}
      style={{ width: "100%", height: "auto", display: "block", touchAction: "none", cursor: "ew-resize" }}>
      <rect x={M.l} y={M.t} width={pw} height={ph} fill="#fff" stroke="#eef2f7" />
      {ymin < 0 && <rect x={M.l} y={sy(0)} width={pw} height={Math.max(0, M.t + ph - sy(0))} fill="#EF4444" opacity={0.05} />}
      {yt.map((v, i) => (
        <g key={i}>
          <line x1={M.l} y1={sy(v)} x2={M.l + pw} y2={sy(v)} stroke="#f1f5f9" />
          <text x={M.l - 7} y={sy(v) + 3.5} textAnchor="end" fontSize={10} fill="#94a3b8" style={{ fontVariantNumeric: "tabular-nums" }}>{fxMxn0(v)}</text>
        </g>
      ))}
      {xt.map((v, i) => (
        <g key={i}>
          <line x1={sx(v)} y1={M.t + ph} x2={sx(v)} y2={M.t + ph + 4} stroke="#cbd5e1" />
          <text x={sx(v)} y={M.t + ph + 16} textAnchor="middle" fontSize={10.5} fill="#94a3b8">{v.toFixed(2)}</text>
        </g>
      ))}
      <rect x={sx(Math.max(x0, FX_MIN))} y={M.t} width={Math.max(0, sx(Math.min(x1, FX_MAX)) - sx(Math.max(x0, FX_MIN)))}
        height={ph} fill="#0f172a" opacity={0.035} />
      <text x={(sx(Math.max(x0, FX_MIN)) + sx(Math.min(x1, FX_MAX))) / 2} y={M.t + ph - 6}
        textAnchor="middle" fontSize={10} fontWeight={700} fill="#cbd5e1">rango del deslizador</text>

      <line x1={M.l} y1={sy(0)} x2={M.l + pw} y2={sy(0)} stroke="#EF4444" strokeWidth={1.6} />
      <text x={M.l + pw - 4} y={sy(0) - 5} textAnchor="end" fontSize={10} fontWeight={800} fill="#EF4444">utilidad cero</text>

      {marker(m.fxBeGaveti, "#E07856", "equilibrio Gaveti", true)}
      {marker(m.fxBeConsol, accent, "equilibrio consolidado", false)}
      {marker(fxBudget, "#64748b", "presupuesto", true)}

      {line(conAt, accent, null)}
      {line(gavAt, "#E07856", "6 4")}

      {/* guía de hover */}
      {hv != null && (
        <g pointerEvents="none">
          <line x1={sx(hv)} y1={M.t} x2={sx(hv)} y2={M.t + ph} stroke="#0f172a" strokeWidth={1} strokeDasharray="2 3" opacity={0.5} />
          <circle cx={sx(hv)} cy={sy(conAt(hv))} r={4} fill="#fff" stroke={accent} strokeWidth={2} />
          <circle cx={sx(hv)} cy={sy(gavAt(hv))} r={4} fill="#fff" stroke="#E07856" strokeWidth={2} />
        </g>
      )}

      {/* punto activo */}
      <line x1={sx(fx)} y1={M.t} x2={sx(fx)} y2={M.t + ph} stroke="#0f172a" strokeWidth={1.8} />
      <circle cx={sx(fx)} cy={sy(conAt(fx))} r={7} fill={accent} stroke="#fff" strokeWidth={2.5} />
      <circle cx={sx(fx)} cy={sy(gavAt(fx))} r={7} fill="#E07856" stroke="#fff" strokeWidth={2.5} />

      {tip && (
        <g pointerEvents="none">
          <rect x={tip.bx} y={tip.by} width={tip.bw} height={tip.bh} rx={8} fill="#0f172a" opacity={0.94} />
          {tip.lines.map(([k, v], i) => (
            <g key={i}>
              <text x={tip.bx + 10} y={tip.by + 20 + i * 16} fontSize={10.5} fill="#94a3b8">{k}</text>
              <text x={tip.bx + tip.bw - 10} y={tip.by + 20 + i * 16} fontSize={10.5} fontWeight={800} textAnchor="end"
                fill={i === 1 ? "#E07856" : i === 2 ? "#fff" : "#e2e8f0"} style={{ fontVariantNumeric: "tabular-nums" }}>{v}</text>
            </g>
          ))}
        </g>
      )}

      <text x={M.l + pw / 2} y={H - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="#64748b">Tipo de cambio USD/MXN · arrastra para mover el escenario</text>
      <text transform={`translate(13,${M.t + ph / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fontWeight={700} fill="#64748b">MXN por caja</text>
    </svg>
  );
}

// ── EBITDA EUA en USD por caja según el arancel. Arrastrable. ──
function FxTariffChart({ m, tariff, accent, narrow, onTariff }) {
  const W = 760, H = narrow ? 250 : 260, M = { l: 58, r: 16, t: 16, b: 42 };
  const pw = W - M.l - M.r, ph = H - M.t - M.b;
  const TMAX = 0.30;
  const svgRef = useRef(null);
  const dragRef = useRef(false);
  const [hoverT, setHoverT] = useState(null);

  const at = (t) => m.ebitdaSinAr - m.fobBase * t;
  const ys = [at(0), at(TMAX), 0];
  let ymin = Math.min(...ys), ymax = Math.max(...ys);
  const pad = Math.max((ymax - ymin) * 0.15, 0.05);
  ymin -= pad; ymax += pad;

  const sx = (t) => M.l + (t / TMAX) * pw;
  const sy = (y) => M.t + ph - ((y - ymin) / (ymax - ymin)) * ph;
  const yt = Array.from({ length: 5 }, (_, i) => ymin + ((ymax - ymin) * i) / 4);
  const xt = [0, 0.05, 0.10, 0.15, 0.20, 0.25, 0.30];
  const tc = Math.min(Math.max(tariff, 0), TMAX);

  const toT = (clientX, snap) => {
    const rect = svgRef.current.getBoundingClientRect();
    const vx = ((clientX - rect.left) / rect.width) * W;
    const raw = ((vx - M.l) / pw) * TMAX;
    const v = Math.max(0, Math.min(TMAX, raw));
    return snap ? Math.round(v / 0.005) * 0.005 : v;
  };
  const onDown = (e) => {
    dragRef.current = true;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    if (onTariff) onTariff(toT(e.clientX, true));
  };
  const onMove = (e) => {
    setHoverT(toT(e.clientX, false));
    if (dragRef.current && onTariff) onTariff(toT(e.clientX, true));
  };
  const onUp = () => { dragRef.current = false; };

  const tip = hoverT == null ? null : (() => {
    const lines = [
      ["Arancel", fxPctT(hoverT)],
      ["Monto arancel", fxUsd(m.fobBase * hoverT) + " USD"],
      ["EBITDA EUA", fxUsd(at(hoverT)) + " USD"],
    ];
    const bw = 170, bh = 20 + lines.length * 16;
    let bx = sx(hoverT) + 14;
    if (bx + bw > M.l + pw) bx = sx(hoverT) - bw - 14;
    return { lines, bw, bh, bx, by: M.t + 26 };
  })();

  return (
    <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      onPointerLeave={() => { dragRef.current = false; setHoverT(null); }}
      style={{ width: "100%", height: "auto", display: "block", touchAction: "none", cursor: "ew-resize" }}>
      <rect x={M.l} y={M.t} width={pw} height={ph} fill="#fff" stroke="#eef2f7" />
      {ymin < 0 && <rect x={M.l} y={sy(0)} width={pw} height={Math.max(0, M.t + ph - sy(0))} fill="#EF4444" opacity={0.05} />}
      {yt.map((v, i) => (
        <g key={i}>
          <line x1={M.l} y1={sy(v)} x2={M.l + pw} y2={sy(v)} stroke="#f1f5f9" />
          <text x={M.l - 7} y={sy(v) + 3.5} textAnchor="end" fontSize={10} fill="#94a3b8" style={{ fontVariantNumeric: "tabular-nums" }}>{fxUsd(v)}</text>
        </g>
      ))}
      {xt.map((v) => (
        <g key={v}>
          <line x1={sx(v)} y1={M.t + ph} x2={sx(v)} y2={M.t + ph + 4} stroke="#cbd5e1" />
          <text x={sx(v)} y={M.t + ph + 16} textAnchor="middle" fontSize={10.5} fill="#94a3b8">{(v * 100).toFixed(0)}%</text>
        </g>
      ))}
      <line x1={M.l} y1={sy(0)} x2={M.l + pw} y2={sy(0)} stroke="#EF4444" strokeWidth={1.6} />
      {m.tariffBeUsd != null && isFinite(m.tariffBeUsd) && m.tariffBeUsd >= 0 && m.tariffBeUsd <= TMAX && (
        <g>
          <line x1={sx(m.tariffBeUsd)} y1={M.t} x2={sx(m.tariffBeUsd)} y2={M.t + ph} stroke="#EF4444" strokeWidth={1.4} strokeDasharray="3 3" />
          <text x={sx(m.tariffBeUsd)} y={M.t + 12} textAnchor="middle" fontSize={10.5} fontWeight={800} fill="#EF4444">máximo {fxPctT(m.tariffBeUsd)}</text>
        </g>
      )}
      <line x1={sx(0)} y1={sy(at(0))} x2={sx(TMAX)} y2={sy(at(TMAX))} stroke={accent} strokeWidth={2.6} strokeLinecap="round" />
      {hoverT != null && (
        <g pointerEvents="none">
          <line x1={sx(hoverT)} y1={M.t} x2={sx(hoverT)} y2={M.t + ph} stroke="#0f172a" strokeWidth={1} strokeDasharray="2 3" opacity={0.5} />
          <circle cx={sx(hoverT)} cy={sy(at(hoverT))} r={4} fill="#fff" stroke={accent} strokeWidth={2} />
        </g>
      )}
      <line x1={sx(tc)} y1={M.t} x2={sx(tc)} y2={M.t + ph} stroke="#0f172a" strokeWidth={1.8} />
      <circle cx={sx(tc)} cy={sy(at(tc))} r={7} fill={accent} stroke="#fff" strokeWidth={2.5} />
      {tip && (
        <g pointerEvents="none">
          <rect x={tip.bx} y={tip.by} width={tip.bw} height={tip.bh} rx={8} fill="#0f172a" opacity={0.94} />
          {tip.lines.map(([k, v], i) => (
            <g key={i}>
              <text x={tip.bx + 10} y={tip.by + 20 + i * 16} fontSize={10.5} fill="#94a3b8">{k}</text>
              <text x={tip.bx + tip.bw - 10} y={tip.by + 20 + i * 16} fontSize={10.5} fontWeight={800} textAnchor="end" fill="#fff" style={{ fontVariantNumeric: "tabular-nums" }}>{v}</text>
            </g>
          ))}
        </g>
      )}
      <text x={M.l + pw / 2} y={H - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="#64748b">Arancel sobre FOB estimado · arrastra para mover el escenario</text>
      <text transform={`translate(11,${M.t + ph / 2}) rotate(-90)`} textAnchor="middle" fontSize={11} fontWeight={700} fill="#64748b">EBITDA EUA USD/caja</text>
    </svg>
  );
}

// ── Matriz TC × arancel. Hover con desglose, clic para fijar el escenario. ──
function FxMatrix({ retail, assump, costMxn, fx, tariff, narrow, fxBudget, onPick }) {
  const wrapRef = useRef(null);
  const [tip, setTip] = useState(null);   // { i, j, left, top }

  const fxRows = [15.50, 16.00, 16.50, 17.00, 17.50, 18.00, 18.50, 19.00];
  const tCols = narrow ? [0, 0.10, 0.25] : [0, 0.05, 0.10, 0.15, 0.20, 0.25];
  const models = fxRows.map((f) => tCols.map((t) => fxModel({ retail, assump, tariff: t, fx: f, costMxn })));
  const maxAbs = Math.max(...models.flat().map((r) => Math.abs(r.consolidadoMxn)), 1);
  const bg = (v) => v < 0
    ? `rgba(239,68,68,${0.10 + 0.45 * Math.min(1, Math.abs(v) / maxAbs)})`
    : `rgba(34,197,94,${0.08 + 0.40 * Math.min(1, v / maxAbs)})`;
  const th = { padding: "6px 6px", fontSize: 10.5, fontWeight: 800, color: "#64748b", textAlign: "center", background: "#f8fafc", whiteSpace: "nowrap" };
  const nearFx = fxRows.reduce((a, b) => (Math.abs(b - fx) < Math.abs(a - fx) ? b : a));
  const nearT = tCols.reduce((a, b) => (Math.abs(b - tariff) < Math.abs(a - tariff) ? b : a));

  const enter = (e, i, j) => {
    const wr = wrapRef.current?.getBoundingClientRect();
    if (!wr) return;
    const cr = e.currentTarget.getBoundingClientRect();
    setTip({ i, j, left: cr.left - wr.left + cr.width / 2, top: cr.top - wr.top });
  };

  const tm = tip ? models[tip.i][tip.j] : null;
  const tipRows = tm ? [
    ["Tipo de cambio", fxRows[tip.i].toFixed(2), "#e2e8f0"],
    ["Arancel", fxPctT(tCols[tip.j]) + " · " + fxUsd(tm.arancel), "#e2e8f0"],
    [`Utilidad ${BRAND_NAME}`, fxMxn(tm.utilGavMxn), tm.utilGavMxn < 0 ? "#FCA5A5" : "#E07856"],
    ["EBITDA EUA", fxUsd(tm.ebitda), tm.ebitda < 0 ? "#FCA5A5" : "#86EFAC"],
    ["Consolidado", fxMxn(tm.consolidadoMxn), tm.consolidadoMxn < 0 ? "#FCA5A5" : "#86EFAC"],
    ["Equilibrio consol.", isFinite(tm.fxBeConsol) ? tm.fxBeConsol.toFixed(2) : "—", "#e2e8f0"],
  ] : [];

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: "left" }}>USD/MXN ↓ · Arancel →</th>
              {tCols.map((t) => <th key={t} style={th}>{(t * 100).toFixed(0)}%</th>)}
            </tr>
          </thead>
          <tbody onMouseLeave={() => setTip(null)}>
            {fxRows.map((f, i) => (
              <tr key={f}>
                <td style={{ padding: "6px 8px", fontSize: 11.5, fontWeight: 800, color: f === nearFx ? "#0f172a" : "#64748b", background: f === nearFx ? "#f1f5f9" : "#fff", borderTop: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                  {f.toFixed(2)}
                </td>
                {models[i].map((r, j) => {
                  const v = r.consolidadoMxn;
                  const isActive = fxRows[i] === nearFx && tCols[j] === nearT;
                  const isHover = tip && tip.i === i && tip.j === j;
                  return (
                    <td key={j}
                      onMouseEnter={(e) => enter(e, i, j)}
                      onClick={() => onPick && onPick(fxRows[i], tCols[j])}
                      style={{
                        padding: "6px 6px", fontSize: 11.5, fontWeight: v < 0 ? 800 : 700, textAlign: "center",
                        color: v < 0 ? "#991B1B" : "#14532D", background: bg(v), cursor: "pointer",
                        borderTop: "1px solid #fff", borderLeft: "1px solid #fff",
                        outline: isHover ? "2px solid #E07856" : isActive ? "2px solid #0f172a" : "none",
                        outlineOffset: -2,
                      }}>{fxMxn0(v)}</td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {tip && (
        <div style={{
          position: "absolute", left: tip.left, top: tip.top, transform: "translate(-50%,-100%)",
          marginTop: -8, background: "#0f172a", color: "#fff", borderRadius: 10, padding: "9px 11px",
          minWidth: 208, pointerEvents: "none", zIndex: 20, boxShadow: "0 8px 24px rgba(15,23,42,.28)",
        }}>
          {tipRows.map(([k, v, col], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 14, marginBottom: i === tipRows.length - 1 ? 0 : 4 }}>
              <span style={{ fontSize: 10.5, color: "#94a3b8" }}>{k}</span>
              <span style={{ fontSize: 10.5, fontWeight: 800, color: col, fontVariantNumeric: "tabular-nums" }}>{v}</span>
            </div>
          ))}
          <div style={{ fontSize: 9.5, color: "#64748b", marginTop: 6, textAlign: "center" }}>clic para fijar este escenario</div>
        </div>
      )}
    </div>
  );
}

// ── Bloque principal ──
// Escenarios nombrados: combinan TC + arancel en un clic para plática con
// cliente. "Escenario base" toma el TC de referencia vigente, no un valor fijo.
const FX_SCENARIOS = [
  { key: "base",    label: "Escenario base",  fx: (cfg) => cfg.fxRef, tariff: 0 },
  { key: "pesofx",  label: "Peso fuerte",     fx: () => 15.5,         tariff: 0 },
  { key: "arancel", label: "Arancel adverso", fx: (cfg) => cfg.fxRef, tariff: 0.25 },
  { key: "peor",    label: "Peor caso",       fx: () => 15.5,         tariff: 0.25 },
];

function FxExposureBlock({ pnl, presentMode, onTogglePresentMode, onResetPnl, role }) {
  // No cuenta nada mientras modo presentación está activo (es una demo de Miguel,
  // no uso autónomo del cliente).
  const track = (action) => { if (!presentMode) logMetric(role, action); };
  const narrow = useIsNarrow(760);
  const [cfg, setCfg] = useState(FX_DEFAULT);
  const [copied, setCopied] = useState(false);
  const [saveState, setSaveState] = useState("idle");   // idle | saving | saved | error
  const [confirmReset, setConfirmReset] = useState(false);

  const saveTimer = useRef(null);
  const pending = useRef(null);
  const lastLocalEdit = useRef(0);

  // Igual que en PriceAnalysisTab: en modo presentación el escenario vive solo
  // en memoria y nunca se escribe a gantt/fx_exposure. `liveCfg` guarda lo
  // último que llegó del documento compartido para restaurarlo al salir.
  const presentModeRef = useRef(false);
  useEffect(() => { presentModeRef.current = presentMode; }, [presentMode]);
  const liveCfg = useRef(FX_DEFAULT);

  // Suscripción estable (sin dependencias): el escenario es compartido.
  useEffect(() => {
    const ref = doc(db, "gantt", "fx_exposure");
    const unsub = onSnapshot(ref, (snap) => {
      if (Date.now() - lastLocalEdit.current < 2000) return;   // no pisar edición en curso
      if (snap.exists()) {
        const merged = fxMergeCfg(snap.data());
        liveCfg.current = merged;
        if (!presentModeRef.current) setCfg(merged);
      } else {
        setDoc(ref, FX_DEFAULT).catch((e) => { console.error("fx_exposure init", e); setSaveState("error"); });
      }
    }, (err) => { console.error("fx_exposure snapshot", err); setSaveState("error"); });
    return () => unsub();
  }, []);

  const flush = () => {
    if (!pending.current || presentModeRef.current) return;
    const data = pending.current;
    pending.current = null;
    setDoc(doc(db, "gantt", "fx_exposure"), data)
      .then(() => setSaveState("saved"))
      .catch((e) => { console.error("fx_exposure save", e); setSaveState("error"); });
  };
  const persist = (data) => {
    if (presentModeRef.current || IS_DEMO) return; // en demo no hay nada real que guardar
    pending.current = data;
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveTimer.current = null; flush(); }, 500);
  };
  // Guardar al desmontar, al ocultar la pestaña y al cerrar el navegador, para que
  // nunca se pierda una edición hecha justo antes de salir.
  useEffect(() => {
    const forceSave = () => { if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; } flush(); };
    const onVis = () => { if (document.visibilityState === "hidden") forceSave(); };
    window.addEventListener("pagehide", forceSave);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pagehide", forceSave);
      document.removeEventListener("visibilitychange", onVis);
      forceSave();
    };
  }, []);

  const patchCfg = (patch) => {
    lastLocalEdit.current = Date.now();
    setCfg((prev) => { const next = { ...prev, ...patch }; persist(next); return next; });
  };
  const patchClub = (patch) => {
    lastLocalEdit.current = Date.now();
    setCfg((prev) => { const next = { ...prev, [prev.club]: { ...prev[prev.club], ...patch } }; persist(next); return next; });
  };
  const resetAll = () => {
    lastLocalEdit.current = Date.now();
    setCfg(FX_DEFAULT);
    persist(FX_DEFAULT);
    setConfirmReset(false);
  };
  const handleTogglePresentMode = () => {
    lastLocalEdit.current = Date.now();
    if (presentMode) setCfg(liveCfg.current); // se está apagando: regresa al escenario real del equipo
    onTogglePresentMode();
  };
  const applyScenario = (s) => {
    lastLocalEdit.current = Date.now();
    setCfg((prev) => { const next = { ...prev, fx: s.fx(prev), tariff: s.tariff }; return next; }); // no persist: modo presentación
  };
  const resetDemo = () => {
    lastLocalEdit.current = Date.now();
    setCfg(FX_DEFAULT);
    onResetPnl();
  };

  const { fx, tariff, club } = cfg;
  const meta = FX_CLUBS.find((cl) => cl.key === club) || FX_CLUBS[0];
  const accent = meta.accent;
  const assump = pnl[club] || PA_ASSUMPTIONS_DEFAULT;
  const c = cfg[club] || FX_DEFAULT[club];

  const m = fxModel({ retail: c.retail, assump, tariff, fx, costMxn: c.costMxn });
  const mBud = fxModel({ retail: c.retail, assump, tariff, fx: cfg.fxBudget, costMxn: c.costMxn });
  const deltaVsBudget = m.consolidadoMxn - mBud.consolidadoMxn;
  const anual = deltaVsBudget * (c.cajasAnio || 0);

  const gavColor = m.utilGavMxn < 0 ? "#DC2626" : "#E07856";
  const conColor = m.consolidadoMxn < 0 ? "#DC2626" : accent;
  const ebColor  = m.ebitda < 0 ? "#DC2626" : "#15803D";

  const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: narrow ? 14 : 18, marginBottom: 16 };
  const secTitle = { fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 8 };

  // ── Lectura del escenario ──
  const lecturas = [];
  lecturas.push(
    `${meta.store}, tipo de cambio ${fx.toFixed(2)} y arancel de ${fxPctT(tariff)}: resultado consolidado de ` +
    `${fxMxn(m.consolidadoMxn)} MXN por caja. La planta aporta ${fxMxn(m.utilGavMxn)} MXN y la operación en ` +
    `Estados Unidos ${fxUsd(m.ebitda)} USD (${m.ebitdaPct.toFixed(1)}% sobre net sales).`
  );
  if (m.fxBeConsol && isFinite(m.fxBeConsol)) {
    const holgura = ((fx - m.fxBeConsol) / fx) * 100;
    lecturas.push(holgura >= 0
      ? `El consolidado cruza el equilibrio en ${m.fxBeConsol.toFixed(2)}. Hay ${holgura.toFixed(0)}% de holgura cambiaria desde el nivel actual.`
      : `El consolidado cruza el equilibrio en ${m.fxBeConsol.toFixed(2)}, arriba del nivel actual: el peso está ${Math.abs(holgura).toFixed(0)}% más fuerte de lo que el modelo tolera.`);
  }
  if (m.fxBeGaveti && isFinite(m.fxBeGaveti)) {
    lecturas.push(
      `A nivel planta, un EXW de ${fxUsd(m.exw)} contra un costo de ${fxMxn(c.costMxn)} MXN exige un tipo de cambio ` +
      `de ${m.fxBeGaveti.toFixed(2)} para cubrir costo. ${fx >= m.fxBeGaveti ? "El nivel actual lo cubre." : "El nivel actual no lo cubre; hay que revisar EXW o costeo."}`
    );
  }
  if (m.tariffBeUsd != null && isFinite(m.tariffBeUsd)) {
    const p = m.tariffBeUsd * 100;
    let f;
    if (p < 0) f = `La operación en Estados Unidos ya opera con EBITDA negativo sin arancel; no hay margen arancelario disponible.`;
    else if (p < 10) f = `La operación en Estados Unidos tolera arancel hasta ${p.toFixed(1)}% sobre base FOB. El 10% de la Sección 301 la deja en EBITDA negativo: el riesgo arancelario aprieta antes que el cambiario.`;
    else if (p < 25) f = `La operación en Estados Unidos tolera arancel hasta ${p.toFixed(1)}% sobre base FOB: absorbe el 10% de la Sección 301, no un escenario de 25%.`;
    else f = `La operación en Estados Unidos tolera arancel hasta ${p.toFixed(1)}% sobre base FOB.`;
    lecturas.push(f);
  }
  lecturas.push(
    `Contra el presupuesto de ${cfg.fxBudget.toFixed(2)}, el escenario actual representa ` +
    `${deltaVsBudget >= 0 ? "un excedente" : "un faltante"} de ${fxMxn(Math.abs(deltaVsBudget))} MXN por caja, ` +
    `${fxMxn0(Math.abs(anual))} MXN anuales sobre ${(c.cajasAnio || 0).toLocaleString("es-MX")} cajas.`
  );
  const lecturaTxt = `${meta.store} · TC ${fx.toFixed(2)} · arancel ${fxPctT(tariff)}\n\n` +
    lecturas.map((t) => "• " + t).join("\n\n");

  const saveLabel = { idle: "", saving: "Guardando…", saved: "Guardado", error: "No se pudo guardar" }[saveState];
  const saveColor = saveState === "error" ? "#DC2626" : saveState === "saved" ? "#15803D" : "#94a3b8";

  return (
    <div style={{ marginTop: 40, paddingTop: 28, borderTop: "2px solid #f1f5f9" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 2 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: "#E07856" }} />
          <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>Exposición cambiaria y arancelaria</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {saveLabel && <span style={{ fontSize: 11, fontWeight: 700, color: saveColor }}>{saveLabel}</span>}
          <button onClick={handleTogglePresentMode}
            style={{
              padding: "5px 11px", borderRadius: 8, cursor: "pointer", fontSize: 11.5, fontWeight: 800,
              border: presentMode ? "1px solid #E07856" : "1px solid #e2e8f0",
              background: presentMode ? "#E07856" : "#fff",
              color: presentMode ? "#fff" : "#64748b",
            }}>
            {presentMode ? "Modo presentación: ON" : "Modo presentación"}
          </button>
          {!presentMode && (confirmReset ? (
            <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: "#64748b" }}>¿Restaurar todo?</span>
              <button onClick={resetAll} style={{ padding: "4px 10px", borderRadius: 7, border: "none", background: "#DC2626", color: "#fff", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>Sí</button>
              <button onClick={() => setConfirmReset(false)} style={{ padding: "4px 10px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>No</button>
            </span>
          ) : (
            <button onClick={() => setConfirmReset(true)}
              style={{ padding: "5px 11px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
              Restaurar valores iniciales
            </button>
          ))}
        </div>
      </div>
      <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: presentMode ? 10 : 16, maxWidth: 720 }}>
        Escenarios de tipo de cambio y arancel. Mueve los deslizadores para ver los distintos escenarios.
      </div>

      {presentMode && (
        <div style={{
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
          background: "#FEF6F2", border: "1px solid #FED7AA", borderRadius: 10,
          padding: "10px 12px", marginBottom: 16,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#9A3412" }}>
            Los cambios no se guardan mientras esté activo.
          </span>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {FX_SCENARIOS.map((s) => (
              <button key={s.key} onClick={() => applyScenario(s)}
                style={{ padding: "5px 11px", borderRadius: 7, border: "1.5px solid #E07856", background: "#fff", color: "#E07856", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
                {s.label}
              </button>
            ))}
          </div>
          <button onClick={resetDemo}
            style={{ padding: "5px 11px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>
            Reiniciar demo
          </button>
        </div>
      )}

      <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 10, padding: 3, marginBottom: 16 }}>
        {FX_CLUBS.map((cl) => (
          <button key={cl.key} onClick={() => patchCfg({ club: cl.key })}
            style={{
              padding: "6px 14px", borderRadius: 7, border: "none", cursor: "pointer",
              fontSize: 12, fontWeight: 800, transition: "all .12s",
              background: club === cl.key ? "#fff" : "transparent",
              color: club === cl.key ? cl.accent : "#94a3b8",
              boxShadow: club === cl.key ? "0 1px 3px rgba(15,23,42,.10)" : "none",
            }}>{cl.store}</button>
        ))}
      </div>

      {/* Deslizadores */}
      <div style={{ ...card, display: "flex", gap: narrow ? 18 : 30, flexWrap: "wrap" }}>
        <FxSlider label="Tipo de cambio USD/MXN" value={fx} min={FX_MIN} max={FX_MAX} step={0.05}
          onChange={(v) => { patchCfg({ fx: v }); track("drag"); }} accent="#E07856" format={(v) => v.toFixed(2)}
          hint={<span>Referencia {cfg.fxRef.toFixed(2)} · presupuesto {cfg.fxBudget.toFixed(2)} ·{" "}
            <button onClick={() => patchCfg({ fx: cfg.fxRef })}
              style={{ border: "none", background: "transparent", color: "#E07856", fontWeight: 800, fontSize: 11, cursor: "pointer", padding: 0 }}>volver a referencia</button></span>} />
        <div style={{ flex: "1 1 260px", minWidth: 240 }}>
          <FxSlider label="Arancel sobre FOB (FOB estimado)" value={tariff} min={0} max={0.30} step={0.005}
            onChange={(v) => { patchCfg({ tariff: v }); track("drag"); }} accent={accent} format={(v) => (v * 100).toFixed(1) + "%"}
            hint={<span>Base FOB {fxUsd(m.fobBase)} = EXW {fxUsd(m.exw)} + 50% del flete/importación</span>} />
          <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap" }}>
            {FX_TARIFF_PRESETS.map((p) => (
              <button key={p.v} onClick={() => patchCfg({ tariff: p.v })}
                style={{
                  padding: "4px 9px", borderRadius: 7, cursor: "pointer",
                  border: Math.abs(tariff - p.v) < 0.0001 ? `1.5px solid ${accent}` : "1.5px solid #e2e8f0",
                  background: Math.abs(tariff - p.v) < 0.0001 ? `${accent}12` : "#fff",
                  fontSize: 11, fontWeight: 800, color: Math.abs(tariff - p.v) < 0.0001 ? accent : "#64748b",
                }}>{p.lab} <span style={{ fontWeight: 600, color: "#94a3b8" }}>{p.sub}</span></button>
            ))}
          </div>
        </div>
      </div>

      {/* Resultados */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <FxStat label={`Utilidad planta ${BRAND_NAME}`} value={fxMxn(m.utilGavMxn)} color={gavColor} bg="#FEF6F2" big={presentMode}
          sub={`MXN/caja · ${m.utilGavPct.toFixed(1)}% sobre ingreso`} />
        <FxStat label="EBITDA operación EE.UU." value={fxUsd(m.ebitda)} color={ebColor} bg="#F0FDF4" big={presentMode}
          sub={`USD/caja · ${m.ebitdaPct.toFixed(1)}% sobre net sales`} />
        <FxStat label="Resultado consolidado" value={fxMxn(m.consolidadoMxn)} color={conColor} bg="#F8FAFC" big={presentMode}
          sub="MXN/caja · planta + operación EE.UU." />
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 }}>
        <FxStat label="TC equilibrio · planta" value={m.fxBeGaveti && isFinite(m.fxBeGaveti) ? m.fxBeGaveti.toFixed(2) : "—"} color="#E07856" bg="#fff"
          sub="TC mínimo para que EXW cubra el costo de producción" wide />
        <FxStat label="TC equilibrio · consolidado" value={m.fxBeConsol && isFinite(m.fxBeConsol) ? m.fxBeConsol.toFixed(2) : "—"} color={accent} bg="#fff"
          sub="TC mínimo para que el proyecto completo quede en cero" wide />
        <FxStat label="Arancel techo · EE.UU." value={fxPctT(m.tariffBeUsd)} color="#B45309" bg="#fff"
          sub="EBITDA en USD = 0 · no depende del TC" wide />
        <FxStat label="Arancel techo · consolidado" value={fxPctT(m.tariffBeCon)} color="#B45309" bg="#FFFBEB"
          sub={`Consolidado = 0 al TC de ${fx.toFixed(2)} · sí depende del TC`} wide />
      </div>

      {/* Impacto vs presupuesto */}
      <div style={{
        ...card, marginBottom: 20,
        background: deltaVsBudget < 0 ? "#FEF2F2" : "#F0FDF4",
        border: `1px solid ${deltaVsBudget < 0 ? "#FECACA" : "#BBF7D0"}`,
      }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: "#475569", marginBottom: 6 }}>
          Desviación contra el presupuesto ({cfg.fxBudget.toFixed(2)})
        </div>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "baseline" }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: deltaVsBudget < 0 ? "#DC2626" : "#15803D", fontVariantNumeric: "tabular-nums" }}>
              {deltaVsBudget >= 0 ? "+" : "−"}${Math.abs(deltaVsBudget).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>MXN por caja</div>
          </div>
          <div>
            <div style={{ fontSize: 24, fontWeight: 800, color: anual < 0 ? "#DC2626" : "#15803D", fontVariantNumeric: "tabular-nums" }}>
              {anual >= 0 ? "+" : "−"}${Math.abs(Math.round(anual)).toLocaleString("es-MX")}
            </div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>MXN anuales · {(c.cajasAnio || 0).toLocaleString("es-MX")} cajas</div>
          </div>
        </div>
      </div>

      {/* Lectura */}
      <div style={{ ...card, background: "#0f172a", border: "none" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>Lectura del escenario</div>
          <button onClick={() => {
            navigator.clipboard.writeText(lecturaTxt).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
              track("copy");
            }).catch(() => {});
          }}
            style={{ padding: "5px 11px", borderRadius: 8, border: "1px solid #334155", background: copied ? "#15803D" : "#1e293b", color: "#fff", fontSize: 11.5, fontWeight: 800, cursor: "pointer" }}>
            {copied ? "Copiado" : "Copiar texto"}
          </button>
        </div>
        {lecturas.map((t, i) => (
          <div key={i} style={{ display: "flex", gap: 8, marginBottom: 7, alignItems: "flex-start" }}>
            <div style={{ width: 5, height: 5, borderRadius: 3, background: "#E07856", marginTop: 6, flexShrink: 0 }} />
            <div style={{ fontSize: 12.5, color: "#e2e8f0", lineHeight: 1.55 }}>{t}</div>
          </div>
        ))}
      </div>

      {/* Gráfica: utilidad vs TC */}
      <div style={card}>
        <div style={secTitle}>Utilidad por caja según el tipo de cambio</div>
        <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 10 }}>
          Con arancel de {fxPctT(tariff)}. Línea punteada salmón: utilidad de la planta {BRAND_NAME}. Línea sólida: resultado consolidado.
          El cruce con la horizontal roja marca el equilibrio.
        </div>
        <FxMarginChart m={m} fx={fx} fxBudget={cfg.fxBudget} accent={accent} narrow={narrow} onFx={(v) => { patchCfg({ fx: v }); track("drag"); }} />
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 8, fontSize: 11.5, color: "#64748b" }}>
          <span><span style={{ display: "inline-block", width: 16, height: 3, background: "#E07856", verticalAlign: "middle", marginRight: 5 }} />Planta {BRAND_NAME} (MXN)</span>
          <span><span style={{ display: "inline-block", width: 16, height: 3, background: accent, verticalAlign: "middle", marginRight: 5 }} />Consolidado (MXN)</span>
        </div>
      </div>

      {/* Gráfica: EBITDA vs arancel */}
      <div style={card}>
        <div style={secTitle}>EBITDA de la operación EE.UU. según el arancel</div>
        <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 10 }}>
          El arancel se causa sobre la base FOB de {fxUsd(m.fobBase)}. Este P&amp;L está denominado en dólares, así que el
          tipo de cambio no altera esta curva.
        </div>
        <FxTariffChart m={m} tariff={tariff} accent={accent} narrow={narrow} onTariff={(v) => { patchCfg({ tariff: v }); track("drag"); }} />
      </div>

      {/* Matriz */}
      <div style={card}>
        <div style={secTitle}>Matriz de escenarios · resultado consolidado (MXN por caja)</div>
        <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 10 }}>
          Verde positivo, rojo negativo. El recuadro negro marca el escenario seleccionado. Pasa el cursor sobre una celda para ver el desglose y haz clic para fijarla.
        </div>
        <FxMatrix retail={c.retail} assump={assump} costMxn={c.costMxn} fx={fx} tariff={tariff} narrow={narrow}
          fxBudget={cfg.fxBudget} onPick={(f, t) => { patchCfg({ fx: f, tariff: t }); track("matrix"); }} />
      </div>

      {/* P&L por caja */}
      <div style={card}>
        <div style={secTitle}>P&amp;L por caja con arancel · {meta.store}</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontVariantNumeric: "tabular-nums" }}>
          <tbody>
            {[
              ["Suggested Retail", c.retail, "input"],
              ["Retailer Margin", -m.retailerMargin, "ded"],
              ["List Price", m.listPrice, "sub"],
              ["Trade Spend", -m.tradeSpend, "ded"],
              ["Net Sales", m.netSales, "sub"],
              ["Delivered COGS (con arancel)", -m.deliveredCogs, "ded"],
              ["Target Price (EXW)", m.exw, "detail"],
              ["Freight in & Import Cost", m.freightCost, "detail"],
              [`Base FOB estimada (EXW + 50% flete)`, m.fobBase, "detail"],
              [`Arancel (${fxPctT(tariff)} sobre base FOB)`, m.arancel, "detail-red"],
              ["Gross Margin", m.grossMargin, "sub"],
              ["Broker clubes", -m.broker, "ded"],
              ["Marketing", -m.marketing, "ded"],
              ["Warehouse & Freight Out", -m.whOut, "ded"],
              ["Contribution Margin", m.contribution, "sub"],
              ["SG&A Parte", -m.sgaParte, "ded"],
              ["SG&A Gaveti", -m.sgaGaveti, "ded"],
              ["EBITDA operación EE.UU. (USD)", m.ebitda, "total"],
            ].map(([label, val, kind], i) => {
              const isTotal = kind === "total", isSub = kind === "sub";
              const isDetail = kind === "detail" || kind === "detail-red";
              return (
                <tr key={i} style={{ background: isTotal ? "#F0FDF4" : isSub ? "#f8fafc" : "transparent" }}>
                  <td style={{
                    padding: isDetail ? "3px 8px 3px 26px" : "6px 8px",
                    fontSize: isDetail ? 11 : 12, fontWeight: isTotal || isSub ? 800 : 600,
                    color: kind === "detail-red" ? "#B45309" : isDetail ? "#94a3b8" : isTotal ? "#14532D" : "#475569",
                    borderTop: isSub || isTotal ? "1px solid #e2e8f0" : "none",
                  }}>{isDetail ? "↳ " : ""}{label}</td>
                  <td style={{
                    padding: isDetail ? "3px 8px" : "6px 8px", textAlign: "right",
                    fontSize: isTotal ? 14 : isDetail ? 11 : 12.5,
                    fontWeight: isTotal || isSub ? 800 : 600,
                    color: kind === "detail-red" ? "#B45309" : isDetail ? "#94a3b8" : isTotal ? (m.ebitda < 0 ? "#DC2626" : "#15803D") : val < 0 ? "#94a3b8" : "#0f172a",
                    borderTop: isSub || isTotal ? "1px solid #e2e8f0" : "none",
                  }}>{fxUsd(val)}</td>
                  <td style={{
                    padding: "6px 8px", textAlign: "right", width: 62,
                    fontSize: 11, fontWeight: 700, color: "#94a3b8",
                    borderTop: isSub || isTotal ? "1px solid #e2e8f0" : "none",
                  }}>{(isSub || isTotal) && m.netSales ? ((val / m.netSales) * 100).toFixed(1) + "%" : ""}</td>
                </tr>
              );
            })}
            <tr><td colSpan={3} style={{ height: 10 }} /></tr>
            <tr style={{ background: "#FEF6F2" }}>
              <td style={{ padding: "6px 8px", fontSize: 12, fontWeight: 800, color: "#9A3412", borderTop: "1px solid #FED7AA" }}>
                Ingreso planta (EXW × {fx.toFixed(2)})
              </td>
              <td colSpan={2} style={{ padding: "6px 8px", textAlign: "right", fontSize: 12.5, fontWeight: 800, color: "#9A3412", borderTop: "1px solid #FED7AA" }}>{fxMxn(m.ingresoGavMxn)}</td>
            </tr>
            <tr style={{ background: "#FEF6F2" }}>
              <td style={{ padding: "6px 8px", fontSize: 12, fontWeight: 600, color: "#9A3412" }}>Costo de producción (MXN)</td>
              <td colSpan={2} style={{ padding: "6px 8px", textAlign: "right", fontSize: 12.5, fontWeight: 600, color: "#9A3412" }}>{fxMxn(-c.costMxn)}</td>
            </tr>
            <tr style={{ background: "#FEF6F2" }}>
              <td style={{ padding: "6px 8px", fontSize: 13, fontWeight: 800, color: "#9A3412", borderTop: "1px solid #FED7AA" }}>Utilidad planta {BRAND_NAME} (MXN/caja)</td>
              <td colSpan={2} style={{ padding: "6px 8px", textAlign: "right", fontSize: 14, fontWeight: 800, color: gavColor, borderTop: "1px solid #FED7AA" }}>{fxMxn(m.utilGavMxn)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Supuestos */}
      <div style={{ ...card, background: "#f8fafc" }}>
        <div style={secTitle}>Supuestos de esta sección</div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 3 }}>Precio de anaquel (USD)</div>
            <PnlNumInput value={c.retail} isPct={false} accent={accent} width={72} onChange={(v) => { patchClub({ retail: v }); track("edit_pnl"); }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 3 }}>Costo producción (MXN/caja)</div>
            <PnlNumInput value={c.costMxn} isPct={false} accent="#E07856" width={80} onChange={(v) => { patchClub({ costMxn: v }); track("edit_pnl"); }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 3 }}>Cajas por año</div>
            <PnlNumInput value={c.cajasAnio} isPct={false} accent="#64748b" width={86} onChange={(v) => { patchClub({ cajasAnio: v }); track("edit_pnl"); }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 3 }}>TC referencia</div>
            <PnlNumInput value={cfg.fxRef} isPct={false} accent="#E07856" width={70} onChange={(v) => { patchCfg({ fxRef: v }); track("edit_pnl"); }} />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 3 }}>TC presupuestado</div>
            <PnlNumInput value={cfg.fxBudget} isPct={false} accent="#64748b" width={70} onChange={(v) => { patchCfg({ fxBudget: v }); track("edit_pnl"); }} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 12, lineHeight: 1.5 }}>
          EXW, flete/importación, broker y SG&amp;A provienen del P&amp;L de arriba (<code>gantt/price_pnl</code>) y se editan ahí.
          El escenario y los supuestos de esta sección se guardan en <code>gantt/fx_exposure</code> y se sincronizan con todo el equipo.
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// RETAIL TRADICIONAL — los 4 SKU de 150 g (Blueberry, Arándano, Chocolate,
// Minis/Chispi Chocs) que van a retail tradicional, NO a clubes (Costco/Sam's
// usan la caja de 840 g, arriba). Misma plantilla vertical que el P&L de
// clubes (mismos campos, mismo bidireccional $/%), independiente por SKU —
// igual que Costco y Sam's son independientes entre sí. El EXW ya es el
// precio al que México le vende a Estados Unidos (ya trae el costo de
// producción incorporado) — no hay un costo en pesos aparte que restar, así
// que abajo se muestra el ingreso bruto combinado (EE.UU. + transferencia a
// México, convertidos a pesos) sin inventar un costo que no conocemos.
// Persiste en gantt/retail_pricing. Oculto en demo — son SKUs y nombres
// reales de Gaveti.
// ══════════════════════════════════════════════════════════════════════════

const RETAIL_TRAD_SKUS_DEFAULT = [
  { id: "blueberry", name: "Blueberry", srp: 5.49, exw: 1.26,
    freightImport: 0.06, broker: 0.05, marketing: 0.09, warehouseFreightOut: 0.13,
    retailerMargin: 0.40, tradeSpend: 0.25, sgaParte: 0.05, sgaGaveti: 0.05 },
  { id: "arandano", name: "Arándano", srp: 5.49, exw: 1.26,
    freightImport: 0.06, broker: 0.05, marketing: 0.09, warehouseFreightOut: 0.13,
    retailerMargin: 0.40, tradeSpend: 0.25, sgaParte: 0.05, sgaGaveti: 0.05 },
  { id: "chocolate", name: "Chocolate", srp: 5.49, exw: 1.26,
    freightImport: 0.06, broker: 0.05, marketing: 0.09, warehouseFreightOut: 0.13,
    retailerMargin: 0.40, tradeSpend: 0.25, sgaParte: 0.05, sgaGaveti: 0.05 },
  { id: "minis", name: "Minis Chocolate Chip Cookies (Chispi Chocs)", srp: 5.99, exw: 2.01,
    freightImport: 0.06, broker: 0.05, marketing: 0.10, warehouseFreightOut: 0.13,
    retailerMargin: 0.40, tradeSpend: 0.25, sgaParte: 0.05, sgaGaveti: 0.05 },
];
const RETAIL_TRAD_FX_DEFAULT = { fx: 16.92, tariff: 0 };

// Definición de filas independiente del SKU — la columna de conceptos
// (izquierda) es una sola, igual que en clubes; cada SKU solo aporta valores.
// Reutiliza fxModel directamente, no duplica la matemática.
const RETAIL_TRAD_ROW_DEFS = [
  { label: "Suggested Retail", kind: "input", editUsdField: "srp", get: (m, s) => s.srp },
  { label: "Retailer Margin", kind: "ded", editPct: "retailerMargin", base: (m, s) => s.srp, get: (m) => -m.retailerMargin },
  { label: "List Price", kind: "sub", get: (m) => m.listPrice },
  { label: "Trade Spend", kind: "ded", editPct: "tradeSpend", base: (m) => m.listPrice, get: (m) => -m.tradeSpend },
  { label: "Net Sales", kind: "sub", get: (m) => m.netSales },
  { label: "Delivered COGS (con arancel)", kind: "ded", get: (m) => -m.deliveredCogs },
  { label: "Target Price (EXW)", kind: "detail", editUsdField: "exw", get: (m, s) => s.exw },
  { label: "Freight in & Import Cost", kind: "detail", editPct: "freightImport", base: (m) => m.netSales, get: (m) => m.freightCost },
  { label: "Base FOB estimada (EXW + 50% flete)", kind: "detail", get: (m) => m.fobBase },
  { label: (tariff) => `Arancel (${fxPctT(tariff)} sobre base FOB)`, kind: "detail-red", get: (m) => m.arancel },
  { label: "Gross Margin", kind: "sub", get: (m) => m.grossMargin },
  { label: "Broker", kind: "ded", editPct: "broker", base: (m) => m.netSales, get: (m) => -m.broker },
  { label: "Marketing", kind: "ded", editPct: "marketing", base: (m) => m.netSales, get: (m) => -m.marketing },
  { label: "Warehouse & Freight Out", kind: "ded", editPct: "warehouseFreightOut", base: (m) => m.netSales, get: (m) => -m.whOut },
  { label: "Contribution Margin", kind: "sub", get: (m) => m.contribution },
  { label: "SG&A Parte", kind: "ded", editPct: "sgaParte", base: (m) => m.netSales, get: (m) => -m.sgaParte },
  { label: "SG&A Gaveti", kind: "ded", editPct: "sgaGaveti", base: (m) => m.netSales, get: (m) => -m.sgaGaveti },
  { label: "EBITDA operación EE.UU. (USD)", kind: "total", get: (m) => m.ebitda },
];
// El EXW ya es el precio al que México le vende a Estados Unidos — ya trae
// el costo de producción incorporado. No hay un costo en pesos aparte que
// conozcamos para estos SKU, así que no se resta nada: se muestra el ingreso
// bruto combinado (lo que gana la operación en EE.UU. + lo que cobra México
// por el EXW, ambos convertidos a pesos) sin pretender un costo que no
// tenemos. usdPreExw = EBITDA + EXW, ya lo calcula fxModel.
const RETAIL_TRAD_MXN_ROW_DEFS = [
  { label: "Ingreso EE.UU. (EBITDA × TC)", get: (m, fx) => m.ebitda * fx },
  { label: "Precio de transferencia en pesos (EXW × TC)", get: (m, fx) => m.exw * fx },
  { label: "Ingreso total bruto (sin costos de producción)", get: (m, fx) => m.usdPreExw * fx, total: true },
];

// Componentes de celda fuera de RetailTradTable a propósito: si se declaran
// dentro, React les da una identidad nueva en cada render del padre y
// remonta el input en cada tecla — el usuario pierde lo que va escribiendo
// (así se rompía teclear centavos). Deben ser estables entre renders.
function RetailTradCell({ def, sku, m, onPatchSku, accent }) {
  const patch = (p) => onPatchSku(sku.id, p);
  if (def.editUsdField) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 2, color: "#64748b", fontSize: 11.5, fontWeight: 700 }}>
        $<PnlNumInput value={sku[def.editUsdField]} isPct={false} accent={accent} width={48} onChange={(v) => patch({ [def.editUsdField]: v })} />
      </span>
    );
  }
  if (def.editPct) {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
        <PnlNumInput value={sku[def.editPct]} isPct accent={accent} width={28} onChange={(v) => patch({ [def.editPct]: v })} />
        <PnlUsdSyncInput pct={sku[def.editPct]} base={def.base(m, sku)} accent={accent} width={44} prefix={def.kind === "ded" ? "−$" : "$"}
          onChangePct={(p) => patch({ [def.editPct]: p })} />
      </span>
    );
  }
  const val = def.get(m, sku);
  const color = def.kind === "total" ? (val >= 0 ? "#047857" : "#b91c1c") : def.kind === "detail-red" ? "#B45309" : def.kind === "ded" ? "#b91c1c" : "#0f172a";
  return <span style={{ fontWeight: def.kind === "total" ? 800 : def.kind === "sub" ? 700 : 500, color }}>{fxUsd(val)}</span>;
}

function RetailTradTable({ skus, fx, tariff, onPatchSku, accent }) {
  const skuData = skus.map(sku => ({ sku, m: fxModel({ retail: sku.srp, assump: sku, tariff, fx, costMxn: 0 }) }));

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontVariantNumeric: "tabular-nums", minWidth: 230 + skus.length * 150 }}>
        <thead>
          <tr>
            <td style={{ padding: "8px 12px" }} />
            {skuData.map(({ sku }) => (
              <td key={sku.id} style={{ padding: "8px 10px", fontSize: 12, fontWeight: 800, color: "#0f172a", textAlign: "right" }}>{sku.name.split(" ")[0]}</td>
            ))}
          </tr>
        </thead>
        <tbody>
          {RETAIL_TRAD_ROW_DEFS.map((def, i) => {
            const isSub = def.kind === "sub", isTot = def.kind === "total", isInput = def.kind === "input";
            const isDed = def.kind === "ded", isDetail = def.kind === "detail" || def.kind === "detail-red";
            const bg = isTot ? "#ECFDF5" : isSub ? "#f8fafc" : isInput ? "#faf5ff" : "transparent";
            const label = typeof def.label === "function" ? def.label(tariff) : def.label;
            return (
              <tr key={i} style={{ background: bg, borderTop: (isSub || isTot) ? "1px solid #e2e8f0" : "1px solid #f1f5f9" }}>
                <td style={{ padding: isDetail ? "4px 12px 4px 26px" : "6px 12px", fontSize: isDetail ? 11 : 12, fontWeight: isTot || isSub || isInput ? 800 : 600, color: isDed ? "#94a3b8" : isDetail ? "#64748b" : "#0f172a" }}>
                  {isDetail ? "↳ " : ""}{label}
                </td>
                {skuData.map(({ sku, m }) => (
                  <td key={sku.id} style={{ padding: isDetail ? "4px 10px" : "6px 10px", textAlign: "right", fontSize: isTot ? 13 : isDetail ? 11 : 12 }}>
                    <RetailTradCell def={def} sku={sku} m={m} onPatchSku={onPatchSku} accent={accent} />
                  </td>
                ))}
              </tr>
            );
          })}
          <tr><td colSpan={1 + skus.length} style={{ height: 8 }} /></tr>
          {RETAIL_TRAD_MXN_ROW_DEFS.map((def, i) => (
            <tr key={i} style={{ background: "#FEF6F2", borderTop: i === 0 || def.total ? "1px solid #FED7AA" : "none" }}>
              <td style={{ padding: "6px 12px", fontSize: def.total ? 13 : 12, fontWeight: def.total ? 800 : 600, color: "#9A3412" }}>{def.label}</td>
              {skuData.map(({ sku, m }) => {
                const val = def.get(m, fx);
                return (
                  <td key={sku.id} style={{ padding: "6px 10px", textAlign: "right", fontSize: def.total ? 13 : 12, fontWeight: def.total ? 800 : 600, color: def.total && val < 0 ? "#DC2626" : "#9A3412" }}>
                    {fxMxn(val)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RetailTradicionalPanel() {
  const [skus, setSkus] = useState(RETAIL_TRAD_SKUS_DEFAULT);
  const [fxCfg, setFxCfg] = useState(RETAIL_TRAD_FX_DEFAULT);
  const saveTimer = useRef(null);
  const pending = useRef(null);
  const lastLocalEdit = useRef(0);

  useEffect(() => {
    const ref = doc(db, "gantt", "retail_pricing");
    const unsub = onSnapshot(ref, (snap) => {
      if (Date.now() - lastLocalEdit.current < 1500) return;
      if (snap.exists()) {
        const d = snap.data();
        if (d.skus) setSkus(d.skus.map(s => ({ ...(RETAIL_TRAD_SKUS_DEFAULT.find(x => x.id === s.id) || {}), ...s })));
        if (d.fxCfg) setFxCfg({ ...RETAIL_TRAD_FX_DEFAULT, ...d.fxCfg });
      } else {
        setDoc(ref, { skus: RETAIL_TRAD_SKUS_DEFAULT, fxCfg: RETAIL_TRAD_FX_DEFAULT }).catch(console.error);
      }
    }, (err) => console.error("retail_pricing snapshot", err));
    return () => unsub();
  }, []);

  const persist = (data) => {
    pending.current = data;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setDoc(doc(db, "gantt", "retail_pricing"), pending.current).catch(console.error);
      saveTimer.current = null; pending.current = null;
    }, 500);
  };
  useEffect(() => () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); if (pending.current) setDoc(doc(db, "gantt", "retail_pricing"), pending.current).catch(console.error); }
  }, []);

  const touch = () => { lastLocalEdit.current = Date.now(); };
  const patchFx = (patch) => { touch(); setFxCfg(prev => { const next = { ...prev, ...patch }; persist({ skus, fxCfg: next }); return next; }); };
  const patchSku = (id, patch) => {
    touch();
    setSkus(prev => {
      const next = prev.map(s => s.id === id ? { ...s, ...patch } : s);
      persist({ skus: next, fxCfg });
      return next;
    });
  };

  const accent = "#7C3AED";
  const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 18, marginBottom: 16 };

  return (
    <div style={{ marginTop: 40, paddingTop: 28, borderTop: "2px solid #f1f5f9" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 16 }}>
        <div style={{ width: 10, height: 10, borderRadius: 3, background: accent }} />
        <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a" }}>Retail tradicional — 4 SKU (150 g)</div>
      </div>

      <div style={{ ...card, display: "flex", gap: 18, flexWrap: "wrap" }}>
        <FxSlider label="Tipo de cambio USD/MXN" value={fxCfg.fx} min={FX_MIN} max={FX_MAX} step={0.05}
          onChange={(v) => patchFx({ fx: v })} accent={accent} format={(v) => v.toFixed(2)} />
        <div style={{ flex: "1 1 220px", minWidth: 200 }}>
          <FxSlider label="Arancel sobre FOB" value={fxCfg.tariff} min={0} max={0.30} step={0.005}
            onChange={(v) => patchFx({ tariff: v })} accent={accent} format={(v) => (v * 100).toFixed(1) + "%"} />
        </div>
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <RetailTradTable skus={skus} fx={fxCfg.fx} tariff={fxCfg.tariff} onPatchSku={patchSku} accent={accent} />
      </div>
    </div>
  );
}

function PriceAnalysisTab({ user }) {
  // Supuestos del P&L por club, persistidos en Firebase (doc gantt/price_pnl).
  // Cada club es independiente; el precio arrastrable y el modo siguen siendo de sesión.
  const [pnl, setPnl] = useState({ costco: PA_ASSUMPTIONS_DEFAULT, sams: PA_ASSUMPTIONS_DEFAULT });
  const role = user?.email?.endsWith("@partecpg.com") ? "team" : "client";
  const saveTimer = useRef(null);
  const pending = useRef(null);
  const lastLocalEdit = useRef(0);

  // Modo presentación: mientras está activo, los cambios (escenarios, reset,
  // edición de supuestos) viven solo en memoria — nunca se escriben a Firestore,
  // para no pisarle el número a Karen o al equipo en medio de una llamada con
  // cliente. `livePnl` sigue reflejando lo último que llegó del documento
  // compartido; al salir de modo presentación se restaura ese valor.
  const [presentMode, setPresentMode] = useState(false);
  const presentModeRef = useRef(false);
  useEffect(() => { presentModeRef.current = presentMode; }, [presentMode]);
  const livePnl = useRef({ costco: PA_ASSUMPTIONS_DEFAULT, sams: PA_ASSUMPTIONS_DEFAULT });

  // Cargar + suscribirse en tiempo real
  useEffect(() => {
    const ref = doc(db, "gantt", "price_pnl");
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        // No pisar ediciones locales recientes (evita saltos mientras se teclea)
        if (Date.now() - lastLocalEdit.current < 1500) return;
        const d = snap.data();
        const merged = {
          costco: { ...PA_ASSUMPTIONS_DEFAULT, ...(d.costco || {}) },
          sams: { ...PA_ASSUMPTIONS_DEFAULT, ...(d.sams || {}) },
        };
        livePnl.current = merged;
        if (!presentModeRef.current) setPnl(merged);
      } else {
        setDoc(ref, { costco: PA_ASSUMPTIONS_DEFAULT, sams: PA_ASSUMPTIONS_DEFAULT }).catch(console.error);
      }
    }, (err) => console.error("price_pnl snapshot", err));
    return () => unsub();
  }, []);

  // Guardado con debounce (evita escribir en cada tecla). En modo presentación
  // no se escribe nunca a Firestore.
  const persist = (data) => {
    if (presentModeRef.current) return;
    pending.current = data;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (!presentModeRef.current) setDoc(doc(db, "gantt", "price_pnl"), pending.current).catch(console.error);
      saveTimer.current = null; pending.current = null;
    }, 500);
  };
  // Flush pendiente al desmontar (p. ej. al cambiar de pestaña justo después de editar)
  useEffect(() => () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      if (pending.current && !presentModeRef.current) setDoc(doc(db, "gantt", "price_pnl"), pending.current).catch(console.error);
    }
  }, []);

  // Métricas de uso: "ver" (se abrió la pestaña) vs "usar" (edición real, sesión
  // larga). No depende de presentMode porque siempre es false al montar.
  useEffect(() => {
    logMetric(role, "views");
    const t = setTimeout(() => { if (!presentModeRef.current) logMetric(role, "session60s"); }, 60000);
    return () => clearTimeout(t);
  }, []);

  const makeSetAssump = (club) => (updater) => {
    lastLocalEdit.current = Date.now();
    if (!presentModeRef.current) logMetric(role, "edit_pnl");
    setPnl(prev => {
      const nextClub = typeof updater === "function" ? updater(prev[club]) : updater;
      const merged = { ...prev, [club]: nextClub };
      persist(merged);
      return merged;
    });
  };

  const togglePresentMode = () => {
    setPresentMode(prev => {
      const next = !prev;
      if (prev) setPnl(livePnl.current); // se está apagando: regresa a lo real del equipo
      return next;
    });
  };
  const resetPnlDefaults = () => {
    setPnl({ costco: PA_ASSUMPTIONS_DEFAULT, sams: PA_ASSUMPTIONS_DEFAULT });
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 16px 60px" }}>
      <div style={{ marginBottom: 6, fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Análisis de precios</div>
      <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 20 }}>
        Posición de {BRAND_NAME} frente al anaquel en Costco y Sam's Club (Texas). El precio está en el eje horizontal y el gramaje total en el vertical; el tamaño de cada burbuja es proporcional a los gramos por empaque o al gramaje total, según el selector. Mueve la galleta {BRAND_NAME} para simular escenarios de precio y gramaje.
      </div>
      <PriceBubbleChart store="Costco — Texas" rows={IS_DEMO ? DEMO_PA_COSTCO : PA_COSTCO} accent="#1D4ED8" assump={pnl.costco} setAssump={makeSetAssump("costco")} />
      <PriceBubbleChart store="Sam's Club — Texas" rows={IS_DEMO ? DEMO_PA_SAMS : PA_SAMS} accent="#7C3AED" assump={pnl.sams} setAssump={makeSetAssump("sams")} />

      {!IS_DEMO && <RetailTradicionalPanel />}

      <FxExposureBlock pnl={pnl} presentMode={presentMode} onTogglePresentMode={togglePresentMode} onResetPnl={resetPnlDefaults} role={role} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
// ANÁLISIS DE CATEGORÍA (demo) — overview → filtro → zoom en una sola
// pantalla, en vez del PPTX lineal de 154 páginas que hoy produce el
// analista de Nielsen (repite cada tabla 3x por geografía, mismo template
// de 5 slides sin importar el tamaño de marca, cero síntesis). Sustituye
// las mega-tablas por UN scatter: participación vs. cambio de velocidad,
// con geografía y filtro BFY como toggles sobre el mismo gráfico.
// Solo datos ficticios, nunca toca Firestore — ver DEMO_CATEGORY_BRANDS.
// ══════════════════════════════════════════════════════════════════════════

function CategoryScatterChart({ brands, geo, onSelect, selectedId }) {
  const SALMON = "#E07856";
  const W = 820, H = 440, M = { l: 56, r: 20, t: 16, b: 44 };
  const pw = W - M.l - M.r, ph = H - M.t - M.b;

  const geoScaled = brands.map(b => ({ ...b, gShare: b.share * (geo === "ca" ? b.caIdx : geo === "tx" ? b.txIdx : 100) / 100 }));

  const xMax = Math.max(...geoScaled.map(b => b.gShare)) * 1.2;
  const yVals = geoScaled.map(b => b.velChg);
  const yMin = Math.min(...yVals, 0) - 4, yMax = Math.max(...yVals, 0) + 4;
  const sx = (v) => M.l + (v / xMax) * pw;
  const sy = (v) => M.t + ph - ((v - yMin) / (yMax - yMin)) * ph;
  const dMax = Math.max(...brands.map(b => b.dollars)), dMin = Math.min(...brands.map(b => b.dollars));
  const rOf = (d) => 9 + Math.sqrt(Math.max(0, d - dMin) / (dMax - dMin || 1)) * 28;

  const xTicks = Array.from({ length: 5 }, (_, i) => (xMax * i) / 4);
  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", overflow: "visible" }}>
      {yTicks.map((v, i) => (
        <g key={i}>
          <line x1={M.l} x2={W - M.r} y1={sy(v)} y2={sy(v)} stroke="#f1f5f9" />
          <text x={M.l - 8} y={sy(v) + 4} fontSize={10} textAnchor="end" fill="#94a3b8">{v.toFixed(0)}%</text>
        </g>
      ))}
      {xTicks.map((v, i) => (
        <text key={i} x={sx(v)} y={H - M.b + 18} fontSize={10} textAnchor="middle" fill="#94a3b8">{v.toFixed(1)}%</text>
      ))}
      <line x1={M.l} x2={W - M.r} y1={sy(0)} y2={sy(0)} stroke="#cbd5e1" strokeDasharray="4 3" />
      <text x={W - M.r} y={sy(0) - 6} fontSize={10} textAnchor="end" fill="#94a3b8">sin cambio de velocidad</text>
      <text x={(M.l + W - M.r) / 2} y={H - 6} fontSize={11} fontWeight={700} textAnchor="middle" fill="#64748b">Participación de mercado ($)</text>
      <text x={14} y={M.t + ph / 2} fontSize={11} fontWeight={700} textAnchor="middle" fill="#64748b" transform={`rotate(-90, 14, ${M.t + ph / 2})`}>Cambio de velocidad YoY</text>

      {geoScaled.map(b => {
        const cx = sx(b.gShare), cy = sy(b.velChg), r = rOf(b.dollars);
        const growing = b.velChg >= 0;
        const fill = b.isSelf ? SALMON : growing ? "#22C55E" : "#DC2626";
        const isSel = selectedId === b.id;
        return (
          <g key={b.id} onClick={() => onSelect(b.id)} style={{ cursor: "pointer" }}>
            <circle cx={cx} cy={cy} r={r} fill={fill} opacity={isSel ? 0.9 : b.isSelf ? 0.75 : 0.45}
              stroke={isSel || b.isSelf ? fill : "none"} strokeWidth={isSel ? 3 : 2} />
            {(isSel || b.isSelf || r > 20) && (
              <text x={cx} y={cy - r - 5} fontSize={10.5} fontWeight={b.isSelf ? 800 : 600} textAnchor="middle" fill={b.isSelf ? SALMON : "#475569"}>{b.name}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function CategoryAnalysisTab() {
  const [geo, setGeo] = useState("national");
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState("andesa");

  const brands = DEMO_CATEGORY_BRANDS.filter(b => filter === "all" || (filter === "bfy" && b.bfy));
  const cat = DEMO_CATEGORY_GEO[geo];
  const sel = DEMO_CATEGORY_BRANDS.find(b => b.id === selectedId) || DEMO_CATEGORY_BRANDS[0];
  const selShare = sel.share * (geo === "ca" ? sel.caIdx : geo === "tx" ? sel.txIdx : 100) / 100;

  const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 18, marginBottom: 16 };
  const chip = (active) => ({
    padding: "6px 13px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 800,
    border: active ? "1.5px solid #1D4ED8" : "1px solid #e2e8f0", background: active ? "#EFF6FF" : "#fff",
    color: active ? "#1D4ED8" : "#64748b",
  });

  const catNarrative = `${cat.label}: la categoría vale $${cat.dollars.toFixed(0)}M y ${cat.dollarChg >= 0 ? "creció" : "cayó"} ${Math.abs(cat.dollarChg)}%, pero la velocidad ${cat.velChg >= 0 ? "subió" : "cayó"} ${Math.abs(cat.velChg)}% mientras la distribución (TDP) subió ${cat.tdpChg}% — el crecimiento se está comprando con distribución, no ganando con demanda.`;

  const selDollarsInGeo = cat.dollars * selShare / 100;
  const brandNarrative = `${sel.name} tiene ${selShare.toFixed(1)}% de participación (~$${selDollarsInGeo.toFixed(1)}M en ${cat.label.toLowerCase()}) y ${sel.dollarChg >= 0 ? "crece" : "cae"} ${Math.abs(sel.dollarChg)}% en dólares. La velocidad ${sel.velChg >= 0 ? "crece" : "cae"} ${Math.abs(sel.velChg)}%${sel.tdpChg > 0 ? `, con la distribución subiendo ${sel.tdpChg}%` : ""} — ${sel.velChg < 0 && sel.tdpChg > 0 ? "el crecimiento (si lo hay) viene de más anaquel, no de más demanda real." : sel.velChg >= 0 ? "un crecimiento sano: gana por demanda, no solo por distribución." : "señal de marca en declive estructural."} Compite en ${sel.subcat}, precio promedio $${sel.avgPrice.toFixed(2)}, sabor líder ${sel.flavor}${sel.bfy ? " · segmento Better-For-You" : ""}.`;

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "20px 16px 60px" }}>
      <div style={{ marginBottom: 6, fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Análisis de Categoría</div>
      <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 20 }}>
        Categoría de alfajores y galletería rellena (datos ficticios, estilo Nielsen). Cada burbuja es una marca — tamaño = ventas, posición vertical = si gana o pierde velocidad. Cambia de geografía o filtra por Better-For-You sin salir de esta pantalla.
      </div>

      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16, alignItems: "center" }}>
        <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
          {[["national", "Nacional"], ["ca", "California"], ["tx", "Texas"]].map(([k, lab]) => (
            <button key={k} onClick={() => setGeo(k)}
              style={{ padding: "6px 14px", borderRadius: 7, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 800,
                background: geo === k ? "#fff" : "transparent", color: geo === k ? "#1D4ED8" : "#94a3b8",
                boxShadow: geo === k ? "0 1px 3px rgba(15,23,42,.10)" : "none" }}>{lab}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <div style={chip(filter === "all")} onClick={() => setFilter("all")}>Todas ({DEMO_CATEGORY_BRANDS.length})</div>
          <div style={chip(filter === "bfy")} onClick={() => setFilter("bfy")}>Better-For-You ({DEMO_CATEGORY_BRANDS.filter(b => b.bfy).length})</div>
        </div>
      </div>

      <div style={{ ...card, background: "#0f172a", border: "none" }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
          {[
            { label: "Tamaño categoría", value: `$${cat.dollars.toFixed(0)}M` },
            { label: "Crecimiento $", value: `${cat.dollarChg >= 0 ? "+" : ""}${cat.dollarChg}%` },
            { label: "Velocidad", value: `${cat.velChg >= 0 ? "+" : ""}${cat.velChg}%`, bad: cat.velChg < 0 },
            { label: "TDP (distribución)", value: `+${cat.tdpChg}%` },
          ].map((s, i) => (
            <div key={i} style={{ flex: "1 1 130px", background: "#1e293b", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{s.label}</div>
              <div style={{ fontSize: 19, fontWeight: 800, color: s.bad ? "#FCA5A5" : "#fff" }}>{s.value}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: "#e2e8f0", lineHeight: 1.55 }}>{catNarrative}</div>
      </div>

      <div style={card}>
        <CategoryScatterChart brands={brands} geo={geo} onSelect={setSelectedId} selectedId={selectedId} />
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 10, fontSize: 11, color: "#64748b" }}>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#E07856", verticalAlign: "middle", marginRight: 5 }} />Andesa (tu marca)</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#22C55E", opacity: 0.6, verticalAlign: "middle", marginRight: 5 }} />Gana velocidad</span>
          <span><span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: "#DC2626", opacity: 0.6, verticalAlign: "middle", marginRight: 5 }} />Pierde velocidad</span>
          <span style={{ color: "#94a3b8" }}>Tamaño de burbuja ∝ ventas · clic para hacer zoom</span>
        </div>
      </div>

      <div style={{ ...card, borderLeft: sel.isSelf ? "3px solid #E07856" : "3px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>{sel.name}{sel.isSelf ? " · tu marca" : ""}</div>
          {sel.bfy && <span style={{ fontSize: 10.5, fontWeight: 800, color: "#7C3AED", background: "#F5F3FF", padding: "2px 9px", borderRadius: 20 }}>Better-For-You</span>}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          {[
            { label: "Participación", value: `${selShare.toFixed(1)}%` },
            { label: "Cambio $", value: `${sel.dollarChg >= 0 ? "+" : ""}${sel.dollarChg}%`, color: sel.dollarChg >= 0 ? "#15803D" : "#DC2626" },
            { label: "Cambio velocidad", value: `${sel.velChg >= 0 ? "+" : ""}${sel.velChg}%`, color: sel.velChg >= 0 ? "#15803D" : "#DC2626" },
            { label: "Cambio TDP", value: `+${sel.tdpChg}%` },
            { label: "Precio promedio", value: `$${sel.avgPrice.toFixed(2)}` },
          ].map((s, i) => (
            <div key={i} style={{ flex: "1 1 110px", background: "#f8fafc", borderRadius: 10, padding: "9px 11px", border: "1px solid #f1f5f9" }}>
              <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{s.label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: s.color || "#0f172a" }}>{s.value}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.55 }}>{brandNarrative}</div>
      </div>

      <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>
        Datos ficticios para demostración. El importador real de Nielsen (CSV o API) se conecta cuando exista un archivo real de referencia — ver roadmap.
      </div>
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════
// TAB: Bloqueos y avance desbloqueado
// Idea central: cuando un frente está bloqueado por una dependencia (producto,
// arte, UPC...), la pregunta útil no es "¿por qué no avanzó?" sino "¿qué SÍ se
// podía avanzar sin la dependencia, y cuánto de eso se hizo?".
//
// Integración: cada avance posible puede enviarse a Pendientes/Urgentes. Una vez
// enlazado, su estado VIVE en client_tasks (una sola captura) y este tab solo lo
// lee. Al cerrar un bloqueo se captura el aprendizaje y pasa al historial.
// Persistencia: doc gantt/bloqueos
// ═══════════════════════════════════════════════════════════════════════════

const BLQ_ESTADOS = {
  pendiente:   { label: "Pendiente",   color: "#DC2626", bg: "#FEF2F2" },
  en_progreso: { label: "En progreso", color: "#F59E0B", bg: "#FFFBEB" },
  listo:       { label: "Listo",       color: "#22C55E", bg: "#F0FDF4" },
};

const BLQ_CATEGORIAS = ["Regulatorio", "Packaging", "Logística", "Comercial", "Capacidad de planta", "Cliente", "Otro"];

function useIsNarrow(bp = 680) {
  const [narrow, setNarrow] = useState(() => (typeof window !== "undefined" ? window.innerWidth < bp : false));
  useEffect(() => {
    const h = () => setNarrow(window.innerWidth < bp);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, [bp]);
  return narrow;
}

const BLQ_SEED = {
  ventanas: [
    { id: "vt1", nombre: "Line review Costco (Texas)", fecha: "2026-09-15", notas: "Ventana de compra. Si se pierde, el siguiente ciclo es semestral." },
    { id: "vt2", nombre: "Reset de anaquel H-E-B", fecha: "2026-10-01", notas: "Fecha externa: no se negocia." },
    { id: "vt3", nombre: "Cierre de altas para temporada holiday", fecha: "2026-08-31", notas: "Distribuidores requieren producto en catálogo con anticipación." },
  ],
  bloqueos: [
    {
      id: "bl1",
      titulo: "Producto reformulado (derivado del estudio de mercado)",
      frente: "Ventas",
      desde: "2026-04-15",
      estado: "activo",
      resueltoEl: "", aprendizaje: "", categoria: "",
      items: [
        { id: "b1i1", texto: "Plan de cuentas objetivo (tier 1 / tier 2) con criterio de priorización", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b1i2", texto: "Presupuesto comercial del año (trade spend, muestras, viáticos, ferias)", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b1i3", texto: "Calendario de line reviews y resets por cadena objetivo", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b1i4", texto: "Requisitos de alta por cadena (vendor forms, seguros, EDI, certificaciones)", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b1i5", texto: "Estructura de precio y trade spend por cuenta (usar tab Análisis de precios)", responsable: "Miguel", estado: "pendiente", fecha: "" },
        { id: "b1i6", texto: "Mapa de brokers y distribuidores por región, con contacto verificado", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b1i7", texto: "Protocolo de envío de muestras (quién, qué, con qué material de apoyo)", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b1i8", texto: "Auditoría de anaquel de competencia en Costco y Sam's (fotos + precios)", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b1i9", texto: "Deck comercial armado (todo menos el arte final del empaque)", responsable: "Miguel", estado: "pendiente", fecha: "" },
        { id: "b1i10", texto: "Introducciones activadas con los tres mentores de ventas senior", responsable: "Miguel", estado: "pendiente", fecha: "" },
      ],
    },
    {
      id: "bl2",
      titulo: "UPC / GTIN-13 sin liberar",
      frente: "Distribuidores",
      desde: "2026-05-01",
      estado: "activo",
      resueltoEl: "", aprendizaje: "", categoria: "",
      items: [
        { id: "b2i1", texto: "Formatos de alta de KeHE y Dot Foods llenos hasta donde el UPC no bloquea", responsable: "Karen", estado: "pendiente", fecha: "" },
        { id: "b2i2", texto: "Diagrama de pallet y ficha logística lista para adjuntar", responsable: "Miguel", estado: "pendiente", fecha: "" },
        { id: "b2i3", texto: "Alta de perfil y contenido en RangeMe / Pod Foods", responsable: "Karen", estado: "pendiente", fecha: "" },
      ],
    },
  ],
};

function BlqBadge({ estado, onClick, clickable }) {
  const m = BLQ_ESTADOS[estado] || BLQ_ESTADOS.pendiente;
  return (
    <button type="button" onClick={onClick} disabled={!clickable} title={clickable ? "Click para cambiar estado" : ""}
      style={{ border: "none", padding: 0, background: "transparent", cursor: clickable ? "pointer" : "default", flexShrink: 0 }}>
      <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 800, color: m.color, background: m.bg, padding: "3px 8px", borderRadius: 999, whiteSpace: "nowrap" }}>
        {m.label}
      </span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// FASES DEL PROYECTO — seis círculos interactivos
// Adaptación del Design Thinking al desarrollo de producto CPG.
// Los arcos superiores NO son decorativos: cada uno representa iteraciones
// reales registradas en los hitos (un hito que regresó de Validar a Definir
// dibuja ese arco). El grosor crece con el número de iteraciones.
// ═══════════════════════════════════════════════════════════════════════════

const TL_FASES = [
  { key: "diagnosticar", label: "Diagnosticar", color: "#D4A72C", grupo: "Comprender",   desc: "Mercado, consumidor, canal, capacidad de planta" },
  { key: "definir",      label: "Definir",      color: "#1E4C8A", grupo: "Comprender",   desc: "Producto objetivo, gramaje, precio, cuenta destino" },
  { key: "formular",     label: "Formular",     color: "#7CB518", grupo: "Explorar",     desc: "Receta, empaque, costos, regulatorio" },
  { key: "prototipar",   label: "Prototipar",   color: "#C2185B", grupo: "Explorar",     desc: "Dummies, muestras, corridas piloto, artes" },
  { key: "validar",      label: "Validar",      color: "#5B21B6", grupo: "Materializar", desc: "Comprador, anaquel, pruebas de llenado, certificaciones" },
  { key: "lanzar",       label: "Lanzar",       color: "#86176B", grupo: "Materializar", desc: "Alta, orden de compra, embarque, reposición" },
];
const TL_FASE_IDX = TL_FASES.reduce((a, f, i) => { a[f.key] = i; return a; }, {});
const TL_GRUPOS = [
  { label: "Comprender",   desde: 0, hasta: 1 },
  { label: "Explorar",     desde: 2, hasta: 3 },
  { label: "Materializar", desde: 4, hasta: 5 },
];

// ═══════════════════ RESULTADOS — 8 frentes y fichas ═══════════════════

// ── Archivos adjuntos de una ficha ────────────────────────────────────────
const RES_TIPOS = {
  pdf:  { label: "PDF",  color: "#DC2626" },
  xlsx: { label: "XLSX", color: "#16A34A" },
  xls:  { label: "XLS",  color: "#16A34A" },
  csv:  { label: "CSV",  color: "#16A34A" },
  pptx: { label: "PPT",  color: "#EA580C" },
  ppt:  { label: "PPT",  color: "#EA580C" },
  docx: { label: "DOC",  color: "#2563EB" },
  doc:  { label: "DOC",  color: "#2563EB" },
};
const resTipo = (a) => {
  const explicito = (a.tipo || "").toLowerCase();
  if (RES_TIPOS[explicito]) return { ext: explicito, ...RES_TIPOS[explicito] };
  const m = (a.nombre || a.url || "").toLowerCase().match(/\.([a-z0-9]{2,4})(?:\?|$)/);
  const ext = m ? m[1] : "";
  return RES_TIPOS[ext] ? { ext, ...RES_TIPOS[ext] } : { ext, label: (ext || "DOC").toUpperCase().slice(0, 4), color: "#64748B" };
};
// Si el archivo vive en Drive, la portada se deduce sola de la liga.
const resDriveId = (url) => {
  if (!url) return null;
  const m = url.match(/\/(?:file|document|spreadsheets|presentation)\/d\/([a-zA-Z0-9_-]{10,})/)
         || url.match(/[?&]id=([a-zA-Z0-9_-]{10,})/);
  return m ? m[1] : null;
};
const resPortada = (a) => {
  if (!a) return null;
  if (a.portada) return a.portada;
  const id = resDriveId(a.url);
  return id ? `https://drive.google.com/thumbnail?id=${id}&sz=w600` : null;
};

const RES_FRENTES = [
  { key: "mercado",   label: "Mercado, Categoría y Competencia", short: "Mercado",     color: "#2563EB" },
  { key: "producto",  label: "Producto e Ingredientes",          short: "Producto",    color: "#7C3AED" },
  { key: "empaque",   label: "Empaque y Etiqueta",               short: "Empaque",     color: "#DB2777" },
  { key: "regulator", label: "Regulatorio FDA / FSVP",           short: "Regulatorio", color: "#DC2626" },
  { key: "precios",   label: "Precios y Financiero",             short: "Precios",     color: "#059669" },
  { key: "ventas",    label: "Ventas y Clientes",                short: "Ventas",      color: "#EA580C" },
  { key: "logistica", label: "Logística y Cadena de Suministro", short: "Logística",   color: "#0891B2" },
  { key: "proveedor", label: "Requisitos de Proveedor",          short: "Proveedor",   color: "#65758B" },
];
const RES_FRENTE_MAP = RES_FRENTES.reduce((a, f) => { a[f.key] = f; return a; }, {});

// Fichas semilla — redactadas a partir de los documentos fuente entregados.
// Todas nacen en borrador: el cliente sólo ve las que Miguel marque como publicadas.
const RES_SEED = [
  {
    id: "res_mkt01", codigo: "MKT-01", frente: "mercado", version: "v1", fecha: "2026-05",
    titulo: "Category Insights: galletas en Estados Unidos (Nielsen)",
    resumen: "Radiografía de la categoría de galletas en Estados Unidos con datos Nielsen a 52 semanas. Mide el tamaño real del mercado, el peso del segmento better-for-you y qué tan sano es el crecimiento en California y Texas.",
    hallazgos: [
      "La categoría de $10 mil millones está plana. El crecimiento en dólares se está fabricando con precio y distribución (TDPs), no con demanda: las velocidades caen 7% a nivel nacional.",
      "Better-For-You es 8.3% de la categoría nacional y 10.4% en California, pero sólo 6.4% en Texas. Texas no está saturado de BFY — está sin desarrollar.",
      "De las 20 principales marcas BFY, sólo 7 crecen. Los retailers están comprando el discurso BFY, pero eso todavía no se traduce en rotación en anaquel.",
    ],
    kpis: [{ valor: "−7%", label: "velocidad de la categoría" }, { valor: "6.4%", label: "BFY en Texas vs 10.4% CA" }],
    link: "", tareas: "", fuente: "Nielsen MULO, 52 semanas al 18/abr/2026", publicado: true,
    archivos: [{ nombre: "Category Insights Cookies.pdf", portada: "data:image/jpeg;base64,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", url: "" }],
  },
  {
    id: "res_mkt02", codigo: "MKT-02", frente: "mercado", version: "v1", fecha: "2026-04",
    titulo: "Evaluación competitiva por SKU en anaquel US",
    resumen: "Ficha comparativa de cada sabor de Gaveti contra su set competitivo en anaquel estadounidense. Incluye claims de frente y reverso, tabla nutrimental, ingredientes, precio base y presencia por retailer para los seis SKU.",
    hallazgos: [
      "Se levantó ficha completa por cada SKU (Chispi Chocs, Blueberry, Arándano, Chocolate, Chía Naranja, FibraFit): claims de frente y reverso, tabla nutrimental, lista de ingredientes, precio base y presencia por retailer.",
      "Gaveti es de los pocos del set competitivo que no declara gluten free. Casi todo el bloque BFY compite con ese claim — es la brecha de posicionamiento más grande frente al set.",
      "El claim de menos azúcar está calculado contra Belvita Blueberry normalizado a porción de 30 g. La aritmética está, pero el expediente documental de sustento todavía no está formalizado.",
    ],
    kpis: [{ valor: "6", label: "sets competitivos por SKU" }, { valor: "11", label: "retailers rastreados" }],
    link: "", tareas: "", fuente: "Cookies Competitor Assessment", publicado: true,
    archivos: [{ nombre: "Cookies Competitor Assessment.xlsx", url: "" }],
  },
  {
    id: "res_pre01", codigo: "PRE-01", frente: "precios", version: "v1", fecha: "2026-06",
    titulo: "Benchmark competitivo de precios — H-E-B Texas",
    resumen: "Levantamiento de precios en el anaquel de H-E-B Texas sobre el bloque premium y better-for-you. Define el corredor de precio por gramo en el que Gaveti puede entrar sin perder margen ni credibilidad premium.",
    hallazgos: [
      "La categoría better-for-you en H-E-B está concentrada alrededor de un ancla de $4.98. Ya existe un umbral premium establecido por Annie's, Partake y Goodie Girl.",
      "El precio por gramo, no el precio de anaquel, es la métrica válida de comparación: el tamaño de paquete distorsiona el valor percibido entre marcas.",
      "El corredor recomendado es 0.024–0.028 USD/g, con base en 0.025 y premium en 0.027. Eso ubica a Gaveti como premium accesible, sin cruzar al tramo funcional de precio alto.",
    ],
    kpis: [{ valor: "$0.025–0.027", label: "USD por gramo objetivo" }, { valor: "$4.98", label: "ancla de anaquel" }],
    link: "", tareas: "", fuente: "Competitive Pricing Benchmark – HEB US", publicado: true,
    archivos: [{ nombre: "Competitive Pricing Benchmark HEB.docx", url: "" }],
  },
  {
    id: "res_pre02", codigo: "PRE-02", frente: "precios", version: "v2", fecha: "2026-07",
    titulo: "Comparativa de precios en clubes — Costco y Sam's Club Texas",
    resumen: "Levantamiento en tienda de 41 referencias de galletas en Costco y Sam's Club de Texas. Compara a Gaveti contra cada una en precio de caja, precio por paquete y precio por kilo.",
    hallazgos: [
      "Levantamiento en tienda de 21 referencias en Costco Texas y 20 en Sam's Club Texas, con contenido neto, número de paquetes, precio de caja, precio por paquete y precio por kilo.",
      "En Costco, Gaveti Minis a $10.89 queda exactamente a la par de Barnum's Animals: mismo gramaje de 840 g y mismo precio de caja. El punto de entrada es defendible.",
      "En Sam's, Gaveti Blueberry empata en precio de caja con Oreo Mini Variety, pero queda arriba en precio por kilo. La desventaja de Gaveti no aparece en el precio de anaquel, aparece en el precio por kilo.",
    ],
    kpis: [{ valor: "41", label: "referencias levantadas" }, { valor: "2", label: "clubes en Texas" }],
    link: "#precios", tareas: "", fuente: "Comparativa Gaveti vs Costco/Sam's", publicado: true,
    archivos: [{ nombre: "Comparativa Gaveti vs Costco y Sams.xlsx", url: "" }],
  },
  {
    id: "res_fin01", codigo: "FIN-01", frente: "precios", version: "v1", fecha: "2026-07",
    titulo: "Costeo unitario y P&L por modelo de canal",
    resumen: "Costeo completo de cada SKU a nivel EXW y proyección de resultados para cuatro modelos de canal. Muestra en qué canal el negocio en Estados Unidos deja margen y en cuál se sostiene apenas.",
    hallazgos: [
      "Se costeó cada SKU a nivel EXW con desglose completo: ingredientes, mano de obra, empaque primario, empaque secundario, master case, tarima y costos indirectos.",
      "En el escenario base con 20% de indirectos, el margen de contribución es positivo en los cuatro modelos de canal, pero el EBITDA queda al filo: entre −0.1% y 6% de ventas netas en directo y distribuidor.",
      "Clubes es el único canal con EBITDA sano en el escenario base. Si los indirectos suben a 50%, el EBITDA se vuelve negativo en tres de los cuatro modelos — el margen depende de la absorción de indirectos, no del precio.",
    ],
    kpis: [{ valor: "$1.26", label: "precio de transferencia EXW objetivo" }, { valor: "0.9%–6.0%", label: "EBITDA escenario base" }],
    link: "", tareas: "", fuente: "Costing and P&L", publicado: false,
    archivos: [{ nombre: "Costing and P&L.xlsx", url: "" }],
  },
  {
    id: "res_reg01", codigo: "REG-01", frente: "regulator", version: "Rev. 01", fecha: "2026-07",
    titulo: "Análisis regulatorio de etiqueta FDA — Blueberry Chia",
    resumen: "Revisión de la etiqueta de Blueberry Chia contra la regulación de etiquetado de la FDA, hecha por AsAlim. Señala apartado por apartado qué cumple, qué no, y qué hay que corregir antes de poder importar.",
    hallazgos: [
      "Revisión punto por punto contra 21 CFR Parte 101 hecha por AsAlim: denominación, contenido neto, ingredientes, alérgenos, tabla nutrimental, claims, datos de fabricante y fecha de consumo preferente.",
      "Cinco apartados no cumplen y bloquean la importación: declaración de ingredientes (harina enriquecida y el uso de essence en lugar de flavor), alérgenos de adición directa sin negritas, la leyenda may contain con la palabra lecithin, seis valores de la tabla nutrimental, y Best before que en California debe decir Best if used by.",
      "Los claims de menos azúcar y dairy free cumplen en forma, pero requieren expediente documental de sustento — identidad del alimento de referencia, su contenido de azúcares por porción de 30 g y el cálculo del porcentaje — que hoy no está armado.",
    ],
    kpis: [{ valor: "5", label: "apartados que no cumplen" }, { valor: "6", label: "valores nutrimentales a corregir" }],
    link: "", tareas: "", fuente: "AsAlim AS-ID-005, Rev. 01", publicado: true,
    archivos: [{ nombre: "Informe de Etiquetado AsAlim AS-ID-005.pdf", url: "" }],
  },
  {
    id: "res_emp01", codigo: "EMP-01", frente: "empaque", version: "v1", fecha: "2026-06",
    titulo: "Propuestas de diseño de empaque para Estados Unidos",
    resumen: "Rutas de diseño de empaque para el mercado estadounidense en cinco líneas de producto, con alternativas cromáticas por línea. Incluye una versión específica para el canal de clubes.",
    hallazgos: [
      "Se desarrollaron rutas de diseño para cinco líneas —Choco Chip, Chocolate, Blueberry Chia, Cranberry y Superfoods— con dos a cinco alternativas cromáticas por línea.",
      "Se desarrolló una versión específica para el canal de clubes, distinta del formato de retail tradicional.",
      "PENDIENTE DE REDACCIÓN: el tercer hallazgo requiere tu criterio sobre qué ruta se eligió y por qué.",
    ],
    kpis: [{ valor: "5", label: "líneas de producto" }, { valor: "2", label: "formatos: retail y club" }],
    link: "", tareas: "", fuente: "Gaveti Empaque PROPUESTAS, may–jun 2026", publicado: false,
    archivos: [{ nombre: "Gaveti Empaque PROPUESTAS.pptx", url: "" }],
  },
  {
    id: "res_emp02", codigo: "EMP-02", frente: "empaque", version: "v1", fecha: "2026-06",
    titulo: "Evaluación de diseño gráfico y prototipos de marca",
    resumen: "PENDIENTE DE REDACCIÓN — requiere tu criterio sobre el diagnóstico de diseño.",
    hallazgos: [
      "PENDIENTE DE REDACCIÓN: los documentos fuente son visuales sin texto extraíble. Requiere tu criterio.",
      "PENDIENTE DE REDACCIÓN.",
      "PENDIENTE DE REDACCIÓN.",
    ],
    kpis: [],
    link: "", tareas: "", fuente: "Graphic Design Assessment + US Market Proto Designs", publicado: false,
    archivos: [{ nombre: "Gaveti Graphic Design Assessment.pptx", url: "" }, { nombre: "Gaveti US Market Proto Designs.pptx", url: "" }],
  },
];

const RES_SEED_MAP = RES_SEED.reduce((a, f) => { a[f.id] = f; return a; }, {});

// ══════════════════════════════════════════════════════════════════════════
// MODO DEMO — demo.partecpg.com
// Caso 100% ficticio para mostrar la herramienta a prospectos, sin contraseña.
// "Andesa" es una marca inventada (alfajores de manjar, exportador mexicano,
// mismo modelo de negocio que Gaveti) compitiendo contra marcas igual de
// inventadas con sonoridad sudamericana — ninguna existe. Nunca se lee ni se
// escribe Firestore en este modo: ver IS_DEMO y los envoltorios de
// onSnapshot/getDoc/setDoc/runTransaction más abajo. Un visitante puede
// arrastrar, editar y explorar todo con total libertad — al recargar, vuelve
// exactamente a este mismo punto de partida.
// ══════════════════════════════════════════════════════════════════════════
const IS_DEMO = (() => {
  try {
    const h = window.location.hostname;
    const q = new URLSearchParams(window.location.search);
    return h.startsWith("demo.") || q.get("demo") === "1";
  } catch { return false; }
})();
// Usado en textos de UI de PriceBubbleChart/FxExposureBlock que antes decían
// "Gaveti" fijo — esos componentes no reciben el nombre de marca por props.
const BRAND_NAME = IS_DEMO ? "Andesa" : "Gaveti";

const DEMO_PA_COSTCO = [
  { n: "Andesa Alfajores de Manjar Clásicos", g: 900, gp: 30, p: 9.49, comp: false },
  { n: "Valle Dorado Alfajores Surtidos", g: 1080, gp: 36, p: 11.99, comp: true },
  { n: "Doña Inés Alfajores de Chocolate", g: 756, gp: 42, p: 10.49, comp: true },
  { n: "Cordillera Snacks Wafers de Manjar", g: 600, gp: 25, p: 7.99, comp: false },
  { n: "Sur Andino Alfajores Premium", g: 1200, gp: 50, p: 14.99, comp: true },
  { n: "Mistral Treats Galletas Rellenas", g: 1400, gp: 35, p: 13.49, comp: false },
  { n: "Raíces del Sur Alfajores Artesanales", g: 1050, gp: 35, p: 15.99, comp: true },
  { n: "Volcán Dulce Barras de Manjar", g: 720, gp: 24, p: 8.49, comp: false },
  { n: "Nevado Foods Cookies Rellenas", g: 1680, gp: 56, p: 17.49, comp: false },
  { n: "Patagonia Bites Mini Alfajores", g: 560, gp: 14, p: 6.99, comp: true },
];
const DEMO_PA_SAMS = [
  { n: "Andesa Alfajores de Manjar en Sam's Club", g: 900, gp: 30, p: 9.29, comp: false },
  { n: "Valle Dorado Alfajores Surtidos (Club Pack)", g: 1200, gp: 40, p: 12.98, comp: true },
  { n: "Sierra Gold Alfajores Tradicionales", g: 850, gp: 42.5, p: 10.98, comp: true },
  { n: "Manjar Real Wafer Sandwich", g: 700, gp: 28, p: 8.98, comp: false },
  { n: "Cordillera Snacks Wafers de Manjar (Club)", g: 660, gp: 27.5, p: 7.48, comp: false },
  { n: "Mistral Treats Galletas Rellenas (Club Pack)", g: 1600, gp: 40, p: 14.98, comp: false },
  { n: "Doña Inés Alfajores Mini Variety", g: 900, gp: 22.5, p: 9.98, comp: true },
  { n: "Nevado Foods Cookies Rellenas (36 ct)", g: 1800, gp: 50, p: 17.98, comp: false },
  { n: "Patagonia Bites Mini Alfajores (Club)", g: 700, gp: 14, p: 8.48, comp: true },
];

const DEMO_PRICE_PNL = {
  costco: { exw: 3.65, freightImport: 0.08, broker: 0.03, sgaParte: 0.05, sgaGaveti: 0.00 },
  sams:   { exw: 3.60, freightImport: 0.08, broker: 0.03, sgaParte: 0.05, sgaGaveti: 0.00 },
};
const DEMO_FX_EXPOSURE = {
  fxRef: 16.92, fxBudget: 17.10, fx: 16.92, tariff: 0, club: "costco",
  costco: { retail: 9.49, costMxn: 58.0, cajasAnio: 15000 },
  sams:   { retail: 9.29, costMxn: 57.5, cajasAnio: 15000 },
};

const DEMO_CLIENT_TASKS = { areas: [
  { id: "dca1", name: "Diseños", rows: [
    { id: "dcr1", paso: "Diseño de empaque primario para Alfajores Clásicos (30 g)", responsable: "Marco R.", fecha: "", estatus: "en_progreso", prio: 0 },
    { id: "dcr2", paso: "Adaptar dieline a formato club (caja de 30 unidades)", responsable: "Marco R.", fecha: "", estatus: "pendiente", prio: 1 },
    { id: "dcr3", paso: "Validar sello Kosher con certificadora en Guadalajara", responsable: "", fecha: "", estatus: "pendiente", prio: 5 },
  ]},
  { id: "dca2", name: "Comercial", rows: [
    { id: "dcr4", paso: "Definir precio de transferencia y precio de lista para Costco y Sam's", responsable: "Miguel", fecha: "", estatus: "en_progreso", prio: 2 },
    { id: "dcr5", paso: "Confirmar gramaje final de venta (900 g vs. alternativa de 1.2 kg)", responsable: "Rodrigo Z.", fecha: "", estatus: "pendiente", prio: 3 },
    { id: "dcr6", paso: "Preparar deck comercial con benchmark de anaquel", responsable: "Miguel", fecha: "", estatus: "pendiente", prio: 6 },
  ]},
  { id: "dca3", name: "Regulatorio", rows: [
    { id: "dcr7", paso: "Análisis de etiqueta FDA — declaración de alérgenos (leche, nuez)", responsable: "", fecha: "", estatus: "pendiente", prio: 4 },
    { id: "dcr8", paso: "FSVP: designar agente calificado en Estados Unidos", responsable: "Rodrigo Z.", fecha: "", estatus: "pendiente", prio: 7 },
  ]},
  { id: "dca4", name: "Logística", rows: [
    { id: "dcr9", paso: "Configuración de pallet — validar máximo de altura por transportista", responsable: "", fecha: "", estatus: "pendiente", prio: 8 },
    { id: "dcr10", paso: "Cotizar flete Guadalajara–Laredo–Texas (LTL vs. FTL)", responsable: "Rodrigo Z.", fecha: "", estatus: "en_progreso", prio: 9 },
  ]},
]};

const DEMO_CRM = { deals: [
  { id: "dcrm1", nombre: "Costco — Texas", contacto: "Buyer regional", fecha: "2026-11-01", etapa: "Cita agendada", notas: "Line review de galletería/snacks dulces. Deck y muestras en preparación." },
  { id: "dcrm2", nombre: "Sam's Club — Texas", contacto: "", fecha: "", etapa: "Contactado", notas: "Interés inicial confirmado vía broker." },
  { id: "dcrm3", nombre: "H-E-B", contacto: "Compradora de snacks", fecha: "2026-10-15", etapa: "Prospecto", notas: "Categoría better-for-you en expansión, buen encaje con el claim de manjar sin azúcar añadida." },
  { id: "dcrm4", nombre: "KeHE Distributors", contacto: "", fecha: "", etapa: "Contactado", notas: "UPC/GTIN en trámite, requisito para alta." },
  { id: "dcrm5", nombre: "Dot Foods", contacto: "", fecha: "", etapa: "Prospecto", notas: "" },
  { id: "dcrm6", nombre: "Whole Foods (regional Texas)", contacto: "", fecha: "", etapa: "Prospecto", notas: "Evaluar si el precio compite en el canal premium." },
]};

const DEMO_TIMELINE = { milestones: [
  { id: "dtl1", area: "Estrategia y Producto", mes: "2026-03", label: "Análisis de categoría y definición de producto", estado: "completado", notas: "" },
  { id: "dtl2", area: "Estrategia y Producto", mes: "2026-04", label: "Selección de canal: clubes (Costco, Sam's) primero", estado: "completado", notas: "" },
  { id: "dtl3", area: "Regulatorio", mes: "2026-05", label: "Análisis de etiqueta y tabla nutrimental", estado: "en_progreso", notas: "" },
  { id: "dtl4", area: "Regulatorio", mes: "2026-06", label: "Registro FSVP y agente calificado en EE.UU.", estado: "pendiente", notas: "" },
  { id: "dtl5", area: "Empaque y Diseño", mes: "2026-05", label: "Diseño de empaque primario y secundario", estado: "en_progreso", notas: "" },
  { id: "dtl6", area: "Empaque y Diseño", mes: "2026-07", label: "Impresión de dummies y aprobación de dieline", estado: "pendiente", notas: "" },
  { id: "dtl7", area: "Altas y Logística", mes: "2026-06", label: "Configuración de pallet y cotización de flete", estado: "en_progreso", notas: "" },
  { id: "dtl8", area: "Altas y Logística", mes: "2026-08", label: "Alta con distribuidores (KeHE, Dot Foods)", estado: "pendiente", notas: "" },
  { id: "dtl9", area: "Ventas / CRM", mes: "2026-07", label: "Presentación comercial y muestras a Costco", estado: "en_progreso", notas: "" },
  { id: "dtl10", area: "Ventas / CRM", mes: "2026-11", label: "Line review Costco Texas", estado: "pendiente", notas: "" },
]};

const DEMO_BLOQUEOS = {
  ventanas: [
    { id: "dvt1", nombre: "Line review Costco (Texas)", fecha: "2026-11-01", notas: "Ventana de compra. Si se pierde, el siguiente ciclo es semestral." },
    { id: "dvt2", nombre: "Cierre de altas para temporada holiday 2027", fecha: "2026-09-30", notas: "Los distribuidores requieren producto en catálogo con anticipación." },
  ],
  bloqueos: [
    {
      id: "dbl1", titulo: "Etiqueta pendiente de validación FDA", frente: "Regulatorio",
      desde: "2026-05-15", estado: "activo", resueltoEl: "", aprendizaje: "", categoria: "Regulatorio",
      items: [
        { id: "dbl1i1", texto: "Confirmar declaración de alérgenos (leche, nuez) con laboratorio", responsable: "", estado: "pendiente", fecha: "" },
        { id: "dbl1i2", texto: "Definir claim de 'sin azúcar añadida' con expediente de sustento", responsable: "Miguel", estado: "pendiente", fecha: "" },
      ],
    },
  ],
};

const DEMO_RESULTADOS = { fichas: [
  {
    id: "dres1", codigo: "MKT-01", frente: "mercado", version: "v1", fecha: "2026-04",
    titulo: "Category Insights: alfajores y galletería premium en EE.UU.",
    resumen: "Radiografía de la categoría de galletería dulce rellena en clubes de EE.UU., con foco en el segmento premium/artesanal.",
    hallazgos: [
      "El segmento premium relleno crece 4% mientras la categoría general está plana — el espacio en anaquel se está reasignando hacia ahí.",
      "Ningún competidor directo declara 'sin azúcar añadida' en el canal de clubes — es un hueco de posicionamiento abierto.",
    ],
    kpis: [{ valor: "+4%", label: "crecimiento del segmento premium" }, { valor: "0", label: "competidores con claim sin azúcar añadida" }],
    link: "", tareas: "", fuente: "Demo — caso sintético", publicado: true, archivos: [],
  },
  {
    id: "dres2", codigo: "PRE-01", frente: "precios", version: "v1", fecha: "2026-06",
    titulo: "Comparativa de precios — Costco y Sam's Club Texas",
    resumen: "Levantamiento de referencias directas en anaquel de clubes de Texas contra Andesa.",
    hallazgos: [
      "Andesa entra a $9.49 en Costco, por debajo del ancla premium de $11.99 — hay espacio para subir precio sin perder el punto de entrada.",
      "En Sam's Club, el precio por kilo de Andesa ya es competitivo frente al set directo.",
    ],
    kpis: [{ valor: "$9.49", label: "precio de entrada Costco" }, { valor: "9", label: "referencias comparadas" }],
    link: "#precios", tareas: "", fuente: "Demo — caso sintético", publicado: true, archivos: [],
  },
  {
    id: "dres3", codigo: "FIN-01", frente: "precios", version: "v1", fecha: "2026-07",
    titulo: "Costeo unitario y exposición cambiaria",
    resumen: "Modelo de P&L por caja con arancel sobre FOB estimado y sensibilidad al tipo de cambio USD/MXN.",
    hallazgos: [
      "En el escenario base el EBITDA de la operación en EE.UU. es sano; se erosiona rápido si el peso se fortalece más allá de 15.5.",
      "El arancel de Sección 301 (10%) es absorbible; un escenario adverso de 25% deja la operación en EBITDA negativo.",
    ],
    kpis: [{ valor: "16.92", label: "TC de referencia" }, { valor: "10%", label: "arancel máximo absorbible" }],
    link: "#precios", tareas: "", fuente: "Demo — caso sintético", publicado: true, archivos: [],
  },
]};

const DEMO_PROJECTS_RAW = [
  {
    id: "ds1", space: "MARKET ANALYSIS", color: SPACE_COLORS["MARKET ANALYSIS"],
    tasks: [
      { id: "dt1", name: "Category Data & Competitive Assessment", owners: ["MR"], start: new Date(2026,2,2), end: new Date(2026,2,20), progress: 100,
        subtasks: [
          { id: "dst1a", name: "Obtain 52-week club-channel sales data", owners: ["MR"], start: new Date(2026,2,2), end: new Date(2026,2,10), progress: 100 },
          { id: "dst1b", name: "Benchmark packaging and shelf presence", owners: ["MR"], start: new Date(2026,2,10), end: new Date(2026,2,20), progress: 100 },
        ]
      },
      { id: "dt2", name: "Store Visits — Texas Clubs", owners: ["RZ"], start: new Date(2026,3,1), end: new Date(2026,3,10), progress: 60,
        subtasks: [
          { id: "dst2a", name: "Plan visits by anchor account priority", owners: ["RZ"], start: new Date(2026,3,1), end: new Date(2026,3,5), progress: 100 },
          { id: "dst2b", name: "Shelf audit — Costco & Sam's Club", owners: ["RZ"], start: new Date(2026,3,5), end: new Date(2026,3,10), progress: 20 },
        ]
      },
    ]
  },
  {
    id: "ds2", space: "REGULATORY COMPLIANCE", color: SPACE_COLORS["REGULATORY COMPLIANCE"],
    tasks: [
      { id: "dt3", name: "FDA Label Compliance", owners: ["MPG"], start: new Date(2026,4,1), end: new Date(2026,5,15), progress: 40,
        subtasks: [
          { id: "dst3a", name: "Allergen declaration review", owners: ["MPG"], start: new Date(2026,4,1), end: new Date(2026,4,20), progress: 70 },
          { id: "dst3b", name: "Nutritional panel & 'no added sugar' claim file", owners: ["MPG"], start: new Date(2026,4,20), end: new Date(2026,5,15), progress: 10 },
        ]
      },
      { id: "dt4", name: "FSVP Registration", owners: ["RZ"], start: new Date(2026,5,1), end: new Date(2026,5,30), progress: 0,
        subtasks: [
          { id: "dst4a", name: "Designate qualified U.S. agent", owners: ["RZ"], start: new Date(2026,5,1), end: new Date(2026,5,15), progress: 0 },
        ]
      },
    ]
  },
  {
    id: "ds3", space: "SALES", color: SPACE_COLORS["SALES"],
    tasks: [
      { id: "dt5", name: "Costco Line Review Prep", owners: ["MPG","RZ"], start: new Date(2026,7,1), end: new Date(2026,9,30), progress: 20,
        subtasks: [
          { id: "dst5a", name: "Broker contract & sample logistics", owners: ["RZ"], start: new Date(2026,7,1), end: new Date(2026,7,20), progress: 60 },
          { id: "dst5b", name: "Commercial deck with shelf benchmark", owners: ["MPG"], start: new Date(2026,7,20), end: new Date(2026,8,15), progress: 0 },
        ]
      },
    ]
  },
];
const DEMO_PROJECTS = { list: serializeProjects(DEMO_PROJECTS_RAW) };
const DEMO_OWNER_COLORS = { colors: OWNER_COLORS_INIT };
const DEMO_OBJECTIVES = { list: [
  { id: "do1", label: "Definición de producto y precio", color: "#3B82F6", progress: 70, date: "" },
  { id: "do2", label: "Cumplimiento regulatorio FDA/FSVP", color: "#F59E0B", progress: 35, date: "" },
  { id: "do3", label: "Line review Costco Texas", color: "#EF4444", progress: 10, date: "" },
]};
const DEMO_META = { companyName: "Parte CPG · Andesa", projectName: "U.S. Expansion — Project Gantt" };
const DEMO_PIZARRA = { avisos: [] };

// ── Análisis de Categoría (demo) — nunca toca Firestore, vive solo en memoria.
// caIdx/txIdx: igual que un "brand index" de Nielsen (100 = en la media);
// escalan participación/dólares al cambiar de geografía sin tener que
// mantener 3 datasets completos por separado.
const DEMO_CATEGORY_BRANDS = [
  { id: "andesa",   name: "Andesa",         isSelf: true,  dollars: 9.2,  share: 3.1,  dollarChg: 18,  velChg: 6,   tdpChg: 11, avgPrice: 9.49,  bfy: true,  subcat: "Alfajor Clásico",    flavor: "Manjar",         caIdx: 145, txIdx: 70 },
  { id: "valle",    name: "Valle Dorado",   isSelf: false, dollars: 42.1, share: 14.2, dollarChg: -3,  velChg: -9,  tdpChg: 5,  avgPrice: 11.99, bfy: false, subcat: "Alfajor Surtido",    flavor: "Surtido",        caIdx: 90,  txIdx: 115 },
  { id: "dona",     name: "Doña Inés",      isSelf: false, dollars: 31.5, share: 10.6, dollarChg: -5,  velChg: -12, tdpChg: 4,  avgPrice: 10.49, bfy: false, subcat: "Alfajor Chocolate",  flavor: "Chocolate",      caIdx: 85,  txIdx: 120 },
  { id: "cordi",    name: "Cordillera",     isSelf: false, dollars: 18.7, share: 6.3,  dollarChg: 2,   velChg: -4,  tdpChg: 8,  avgPrice: 7.99,  bfy: false, subcat: "Wafer de Manjar",    flavor: "Manjar",         caIdx: 75,  txIdx: 130 },
  { id: "sur",      name: "Sur Andino",     isSelf: false, dollars: 27.3, share: 9.2,  dollarChg: -8,  velChg: -15, tdpChg: 3,  avgPrice: 14.99, bfy: true,  subcat: "Alfajor Premium",    flavor: "Chocolate Blanco", caIdx: 160, txIdx: 55 },
  { id: "mistral",  name: "Mistral Treats", isSelf: false, dollars: 22.9, share: 7.7,  dollarChg: 1,   velChg: -6,  tdpChg: 7,  avgPrice: 13.49, bfy: false, subcat: "Galleta Rellena",    flavor: "Vainilla",       caIdx: 95,  txIdx: 105 },
  { id: "raices",   name: "Raíces del Sur", isSelf: false, dollars: 15.2, share: 5.1,  dollarChg: -11, velChg: -19, tdpChg: 2,  avgPrice: 15.99, bfy: true,  subcat: "Alfajor Artesanal",  flavor: "Coco",           caIdx: 150, txIdx: 60 },
  { id: "volcan",   name: "Volcán Dulce",   isSelf: false, dollars: 12.6, share: 4.2,  dollarChg: 6,   velChg: 2,   tdpChg: 9,  avgPrice: 8.49,  bfy: false, subcat: "Barra de Manjar",    flavor: "Manjar",         caIdx: 80,  txIdx: 125 },
  { id: "nevado",   name: "Nevado Foods",   isSelf: false, dollars: 19.8, share: 6.7,  dollarChg: -2,  velChg: -8,  tdpChg: 5,  avgPrice: 17.49, bfy: false, subcat: "Galleta Rellena",    flavor: "Chocolate",      caIdx: 100, txIdx: 100 },
  { id: "patagonia",name: "Patagonia Bites",isSelf: false, dollars: 8.4,  share: 2.8,  dollarChg: 34,  velChg: 21,  tdpChg: 18, avgPrice: 6.99,  bfy: true,  subcat: "Mini Alfajor",       flavor: "Mixto",          caIdx: 130, txIdx: 90 },
  { id: "sierra",   name: "Sierra Gold",    isSelf: false, dollars: 11.1, share: 3.7,  dollarChg: 9,   velChg: 3,   tdpChg: 10, avgPrice: 10.98, bfy: false, subcat: "Alfajor Tradicional",flavor: "Manjar",         caIdx: 70,  txIdx: 140 },
  { id: "manjar",   name: "Manjar Real",    isSelf: false, dollars: 7.9,  share: 2.7,  dollarChg: -6,  velChg: -10, tdpChg: 1,  avgPrice: 8.98,  bfy: false, subcat: "Wafer Sandwich",     flavor: "Manjar",         caIdx: 65,  txIdx: 135 },
];
const DEMO_CATEGORY_GEO = {
  national: { label: "Nacional",   dollars: 227,  dollarChg: 1,  velChg: -6, tdpChg: 5 },
  ca:       { label: "California", dollars: 19.4, dollarChg: 2,  velChg: -8, tdpChg: 6 },
  tx:       { label: "Texas",      dollars: 21.8, dollarChg: 0,  velChg: -5, tdpChg: 7 },
};

const DEMO_SEED = {
  "gantt/client_tasks": DEMO_CLIENT_TASKS,
  "gantt/crm_deals": DEMO_CRM,
  "gantt/timeline": DEMO_TIMELINE,
  "gantt/bloqueos": DEMO_BLOQUEOS,
  "gantt/resultados": DEMO_RESULTADOS,
  "gantt/price_pnl": DEMO_PRICE_PNL,
  "gantt/fx_exposure": DEMO_FX_EXPOSURE,
  "gantt/projects": DEMO_PROJECTS,
  "gantt/ownerColors": DEMO_OWNER_COLORS,
  "gantt/objectives": DEMO_OBJECTIVES,
  "gantt/meta": DEMO_META,
  "gantt/pizarra": DEMO_PIZARRA,
};

// Las fichas viven en Firestore, así que una versión anterior puede traer el
// campo "pregunta". No se hereda: se sustituye por el resumen del seed y, si la
// ficha es de cosecha propia y no tiene resumen, queda vacío para que se note.
const resMigrate = (list) => (list || []).map(f => ({
  id: f.id || "res" + Math.random().toString(36).slice(2, 9),
  codigo: f.codigo || "", frente: RES_FRENTE_MAP[f.frente] ? f.frente : "mercado",
  titulo: f.titulo || "Sin título",
  resumen: f.resumen || (RES_SEED_MAP[f.id] ? RES_SEED_MAP[f.id].resumen : ""),
  hallazgos: Array.isArray(f.hallazgos) ? f.hallazgos : [],
  kpis: Array.isArray(f.kpis) ? f.kpis : [],
  version: f.version || "v1", fecha: f.fecha || "",
  // "#precios" y similares son saltos internos, no documentos: se conservan aparte.
  link: (f.link || "").startsWith("#") ? f.link : "",
  archivos: Array.isArray(f.archivos) && f.archivos.length
    ? f.archivos.filter(a => a && (a.nombre || a.url))
    : (f.link && !f.link.startsWith("#") ? [{ nombre: "Documento", url: f.link }]
       : (RES_SEED_MAP[f.id] ? (RES_SEED_MAP[f.id].archivos || []) : [])),
  tareas: f.tareas || "", fuente: f.fuente || "", publicado: !!f.publicado,
}));

const resFechaLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const meses = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
  const mi = parseInt(m, 10) - 1;
  return meses[mi] ? `${meses[mi]} ${y}` : ym;
};

function TlFasesBar({ milestones, fase, setFase }) {
  const narrow = useIsNarrow(760);

  // Conteos por fase y arcos de iteración reales
  const conteo = {}, hechos = {};
  TL_FASES.forEach(f => { conteo[f.key] = 0; hechos[f.key] = 0; });
  let sinFase = 0;
  const arcos = {}; // "de>a" -> count
  (milestones || []).forEach(m => {
    if (m.fase && conteo[m.fase] !== undefined) {
      conteo[m.fase]++;
      if (m.estado === "completado" || m.estado === "listo") hechos[m.fase]++;
    } else sinFase++;
    (m.iteraciones || []).forEach(it => {
      if (!it.de || !it.a || it.de === it.a) return;
      if (TL_FASE_IDX[it.de] === undefined || TL_FASE_IDX[it.a] === undefined) return;
      const k = it.de + ">" + it.a;
      arcos[k] = (arcos[k] || 0) + 1;
    });
  });

  const W = 760, H = 230;
  const cx = (i) => 68 + i * 125;
  const CY = 158, R = 33;

  return (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: narrow ? "14px 10px" : "16px 18px", marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Fases del proyecto</div>
        {fase && (
          <button onClick={() => setFase(null)} style={{ fontSize: 11, fontWeight: 700, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 7, padding: "4px 10px", cursor: "pointer" }}>
            Quitar filtro
          </button>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: "#94a3b8", lineHeight: 1.5, marginBottom: 6 }}>
        Un proceso iterativo, no lineal. Los arcos de arriba son iteraciones reales registradas en los hitos: regresar es parte del proceso, no un incumplimiento.
      </div>

      <div style={{ overflowX: "auto" }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", minWidth: narrow ? 620 : 0, display: "block" }} xmlns="http://www.w3.org/2000/svg">
          <defs>
            {TL_FASES.map(f => (
              <marker key={f.key} id={`arrow-${f.key}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={f.color} />
              </marker>
            ))}
          </defs>

          {/* Arcos de iteración (de fase posterior de regreso a fase previa) */}
          {Object.entries(arcos).map(([k, n]) => {
            const [de, a] = k.split(">");
            const i = TL_FASE_IDX[de], j = TL_FASE_IDX[a];
            const x1 = cx(i), x2 = cx(j);
            const span = Math.abs(i - j);
            const alto = CY - R - 16 - span * 22;
            const col = TL_FASES[j].color;
            return (
              <g key={k}>
                <path d={`M ${x1} ${CY - R - 6} Q ${(x1 + x2) / 2} ${alto} ${x2} ${CY - R - 6}`}
                  fill="none" stroke={col} strokeWidth={1.4 + Math.min(n, 4) * 0.8} opacity="0.72"
                  markerEnd={`url(#arrow-${TL_FASES[j].key})`} />
                {n > 1 && (
                  <text x={(x1 + x2) / 2} y={alto + span * 7 + 4} textAnchor="middle" fontSize="11" fontWeight="800" fill={col}>{n}×</text>
                )}
              </g>
            );
          })}

          {/* Conectores entre círculos */}
          {TL_FASES.slice(0, -1).map((f, i) => (
            <line key={"c" + i} x1={cx(i) + R + 4} y1={CY} x2={cx(i + 1) - R - 4} y2={CY} stroke="#e2e8f0" strokeWidth="3" strokeLinecap="round" />
          ))}

          {/* Círculos */}
          {TL_FASES.map((f, i) => {
            const sel = fase === f.key;
            const n = conteo[f.key], ok = hechos[f.key];
            const pct = n ? ok / n : 0;
            const circ = 2 * Math.PI * R;
            return (
              <g key={f.key} onClick={() => setFase(sel ? null : f.key)} style={{ cursor: "pointer" }}>
                <title>{f.label} — {f.desc}</title>
                <text x={cx(i)} y={CY - R - 12} textAnchor="middle" fontSize="12.5" fontWeight="800"
                  fill={sel ? f.color : "#94a3b8"} letterSpacing="0.4">{f.label.toUpperCase()}</text>
                <circle cx={cx(i)} cy={CY} r={R + 6} fill={sel ? f.color + "1A" : "transparent"} />
                <circle cx={cx(i)} cy={CY} r={R} fill="#fff" stroke="#e2e8f0" strokeWidth="6" />
                <circle cx={cx(i)} cy={CY} r={R} fill="none" stroke={f.color} strokeWidth="6"
                  strokeDasharray={`${circ * pct} ${circ}`} transform={`rotate(-90 ${cx(i)} ${CY})`} strokeLinecap="round" opacity={pct ? 1 : 0} />
                <text x={cx(i)} y={CY + 2} textAnchor="middle" fontSize="19" fontWeight="800" fill={sel ? f.color : "#1e293b"}>{n}</text>
                <text x={cx(i)} y={CY + 17} textAnchor="middle" fontSize="9.5" fontWeight="700" fill="#94a3b8">{ok} listos</text>
              </g>
            );
          })}

          {/* Grupos */}
          {TL_GRUPOS.map(g => {
            const x1 = cx(g.desde) - R, x2 = cx(g.hasta) + R;
            return (
              <g key={g.label}>
                <line x1={x1} y1={CY + R + 14} x2={x2} y2={CY + R + 14} stroke="#e2e8f0" strokeWidth="2" strokeLinecap="round" />
                <text x={(x1 + x2) / 2} y={CY + R + 31} textAnchor="middle" fontSize="11" fontWeight="800" fill="#64748b" letterSpacing="0.6">{g.label.toUpperCase()}</text>
              </g>
            );
          })}
        </svg>
      </div>

      {sinFase > 0 && (
        <div style={{ marginTop: 6, fontSize: 11.5, color: "#94a3b8" }}>
          {sinFase} hito{sinFase === 1 ? "" : "s"} sin fase asignada. Ábrelos y asígnales una para que aparezcan aquí.
        </div>
      )}
      {fase && (
        <div style={{ marginTop: 8, fontSize: 11.5, color: "#64748b", background: "#f8fafc", borderRadius: 8, padding: "8px 11px", lineHeight: 1.5 }}>
          <b style={{ color: TL_FASES[TL_FASE_IDX[fase]].color }}>{TL_FASES[TL_FASE_IDX[fase]].label}:</b> {TL_FASES[TL_FASE_IDX[fase]].desc}
        </div>
      )}
    </div>
  );
}

function BloqueosTab({ canEdit, clientAreas, onClientAreas }) {
  const [data, setData] = useState(BLQ_SEED);
  const [loaded, setLoaded] = useState(false);
  const [openBlq, setOpenBlq] = useState({});
  const [sendPicker, setSendPicker] = useState(null);   // { bid, iid }
  const [resolveModal, setResolveModal] = useState(null); // { bid, aprendizaje, categoria }
  const [verHistorial, setVerHistorial] = useState(false);
  const narrow = useIsNarrow();
  const saveTimer = useRef(null);
  const pending = useRef(null);
  const lastLocalEdit = useRef(0);

  // ── Carga + suscripción en tiempo real ───────────────────────────────────
  useEffect(() => {
    const ref = doc(db, "gantt", "bloqueos");
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        if (Date.now() - lastLocalEdit.current < 1500) return;
        const d = snap.data();
        setData({ ventanas: d.ventanas || [], bloqueos: d.bloqueos || [] });
      } else {
        setDoc(ref, BLQ_SEED).catch(console.error);
      }
      setLoaded(true);
    }, (err) => { console.error("bloqueos snapshot", err); setLoaded(true); });
    return () => unsub();
  }, []);

  const persist = (next) => {
    pending.current = next;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setDoc(doc(db, "gantt", "bloqueos"), pending.current).catch(console.error);
      saveTimer.current = null; pending.current = null;
    }, 500);
  };
  useEffect(() => () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); if (pending.current) setDoc(doc(db, "gantt", "bloqueos"), pending.current).catch(console.error); }
  }, []);

  const update = (mutator) => {
    if (!canEdit) return;
    lastLocalEdit.current = Date.now();
    setData(prev => { const next = mutator(prev); persist(next); return next; });
  };

  // ── Fechas ───────────────────────────────────────────────────────────────
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const hoyIso = hoy.toISOString().slice(0, 10);
  const diasDesde = (iso) => { if (!iso) return null; const d = new Date(iso + "T00:00:00"); if (isNaN(d)) return null; return Math.floor((hoy - d) / 86400000); };
  const diasEntre = (a, b) => { if (!a || !b) return null; const x = new Date(a + "T00:00:00"), y = new Date(b + "T00:00:00"); if (isNaN(x) || isNaN(y)) return null; return Math.floor((y - x) / 86400000); };
  const diasHasta = (iso) => { if (!iso) return null; const d = new Date(iso + "T00:00:00"); if (isNaN(d)) return null; return Math.ceil((d - hoy) / 86400000); };

  // ── Enlace con Pendientes / Urgentes ─────────────────────────────────────
  const areas = clientAreas || [];
  const rowOf = (item) => {
    if (!item.clientRowId) return null;
    for (const a of areas) { const r = (a.rows || []).find(x => x.id === item.clientRowId); if (r) return { row: r, areaId: a.id, areaName: a.name }; }
    return null;
  };
  const estadoDe = (item) => {
    const l = rowOf(item);
    if (!l) return item.estado || "pendiente";
    return l.row.estatus === "completado" ? "listo" : l.row.estatus === "en_progreso" ? "en_progreso" : "pendiente";
  };
  const cycleEstado = (bid, item) => {
    if (!canEdit) return;
    const orden = ["pendiente", "en_progreso", "listo"];
    const nxt = orden[(orden.indexOf(estadoDe(item)) + 1) % 3];
    const l = rowOf(item);
    if (l && onClientAreas) {
      const estatus = nxt === "listo" ? "completado" : nxt;
      onClientAreas(areas.map(a => a.id !== l.areaId ? a : { ...a, rows: a.rows.map(r => r.id !== item.clientRowId ? r : { ...r, estatus }) }));
    } else {
      setItem(bid, item.id, { estado: nxt });
    }
  };
  const enviarAPendientes = (bid, item, areaId, urgente) => {
    if (!canEdit || !onClientAreas) return;
    const maxPos = Math.max(0, ...areas.flatMap(a => (a.rows || []).filter(r => r.urgente).map(r => r.urgentePos || 0)));
    const rowId = "cr" + Date.now();
    const estatus = item.estado === "listo" ? "completado" : (item.estado || "pendiente");
    onClientAreas(areas.map(a => a.id !== areaId ? a : {
      ...a,
      rows: [...(a.rows || []), { id: rowId, paso: item.texto, responsable: item.responsable || "", fecha: item.fecha || "", estatus, urgente: !!urgente, urgentePos: urgente ? maxPos + 1 : null }],
    }));
    setItem(bid, item.id, { clientRowId: rowId, clientAreaId: areaId });
    setSendPicker(null);
  };
  const desenlazar = (bid, item) => {
    const l = rowOf(item);
    setItem(bid, item.id, { clientRowId: null, clientAreaId: null, estado: l ? estadoDe(item) : (item.estado || "pendiente") });
  };

  // ── Derivados ────────────────────────────────────────────────────────────
  const bloqueos = data.bloqueos || [];
  const ventanas = [...(data.ventanas || [])].sort((a, b) => (a.fecha || "9999").localeCompare(b.fecha || "9999"));
  const activos = bloqueos.filter(b => b.estado !== "resuelto");
  const resueltos = bloqueos.filter(b => b.estado === "resuelto");

  const pctDe = (b) => {
    const its = b.items || [];
    if (!its.length) return 0;
    return Math.round(100 * its.filter(i => estadoDe(i) === "listo").length / its.length);
  };
  const totalItems = activos.reduce((a, b) => a + (b.items || []).length, 0);
  const listosItems = activos.reduce((a, b) => a + (b.items || []).filter(i => estadoDe(i) === "listo").length, 0);
  const pctGlobal = totalItems ? Math.round(100 * listosItems / totalItems) : 0;
  const diasAcum = activos.reduce((a, b) => a + (diasDesde(b.desde) || 0), 0);

  // ── Mutadores ────────────────────────────────────────────────────────────
  const setBlq = (id, patch) => update(p => ({ ...p, bloqueos: p.bloqueos.map(b => b.id === id ? { ...b, ...patch } : b) }));
  const setItem = (bid, iid, patch) => update(p => ({ ...p, bloqueos: p.bloqueos.map(b => b.id !== bid ? b : { ...b, items: b.items.map(i => i.id === iid ? { ...i, ...patch } : i) }) }));
  const addItem = (bid) => update(p => ({ ...p, bloqueos: p.bloqueos.map(b => b.id !== bid ? b : { ...b, items: [...(b.items || []), { id: "i" + Date.now(), texto: "Nueva subtarea", responsable: "", estado: "pendiente", fecha: "" }] }) }));
  const delItem = (bid, iid) => update(p => ({ ...p, bloqueos: p.bloqueos.map(b => b.id !== bid ? b : { ...b, items: b.items.filter(i => i.id !== iid) }) }));
  const addBlq = () => { const id = "bl" + Date.now(); setOpenBlq(o => ({ ...o, [id]: true })); update(p => ({ ...p, bloqueos: [...p.bloqueos, { id, titulo: "Nuevo bloqueo", frente: "", desde: hoyIso, estado: "activo", resueltoEl: "", aprendizaje: "", categoria: "", items: [] }] })); };
  const delBlq = (id) => update(p => ({ ...p, bloqueos: p.bloqueos.filter(b => b.id !== id) }));
  const addVentana = () => update(p => ({ ...p, ventanas: [...(p.ventanas || []), { id: "vt" + Date.now(), nombre: "Nueva fecha externa", fecha: "", notas: "" }] }));
  const setVentana = (id, patch) => update(p => ({ ...p, ventanas: p.ventanas.map(v => v.id === id ? { ...v, ...patch } : v) }));
  const delVentana = (id) => update(p => ({ ...p, ventanas: p.ventanas.filter(v => v.id !== id) }));

  const confirmarResolucion = () => {
    if (!resolveModal) return;
    const b = bloqueos.find(x => x.id === resolveModal.bid);
    if (!b) { setResolveModal(null); return; }
    setBlq(b.id, {
      estado: "resuelto",
      resueltoEl: hoyIso,
      diasFinal: diasDesde(b.desde) ?? 0,
      pctFinal: pctDe(b),
      aprendizaje: resolveModal.aprendizaje || "",
      categoria: resolveModal.categoria || "",
    });
    setResolveModal(null);
  };

  const inp = { fontSize: 13, border: "1px solid transparent", borderRadius: 6, padding: "3px 6px", outline: "none", background: "transparent", color: "#0f172a", width: "100%", boxSizing: "border-box" };
  const btnMini = { fontSize: 11, fontWeight: 700, borderRadius: 7, padding: "4px 9px", cursor: "pointer", whiteSpace: "nowrap" };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "20px 14px 80px" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>Tareas bloqueadas</div>
      <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 20, lineHeight: 1.55 }}>
        Una tarea bloqueada no debería ser una tarea detenida. Cuando ocurra una tarea que se bloquea por algún motivo, podemos listar
        aquellas <b>subtareas que sí pueden avanzar</b> aún si continuara bloqueada la tarea anterior. Esas subtareas se envían a Pendientes
        o Urgentes para trabajarse en un solo lugar.
      </div>

      {/* ── Métricas ───────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 24 }}>
        {[
          { label: "Bloqueos activos", valor: activos.length, color: activos.length ? "#DC2626" : "#22C55E" },
          { label: "Días-bloqueo acumulados", valor: diasAcum, color: "#F59E0B" },
          { label: "Avance desbloqueado", valor: pctGlobal + "%", color: pctGlobal >= 70 ? "#22C55E" : pctGlobal >= 40 ? "#F59E0B" : "#DC2626" },
          { label: "Pendientes disponibles", valor: totalItems - listosItems, color: "#1D4ED8" },
        ].map((m, k) => (
          <div key={k} style={{ flex: narrow ? "1 1 45%" : "1 1 150px", minWidth: 130, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: m.color, lineHeight: 1.1 }}>{m.valor}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, marginTop: 4 }}>{m.label}</div>
          </div>
        ))}
      </div>

      {/* ── Ventanas externas ──────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Fechas externas inamovibles</div>
        {canEdit && <button onClick={addVentana} style={{ ...btnMini, color: "#1D4ED8", background: "#EFF6FF", border: "none" }}>+ Fecha</button>}
      </div>
      <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 12 }}>Contra estas fechas se mide todo lo demás. No se negocian con nadie.</div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 30 }}>
        {ventanas.map(v => {
          const d = diasHasta(v.fecha);
          const sem = d === null ? null : Math.floor(d / 7);
          const col = d === null ? "#94a3b8" : d < 0 ? "#64748b" : d <= 56 ? "#DC2626" : d <= 112 ? "#F59E0B" : "#22C55E";
          return (
            <div key={v.id} style={{ flex: "1 1 250px", minWidth: 220, background: "#fff", border: "1px solid #e2e8f0", borderLeft: `4px solid ${col}`, borderRadius: 12, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <input value={v.nombre} readOnly={!canEdit} onChange={e => setVentana(v.id, { nombre: e.target.value })} style={{ ...inp, fontSize: 13.5, fontWeight: 800 }} />
                {canEdit && <button onClick={() => delVentana(v.id)} title="Eliminar" style={{ border: "none", background: "transparent", color: "#cbd5e1", cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: col, margin: "6px 0 2px" }}>
                {d === null ? "—" : d < 0 ? "Ya pasó" : sem >= 1 ? `${sem} semana${sem === 1 ? "" : "s"}` : `${d} día${d === 1 ? "" : "s"}`}
              </div>
              <div style={{ marginTop: 6, maxWidth: 160 }}>
                {canEdit
                  ? <DateField value={v.fecha} onChange={val => setVentana(v.id, { fecha: val })} inputStyle={{ fontSize: 12, padding: "5px 28px 5px 8px" }} />
                  : <div style={{ fontSize: 12, color: "#64748b" }}>{isoToDisplay(v.fecha)}</div>}
              </div>
              <input value={v.notas || ""} readOnly={!canEdit} placeholder={canEdit ? "Nota…" : ""} onChange={e => setVentana(v.id, { notas: e.target.value })} style={{ ...inp, fontSize: 11.5, color: "#94a3b8", marginTop: 6 }} />
            </div>
          );
        })}
        {!ventanas.length && <div style={{ fontSize: 12.5, color: "#cbd5e1", padding: "16px 0" }}>Sin fechas externas registradas.</div>}
      </div>

      {/* ── Bloqueos activos ───────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Tareas bloqueadas activas</div>
        {canEdit && <button onClick={addBlq} style={{ ...btnMini, color: "#1D4ED8", background: "#EFF6FF", border: "none" }}>+ Tarea bloqueada</button>}
      </div>

      {activos.map(b => {
        const dias = diasDesde(b.desde);
        const pct = pctDe(b);
        const alerta = dias !== null && dias > 21 && pct < 60;
        const abierto = openBlq[b.id] !== false;
        const its = b.items || [];
        const listos = its.filter(i => estadoDe(i) === "listo").length;
        return (
          <div key={b.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderLeft: `4px solid ${alerta ? "#DC2626" : "#E07856"}`, borderRadius: 12, marginBottom: 14, overflow: "hidden" }}>

            <div style={{ padding: "14px 14px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 240px", minWidth: 180 }}>
                  <input value={b.titulo} readOnly={!canEdit} onChange={e => setBlq(b.id, { titulo: e.target.value })} style={{ ...inp, fontSize: 14.5, fontWeight: 800 }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>Frente:</span>
                    <input value={b.frente || ""} readOnly={!canEdit} placeholder="—" onChange={e => setBlq(b.id, { frente: e.target.value })} style={{ ...inp, fontSize: 11.5, fontWeight: 700, color: "#E07856", width: 130 }} />
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700 }}>Desde:</span>
                    <div style={{ width: 125 }}>
                      {canEdit
                        ? <DateField value={b.desde} onChange={val => setBlq(b.id, { desde: val })} inputStyle={{ fontSize: 11.5, padding: "4px 26px 4px 8px" }} />
                        : <span style={{ fontSize: 11.5, color: "#64748b" }}>{isoToDisplay(b.desde)}</span>}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: "right", minWidth: 90 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#DC2626", lineHeight: 1.1 }}>{dias === null ? "—" : `${dias} d`}</div>
                  <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700 }}>bloqueado</div>
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 5 }}>
                  <span>Avance desbloqueado — {listos} de {its.length}</span>
                  <span style={{ color: pct >= 70 ? "#22C55E" : pct >= 40 ? "#F59E0B" : "#DC2626" }}>{pct}%</span>
                </div>
                <div style={{ height: 7, background: "#f1f5f9", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{ width: pct + "%", height: "100%", background: pct >= 70 ? "#22C55E" : pct >= 40 ? "#F59E0B" : "#DC2626", borderRadius: 999, transition: "width .25s" }} />
                </div>
              </div>

              {alerta && (
                <div style={{ marginTop: 10, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 9, padding: "8px 11px", fontSize: 11.5, color: "#B91C1C", lineHeight: 1.5 }}>
                  <b>Tiempo bloqueado desaprovechado.</b> Llevamos {dias} días esperando y solo {pct}% del trabajo que no depende del bloqueo está listo.
                </div>
              )}

              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap", alignItems: "center" }}>
                <button onClick={() => setOpenBlq(o => ({ ...o, [b.id]: !abierto }))} style={{ ...btnMini, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  {abierto ? "Ocultar subtareas" : `Ver ${its.length} subtareas`}
                </button>
                {canEdit && (
                  <>
                    <button onClick={() => setResolveModal({ bid: b.id, aprendizaje: "", categoria: "" })} style={{ ...btnMini, color: "#22C55E", background: "#F0FDF4", border: "1px solid #e2e8f0" }}>Marcar resuelto</button>
                    <button onClick={() => delBlq(b.id)} style={{ ...btnMini, color: "#DC2626", background: "transparent", border: "1px solid #FECACA" }}>Eliminar</button>
                  </>
                )}
              </div>
            </div>

            {/* Avances posibles */}
            {abierto && (
              <div style={{ borderTop: "1px solid #f1f5f9", background: "#fcfcfd", padding: "10px 10px 14px" }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, color: "#94a3b8", padding: "4px 4px 8px", letterSpacing: 0.3 }}>
                  SUBTAREAS QUE SÍ PUEDEN AVANZAR AUNQUE ESTO SIGA BLOQUEADO
                </div>
                {its.map(i => {
                  const est = estadoDe(i);
                  const link = rowOf(i);
                  const picking = sendPicker && sendPicker.bid === b.id && sendPicker.iid === i.id;
                  return (
                    <div key={i.id} style={{ padding: "8px 4px", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ display: "flex", alignItems: narrow ? "flex-start" : "center", gap: 8, flexDirection: narrow ? "column" : "row" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, width: narrow ? "100%" : "auto", flex: narrow ? "none" : "1 1 auto", minWidth: 0 }}>
                          <BlqBadge estado={est} clickable={canEdit} onClick={() => cycleEstado(b.id, i)} />
                          <input value={i.texto} readOnly={!canEdit || !!link} onChange={e => setItem(b.id, i.id, { texto: e.target.value })}
                            title={link ? "Este texto vive en Pendientes; edítalo allá" : ""}
                            style={{ ...inp, flex: 1, minWidth: 0, fontSize: 12.5, textDecoration: est === "listo" ? "line-through" : "none", color: est === "listo" ? "#94a3b8" : "#0f172a" }} />
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, flexWrap: "wrap", paddingLeft: narrow ? 6 : 0, width: narrow ? "100%" : "auto" }}>
                          <input value={i.responsable || ""} readOnly={!canEdit} placeholder="Resp." onChange={e => setItem(b.id, i.id, { responsable: e.target.value })}
                            style={{ ...inp, width: 82, flexShrink: 0, fontSize: 11.5, fontWeight: 700, color: "#64748b", border: "1px solid #eef2f7", background: "#fff" }} />
                          <div style={{ width: 118, flexShrink: 0 }}>
                            {canEdit
                              ? <DateField value={i.fecha} onChange={val => setItem(b.id, i.id, { fecha: val })} inputStyle={{ fontSize: 11.5, padding: "4px 26px 4px 8px" }} />
                              : <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{isoToDisplay(i.fecha)}</span>}
                          </div>
                          {link ? (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <span style={{ fontSize: 10, fontWeight: 800, color: link.row.urgente ? "#DC2626" : "#1D4ED8", background: link.row.urgente ? "#FEF2F2" : "#EFF6FF", borderRadius: 999, padding: "3px 8px", whiteSpace: "nowrap" }}>
                                {link.row.urgente ? "En Urgentes" : "En Pendientes"} · {link.areaName}
                              </span>
                              {canEdit && <button onClick={() => desenlazar(b.id, i)} title="Desenlazar" style={{ border: "none", background: "transparent", color: "#cbd5e1", cursor: "pointer", fontSize: 13 }}>⤫</button>}
                            </span>
                          ) : canEdit && areas.length > 0 ? (
                            <button onClick={() => setSendPicker(picking ? null : { bid: b.id, iid: i.id })} style={{ ...btnMini, color: "#1D4ED8", background: "#EFF6FF", border: "none" }}>
                              → Pendientes
                            </button>
                          ) : null}
                          {canEdit && !link && <button onClick={() => delItem(b.id, i.id)} style={{ border: "none", background: "transparent", color: "#cbd5e1", cursor: "pointer", fontSize: 16, lineHeight: 1 }}>×</button>}
                        </div>
                      </div>

                      {picking && (
                        <div style={{ marginTop: 8, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px" }}>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", marginBottom: 8 }}>¿A qué área lo mando?</div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                            {areas.map(a => (
                              <button key={a.id} onClick={() => enviarAPendientes(b.id, i, a.id, false)} style={{ ...btnMini, color: "#334155", background: "#f8fafc", border: "1px solid #e2e8f0" }}>{a.name}</button>
                            ))}
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", marginBottom: 8 }}>…o directo a Urgentes</div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {areas.map(a => (
                              <button key={a.id} onClick={() => enviarAPendientes(b.id, i, a.id, true)} style={{ ...btnMini, color: "#DC2626", background: "#FEF2F2", border: "1px solid #FECACA" }}>{a.name} · urgente</button>
                            ))}
                          </div>
                          <button onClick={() => setSendPicker(null)} style={{ ...btnMini, marginTop: 10, color: "#94a3b8", background: "transparent", border: "1px solid #e2e8f0" }}>Cancelar</button>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!its.length && <div style={{ fontSize: 12, color: "#cbd5e1", padding: "8px 4px" }}>Aún no hay subtareas listadas. Si no puedes listar ninguna, la tarea sí está realmente detenida.</div>}
                {canEdit && (
                  <button onClick={() => addItem(b.id)} style={{ ...btnMini, marginTop: 10, color: "#1D4ED8", background: "#EFF6FF", border: "none" }}>+ Subtarea</button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!activos.length && loaded && (
        <div style={{ background: "#fff", border: "1px dashed #e2e8f0", borderRadius: 12, padding: "26px 20px", textAlign: "center", fontSize: 13, color: "#94a3b8" }}>
          Sin tareas bloqueadas activas.
        </div>
      )}

      {/* ── Historial / aprendizajes ───────────────────────────────────── */}
      {resueltos.length > 0 && (
        <div style={{ marginTop: 30 }}>
          <button onClick={() => setVerHistorial(v => !v)}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: 0, marginBottom: 12 }}>
            <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>Historial de tareas desbloqueadas ({resueltos.length})</span>
            {verHistorial ? <IcChevronUp size={14} color="#94a3b8" /> : <IcChevronDown size={14} color="#94a3b8" />}
          </button>
          {verHistorial && (
            <>
              <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 12, lineHeight: 1.5 }}>
                Este es el material de la retro. Con cinco o seis registros aquí, tienes tu propio checklist de diagnóstico: las categorías que se repiten son las preguntas que faltan en la propuesta.
              </div>
              {resueltos.map(b => {
                const dur = b.diasFinal ?? diasEntre(b.desde, b.resueltoEl) ?? null;
                return (
                  <div key={b.id} style={{ background: "#fff", border: "1px solid #e2e8f0", borderLeft: "4px solid #22C55E", borderRadius: 12, padding: "12px 14px", marginBottom: 10 }}>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
                      <div style={{ flex: "1 1 240px", minWidth: 180 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 800, color: "#1e293b" }}>{b.titulo}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, marginTop: 3 }}>
                          {b.frente || "—"} · {isoToDisplay(b.desde)} → {isoToDisplay(b.resueltoEl)}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 14 }}>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 17, fontWeight: 800, color: "#64748b", lineHeight: 1.1 }}>{dur === null ? "—" : dur + " d"}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>duró</div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <div style={{ fontSize: 17, fontWeight: 800, color: (b.pctFinal ?? 0) >= 70 ? "#22C55E" : (b.pctFinal ?? 0) >= 40 ? "#F59E0B" : "#DC2626", lineHeight: 1.1 }}>{b.pctFinal ?? 0}%</div>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700 }}>capturado</div>
                        </div>
                      </div>
                    </div>
                    {(b.categoria || b.aprendizaje) && (
                      <div style={{ marginTop: 10, background: "#f8fafc", borderRadius: 9, padding: "9px 11px" }}>
                        {b.categoria && <span style={{ fontSize: 10, fontWeight: 800, color: "#E07856", background: "#E0785614", border: "1px solid #E0785633", borderRadius: 6, padding: "2px 7px", textTransform: "uppercase", letterSpacing: 0.3 }}>{b.categoria}</span>}
                        {b.aprendizaje && <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.5, marginTop: b.categoria ? 6 : 0 }}>{b.aprendizaje}</div>}
                      </div>
                    )}
                    {canEdit && (
                      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                        <button onClick={() => setBlq(b.id, { estado: "activo", resueltoEl: "" })} style={{ ...btnMini, color: "#64748b", background: "#f8fafc", border: "1px solid #e2e8f0" }}>Reabrir</button>
                        <button onClick={() => delBlq(b.id)} style={{ ...btnMini, color: "#DC2626", background: "transparent", border: "1px solid #FECACA" }}>Eliminar</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}

      <div style={{ marginTop: 26, fontSize: 11.5, color: "#94a3b8", lineHeight: 1.6, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "12px 14px" }}>
        <b>Cómo se usa.</b> Cuando una tarea se bloquea, se registra aquí y se listan de inmediato las subtareas que no dependen del bloqueo.
        Esa lista se acuerda al inicio, no al final. Cada subtarea se manda a Pendientes o Urgentes con un click y desde ahí se trabaja:
        el estado se sincroniza solo. Al desbloquear se captura qué aprendimos. Todo se guarda automáticamente.
      </div>

      {/* ── Modal: cerrar bloqueo capturando el aprendizaje ─────────────── */}
      {resolveModal && (() => {
        const b = bloqueos.find(x => x.id === resolveModal.bid);
        if (!b) return null;
        const dias = diasDesde(b.desde);
        const pct = pctDe(b);
        return (
          <div style={{ position: "fixed", inset: 0, background: "#00000055", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={e => { if (e.target === e.currentTarget) setResolveModal(null); }}>
            <div style={{ background: "#fff", borderRadius: 16, padding: 22, width: "100%", maxWidth: 460, boxShadow: "0 20px 60px #0003", maxHeight: "90vh", overflowY: "auto" }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>Desbloquear tarea</div>
              <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 14, lineHeight: 1.5 }}>{b.titulo}</div>
              <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
                <div style={{ flex: 1, background: "#f8fafc", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 19, fontWeight: 800, color: "#64748b" }}>{dias ?? "—"} d</div>
                  <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700 }}>duró bloqueado</div>
                </div>
                <div style={{ flex: 1, background: "#f8fafc", borderRadius: 10, padding: "10px 12px" }}>
                  <div style={{ fontSize: 19, fontWeight: 800, color: pct >= 70 ? "#22C55E" : pct >= 40 ? "#F59E0B" : "#DC2626" }}>{pct}%</div>
                  <div style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 700 }}>avance capturado</div>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Categoría del hallazgo</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {BLQ_CATEGORIAS.map(c => {
                    const active = resolveModal.categoria === c;
                    return <div key={c} onClick={() => setResolveModal(p => ({ ...p, categoria: c }))}
                      style={{ padding: "5px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? "#E07856" : "#e2e8f0"}`, background: active ? "#E0785618" : "#f8fafc", color: active ? "#E07856" : "#94a3b8" }}>{c}</div>;
                  })}
                </div>
              </div>
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>¿Qué no sabíamos al proponer?</div>
                <textarea autoFocus rows={4} value={resolveModal.aprendizaje} onChange={e => setResolveModal(p => ({ ...p, aprendizaje: e.target.value }))}
                  placeholder="Ej.: el estudio de mercado puede obligar a reformular; hay que preverlo como escenario y definir desde el inicio qué hace Ventas mientras tanto."
                  style={{ width: "100%", fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setResolveModal(null)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                <button onClick={confirmarResolucion} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#22C55E", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Cerrar y guardar</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════ TAB: RESULTADOS ═══════════════════
// Vitrina de trabajo cerrado. El Drive sigue siendo el taller (WIP, versiones,
// comentarios); aquí sólo entra lo publicado. Una ficha vale por sí sola: el
// cliente debe llevarse el 80% del valor sin abrir el documento.
function ResultadosTab({ fichas, onSave, canEdit, frente, setFrente, verId, setVerId, draft, setDraft, goTab, companyName }) {
  const publicadas = fichas.filter(f => f.publicado);
  const borradores = fichas.filter(f => !f.publicado);
  // El cliente sólo ve publicadas; el equipo interno ve todo.
  const universo = canEdit ? fichas : publicadas;
  const visibles = frente ? universo.filter(f => f.frente === frente) : universo;
  const ordenadas = [];

  const frentesConDatos = RES_FRENTES.filter(fr => universo.some(f => f.frente === fr.key));
  const ultima = [...publicadas].sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""))[0];
  const desde = publicadas.length
    ? [...publicadas].sort((a, b) => (a.fecha || "zzz").localeCompare(b.fecha || "zzz"))[0]?.fecha
    : "";

  const ficha = verId ? fichas.find(f => f.id === verId) : null;

  // Orden personal: vive en el navegador de cada quien, no en Firestore.
  // Si se guardara compartido, el reacomodo de una persona se le movería a todos.
  const ORDEN_KEY = "gaveti_res_orden";
  const [orden, setOrden] = useState(() => {
    try { const v = JSON.parse(localStorage.getItem(ORDEN_KEY)); return Array.isArray(v) ? v : []; }
    catch { return []; }
  });
  const [arrastre, setArrastre] = useState(null); // { id, sobre }
  const guardarOrden = (ids) => {
    setOrden(ids);
    try { localStorage.setItem(ORDEN_KEY, JSON.stringify(ids)); } catch {}
  };
  const rankDe = (f) => {
    const i = orden.indexOf(f.id);
    return i === -1 ? 9999 : i;
  };
  const porDefecto = (a, b) => (b.fecha || "").localeCompare(a.fecha || "");
  ordenadas.push(...[...visibles].sort((a, b) => {
    const ra = rankDe(a), rb = rankDe(b);
    return ra !== rb ? ra - rb : porDefecto(a, b);
  }));

  // Al soltar una tarjeta se reconstruye el orden global respetando lo no visible.
  const soltarEn = (idArrastrada, idDestino) => {
    if (!idArrastrada || idArrastrada === idDestino) return;
    const secuencia = ordenadas.map(f => f.id);
    const desde = secuencia.indexOf(idArrastrada);
    const hasta = secuencia.indexOf(idDestino);
    if (desde === -1 || hasta === -1) return;
    secuencia.splice(hasta, 0, secuencia.splice(desde, 1)[0]);
    // Base global: todo lo que existe, en el orden efectivo actual
    const global = [...fichas].sort((a, b) => {
      const ra = rankDe(a), rb = rankDe(b);
      return ra !== rb ? ra - rb : porDefecto(a, b);
    }).map(f => f.id);
    const enVista = new Set(secuencia);
    let k = 0;
    guardarOrden(global.map(id => enVista.has(id) ? secuencia[k++] : id));
  };
  const moverUno = (id, delta) => {
    const secuencia = ordenadas.map(f => f.id);
    const i = secuencia.indexOf(id);
    const j = i + delta;
    if (i === -1 || j < 0 || j >= secuencia.length) return;
    soltarEn(id, secuencia[j]);
  };

  const guardar = () => {
    if (!draft) return;
    const limpia = { ...draft, hallazgos: (draft.hallazgos || []).filter(h => h && h.trim()), kpis: (draft.kpis || []).filter(k => k && k.valor),
      archivos: (draft.archivos || []).filter(a => a && (a.nombre || a.url)) };
    const existe = fichas.some(f => f.id === limpia.id);
    onSave(existe ? fichas.map(f => f.id === limpia.id ? limpia : f) : [...fichas, limpia]);
    setDraft(null);
  };
  const eliminar = (id) => { onSave(fichas.filter(f => f.id !== id)); setDraft(null); setVerId(null); };
  const togglePublicar = (id) => onSave(fichas.map(f => f.id === id ? { ...f, publicado: !f.publicado } : f));
  const nueva = () => setDraft({
    id: "res" + Date.now(), codigo: "", frente: frente || "mercado", titulo: "", resumen: "",
    hallazgos: ["", "", ""], kpis: [{ valor: "", label: "" }], version: "v1",
    fecha: new Date().toISOString().slice(0, 7), link: "", tareas: "", fuente: "", publicado: false,
    archivos: [{ nombre: "", url: "" }],
  });

  // Herramientas vivas: no son fichas congeladas, son tableros que cambian.
  const HERRAMIENTAS = {
    precios: { label: "Herramienta de precios y P&L", desc: "Burbujas de Costco y Sam's más cascada de P&L con supuestos editables.", tab: "precios" },
    ventas:  { label: "Tablero de ventas (CRM)",      desc: "Kanban de 10 etapas con prioridad, valor y seguimiento vencido.",     tab: "ventas" },
  };
  const herramienta = frente ? HERRAMIENTAS[frente] : null;

  const Chip = ({ fr }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 10.5, fontWeight: 800, color: fr.color,
      background: fr.color + "14", border: "1px solid " + fr.color + "33", padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap" }}>
      {fr.short}
    </span>
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px 60px" }}>

      {/* ── Encabezado: el número que ataca la sensación de "poco concretado" ── */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: "#1D4ED8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 6 }}>{companyName}</div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 34, fontWeight: 900, color: "#0f172a", lineHeight: 1 }}>{publicadas.length}</div>
          <div style={{ fontSize: 19, fontWeight: 800, color: "#0f172a" }}>documento{publicadas.length !== 1 ? "s" : ""} publicado{publicadas.length !== 1 ? "s" : ""}</div>
        </div>
        <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 6 }}>
          {frentesConDatos.length} frente{frentesConDatos.length !== 1 ? "s" : ""} de trabajo
          {desde ? ` · desde ${resFechaLabel(desde)}` : ""}
        </div>
        {ultima && (
          <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 3 }}>
            Último: {ultima.titulo} · {resFechaLabel(ultima.fecha)}
          </div>
        )}
        {canEdit && borradores.length > 0 && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 10, padding: "5px 11px", borderRadius: 20, background: "#FFFBEB", border: "1px solid #FDE68A" }}>
            <IcPencil size={11} color="#D97706" />
            <span style={{ fontSize: 11.5, fontWeight: 700, color: "#B45309" }}>{borradores.length} en borrador — sólo visibles para Parte CPG</span>
          </div>
        )}
      </div>

      {/* ── Filtros por frente ── */}
      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 18 }}>
        <button onClick={() => setFrente(null)}
          style={{ fontSize: 11.5, fontWeight: 800, padding: "6px 13px", borderRadius: 20, cursor: "pointer",
            border: frente === null ? "1.5px solid #0f172a" : "1px solid #e2e8f0",
            background: frente === null ? "#0f172a" : "#fff", color: frente === null ? "#fff" : "#64748b" }}>
          Todos ({universo.length})
        </button>
        {RES_FRENTES.map(fr => {
          const n = universo.filter(f => f.frente === fr.key).length;
          const on = frente === fr.key;
          return (
            <button key={fr.key} onClick={() => setFrente(on ? null : fr.key)}
              style={{ fontSize: 11.5, fontWeight: 800, padding: "6px 13px", borderRadius: 20, cursor: "pointer",
                border: on ? `1.5px solid ${fr.color}` : "1px solid #e2e8f0",
                background: on ? fr.color : "#fff", color: on ? "#fff" : (n ? "#64748b" : "#cbd5e1") }}>
              {fr.short}{n > 0 ? ` (${n})` : ""}
            </button>
          );
        })}
      </div>

      {/* ── Herramienta viva del frente ── */}
      {herramienta && (
        <button onClick={() => goTab(herramienta.tab)}
          style={{ width: "100%", textAlign: "left", marginBottom: 16, padding: "14px 16px", borderRadius: 12, cursor: "pointer",
            border: "1px solid #FED7AA", background: "linear-gradient(90deg,#FFF7ED,#fff)", display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#F97316", flexShrink: 0, display: "inline-block" }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#9A3412" }}>{herramienta.label}</div>
            <div style={{ fontSize: 11.5, color: "#C2410C", marginTop: 2 }}>{herramienta.desc}</div>
          </div>
          <span style={{ fontSize: 11.5, fontWeight: 800, color: "#EA580C", whiteSpace: "nowrap" }}>Abrir →</span>
        </button>
      )}

      {canEdit && (
        <button onClick={nueva}
          style={{ marginBottom: 16, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 9,
            border: "1.5px dashed #cbd5e1", background: "#fff", fontSize: 12, fontWeight: 800, color: "#475569", cursor: "pointer" }}>
          <IcPlus size={13} /> Nuevo documento
        </button>
      )}

      {/* ── Rejilla de fichas ── */}
      {ordenadas.length === 0 ? (
        <div style={{ padding: "50px 20px", textAlign: "center", color: "#cbd5e1", fontSize: 13, background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0" }}>
          Todavía no hay documentos publicados en este frente.
        </div>
      ) : (
        <div style={{ background: "#EDF1F6", borderRadius: 16, padding: "28px 24px 30px",
          display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(268px, 1fr))", gap: 26, alignItems: "start" }}>
          {ordenadas.map((f, idx) => {
            const fr = RES_FRENTE_MAP[f.frente] || RES_FRENTES[0];
            const esDestino = arrastre && arrastre.sobre === f.id && arrastre.id !== f.id;
            const seArrastra = arrastre && arrastre.id === f.id;
            return (
              <div key={f.id}
                draggable
                onDragStart={() => setArrastre({ id: f.id, sobre: f.id })}
                onDragOver={e => { e.preventDefault(); setArrastre(a => a ? { ...a, sobre: f.id } : a); }}
                onDrop={e => { e.preventDefault(); soltarEn(arrastre?.id, f.id); setArrastre(null); }}
                onDragEnd={() => setArrastre(null)}
                style={{ opacity: seArrastra ? 0.35 : 1, transition: "opacity .12s" }}>

                {/* Rótulo numerado arriba de la tarjeta */}
                <div style={{ display: "flex", alignItems: "center", gap: 7, padding: "0 3px 7px" }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: "#64748b", letterSpacing: 0.6 }}>
                    {String(idx + 1).padStart(2, "0")}.
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.4 }}>
                    {f.codigo || fr.short}
                  </span>
                  <span style={{ flex: 1 }} />
                  <button onClick={e => { e.stopPropagation(); moverUno(f.id, -1); }} title="Mover antes"
                    style={{ border: "none", background: "transparent", color: "#94a3b8", fontSize: 13, lineHeight: 1, cursor: "pointer", padding: "1px 3px", fontWeight: 900 }}>‹</button>
                  <button onClick={e => { e.stopPropagation(); moverUno(f.id, 1); }} title="Mover después"
                    style={{ border: "none", background: "transparent", color: "#94a3b8", fontSize: 13, lineHeight: 1, cursor: "pointer", padding: "1px 3px", fontWeight: 900 }}>›</button>
                  <span title="Arrastra para reacomodar" style={{ color: "#b8c2ce", fontSize: 12, cursor: "grab", letterSpacing: "-1.5px", userSelect: "none", paddingLeft: 2 }}>⠿</span>
                </div>

                {/* Tarjeta */}
                <div onClick={() => setVerId(f.id)}
                  style={{ background: "#fff", borderRadius: 10, cursor: "pointer", overflow: "hidden",
                    border: esDestino ? `1.5px solid ${fr.color}` : "1px solid transparent",
                    boxShadow: esDestino ? `0 0 0 3px ${fr.color}2e` : "0 2px 10px rgba(15,23,42,0.09)",
                    display: "flex", flexDirection: "column", minHeight: 236, opacity: f.publicado ? 1 : 0.78 }}>
                  <div style={{ height: 4, background: fr.color }} />
                  {(() => {
                    const port = resPortada((f.archivos || [])[0]);
                    if (!port) return null;
                    return (
                      <div style={{ height: 116, background: "#f8fafc", overflow: "hidden", borderBottom: "1px solid #eef2f7" }}>
                        <img src={port} alt="" loading="lazy"
                          onError={e => { e.currentTarget.parentNode.style.display = "none"; }}
                          style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }} />
                      </div>
                    );
                  })()}
                  <div style={{ padding: "17px 18px 18px", display: "flex", flexDirection: "column", gap: 9, flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <Chip fr={fr} />
                      {!f.publicado && <span style={{ fontSize: 9, fontWeight: 800, color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A", padding: "1px 6px", borderRadius: 20 }}>BORRADOR</span>}
                    </div>
                    <div style={{ fontSize: 13.5, fontWeight: 800, color: "#0f172a", lineHeight: 1.35 }}>{f.titulo}</div>
                    {f.resumen && <div style={{ fontSize: 11.5, color: "#64748b", lineHeight: 1.55 }}>{f.resumen}</div>}
                    {f.kpis && f.kpis.length > 0 && (
                      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 2 }}>
                        {f.kpis.slice(0, 2).map((k, i) => (
                          <div key={i}>
                            <div style={{ fontSize: 16, fontWeight: 900, color: fr.color, lineHeight: 1.1 }}>{k.valor}</div>
                            <div style={{ fontSize: 9.5, color: "#94a3b8", fontWeight: 600, marginTop: 1 }}>{k.label}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto", paddingTop: 10 }}>
                      <span style={{ fontSize: 10.5, color: "#cbd5e1", fontWeight: 700 }}>{resFechaLabel(f.fecha)}{f.version ? ` · ${f.version}` : ""}</span>
                      {(f.archivos || []).length > 0 && (
                        <span style={{ fontSize: 10, fontWeight: 800, color: "#64748b", background: "#f1f5f9", padding: "2px 7px", borderRadius: 5 }}>
                          {f.archivos.length} archivo{f.archivos.length !== 1 ? "s" : ""}
                        </span>
                      )}
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 11, fontWeight: 800, color: fr.color }}>Ver →</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {orden.length > 0 && (
        <button onClick={() => guardarOrden([])}
          style={{ marginTop: 16, fontSize: 11, fontWeight: 700, color: "#94a3b8", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
          Restablecer el orden original
        </button>
      )}

      {/* ── Detalle de ficha ── */}
      {ficha && (() => {
        const fr = RES_FRENTE_MAP[ficha.frente] || RES_FRENTES[0];
        return (
          <div onClick={() => setVerId(null)}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, zIndex: 900, overflowY: "auto" }}>
            <div onClick={e => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 14, maxWidth: 660, width: "100%", marginTop: 30, marginBottom: 30, overflow: "hidden" }}>
              <div style={{ height: 5, background: fr.color }} />
              <div style={{ padding: "22px 26px 26px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                  {ficha.codigo && <span style={{ fontSize: 10.5, fontWeight: 900, color: "#94a3b8", letterSpacing: 0.8 }}>{ficha.codigo}</span>}
                  <Chip fr={fr} />
                  <span style={{ fontSize: 10.5, color: "#cbd5e1", fontWeight: 700 }}>{resFechaLabel(ficha.fecha)}{ficha.version ? ` · ${ficha.version}` : ""}</span>
                  {!ficha.publicado && <span style={{ fontSize: 9.5, fontWeight: 800, color: "#B45309", background: "#FFFBEB", border: "1px solid #FDE68A", padding: "1px 7px", borderRadius: 20 }}>BORRADOR</span>}
                  <span style={{ flex: 1 }} />
                  <button onClick={() => setVerId(null)} style={{ border: "none", background: "#f1f5f9", color: "#64748b", width: 26, height: 26, borderRadius: "50%", fontSize: 13, fontWeight: 900, cursor: "pointer" }}>✕</button>
                </div>

                <div style={{ fontSize: 21, fontWeight: 900, color: "#0f172a", lineHeight: 1.3, marginBottom: 10 }}>{ficha.titulo}</div>

                {ficha.resumen && (
                  <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.65, marginBottom: 20 }}>{ficha.resumen}</div>
                )}

                {ficha.kpis && ficha.kpis.length > 0 && (
                  <div style={{ display: "flex", gap: 26, flexWrap: "wrap", marginBottom: 20 }}>
                    {ficha.kpis.map((k, i) => (
                      <div key={i}>
                        <div style={{ fontSize: 27, fontWeight: 900, color: fr.color, lineHeight: 1.1 }}>{k.valor}</div>
                        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginTop: 2 }}>{k.label}</div>
                      </div>
                    ))}
                  </div>
                )}

                {ficha.hallazgos && ficha.hallazgos.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 9 }}>Hallazgos clave</div>
                    {ficha.hallazgos.map((h, i) => (
                      <div key={i} style={{ display: "flex", gap: 11, marginBottom: 11 }}>
                        <div style={{ width: 21, height: 21, borderRadius: "50%", background: fr.color + "18", color: fr.color, fontSize: 11, fontWeight: 900,
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>{i + 1}</div>
                        <div style={{ fontSize: 13, color: "#334155", lineHeight: 1.6, flex: 1 }}>{h}</div>
                      </div>
                    ))}
                  </div>
                )}

                {(ficha.fuente || ficha.tareas) && (
                  <div style={{ fontSize: 11.5, color: "#94a3b8", lineHeight: 1.6, paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
                    {ficha.fuente && <div>Fuente: {ficha.fuente}</div>}
                    {ficha.tareas && <div>{ficha.tareas}</div>}
                  </div>
                )}

                {(ficha.archivos || []).length > 0 && (
                  <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #f1f5f9" }}>
                    <div style={{ fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                      Documentos ({ficha.archivos.length})
                    </div>
                    {ficha.archivos.map((a, i) => {
                      const t = resTipo(a);
                      const port = resPortada(a);
                      const abrible = !!a.url;
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 11px", borderRadius: 9,
                          border: "1px solid #eef2f7", background: "#fcfdfe", marginBottom: 8 }}>
                          {port ? (
                            <img src={port} alt="" loading="lazy"
                              onError={e => { e.currentTarget.style.display = "none"; }}
                              style={{ width: 52, height: 38, objectFit: "cover", objectPosition: "top", borderRadius: 5, border: "1px solid #e2e8f0", flexShrink: 0, display: "block" }} />
                          ) : (
                            <div style={{ width: 52, height: 38, borderRadius: 5, background: t.color + "12", border: "1px solid " + t.color + "2e",
                              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                              fontSize: 9.5, fontWeight: 900, color: t.color, letterSpacing: 0.4 }}>{t.label}</div>
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.nombre || "Documento"}</div>
                            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, marginTop: 2 }}>
                              {t.label}{abrible ? "" : " · liga pendiente"}
                            </div>
                          </div>
                          {abrible ? (
                            <a href={a.url} target="_blank" rel="noreferrer"
                              style={{ fontSize: 11.5, fontWeight: 800, color: "#fff", background: fr.color, padding: "6px 13px", borderRadius: 7, textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0 }}>
                              Abrir
                            </a>
                          ) : (
                            <span style={{ fontSize: 11, fontWeight: 700, color: "#cbd5e1", whiteSpace: "nowrap", flexShrink: 0 }}>Sin liga</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 18 }}>
                  {ficha.link && (ficha.link.startsWith("#") ? (
                    <button onClick={() => { goTab(ficha.link.slice(1)); setVerId(null); }}
                      style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", borderRadius: 9, border: "none", background: fr.color, color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>
                      <IcChart size={13} /> Abrir herramienta
                    </button>
                  ) : null)}
                  {canEdit && (
                    <>
                      <button onClick={() => { setDraft({ ...ficha, hallazgos: [...(ficha.hallazgos || [])], kpis: (ficha.kpis || []).map(k => ({ ...k })) }); setVerId(null); }}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12.5, fontWeight: 800, color: "#475569", cursor: "pointer" }}>
                        <IcPencil size={12} /> Editar
                      </button>
                      <button onClick={() => togglePublicar(ficha.id)}
                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 800,
                          background: ficha.publicado ? "#F1F5F9" : "#16A34A", color: ficha.publicado ? "#64748b" : "#fff" }}>
                        {ficha.publicado ? "Regresar a borrador" : "Publicar al cliente"}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Editor de ficha (sólo Parte CPG) ── */}
      {draft && canEdit && (
        <div onClick={() => setDraft(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, zIndex: 950, overflowY: "auto" }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, maxWidth: 660, width: "100%", marginTop: 26, marginBottom: 26, padding: "22px 26px 26px" }}>
            <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a", marginBottom: 16 }}>
              {fichas.some(f => f.id === draft.id) ? "Editar documento" : "Nuevo documento"}
            </div>

            <div style={{ display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
              <div style={{ width: 110 }}>
                <ResLabel>Código</ResLabel>
                <ResInput value={draft.codigo} onChange={v => setDraft({ ...draft, codigo: v })} placeholder="MKT-01" />
              </div>
              <div style={{ flex: 1, minWidth: 170 }}>
                <ResLabel>Frente</ResLabel>
                <select value={draft.frente} onChange={e => setDraft({ ...draft, frente: e.target.value })}
                  style={{ width: "100%", fontSize: 12.5, padding: "7px 9px", borderRadius: 8, border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", background: "#fff", cursor: "pointer" }}>
                  {RES_FRENTES.map(fr => <option key={fr.key} value={fr.key}>{fr.label}</option>)}
                </select>
              </div>
              <div style={{ width: 100 }}>
                <ResLabel>Fecha</ResLabel>
                <ResInput value={draft.fecha} onChange={v => setDraft({ ...draft, fecha: v })} placeholder="2026-07" />
              </div>
              <div style={{ width: 80 }}>
                <ResLabel>Versión</ResLabel>
                <ResInput value={draft.version} onChange={v => setDraft({ ...draft, version: v })} placeholder="v1" />
              </div>
            </div>

            <ResLabel>Título</ResLabel>
            <ResInput value={draft.titulo} onChange={v => setDraft({ ...draft, titulo: v })} placeholder="Category Insights: galletas en Estados Unidos" />

            <div style={{ height: 12 }} />
            <ResLabel>Resumen</ResLabel>
            <ResArea value={draft.resumen} onChange={v => setDraft({ ...draft, resumen: v })} rows={3}
              placeholder="Dos frases sobre qué van a encontrar aquí y qué mide." />

            <div style={{ height: 14 }} />
            <ResLabel>Hallazgos clave</ResLabel>
            {(draft.hallazgos || []).map((h, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 7 }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#f1f5f9", color: "#94a3b8", fontSize: 10.5, fontWeight: 900,
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 6 }}>{i + 1}</div>
                <div style={{ flex: 1 }}>
                  <ResArea value={h} rows={2} onChange={v => { const hs = [...draft.hallazgos]; hs[i] = v; setDraft({ ...draft, hallazgos: hs }); }} />
                </div>
                <button onClick={() => setDraft({ ...draft, hallazgos: draft.hallazgos.filter((_, j) => j !== i) })}
                  style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 11, fontWeight: 900, cursor: "pointer", flexShrink: 0, marginTop: 6 }}>✕</button>
              </div>
            ))}
            <button onClick={() => setDraft({ ...draft, hallazgos: [...(draft.hallazgos || []), ""] })}
              style={{ fontSize: 11.5, fontWeight: 800, color: "#1D4ED8", background: "none", border: "none", cursor: "pointer", padding: 0 }}>+ Agregar hallazgo</button>

            <div style={{ height: 16 }} />
            <ResLabel>Números destacados</ResLabel>
            {(draft.kpis || []).map((k, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 7 }}>
                <div style={{ width: 110 }}><ResInput value={k.valor} placeholder="−7%" onChange={v => { const ks = [...draft.kpis]; ks[i] = { ...ks[i], valor: v }; setDraft({ ...draft, kpis: ks }); }} /></div>
                <div style={{ flex: 1 }}><ResInput value={k.label} placeholder="velocidad de la categoría" onChange={v => { const ks = [...draft.kpis]; ks[i] = { ...ks[i], label: v }; setDraft({ ...draft, kpis: ks }); }} /></div>
                <button onClick={() => setDraft({ ...draft, kpis: draft.kpis.filter((_, j) => j !== i) })}
                  style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 11, fontWeight: 900, cursor: "pointer", flexShrink: 0, marginTop: 6 }}>✕</button>
              </div>
            ))}
            <button onClick={() => setDraft({ ...draft, kpis: [...(draft.kpis || []), { valor: "", label: "" }] })}
              style={{ fontSize: 11.5, fontWeight: 800, color: "#1D4ED8", background: "none", border: "none", cursor: "pointer", padding: 0 }}>+ Agregar número</button>

            <div style={{ height: 18 }} />
            <ResLabel>Documentos</ResLabel>
            <div style={{ fontSize: 10.5, color: "#94a3b8", marginBottom: 8, lineHeight: 1.5 }}>
              Pega la liga de Drive con permiso de lectura. Si el archivo vive en Drive, la miniatura se genera sola.
            </div>
            {(draft.archivos || []).map((a, i) => {
              const t = resTipo(a);
              const port = resPortada(a);
              return (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                  {port ? (
                    <img src={port} alt="" onError={e => { e.currentTarget.style.visibility = "hidden"; }}
                      style={{ width: 42, height: 32, objectFit: "cover", objectPosition: "top", borderRadius: 5, border: "1px solid #e2e8f0", flexShrink: 0, marginTop: 5 }} />
                  ) : (
                    <div style={{ width: 42, height: 32, borderRadius: 5, background: t.color + "12", border: "1px solid " + t.color + "2e", flexShrink: 0, marginTop: 5,
                      display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8.5, fontWeight: 900, color: t.color }}>{t.label}</div>
                  )}
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                    <ResInput value={a.nombre} placeholder="Nombre del archivo.pdf"
                      onChange={v => { const xs = [...draft.archivos]; xs[i] = { ...xs[i], nombre: v }; setDraft({ ...draft, archivos: xs }); }} />
                    <ResInput value={a.url} placeholder="https://drive.google.com/file/d/..."
                      onChange={v => { const xs = [...draft.archivos]; xs[i] = { ...xs[i], url: v }; setDraft({ ...draft, archivos: xs }); }} />
                  </div>
                  <button onClick={() => setDraft({ ...draft, archivos: draft.archivos.filter((_, j) => j !== i) })}
                    style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 11, fontWeight: 900, cursor: "pointer", flexShrink: 0, marginTop: 10 }}>✕</button>
                </div>
              );
            })}
            <button onClick={() => setDraft({ ...draft, archivos: [...(draft.archivos || []), { nombre: "", url: "" }] })}
              style={{ fontSize: 11.5, fontWeight: 800, color: "#1D4ED8", background: "none", border: "none", cursor: "pointer", padding: 0 }}>+ Agregar documento</button>

            <div style={{ height: 14 }} />
            <ResLabel>Salto a herramienta interna (opcional)</ResLabel>
            <ResInput value={draft.link} onChange={v => setDraft({ ...draft, link: v })} placeholder="#precios  ·  #ventas" />

            <div style={{ height: 12 }} />
            <ResLabel>Fuente</ResLabel>
            <ResInput value={draft.fuente} onChange={v => setDraft({ ...draft, fuente: v })} placeholder="Nielsen MULO, 52 semanas al 18/abr/2026" />

            <div style={{ height: 12 }} />
            <ResLabel>Trabajo detrás (opcional)</ResLabel>
            <ResInput value={draft.tareas} onChange={v => setDraft({ ...draft, tareas: v })} placeholder="14 tareas cerradas entre feb y abr 2026" />

            <div style={{ display: "flex", gap: 9, marginTop: 22, flexWrap: "wrap" }}>
              <button onClick={guardar}
                style={{ padding: "10px 20px", borderRadius: 9, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>Guardar</button>
              <button onClick={() => setDraft(null)}
                style={{ padding: "10px 18px", borderRadius: 9, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>Cancelar</button>
              <span style={{ flex: 1 }} />
              {fichas.some(f => f.id === draft.id) && (
                <button onClick={() => eliminar(draft.id)}
                  style={{ padding: "10px 16px", borderRadius: 9, border: "1px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}>Eliminar</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ResLabel = ({ children }) => (
  <div style={{ fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.9, marginBottom: 4 }}>{children}</div>
);
const ResInput = ({ value, onChange, placeholder }) => (
  <input value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ width: "100%", fontSize: 12.5, padding: "7px 9px", borderRadius: 8, border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", fontFamily: "inherit" }} />
);
const ResArea = ({ value, onChange, rows, placeholder }) => (
  <textarea value={value || ""} rows={rows || 2} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    style={{ width: "100%", fontSize: 12.5, padding: "7px 9px", borderRadius: 8, border: "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", lineHeight: 1.5 }} />
);

export default function GavettiGantt() {
  // ── Auth state ────────────────────────────────────────────────────────────
  const [user, setUser]           = useState(null);   // Firebase user or null
  const [authLoading, setAuthLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [dbLoading, setDbLoading] = useState(true);

  // ── App state ─────────────────────────────────────────────────────────────
  const [ownerColors, setOwnerColors] = useState(OWNER_COLORS_INIT);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUser, setEditUser]   = useState(null);
  const [projects, setProjects]   = useState(INITIAL_PROJECTS);
  const [expanded, setExpanded]   = useState(() => Object.fromEntries(INITIAL_PROJECTS.map(p => [p.id, true])));

  // guestMode = true when no user is logged in
  const guestMode = !user;

  // ── Login toast ───────────────────────────────────────────────────────────
  const [toast, setToast] = useState(false);
  const [barTooltip, setBarTooltip] = useState(null); // { x, y, label }
  const showLoginToast = () => {
    setToast(true);
    setTimeout(() => setToast(false), 3000);
  };
  const [expandedTasks, setExpandedTasks] = useState({});
  const [editingProgress, setEditingProgress] = useState(null);
  const [editingName, setEditingName] = useState(null);   // { spaceId, taskId, subtaskId? }
  const [editingSpace, setEditingSpace] = useState(null); // spaceId being renamed
  const [companyName, setCompanyName] = useState("Parte CPG · Gaveti");
  const [projectName, setProjectName] = useState("U.S. Expansion — Project Gantt");
  const [editingHeader, setEditingHeader] = useState(null); // "company" | "project"
  const OBJECTIVE_COLORS = ["#3B82F6","#22C55E","#F59E0B","#EF4444","#F97316","#8B5CF6"];
  const [objectives, setObjectives] = useState([
    { id: "o1", label: "Definición de 4 P's", color: "#3B82F6", progress: 30, date: "" },
    { id: "o2", label: "Unit Financials", color: "#22C55E", progress: 15, date: "" },
    { id: "o3", label: "Subsidiaria Importadora", color: "#F59E0B", progress: 0, date: "" },
    { id: "o4", label: "Plan Comercial y KPI's", color: "#EF4444", progress: 0, date: "" },
    { id: "o5", label: "Plan para 2027", color: "#F97316", progress: 0, date: "" },
  ]);
  const [editingObjective, setEditingObjective] = useState(null); // { id, field: "label"|"progress" }
  const [editingComment, setEditingComment] = useState(null); // { spaceId, taskId, subtaskId? }

  // ── Pizarra de Avisos ─────────────────────────────────────────────────────
  const [avisos, setAvisos] = useState([]); // [{ id, texto, fecha, done }]
  const [editingAviso, setEditingAviso] = useState(null); // id being edited inline

  // ── Time Tracker state ────────────────────────────────────────────────────
  const [timeEntries, setTimeEntries] = useState([]); // [{ id, date, seconds, note }]
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerNote, setTimerNote] = useState("");
  const [timerPendingSave, setTimerPendingSave] = useState(false); // stopped, awaiting description
  const [timerDate, setTimerDate] = useState(new Date().toISOString().slice(0,10));
  const [manualHours, setManualHours] = useState("");
  const [manualMins, setManualMins] = useState("");
  const [manualNote, setManualNote] = useState("");
  const [manualDate, setManualDate] = useState(new Date().toISOString().slice(0,10));
  const [showTimeHistory, setShowTimeHistory] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // { id, seconds, note, date }
  const [timeArchives, setTimeArchives] = useState([]); // [{monthKey, users:[{uid,name,email,entries}]}]
  const [expandedArchive, setExpandedArchive] = useState(null); // monthKey being expanded
  const [addingToArchive, setAddingToArchive] = useState(null); // { monthKey, h, m, date, note }
  const timerRef = useRef(null);
  const timerStartRef = useRef(null);
  const timerHydratedUid = useRef(null);
  const [pipWin, setPipWin] = useState(null);
  // Admin-only: view another user's time
  const [timeUserList, setTimeUserList] = useState([]); // [{uid, name, email}]
  const [watchedUid, setWatchedUid] = useState(null);   // uid being viewed by admin
  const [watchedEntries, setWatchedEntries] = useState([]);
  const [watchedName, setWatchedName] = useState("");
  const [editNameVal, setEditNameVal] = useState("");
  const [ownerPicker, setOwnerPicker] = useState(null);
  const [raciPicker, setRaciPicker] = useState(null); // { spaceId, taskId, role: "R"|"A"|"C"|"I" }
  const RACI_ROLES = ["R", "A", "C", "I"];
  const RACI_W = 28; // px per RACI column
  const [linksPicker, setLinksPicker] = useState(null); // { spaceId, taskId, subtaskId?, name }   // { spaceId, taskId, subtaskId? }
  const [modal, setModal] = useState(null); // { spaceId, taskId?, type:"task"|"subtask", spaceColor }
  const [confirm, setConfirm] = useState(null); // { message, onConfirm }
  const [menu, setMenu] = useState(null); // { spaceId, taskId, subtaskId?, x, y }
  const [dragItem, setDragItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 }); // ghost position
  const dragRef = useRef(null); // stores { item, startY, rowEls }
  const [filterOwner, setFilterOwner] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const chartRef = useRef();
  const ganttBodyRef = useRef();
  const ganttScrollRef = useRef();
  const [labelW, setLabelW] = useState(300);
  const LABEL_W = labelW;
  const [zoomMonths, setZoomMonths] = useState(12); // 3, 6, 12, or 24
  const ROW_H = 36;

  // ── Tabs: pública (Pendientes Gaveti) vs privada (Gantt completo) ──────────
  const [activeTab, setActiveTab] = useState(() => {
    // Deep link: gaveti.partecpg.com/#urgentes abre directo esa pestaña;
    // si algún día apuntas urgentes.partecpg.com a este mismo proyecto, también funciona.
    try {
      if (window.location.hostname.startsWith("urgentes.")) return "urgentes";
      const h = window.location.hash.replace("#", "");
      if (["client","timeline","urgentes","precios","ventas","resultados","gantt"].includes(h)) return h;
    } catch {}
    return "client";
  }); // "client" | "urgentes" | "ventas" | "gantt"
  useEffect(() => { try { window.history.replaceState(null, "", "#" + activeTab); } catch {} }, [activeTab]);
  const CLIENT_ACCESS_CODE = "Gaveti2026";
  const [clientUnlocked, setClientUnlocked] = useState(() => {
    if (IS_DEMO) return true; // demo.partecpg.com nunca pide contraseña
    try { return localStorage.getItem("gaveti_client_access") === "granted"; } catch(e) { return false; }
  });
  const [accessCodeInput, setAccessCodeInput] = useState("");
  const [accessCodeError, setAccessCodeError] = useState(false);
  const submitAccessCode = () => {
    if (accessCodeInput.trim() === CLIENT_ACCESS_CODE) {
      try { localStorage.setItem("gaveti_client_access", "granted"); } catch(e) {}
      setClientUnlocked(true);
      setAccessCodeError(false);
    } else {
      setAccessCodeError(true);
    }
  };

  // ── Métricas de uso — visible solo para Miguel, pestaña Gantt ───────────────
  const isMetricsViewer = user?.email === METRICS_VIEWER;
  const [showMetrics, setShowMetrics] = useState(false);
  const [metricsMonths, setMetricsMonths] = useState(null); // [{key, label, data}]
  const [metricsLoading, setMetricsLoading] = useState(false);
  const openMetrics = () => {
    setShowMetrics(true);
    setMetricsLoading(true);
    const now = new Date();
    const keys = [0, 1, 2].map(i => analyticsMonthKey(new Date(now.getFullYear(), now.getMonth() - i, 1)));
    Promise.all(keys.map(k => getDoc(DOC_ANALYTICS(k)).then(snap => ({ key: k, data: snap.exists() ? snap.data() : {} }))))
      .then(results => {
        setMetricsMonths(results.map(r => ({
          key: r.key,
          label: new Date(r.key + "-02").toLocaleDateString("es-MX", { month: "long", year: "numeric" }),
          data: r.data,
        })));
      })
      .catch(e => { console.error("metrics load", e); setMetricsMonths([]); })
      .finally(() => setMetricsLoading(false));
  };

  // ── CRM Ventas state ──────────────────────────────────────────────────────
  const CRM_STAGES = ["Prospecto","Submission","Contactado","Calificado","Cita agendada","Muestra enviada","Propuesta / Cotización","Revisión interna","Negociación","Cerrado Ganado","Cerrado / Retomar"];
  const CRM_TIPOS = ["Distribuidor","Broker","Cadena Retail","Cuenta Directa"];
  const CRM_PRIORIDADES = { Alta: "#EF4444", Media: "#F59E0B", Baja: "#94A3B8" };
  // Migración: etapas del esquema anterior → esquema nuevo (para deals ya guardados en Firebase)
  const migrateStage = (etapa) => ({ "Propuesta": "Propuesta / Cotización", "Cerrado": "Cerrado Ganado", "Cerrado Perdido": "Cerrado / Retomar" }[etapa] || etapa);
  const CRM_SEED = [
    { id:"crm1", nombre:"HEB", contacto:"", fecha:"", etapa:"Contactado", notas:"Respuesta de compradora (domicilio para envío de muestras)" },
    { id:"crm2", nombre:"Walmart Marketplace", contacto:"", fecha:"2026-06-30", etapa:"Negociación", notas:"Variety pack vs. 5 packs. WSS administra envíos. Trigave revisa proceso legal." },
    { id:"crm3", nombre:"Costco", contacto:"", fecha:"", etapa:"Cita agendada", notas:"Contrato de Brokers, fechas objetivo, deck, muestras, presentación. Seguimiento semanal." },
    { id:"crm4", nombre:"Target", contacto:"Broker", fecha:"2026-06-22", etapa:"Cita agendada", notas:"Junta con Broker lunes/martes 22-23 Junio" },
    { id:"crm5", nombre:"UNFI", contacto:"Karen", fecha:"2026-07-01", etapa:"Contactado", notas:"Al ser natural, esperamos cambios de ingredientes." },
    { id:"crm6", nombre:"KeHE", contacto:"Karen", fecha:"", etapa:"Contactado", notas:"UPC pendientes y diagrama de pallet." },
    { id:"crm7", nombre:"Dot Foods", contacto:"Karen", fecha:"2026-06-30", etapa:"Contactado", notas:"UPC pendientes y diagrama de pallet." },
    { id:"crm8", nombre:"Pod Foods", contacto:"Karen", fecha:"", etapa:"Prospecto", notas:"Dar de alta productos en su plataforma de descubrimiento." },
    { id:"crm9", nombre:"Adventure CPG", contacto:"Karen", fecha:"", etapa:"Prospecto", notas:"Distribuidor a consignación con membresía. Hablar para conocer el proceso de alta." },
    { id:"crm10", nombre:"DSD Distributors", contacto:"Karen", fecha:"", etapa:"Prospecto", notas:"Chicago. Opción complementaria a futuro." },
    { id:"crm11", nombre:"RangeMe", contacto:"Karen", fecha:"2026-06-30", etapa:"Contactado", notas:"Monitorear eventos. Explorar programas y actualizar productos, fotos e info actualizada." },
    { id:"crm12", nombre:"Startup CPG", contacto:"Karen", fecha:"", etapa:"Prospecto", notas:"Monitorear eventos. Darse de alta en comunidad." },
  ];
  const [crmDeals, setCrmDeals] = useState(CRM_SEED);
  const [crmModal, setCrmModal] = useState(null); // { deal } or { new: true }
  const [crmDraft, setCrmDraft] = useState(null); // editing copy
  const [crmDragId, setCrmDragId] = useState(null); // deal siendo arrastrado en kanban
  const [crmDragOverStage, setCrmDragOverStage] = useState(null);
  const [crmFilterPrioridad, setCrmFilterPrioridad] = useState("Todas");
  const [crmFilterTipo, setCrmFilterTipo] = useState("Todos");
  const [crmFilterResp, setCrmFilterResp] = useState("Todos");
  const [urgentDragId, setUrgentDragId] = useState(null); // card urgente siendo arrastrada

  // ── Línea del tiempo (Hitos del proyecto) ─────────────────────────────────
  // El "área" es un atributo de cada hito (columna), y los hitos se ordenan por MES
  // sobre una sola línea continua. Para agregar meses/años en el futuro, basta con
  // extender TL_MONTHS (ej. "2027-01" → "Enero 2027").
  const TL_AREAS = ["Estrategia y Producto", "Regulatorio", "Empaque y Diseño", "Altas y Logística", "Ventas / CRM"];
  const TL_MONTHS = [
    { key:"2026-02", label:"Febrero 2026" },
    { key:"2026-03", label:"Marzo 2026" },
    { key:"2026-04", label:"Abril 2026" },
    { key:"2026-05", label:"Mayo 2026" },
    { key:"2026-06", label:"Junio 2026" },
    { key:"2026-07", label:"Julio 2026" },
    { key:"2026-08", label:"Agosto 2026" },
    { key:"2026-09", label:"Septiembre 2026" },
    { key:"2026-10", label:"Octubre 2026" },
    { key:"2026-11", label:"Noviembre 2026" },
    { key:"2026-12", label:"Diciembre 2026" },
  ];
  const TL_ESTADOS = {
    completado:  { label: "Completado",  color: "#22C55E", bg: "#F0FDF4" },
    en_progreso: { label: "En proceso",  color: "#F59E0B", bg: "#FFFBEB" },
    pausa:       { label: "En pausa",     color: "#94A3B8", bg: "#F1F5F9" },
    pendiente:   { label: "Pendiente",    color: "#1D4ED8", bg: "#EFF6FF" },
  };
  // Mes inicial sembrado según estado (completados → Feb/Mar/Abr; en proceso → May/Jun/Jul;
  // pausa → Jul; pendientes → Ago). Se distribuye ciclando para no amontonar todo en un mes.
  const tlMesFor = (estado, i) => {
    if (estado === "completado")  return ["2026-02","2026-03","2026-04"][i % 3];
    if (estado === "en_progreso") return ["2026-05","2026-06","2026-07"][i % 3];
    if (estado === "pausa")       return "2026-07";
    return "2026-08"; // pendiente
  };
  const TL_RAW = [
    { area:"Estrategia y Producto", label:"Análisis de mercado con data de Nielsen", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Definición de estrategia de expansión", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Definición de producto", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Ajuste de contenido neto y porción al mercado US", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Definición de precio", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Definición de canales", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Selección de regiones de arranque: TX y CA", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Investigación de competencia en tienda (surtido y precios)", estado:"completado", notas:"" },
    { area:"Estrategia y Producto", label:"Cálculo de costos unitarios", estado:"en_progreso", notas:"Datos estimados; el valor final depende de la cercanía al momento de exportar." },
    { area:"Regulatorio", label:"Migración a saborizantes naturales", estado:"en_progreso", notas:"Blueberry listo; el resto en proceso. Fruta deshidratada sin saborizantes." },
    { area:"Regulatorio", label:"Análisis de etiqueta y tabla nutrimental (Blueberry y Chispi Chocs)", estado:"pendiente", notas:"" },
    { area:"Regulatorio", label:"Pago a AsAlim para revisión regulatoria de empaque", estado:"pendiente", notas:"" },
    { area:"Regulatorio", label:"Revisión regulatoria FDA de etiquetas", estado:"pendiente", notas:"Arranca con los nutrimentales listos." },
    { area:"Empaque y Diseño", label:"Diseño de empaques", estado:"completado", notas:"" },
    { area:"Empaque y Diseño", label:"Landing page provisional USA", estado:"completado", notas:"" },
    { area:"Empaque y Diseño", label:"Configuración de pallet master (150 g) validada", estado:"completado", notas:"174 cajas · 6 camas · 261 kg · 94.7% de uso." },
    { area:"Empaque y Diseño", label:"Nuevos diseños de empaque (Marco Rodríguez)", estado:"en_progreso", notas:"" },
    { area:"Empaque y Diseño", label:"Envío de empaques a preprensa", estado:"pendiente", notas:"" },
    { area:"Empaque y Diseño", label:"Impresión de dummies", estado:"pendiente", notas:"" },
    { area:"Empaque y Diseño", label:"Impresión de cajas", estado:"pendiente", notas:"" },
    { area:"Altas y Logística", label:"Definición GTIN-13 (destrabado)", estado:"completado", notas:"Se trabaja con GTIN-13 en lugar de UPC-12." },
    { area:"Altas y Logística", label:"Alta con proveedores logísticos", estado:"en_progreso", notas:"" },
    { area:"Altas y Logística", label:"Contacto para alta en mayoristas", estado:"en_progreso", notas:"" },
    { area:"Altas y Logística", label:"Contacto para alta en plataformas", estado:"en_progreso", notas:"" },
    { area:"Altas y Logística", label:"DUNS en proceso", estado:"en_progreso", notas:"" },
    { area:"Altas y Logística", label:"Alta en RJW", estado:"en_progreso", notas:"Contrato aceptado; falta firma y formularios (requiere UPC/GTIN)." },
    { area:"Altas y Logística", label:"Concretar altas con códigos", estado:"pendiente", notas:"Ya destrabado con GTIN-13." },
    { area:"Altas y Logística", label:"Liability Insurance (seguro de responsabilidad civil)", estado:"pendiente", notas:"" },
    { area:"Ventas / CRM", label:"Contacto inicial con retailers (HEB, Walmart, Costco, Target...)", estado:"completado", notas:"" },
    { area:"Ventas / CRM", label:"Contrato de brokers", estado:"en_progreso", notas:"" },
    { area:"Ventas / CRM", label:"Presentación comercial actualizada", estado:"pendiente", notas:"Incluir config de pallet nueva y nuevos diseños." },
    { area:"Ventas / CRM", label:"Envío de muestras a compradores", estado:"pendiente", notas:"" },
    { area:"Ventas / CRM", label:"Frente de Ventas (hold parcial)", estado:"pausa", notas:"En pausa mientras el cliente define el perfil de saborizantes." },
  ];
  const TL_SEED = (() => {
    const c = {};
    return TL_RAW.map((m, idx) => {
      const i = (c[m.estado] = (c[m.estado] || 0) + 1) - 1;
      return { id: "tl" + (idx + 1), mes: tlMesFor(m.estado, i), ...m };
    });
  })();
  // Compatibilidad hacia atrás: rellena area/mes en datos antiguos (frente/fecha) sin mes.
  const tlMigrate = (list) => {
    const c = {};
    return (list || []).map(m => {
      const i = (c[m.estado] = (c[m.estado] || 0) + 1) - 1;
      return { ...m, area: m.area || m.frente || TL_AREAS[0], mes: m.mes || tlMesFor(m.estado, i) };
    });
  };
  const [milestones, setMilestones] = useState(TL_SEED);
  const [tlModal, setTlModal] = useState(null); // { new: true } | { edit: true }
  const [tlDraft, setTlDraft] = useState(null);
  const [tlIterMotivo, setTlIterMotivo] = useState("");
  const [tlIterFase, setTlIterFase] = useState(null);   // fase a la que regresa la iteración // motivo de la iteración que se está registrando
  const [tlFilterEstado, setTlFilterEstado] = useState("Todos");
  const [tlFilterFase, setTlFilterFase] = useState(null); // key de TL_FASES o null
  const [tlLinkEditing, setTlLinkEditing] = useState(null); // id del hito con el input de link abierto
  const [tlLinkInput, setTlLinkInput] = useState("");

  // ── Client Tasks (Pendientes Gaveti) — public, no login required ──────────
  const CLIENT_AREA_SEED = [
    { id: "ca1", name: "Diseños", rows: [
      { id: "cr1", paso: "Sachets de Chispi Chocs. Roman tiene pendiente mandar medidas de sachet de 30g para diseño, validar en prueba de llenado. Está pendiente que lleguen las cajas para ver qué tanto sobrante de bolsa le pueden dejar y ver como caben", responsable: "Roman", fecha: "", estatus: "pendiente" },
      { id: "cr2", paso: "Prueba de empaque en prototipo de caja (5 envolturas por caja)", responsable: "Roman", fecha: "", estatus: "pendiente" },
      { id: "cr3", paso: "Se decidió que ambos sachets para clubes llevan diseño. Proveer plano mecánico (die-line) a Marco", responsable: "Alberto", fecha: "", estatus: "pendiente" },
      { id: "cr4", paso: "Diseño de envoltura (empaque primario) para Blueberry y Chispi Chocs de 30 g", responsable: "Marco", fecha: "", estatus: "pendiente" },
      { id: "cr5", paso: "Se decide continuar con el sello actual de Kosher (KA Kosher, www.kakosher.com) y migrar Ortodox en la siguiente impresión posterior a nueva auditoría", responsable: "", fecha: "", estatus: "pendiente" },
    ]},
    { id: "ca2", name: "Comercial", rows: [
      { id: "cr6", paso: "Cambios de ingredientes: se decide hacer el cambio a saborizantes naturales, blueberry ya lo tienen listo, están en proceso los demás, y fruta deshidratada sin saborizantes", responsable: "Román", fecha: "", estatus: "en_progreso" },
      { id: "cr7", paso: "Respecto a las chispi chocs, se decide ofrecerlas como están, porque las chispas se están evaluando y van a tomar más tiempo (sin azúcar y sin leche). Maltitol", responsable: "", fecha: "", estatus: "pendiente" },
      { id: "cr8", paso: "Van a procesarse los nutrimentales con los análisis de laboratorio que ya tienen. Enviar resultados de análisis para el ing. de alimentos (blueberry)", responsable: "Jorge", fecha: "", estatus: "pendiente" },
      { id: "cr9", paso: "Procesar pago a AsAlim para revisión regulatoria de empaque", responsable: "Marcos", fecha: "", estatus: "pendiente" },
      { id: "cr10", paso: "Con nutrimentales listos arrancamos revisión regulatoria", responsable: "Miguel", fecha: "", estatus: "pendiente" },
    ]},
    { id: "ca3", name: "Corrugado y Pallet", rows: [
      { id: "cr11", paso: "Configuración de pallet final lo tienen al final de esta semana con pruebas en producción (6/19)", responsable: "", fecha: "", estatus: "pendiente" },
      { id: "cr12", paso: "RJW tiene como límite 228 cm para una sola estiba", responsable: "", fecha: "", estatus: "pendiente" },
      { id: "cr13", paso: "RJW puede usar Slip Sheet con costo extra. Ya respondió", responsable: "", fecha: "", estatus: "completado" },
      { id: "cr14", paso: "Se realizó carta de PARTE CPG solicitando UPC 12, enviar a GS1 México", responsable: "", fecha: "", estatus: "en_progreso" },
      { id: "cr15", paso: "Falta confirmar tipo de tarima aceptada", responsable: "Karen Culebro", fecha: "", estatus: "pendiente" },
    ]},
    { id: "ca4", name: "Análisis de Mercado", rows: [
      { id: "cr16", paso: "Izzy presentó la data", responsable: "", fecha: "", estatus: "completado" },
      { id: "cr17", paso: "Considerar si hacer análisis de categoría después de back to school y halloween, o hasta inicios del 2027, para incluir también temporada de Thanksgiving y Holiday Season", responsable: "Miguel", fecha: "", estatus: "pendiente" },
      { id: "cr18", paso: "Price & Pack Size Recommendations for Costco & Sam's", responsable: "Miguel", fecha: "", estatus: "pendiente" },
    ]},
    { id: "ca5", name: "Requisitos para alta de clientes", rows: [
      { id: "cr19", paso: "DUNS está en proceso", responsable: "", fecha: "", estatus: "en_progreso" },
      { id: "cr20", paso: "Alta en RJW, aceptaron cambios en contrato, falta que lo manden para firmar y llenar formularios (se requiere datos de UPC)", responsable: "", fecha: "", estatus: "pendiente" },
      { id: "cr21", paso: "Liability Insurance (Seguro de Responsabilidad Civil)", responsable: "", fecha: "", estatus: "pendiente" },
    ]},
    { id: "ca6", name: "Ventas", rows: [
      { id: "cr22", paso: "HEB: Respuesta de compradora (domicilio para envío de muestras)", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr23", paso: "Walmart marketplace (Variety pack vs. 5 packs)", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr24", paso: "Costco: Contrato de Brokers, fechas objetivo para el proyecto (finalizar deck, muestras de producto, presentación a Costco)", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr25", paso: "Target: Junta con Broker para lunes o martes Junio 22-23", responsable: "Rodrigo", fecha: "", estatus: "pendiente" },
      { id: "cr26", paso: "UNFI", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr27", paso: "KeHE", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr28", paso: "Dot Foods", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr29", paso: "Pod Foods", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr30", paso: "Adventure CPG", responsable: "Karen", fecha: "", estatus: "pendiente" },
      { id: "cr31", paso: "DSD Distributors", responsable: "Karen", fecha: "", estatus: "pendiente" },
    ]},
  ];
  const [clientAreas, setClientAreas] = useState(CLIENT_AREA_SEED);
  const [editingClientCell, setEditingClientCell] = useState(null); // { areaId, rowId, field }
  const [editingAreaName, setEditingAreaName] = useState(null); // areaId
  const [dragRowState, setDragRowState] = useState(null); // { id, sobre }
  const [filtroArea, setFiltroArea] = useState(null); // id de área o null ("Todas") — pestaña Pendientes

  // ── Resultados: fichas de trabajo cerrado y publicado ──────────────────────
  const [resultados, setResultados] = useState(RES_SEED);
  const [resFrente, setResFrente]   = useState(null);   // key de RES_FRENTES o null
  const [resVer, setResVer]         = useState(null);   // id de ficha abierta en detalle
  const [resDraft, setResDraft]     = useState(null);   // ficha en edición
  const [showCompletados, setShowCompletados] = useState(false);

  // ── Firebase: listen to auth state ────────────────────────────────────────
  useEffect(() => {
    // Completa el login por redirect (Safari) al regresar de Google; onAuthStateChanged recibe el usuario.
    getRedirectResult(auth).catch(console.error);
    const unsub = onAuthStateChanged(auth, (u) => {
      if (u && !u.email?.endsWith("@partecpg.com")) {
        // Not allowed — sign them out immediately
        signOut(auth);
        setUser(null);
        setUnauthorized(true);
      } else {
        setUser(u);
        setUnauthorized(false);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  // ── Clear bar tooltip on mouseup (end of drag) ──────────────────────────
  useEffect(() => {
    const onUp = () => setBarTooltip(null);
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  // ── Label column resize ───────────────────────────────────────────────────
  const startLabelResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = labelW;
    const onMove = (mv) => {
      const newW = Math.max(160, Math.min(600, startW + mv.clientX - startX));
      setLabelW(newW);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
    };
    document.body.style.cursor = "ew-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ── Firebase: real-time listener for projects ─────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_PROJECTS(), (snap) => {
      if (snap.exists()) {
        try {
          const data = snap.data();
          setProjects(deserializeProjects(data.list));
        } catch(e) { console.error("deserialize error", e); }
      }
      setDbLoading(false);
    }, (err) => {
      console.error("Firestore error", err);
      setDbLoading(false);
    });
    return unsub;
  }, []);

  // ── Firebase: real-time listener for ownerColors ──────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_OWNER_COLORS(), (snap) => {
      if (snap.exists()) setOwnerColors(snap.data().colors);
    });
    return unsub;
  }, []);

  // ── Firebase: save projects whenever they change (debounced 800ms) ────────
  const saveTimerRef = useRef(null);
  const saveProjects = (next) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await setDoc(DOC_PROJECTS(), { list: serializeProjects(next) });
      } catch(e) { console.error("save error", e); }
    }, 800);
  };

  // ── Firebase: real-time listener for objectives ───────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_OBJECTIVES(), (snap) => {
      if (snap.exists()) setObjectives(snap.data().list);
    });
    return unsub;
  }, []);

  // ── Firebase: real-time listener for pizarra ─────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_PIZARRA(), (snap) => {
      if (snap.exists()) setAvisos(snap.data().avisos || []);
    });
    return unsub;
  }, []);

  const saveAvisos = async (next) => {
    try { await setDoc(DOC_PIZARRA(), { avisos: next }); }
    catch(e) { console.error("save pizarra error", e); }
  };

  // ── Firebase: real-time listener for client tasks (pública) ───────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_CLIENT_TASKS(), (snap) => {
      if (snap.exists()) setClientAreas(snap.data().areas || []);
      else setDoc(DOC_CLIENT_TASKS(), { areas: CLIENT_AREA_SEED }).catch(console.error);
    });
    return unsub;
  }, []);

  const saveClientAreas = async (next) => {
    try { await setDoc(DOC_CLIENT_TASKS(), { areas: next }); }
    catch(e) { console.error("save client tasks error", e); }
  };

  // ── Firebase: real-time listener for CRM deals ────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_CRM(), (snap) => {
      if (snap.exists()) setCrmDeals((snap.data().deals || []).map(d => ({ ...d, etapa: migrateStage(d.etapa) })));
      else setDoc(DOC_CRM(), { deals: CRM_SEED }).catch(console.error);
    });
    return unsub;
  }, []);

  const saveCrmDeals = async (next) => {
    try { await setDoc(DOC_CRM(), { deals: next }); }
    catch(e) { console.error("save crm error", e); }
  };

  // ── Firebase: real-time listener for timeline milestones ──────────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_TIMELINE(), (snap) => {
      if (snap.exists()) setMilestones(tlMigrate(snap.data().milestones));
      else setDoc(DOC_TIMELINE(), { milestones: TL_SEED }).catch(console.error);
    });
    return unsub;
  }, []);

  const saveMilestones = async (next) => {
    setMilestones(next);
    try { await setDoc(DOC_TIMELINE(), { milestones: next }); }
    catch(e) { console.error("save timeline error", e); }
  };

  // ── Firebase: real-time listener para las fichas de Resultados ────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_RESULTADOS(), (snap) => {
      if (snap.exists()) {
        const crudo = snap.data().fichas || [];
        const migradas = resMigrate(crudo);
        setResultados(migradas);
        // Escribe la versión migrada una sola vez para que el campo viejo no siga
        // guardado. resMigrate es idempotente, así que no se cicla.
        if (JSON.stringify(crudo) !== JSON.stringify(migradas)) {
          setDoc(DOC_RESULTADOS(), { fichas: migradas }).catch(console.error);
        }
      } else setDoc(DOC_RESULTADOS(), { fichas: RES_SEED }).catch(console.error);
    });
    return unsub;
  }, []);

  const saveResultados = async (next) => {
    setResultados(next);
    try { await setDoc(DOC_RESULTADOS(), { fichas: next }); }
    catch(e) { console.error("save resultados error", e); }
  };

  // ── Firebase: real-time listener for meta (companyName, projectName) ──────
  useEffect(() => {
    const unsub = onSnapshot(DOC_META(), (snap) => {
      if (snap.exists()) {
        const d = snap.data();
        if (d.companyName !== undefined) setCompanyName(d.companyName);
        if (d.projectName !== undefined) setProjectName(d.projectName);
      }
    });
    return unsub;
  }, []);

  // ── Firebase: save ownerColors ────────────────────────────────────────────
  const saveOwnerColors = async (next) => {
    try {
      await setDoc(DOC_OWNER_COLORS(), { colors: next });
    } catch(e) { console.error("save ownerColors error", e); }
  };

  const saveObjectives = async (next) => {
    try { await setDoc(DOC_OBJECTIVES(), { list: next }); }
    catch(e) { console.error("save objectives error", e); }
  };

  const saveMeta = async (patch) => {
    try { await setDoc(DOC_META(), patch, { merge: true }); }
    catch(e) { console.error("save meta error", e); }
  };

  // ── Time Tracker: own entries listener ────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const unsub = onSnapshot(DOC_TIME(user.uid), (snap) => {
      if (snap.exists()) setTimeEntries(snap.data().entries || []);
      else setTimeEntries([]);
    });
    return unsub;
  }, [user]);

  // ── Time Tracker: register self in index when first entry saved ────────────
  const ensureInIndex = async () => {
    if (!user) return;
    const snap = await getDoc(DOC_TIME_INDEX()).catch(() => null);
    const existing = snap?.data()?.users || [];
    if (!existing.some(u => u.uid === user.uid)) {
      const updated = [...existing, { uid: user.uid, name: user.displayName || user.email, email: user.email }];
      await setDoc(DOC_TIME_INDEX(), { users: updated }, { merge: true }).catch(console.error);
    }
  };

  // ── Time Tracker: admin index listener ────────────────────────────────────
  const isTimeAdmin = user && TIME_ADMINS.has(user.email);
  useEffect(() => {
    if (!isTimeAdmin) return;
    const unsub = onSnapshot(DOC_TIME_INDEX(), (snap) => {
      setTimeUserList(snap.exists() ? (snap.data().users || []) : []);
    });
    return unsub;
  }, [isTimeAdmin]);

  // ── Time Tracker: admin watched user listener ──────────────────────────────
  useEffect(() => {
    if (!watchedUid) { setWatchedEntries([]); return; }
    const unsub = onSnapshot(DOC_TIME(watchedUid), (snap) => {
      setWatchedEntries(snap.exists() ? (snap.data().entries || []) : []);
    });
    return unsub;
  }, [watchedUid]);

  // ── Time Tracker: archive listener ────────────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(DOC_TIME_ARCHIVE(), (snap) => {
      setTimeArchives(snap.exists() ? (snap.data().archives || []) : []);
    });
    return unsub;
  }, []);

  // ── Time Tracker: auto-archive previous month on first open ───────────────
  // NEW APPROACH: at month close, ALL unarchived entries (regardless of their
  // own date) get moved into the previous month's archive bucket.
  // Restricted to miguel@partecpg.com for testing before wider rollout.
  useEffect(() => {
    if (!user) return;
    if (user.email !== "miguel@partecpg.com") return;
    const runArchive = async () => {
      try {
        // Compute previous month key e.g. "2026-05"
        const now = new Date();
        const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const monthKey = `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}`;

        await runTransaction(db, async (tx) => {
          const archiveSnap = await tx.get(DOC_TIME_ARCHIVE());
          const existing = archiveSnap.exists() ? (archiveSnap.data().archives || []) : [];

          // Already archived this month? bail out
          if (existing.some(a => a.monthKey === monthKey)) return;

          // Fetch time_index to get all users
          const indexSnap = await tx.get(DOC_TIME_INDEX());
          const users = indexSnap.exists() ? (indexSnap.data().users || []) : [];

          const archiveUsers = [];
          const userUpdates = []; // { uid, email, name }

          for (const u of users) {
            const userSnap = await tx.get(DOC_TIME(u.uid));
            if (!userSnap.exists()) continue;
            const allEntries = userSnap.data().entries || [];
            if (allEntries.length === 0) continue;
            // Move EVERYTHING unarchived into this month's bucket, regardless of date
            archiveUsers.push({ uid: u.uid, name: u.name || "", email: u.email || "", entries: allEntries });
            userUpdates.push({ uid: u.uid, email: u.email || "", name: u.name || "" });
          }

          if (archiveUsers.length === 0) return; // nothing to archive

          // Write archive
          tx.set(DOC_TIME_ARCHIVE(), {
            archives: [...existing, { monthKey, users: archiveUsers }]
          });

          // Clear each user's active doc — everything has been archived
          for (const u of userUpdates) {
            tx.set(DOC_TIME(u.uid), { entries: [], email: u.email, name: u.name });
          }
        });
      } catch(e) { console.error("archive error", e); }
    };
    runArchive();
  }, [user]);

  // ── Time Tracker: timer tick ───────────────────────────────────────────────
  useEffect(() => {
    const host = pipWin || window;
    if (timerRunning) {
      timerStartRef.current = timerStartRef.current || Date.now() - timerSeconds * 1000;
      timerRef.current = host.setInterval(() => {
        setTimerSeconds(Math.floor((Date.now() - timerStartRef.current) / 1000));
      }, 1000);
    }
    return () => { try { host.clearInterval(timerRef.current); } catch(e) {} };
  }, [timerRunning, pipWin]);

  // Timer persistence (personal preference → localStorage): survives reload/close of the tab
  const timerKey = user ? `gaveti_timer_${user.uid}` : null;
  const timerIdleSeconds = timerRunning ? 0 : timerSeconds;
  useEffect(() => {
    if (!timerKey || timerHydratedUid.current === timerKey) return;
    timerHydratedUid.current = timerKey;
    try {
      const saved = JSON.parse(localStorage.getItem(timerKey) || "null");
      if (!saved) return;
      setTimerNote(saved.note || "");
      if (saved.date) setTimerDate(saved.date);
      setTimerPendingSave(!!saved.pending);
      if (saved.running && saved.startedAt) {
        timerStartRef.current = saved.startedAt;
        setTimerSeconds(Math.floor((Date.now() - saved.startedAt) / 1000));
        setTimerRunning(true);
      } else {
        setTimerSeconds(saved.seconds || 0);
      }
    } catch(e) {}
  }, [timerKey]);
  useEffect(() => {
    if (!timerKey || timerHydratedUid.current !== timerKey) return;
    try {
      if (!timerRunning && timerSeconds === 0 && !timerPendingSave && !timerNote) localStorage.removeItem(timerKey);
      else localStorage.setItem(timerKey, JSON.stringify({
        running: timerRunning, startedAt: timerRunning ? timerStartRef.current : null,
        seconds: timerSeconds, pending: timerPendingSave, note: timerNote, date: timerDate,
      }));
    } catch(e) {}
  }, [timerKey, timerRunning, timerIdleSeconds, timerPendingSave, timerNote, timerDate]);

  const saveTimeEntry = async (seconds, note = "", date = null) => {
    if (!user || seconds < 1) return;
    const entry = {
      id: Date.now().toString(),
      date: date || new Date().toISOString().slice(0, 10),
      seconds,
      note: note.trim(),
    };
    const next = [entry, ...timeEntries].sort((a,b) => (b.date||"").localeCompare(a.date||""));
    setTimeEntries(next);
    try {
      await setDoc(DOC_TIME(user.uid), { entries: next, email: user.email, name: user.displayName });
      ensureInIndex();
    } catch(e) { console.error("save time error", e); }
  };

  const deleteTimeEntry = async (id) => {
    const next = timeEntries.filter(e => e.id !== id);
    setTimeEntries(next);
    try { await setDoc(DOC_TIME(user.uid), { entries: next, email: user.email, name: user.displayName }); }
    catch(e) { console.error("delete time error", e); }
  };

  // Add an entry directly to a closed archive month
  const saveToArchive = async (monthKey, seconds, note, date) => {
    if (!user || seconds < 1) return;
    const entry = { id: Date.now().toString(), date, seconds, note: note.trim() };
    try {
      const snap = await getDoc(DOC_TIME_ARCHIVE());
      const archives = snap.exists() ? (snap.data().archives || []) : [];
      const next = archives.map(a => {
        if (a.monthKey !== monthKey) return a;
        const users = a.users.map(u => {
          if (u.uid !== user.uid) return u;
          return { ...u, entries: [...(u.entries||[]), entry].sort((x,y) => (y.date||"").localeCompare(x.date||"")) };
        });
        // If user not in archive yet, add them
        const userExists = a.users.some(u => u.uid === user.uid);
        if (!userExists) users.push({ uid: user.uid, name: user.displayName||"", email: user.email||"", entries: [entry] });
        return { ...a, users };
      });
      await setDoc(DOC_TIME_ARCHIVE(), { archives: next });
    } catch(e) { console.error("saveToArchive error", e); }
  };

  const stopAndSaveTimer = () => {
    setTimerRunning(false);
    timerStartRef.current = null;
    if (timerSeconds > 0) {
      setTimerDate(new Date().toISOString().slice(0,10));
      setTimerPendingSave(true); // show description + date form before committing
    }
  };

  const commitTimerSave = () => {
    saveTimeEntry(timerSeconds, timerNote, timerDate);
    setTimerSeconds(0);
    setTimerNote("");
    setTimerDate(new Date().toISOString().slice(0,10));
    setTimerPendingSave(false);
  };

  const pipSupported = typeof window !== "undefined" && "documentPictureInPicture" in window;
  const toggleTimerPip = async () => {
    if (pipWin) { pipWin.close(); return; }
    try {
      const w = await window.documentPictureInPicture.requestWindow({ width: 220, height: 62 });
      w.document.body.style.cssText = "margin:0;padding:6px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;background:#fff;display:flex;flex-direction:column;gap:8px";
      w.addEventListener("pagehide", () => setPipWin(null));
      setPipWin(w);
    } catch(e) { console.error("pip error", e); }
  };

  useEffect(() => {
    if (!pipWin) return;
    try { pipWin.resizeTo(220, timerPendingSave ? 150 : 62); } catch(e) {}
  }, [pipWin, timerPendingSave]);

  const renderTimerControls = (big) => (
    <>
      <div style={{ display:"flex",alignItems:"center",gap:big?8:5,background:timerRunning?"#FEF3C7":"#f1f5f9",borderRadius:big?8:6,padding:big?"5px 10px":"4px 8px",border:timerRunning?"1px solid #FDE68A":"1px solid #e2e8f0" }}>
        <div style={{ flex:1,fontSize:big?22:15,fontWeight:900,color:timerRunning?"#F59E0B":"#64748b",fontVariantNumeric:"tabular-nums" }}>{fmt(timerSeconds)}</div>
        {!timerRunning
          ?<button onClick={()=>{timerStartRef.current=Date.now()-timerSeconds*1000;setTimerRunning(true);setTimerPendingSave(false);}}
              style={{ width:big?28:24,height:big?28:24,borderRadius:"50%",background:"#22C55E",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:big?11:10,color:"#fff",flexShrink:0 }}>▶</button>
          :<button onClick={stopAndSaveTimer}
              style={{ width:big?28:24,height:big?28:24,borderRadius:"50%",background:"#EF4444",border:"none",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontSize:big?9:8,color:"#fff",flexShrink:0,fontWeight:900 }}>■</button>
        }
        {timerSeconds>0&&!timerRunning&&!timerPendingSave&&(
          <button onClick={()=>{setTimerSeconds(0);timerStartRef.current=null;}} style={{ fontSize:big?12:10,color:"#94a3b8",background:"none",border:"none",cursor:"pointer",padding:"0 2px" }}>✕</button>
        )}
        {!big && pipSupported && (
          <button onClick={toggleTimerPip} title={pipWin?"Cerrar timer flotante":"Timer flotante"}
            style={{ fontSize:11,color:pipWin?"#6366F1":"#94a3b8",background:"none",border:"none",cursor:"pointer",padding:"0 2px",flexShrink:0 }}>⧉</button>
        )}
      </div>
      {timerPendingSave && (
        <div style={{ display:"flex",flexDirection:"column",gap:big?4:4,background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:big?8:6,padding:big?"5px 7px":"6px 8px" }}>
          <div style={{ fontSize:big?10:8,color:"#92400e",fontWeight:700 }}>Guardar {fmt(timerSeconds)}</div>
          <input autoFocus placeholder="Describe en qué se usó el tiempo" maxLength={180} value={timerNote}
            onChange={e=>setTimerNote(e.target.value)} onKeyDown={e=>e.key==="Enter"&&commitTimerSave()}
            style={{ fontSize:big?11:10,border:"1px solid #FDE68A",borderRadius:5,padding:big?"3px 6px":"3px 6px",outline:"none",color:"#334155",background:"#fff" }} />
          <div style={{ display:"flex",gap:4,alignItems:"center" }}>
            <input type="date" value={timerDate} onChange={e=>setTimerDate(e.target.value)}
              style={{ flex:1,minWidth:0,fontSize:big?10:9,border:"1px solid #FDE68A",borderRadius:5,padding:big?"2px 4px":"3px 5px",outline:"none",color:"#334155",background:"#fff" }} />
            <button onClick={commitTimerSave} style={{ padding:big?"3px 8px":"3px 10px",fontSize:big?10:10,fontWeight:700,background:"#6366F1",color:"#fff",border:"none",borderRadius:5,cursor:"pointer" }}>Guardar</button>
            <button onClick={()=>{setTimerPendingSave(false);setTimerSeconds(0);setTimerNote("");}} style={{ fontSize:big?11:10,color:"#94a3b8",background:"none",border:"none",cursor:"pointer" }}>✕</button>
          </div>
        </div>
      )}
    </>
  );

  const updateTimeEntry = async (id, patch) => {
    const next = timeEntries
      .map(e => e.id === id ? { ...e, ...patch } : e)
      .sort((a,b) => (b.date||"").localeCompare(a.date||""));
    setTimeEntries(next);
    try { await setDoc(DOC_TIME(user.uid), { entries: next, email: user.email, name: user.displayName }); }
    catch(e) { console.error("update time error", e); }
  };

  const fmt = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return h > 0 ? `${h}h ${String(m).padStart(2,"0")}m` : `${String(m).padStart(2,"0")}:${String(sec).padStart(2,"0")}`;
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const curMonthPrefix = todayStr.slice(0, 7); // e.g. "2026-05"
  const totalSeconds   = timeEntries.reduce((a, e) => a + e.seconds, 0); // all active
  const todaySeconds   = timeEntries.filter(e => e.date === todayStr).reduce((a, e) => a + e.seconds, 0);

  // For admin view: totals of watched user
  const watchedTotal = watchedEntries.reduce((a, e) => a + e.seconds, 0);
  const watchedToday = watchedEntries.filter(e => e.date === todayStr).reduce((a, e) => a + e.seconds, 0);

  // ── Auth helpers ──────────────────────────────────────────────────────────
  const signIn  = () => {
    // Safari (incl. iOS) suele bloquear/congelar el popup de OAuth por su ITP → usamos redirect.
    const isSafari = /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent);
    if (isSafari) return signInWithRedirect(auth, googleProvider).catch(console.error);
    return signInWithPopup(auth, googleProvider).catch(err => {
      // Si el popup se bloquea/cancela en cualquier navegador, caemos a redirect.
      if (["auth/popup-blocked", "auth/cancelled-popup-request", "auth/popup-closed-by-user", "auth/operation-not-supported-in-environment", "auth/web-storage-unsupported"].includes(err?.code)) {
        return signInWithRedirect(auth, googleProvider).catch(console.error);
      }
      console.error(err);
    });
  };
  const signOut_ = () => signOut(auth).catch(console.error);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const updateTask = (spaceId, taskId, patch, subtaskId) => {
    setProjects(prev => {
      const next = prev.map(p => {
        if (p.id !== spaceId) return p;
        return {
          ...p, tasks: p.tasks.map(t => {
            if (t.id !== taskId) return t;
            if (subtaskId) return { ...t, subtasks: t.subtasks.map(s => s.id !== subtaskId ? s : { ...s, ...patch }) };
            return { ...t, ...patch };
          })
        };
      });
      saveProjects(next);
      return next;
    });
  };

  const handleAdd = ({ spaceId, taskId, type, item }) => {
    setProjects(prev => {
      const next = prev.map(p => {
        if (p.id !== spaceId) return p;
        if (type === "task") return { ...p, tasks: [...p.tasks, item] };
        return { ...p, tasks: p.tasks.map(t => t.id !== taskId ? t : { ...t, subtasks: [...(t.subtasks||[]), item] }) };
      });
      saveProjects(next);
      return next;
    });
  };

  const deleteUser = (key) => {
    // Remove user from ownerColors
    setOwnerColors(prev => { const n = {...prev}; delete n[key]; saveOwnerColors(n); return n; });
    // Also remove them from all tasks/subtasks
    setProjects(prev => prev.map(p => ({
      ...p, tasks: p.tasks.map(t => ({
        ...t,
        owners: t.owners.filter(o => o !== key),
        subtasks: t.subtasks.map(s => ({ ...s, owners: s.owners.filter(o => o !== key) }))
      }))
    })));
  };

  const saveEditUser = (oldKey, newKey, newData) => {
    setOwnerColors(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        if (k === oldKey) next[newKey] = newData;
        else next[k] = v;
      });
      saveOwnerColors(next);
      return next;
    });
    // If initials changed, update all task/subtask owner arrays
    if (oldKey !== newKey) {
      setProjects(prev => prev.map(p => ({
        ...p, tasks: p.tasks.map(t => ({
          ...t,
          owners: t.owners.map(o => o === oldKey ? newKey : o),
          subtasks: t.subtasks.map(s => ({ ...s, owners: s.owners.map(o => o === oldKey ? newKey : o) }))
        }))
      })));
    }
  };

  const deleteTask = (spaceId, taskId) => {
    setConfirm({ message: "Se eliminará esta tarea y todas sus subtareas.", onConfirm: () => {
      setProjects(prev => {
        const next = prev.map(p => p.id !== spaceId ? p : { ...p, tasks: p.tasks.filter(t => t.id !== taskId) });
        saveProjects(next);
        return next;
      });
      setConfirm(null);
    }});
  };

  const deleteSubtask = (spaceId, taskId, subtaskId) => {
    setConfirm({ message: "Se eliminará esta subtarea.", onConfirm: () => {
      setProjects(prev => {
        const next = prev.map(p => p.id !== spaceId ? p : {
          ...p, tasks: p.tasks.map(t => t.id !== taskId ? t : { ...t, subtasks: t.subtasks.filter(s => s.id !== subtaskId) })
        });
        saveProjects(next);
        return next;
      });
      setConfirm(null);
    }});
  };

  // ── Reorder helpers ──────────────────────────────────────────────────────────
  // ── Space CRUD ───────────────────────────────────────────────────────────────
  const SPACE_COLORS = ["#6366F1","#F59E0B","#10B981","#EF4444","#3B82F6","#EC4899","#8B5CF6","#14B8A6"];

  const addSpace = () => {
    const id = "s" + Date.now();
    const color = SPACE_COLORS[projects.length % SPACE_COLORS.length];
    const newSpace = { id, space: "Nueva Área", color, tasks: [] };
    setProjects(prev => {
      const next = [...prev, newSpace];
      saveProjects(next);
      return next;
    });
    setExpanded(ex => ({ ...ex, [id]: true }));
    setEditingSpace(id);
  };

  const renameSpace = (spaceId, name) => {
    if (!name.trim()) return;
    setProjects(prev => {
      const next = prev.map(p => p.id !== spaceId ? p : { ...p, space: name.trim() });
      saveProjects(next);
      return next;
    });
    setEditingSpace(null);
  };

  const deleteSpace = (spaceId) => {
    const space = projects.find(p => p.id === spaceId);
    setConfirm({ message: `¿Eliminar el área "${space?.space}" y todas sus tareas?`, onConfirm: () => {
      setProjects(prev => {
        const next = prev.filter(p => p.id !== spaceId);
        saveProjects(next);
        return next;
      });
      setConfirm(null);
    }});
  };

  const moveTask = (spaceId, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setProjects(prev => prev.map(p => {
      if (p.id !== spaceId) return p;
      const tasks = [...p.tasks];
      const [moved] = tasks.splice(fromIdx, 1);
      tasks.splice(toIdx, 0, moved);
      return { ...p, tasks };
    }));
  };

  const moveSubtask = (spaceId, taskId, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setProjects(prev => prev.map(p => {
      if (p.id !== spaceId) return p;
      return { ...p, tasks: p.tasks.map(t => {
        if (t.id !== taskId) return t;
        const subs = [...t.subtasks];
        const [moved] = subs.splice(fromIdx, 1);
        subs.splice(toIdx, 0, moved);
        return { ...t, subtasks: subs };
      })};
    }));
  };

  const moveSpace = (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    setProjects(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, moved);
      return arr;
    });
  };

  // ── Mouse-based drag — works in Safari, Chrome, Firefox ─────────────────────
  // Key design: dragRef stores ALL mutable drag state so closures never go stale.
  // React state (dragItem, dragOver) is only used for rendering indicators.
  const startRowDrag = (e, item) => {
    if (guestMode) { showLoginToast(); return; }
    e.preventDefault();
    e.stopPropagation();

    // Everything lives in the ref — no closure staleness
    dragRef.current = { item, startY: e.clientY, moved: false, over: null };

    const resolveDragOver = (clientY) => {
      const els = document.querySelectorAll("[data-drag-id]");
      let found = null;
      els.forEach(el => {
        const r = el.getBoundingClientRect();
        if (clientY >= r.top && clientY <= r.bottom) {
          const id = el.getAttribute("data-drag-id");
          const pos = clientY < r.top + r.height / 2 ? "before" : "after";
          found = { id, pos };
        }
      });
      if (!found) return null;
      const parts = found.id.split(":");
      const type = parts[0];
      if (type === "space" && !item.taskId)
        return { spaceId: parts[1], position: found.pos };
      if (type === "task" && item.taskId && !item.subtaskId)
        return { spaceId: parts[1], taskId: parts[2], position: found.pos };
      if (type === "sub" && item.subtaskId && item.taskId === parts[2])
        return { spaceId: parts[1], taskId: parts[2], subtaskId: parts[3], position: found.pos };
      return null;
    };

    const onMove = (mv) => {
      const dy = Math.abs(mv.clientY - dragRef.current.startY);
      if (dy < 5) return;
      if (!dragRef.current.moved) {
        dragRef.current.moved = true;
        setDragItem(item);
        document.body.style.cursor = "grabbing";
      }
      const over = resolveDragOver(mv.clientY);
      dragRef.current.over = over;   // always up-to-date in ref
      setDragOver(over);             // triggers re-render for indicators
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";

      const { moved, item: draggedItem, over } = dragRef.current || {};
      dragRef.current = null;
      setDragItem(null);
      setDragOver(null);

      if (!moved || !over) return;

      // ── Commit move using values directly from ref (never stale) ──
      const isSpaceDrag = draggedItem.spaceId && !draggedItem.taskId;
      if (isSpaceDrag && over.spaceId && !over.taskId && draggedItem.spaceId !== over.spaceId) {
        setProjects(prev => {
          const arr = [...prev];
          const from = arr.findIndex(p => p.id === draggedItem.spaceId);
          let to = arr.findIndex(p => p.id === over.spaceId);
          if (from === -1 || to === -1) return prev;
          if (over.position === "after") to += 1;
          if (from < to) to -= 1;
          const [moved] = arr.splice(from, 1);
          arr.splice(to, 0, moved);
          saveProjects(arr);
          return arr;
        });
        return;
      }

      if (draggedItem.taskId && !draggedItem.subtaskId && over.taskId && !over.subtaskId
          && draggedItem.spaceId === over.spaceId && draggedItem.taskId !== over.taskId) {
        setProjects(prev => {
          const next = prev.map(p => {
            if (p.id !== draggedItem.spaceId) return p;
            const tasks = [...p.tasks];
            const from = tasks.findIndex(t => t.id === draggedItem.taskId);
            let to = tasks.findIndex(t => t.id === over.taskId);
            if (from === -1 || to === -1) return p;
            if (over.position === "after") to += 1;
            if (from < to) to -= 1;
            const [moved] = tasks.splice(from, 1);
            tasks.splice(to, 0, moved);
            return { ...p, tasks };
          });
          saveProjects(next);
          return next;
        });
        return;
      }

      if (draggedItem.subtaskId && over.subtaskId
          && draggedItem.taskId === over.taskId
          && draggedItem.subtaskId !== over.subtaskId) {
        setProjects(prev => {
          const next = prev.map(p => {
            if (p.id !== draggedItem.spaceId) return p;
            return { ...p, tasks: p.tasks.map(t => {
              if (t.id !== draggedItem.taskId) return t;
              const subs = [...t.subtasks];
              const from = subs.findIndex(s => s.id === draggedItem.subtaskId);
              let to = subs.findIndex(s => s.id === over.subtaskId);
              if (from === -1 || to === -1) return t;
              if (over.position === "after") to += 1;
              if (from < to) to -= 1;
              const [moved] = subs.splice(from, 1);
              subs.splice(to, 0, moved);
              return { ...t, subtasks: subs };
            })};
          });
          saveProjects(next);
          return next;
        });
      }
    };

    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ── Drag-to-resize — uses snapshot of dates at drag-start ──────────────────
  const startDrag = (e, spaceId, taskId, subtaskId, edge) => {
    e.preventDefault();
    e.stopPropagation();
    if (guestMode) { showLoginToast(); return; }
    const chartW = chartRef.current?.offsetWidth || 900;
    const msPerPx = viewMs / chartW;
    const startX = e.clientX;

    // Snapshot the exact start/end at the moment of mousedown
    let snapStart, snapEnd;
    const space = projects.find(p => p.id === spaceId);
    if (space) {
      const task = space.tasks.find(t => t.id === taskId);
      if (task) {
        const item = subtaskId ? task.subtasks.find(s => s.id === subtaskId) : task;
        if (item) { snapStart = item.start.getTime(); snapEnd = item.end.getTime(); }
      }
    }
    if (!snapStart) return;

    const onMove = (me) => {
      const dx = me.clientX - startX;
      const dms = dx * msPerPx;
      const MIN_DUR = 86400000;
      let patch, tooltipDate;
      if (edge === "right") {
        tooltipDate = new Date(Math.max(snapStart + MIN_DUR, snapEnd + dms));
        patch = { end: tooltipDate };
      } else {
        tooltipDate = new Date(Math.min(snapEnd - MIN_DUR, snapStart + dms));
        patch = { start: tooltipDate };
      }
      updateTask(spaceId, taskId, patch, subtaskId);
      setBarTooltip({ edge: edge, label: tooltipDate.toLocaleDateString("es-MX", { day: "numeric", month: "short" }) });
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setBarTooltip(null);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // ── Name editing ─────────────────────────────────────────────────────────────
  const startEditName = (spaceId, taskId, currentName, subtaskId) => {
    setEditingName({ spaceId, taskId, subtaskId });
    setEditNameVal(currentName);
  };
  const commitName = () => {
    if (editingName && editNameVal.trim()) {
      updateTask(editingName.spaceId, editingName.taskId, { name: editNameVal.trim() }, editingName.subtaskId);
    }
    setEditingName(null);
  };

  // ── Owner save ───────────────────────────────────────────────────────────────
  const saveOwners = (owners) => {
    if (ownerPicker) updateTask(ownerPicker.spaceId, ownerPicker.taskId, { owners }, ownerPicker.subtaskId);
  };

  // ── Links save ────────────────────────────────────────────────────────────────
  const saveLinks = (links) => {
    if (linksPicker) updateTask(linksPicker.spaceId, linksPicker.taskId, { links }, linksPicker.subtaskId);
  };

  // ── Zoom: viewStart = January of current year, scroll starts at today ──────
  const viewStart = React.useMemo(() => {
    return new Date(TODAY.getFullYear(), 0, 1); // Jan 1 of current year
  }, []);

  // viewEnd = far enough to cover all projects (at least 24 months out)
  const viewEnd = React.useMemo(() => {
    const minEnd = new Date(viewStart.getFullYear(), viewStart.getMonth() + 24, 1);
    return PROJECT_END > minEnd ? PROJECT_END : minEnd;
  }, [viewStart]);

  const viewMs = viewEnd - viewStart;

  const vpct = (date) => Math.max(0, Math.min(100, ((date - viewStart) / viewMs) * 100));

  // All months from viewStart to viewEnd
  const visibleMonths = React.useMemo(() => {
    const months = [];
    let cur = new Date(viewStart.getFullYear(), viewStart.getMonth(), 1);
    while (cur <= viewEnd) {
      months.push(new Date(cur));
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    return months;
  }, [viewStart, viewEnd]);

  const totalMonths = visibleMonths.length;

  // Scroll chart to show today on mount and when zoom changes
  useEffect(() => {
    if (!ganttScrollRef.current) return;
    const todayFraction = (TODAY - viewStart) / viewMs;
    const scrollableW = ganttScrollRef.current.scrollWidth - ganttScrollRef.current.clientWidth;
    ganttScrollRef.current.scrollLeft = Math.max(0, todayFraction * (ganttScrollRef.current.scrollWidth) - ganttScrollRef.current.clientWidth * 0.15);
  }, [zoomMonths]);

  const todayPct = vpct(TODAY);

  const totalDelayed = projects.reduce((a, p) => a + p.tasks.filter(t => TODAY > t.end && t.progress < 100).length, 0);
  const totalInProgress = projects.reduce((a, p) => a + p.tasks.filter(t => TODAY >= t.start && TODAY <= t.end && t.progress < 100).length, 0);

  // ── Filter helper ────────────────────────────────────────────────────────────
  const taskMatchesFilter = (task) => {
    if (!filterOwner) return true;
    if (task.owners.includes(filterOwner)) return true;
    if (task.subtasks?.some(s => s.owners.includes(filterOwner))) return true;
    return false;
  };


// ── LinksModal ────────────────────────────────────────────────────────────────
function LinksModal({ links = [], onSave, onClose, taskName }) {
  const [items, setItems] = React.useState(
    links.length ? links.map(l => ({ ...l })) : []
  );
  const [editIdx, setEditIdx] = React.useState(null);
  const [editUrl, setEditUrl]   = React.useState("");
  const [editLabel, setEditLabel] = React.useState("");

  const startEdit = (idx) => {
    setEditIdx(idx);
    setEditUrl(items[idx]?.url || "");
    setEditLabel(items[idx]?.label || "");
  };
  const startAdd = () => {
    if (items.length >= 5) return;
    const idx = items.length;
    setItems(prev => [...prev, { url: "", label: "" }]);
    setEditIdx(idx);
    setEditUrl("");
    setEditLabel("");
  };
  const commitEdit = () => {
    if (editIdx === null) return;
    const url = editUrl.trim();
    if (!url) { setItems(prev => prev.filter((_, i) => i !== editIdx)); }
    else {
      setItems(prev => prev.map((it, i) => i === editIdx ? { url, label: editLabel.trim() || url } : it));
    }
    setEditIdx(null);
  };
  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  return (
    <div style={{ position: "fixed", inset: 0, background: "#0006", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) { commitEdit(); onClose(); } }}>
      <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: 480, maxWidth: "95vw", boxShadow: "0 20px 60px #0003" }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "#6366F1", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Links de evidencia</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: "#1e293b", lineHeight: 1.3 }}>{taskName}</div>
        </div>

        {/* Links list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, minHeight: 40 }}>
          {items.length === 0 && editIdx === null && (
            <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: "16px 0" }}>
              Sin links todavía. Agrega hasta 5 links de Google Drive.
            </div>
          )}
          {items.map((item, idx) => (
            <div key={idx}>
              {editIdx === idx ? (
                <div style={{ background: "#f8fafc", borderRadius: 10, padding: 12, border: "1.5px solid #6366F1" }}>
                  <input
                    autoFocus
                    placeholder="URL de Google Drive"
                    value={editUrl}
                    onChange={e => setEditUrl(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && commitEdit()}
                    style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 10px", fontSize: 12, marginBottom: 6, outline: "none" }}
                  />
                  <input
                    placeholder="Nombre del link (opcional)"
                    value={editLabel}
                    onChange={e => setEditLabel(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && commitEdit()}
                    style={{ width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 10px", fontSize: 12, marginBottom: 8, outline: "none" }}
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={commitEdit}
                      style={{ padding: "5px 14px", borderRadius: 6, border: "none", background: "#6366F1", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                      Guardar
                    </button>
                    <button onClick={() => { setItems(prev => prev.filter((_, i) => i !== idx)); setEditIdx(null); }}
                      style={{ padding: "5px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer" }}>
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", borderRadius: 8, padding: "8px 12px", border: "1px solid #e2e8f0" }}>
                  <span style={{ fontSize: 14 }}>🔗</span>
                  <a href={item.url} target="_blank" rel="noopener noreferrer"
                    style={{ flex: 1, fontSize: 12, color: "#1D4ED8", fontWeight: 600, textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    title={item.url}>
                    {item.label || item.url}
                  </a>
                  <button onClick={() => startEdit(idx)}
                    style={{ padding: "2px 8px", borderRadius: 5, border: "1px solid #e2e8f0", background: "#fff", fontSize: 11, cursor: "pointer", color: "#64748b" }}>
                    <IcPencil size={12} color="#64748b" />
                  </button>
                  <button onClick={() => removeItem(idx)}
                    style={{ padding: "2px 8px", borderRadius: 5, border: "none", background: "#FEE2E2", fontSize: 11, cursor: "pointer", color: "#EF4444" }}>
                    ✕
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Add button */}
        {items.length < 5 && editIdx === null && (
          <button onClick={startAdd}
            style={{ width: "100%", padding: "8px", borderRadius: 8, border: "2px dashed #cbd5e1", background: "#f8fafc",
              fontSize: 12, fontWeight: 600, color: "#64748b", cursor: "pointer", marginBottom: 16 }}>
            + Agregar link {items.length > 0 ? `(${items.length}/5)` : ""}
          </button>
        )}

        {/* Footer */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={() => { commitEdit(); onClose(); }}
            style={{ padding: "7px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={() => { commitEdit(); onSave(items.filter(i => i.url)); onClose(); }}
            style={{ padding: "7px 20px", borderRadius: 8, border: "none", background: "#6366F1", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Guardar links
          </button>
        </div>
      </div>
    </div>
  );
}

  // ── GanttBar component ───────────────────────────────────────────────────────
  const clientXToDate = (clientX) => {
    const chartEl = chartRef.current;
    if (!chartEl) return null;
    const rect = chartEl.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return new Date(viewStart.getTime() + ratio * viewMs);
  };

  function GanttBar({ bar, spaceId, taskId, subtaskId, taskName }) {
    const left = vpct(bar.start);
    const width = Math.max(0.3, vpct(bar.end) - vpct(bar.start));
    const color = taskColor(bar);
    const hasLinks = bar.links && bar.links.length > 0;
    const hasComment = bar.comment && bar.comment.trim().length > 0;
    // Local tooltip state — isolated per bar instance
    const [tip, setTip] = useState(null); // "left" | "right" | null
    return (
      <div style={{ position: "absolute", left: `${left}%`, width: `${width}%`, top: "50%", transform: "translateY(-50%)", height: subtaskId ? 14 : 18, minWidth: 4 }}>
        <div style={{ position: "absolute", inset: 0, borderRadius: 4, background: `${color}22`, border: `1.5px solid ${color}66` }} />
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${bar.progress}%`, borderRadius: 4, background: color, opacity: 0.85, transition: "width 0.3s" }} />
        {/* Left drag handle */}
        <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 14, zIndex: 5, cursor: "ew-resize" }}
          onMouseEnter={() => setTip("left")}
          onMouseLeave={() => setTip(null)}
          onMouseDown={e => { setTip(null); startDrag(e, spaceId, taskId, subtaskId, "left"); }}>
          {tip === "left" && (
            <div style={{ position: "absolute", bottom: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
              background: "#1e293b", color: "#fff", padding: "3px 8px", borderRadius: 6,
              fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", pointerEvents: "none", zIndex: 20,
              boxShadow: "0 2px 8px #0003" }}>
              {bar.start.toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
            </div>
          )}
        </div>
        {/* Right drag handle */}
        <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 14, zIndex: 5, cursor: "ew-resize" }}
          onMouseEnter={() => setTip("right")}
          onMouseLeave={() => setTip(null)}
          onMouseDown={e => { setTip(null); startDrag(e, spaceId, taskId, subtaskId, "right"); }}>
          {tip === "right" && (
            <div style={{ position: "absolute", bottom: "calc(100% + 6px)", right: 0,
              background: "#1e293b", color: "#fff", padding: "3px 8px", borderRadius: 6,
              fontSize: 11, fontWeight: 700, whiteSpace: "nowrap", pointerEvents: "none", zIndex: 20,
              boxShadow: "0 2px 8px #0003" }}>
              {bar.end.toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
            </div>
          )}
        </div>
        {width > 3 && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", paddingLeft: 10, fontSize: 10, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", pointerEvents: "none" }}>
            {subtaskId
              ? (bar.owners?.slice(0,2).join(", "))
              : (bar.raci?.R ? bar.raci.R : bar.owners?.slice(0,1).join(", "))}
          </div>
        )}
        {/* Links indicator dot — floats above top-right of bar */}
        <div
          onClick={e => { e.stopPropagation(); if (!guestMode) setLinksPicker({ spaceId, taskId, subtaskId: subtaskId || null, name: taskName }); else showLoginToast(); }}
          title={hasLinks ? `${bar.links.length} link(s) de evidencia — click para ver` : "Click para agregar evidencia"}
          style={{
            position: "absolute",
            top: subtaskId ? -5 : -6,
            right: -4,
            width: subtaskId ? 7 : 9,
            height: subtaskId ? 7 : 9,
            borderRadius: "50%",
            background: hasLinks ? "#6366F1" : "#94a3b8",
            border: "1.5px solid #fff",
            cursor: "pointer",
            zIndex: 8,
            boxShadow: hasLinks ? "0 0 0 2px #6366F133" : "none",
            transition: "all 0.15s",
          }}
        />
        {/* Comment — rendered to the right of the bar */}
        <div
          onDoubleClick={e => { e.stopPropagation(); if (!guestMode) setEditingComment({ spaceId, taskId, subtaskId: subtaskId || null }); else showLoginToast(); }}
          title={hasComment ? bar.comment : (guestMode ? "" : "Doble click para agregar comentario")}
          style={{
            position: "absolute",
            left: "calc(100% + 8px)",
            top: "50%",
            transform: "translateY(-50%)",
            whiteSpace: "nowrap",
            fontSize: 10,
            color: hasComment ? "#94a3b8" : "#cbd5e1",
            pointerEvents: "auto",
            cursor: guestMode ? "default" : "text",
            maxWidth: 360,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
          {editingComment?.spaceId === spaceId && editingComment?.taskId === taskId && (editingComment?.subtaskId || null) === (subtaskId || null) ? (
            <input
              autoFocus
              maxLength={240}
              defaultValue={bar.comment || ""}
              placeholder="···"
              onBlur={e => { updateTask(spaceId, taskId, { comment: e.target.value }, subtaskId || undefined); setEditingComment(null); }}
              onKeyDown={e => { if (e.key === "Enter" || e.key === "Escape") { updateTask(spaceId, taskId, { comment: e.target.value }, subtaskId || undefined); setEditingComment(null); } }}
              onClick={e => e.stopPropagation()}
              style={{ fontSize: 10, color: "#64748b", border: "none", borderBottom: "1px solid #cbd5e1", outline: "none", background: "transparent", width: 140, padding: "0 0 1px 0" }}
            />
          ) : (
            hasComment ? bar.comment : "···"
          )}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#F8FAFC", minHeight: "100vh", color: "#1e293b", userSelect: "none" }}>

      {/* Links Modal */}
      {linksPicker && (
        <LinksModal
          links={(() => {
            const sp = projects.find(p => p.id === linksPicker.spaceId);
            const tk = sp?.tasks.find(t => t.id === linksPicker.taskId);
            const item = linksPicker.subtaskId ? tk?.subtasks?.find(s => s.id === linksPicker.subtaskId) : tk;
            return item?.links || [];
          })()}
          taskName={linksPicker.name}
          onSave={saveLinks}
          onClose={() => setLinksPicker(null)}
        />
      )}

      {/* Login toast */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
          background: "#1e293b", color: "#fff", padding: "12px 20px", borderRadius: 12,
          fontSize: 13, fontWeight: 600, zIndex: 9999,
          boxShadow: "0 8px 32px #0004",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <IcLock size={16} color="#fff" />
          Inicia sesión para editar
          <button onClick={() => { setToast(false); signIn(); }}
            style={{ marginLeft: 4, padding: "4px 12px", borderRadius: 8, border: "none",
              background: "#6366F1", color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
            Iniciar sesión
          </button>
          <span onClick={() => setToast(false)}
            style={{ marginLeft: 4, cursor: "pointer", color: "#94a3b8", fontSize: 16, lineHeight: 1 }}>✕</span>
        </div>
      )}

      {/* Modals */}
      {modal && <AddModal config={modal} onAdd={handleAdd} onClose={() => setModal(null)} ownerColors={ownerColors} />}
      {editUser && (
        <EditUserModal
          userKey={editUser.key}
          userData={editUser.data}
          ownerColors={ownerColors}
          onSave={saveEditUser}
          onClose={() => setEditUser(null)}
        />
      )}
      {addUserOpen && (
        <AddUserModal
          usedColors={new Set(Object.values(ownerColors).map(c => c.bg))}
          onAdd={(key, val) => setOwnerColors(prev => { const next = { ...prev, [key]: val }; saveOwnerColors(next); return next; })}
          onClose={() => setAddUserOpen(false)}
        />
      )}
      {confirm && <ConfirmModal message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {menu && (
        <ContextMenu
          menu={menu}
          sub={!!menu.subtaskId}
          onAddSubtask={() => { setModal({ spaceId: menu.spaceId, taskId: menu.taskId, type: "subtask", spaceColor: menu.spaceColor }); setExpandedTasks(e => ({ ...e, [menu.taskId]: true })); }}
          onDelete={() => menu.subtaskId ? deleteSubtask(menu.spaceId, menu.taskId, menu.subtaskId) : deleteTask(menu.spaceId, menu.taskId)}
          onClose={() => setMenu(null)}
        />
      )}
      {ownerPicker && (
        <OwnerPicker
          ownerColors={ownerColors}
          currentOwners={(() => {
            const sp = projects.find(p => p.id === ownerPicker.spaceId);
            const tk = sp?.tasks.find(t => t.id === ownerPicker.taskId);
            return ownerPicker.subtaskId ? (tk?.subtasks.find(s => s.id === ownerPicker.subtaskId)?.owners || []) : (tk?.owners || []);
          })()}
          onSave={saveOwners}
          onClose={() => setOwnerPicker(null)}
          onEditUser={(key, data) => { setOwnerPicker(null); setEditUser({ key, data }); }}
        />
      )}

      {raciPicker && (
        <RaciPicker
          role={raciPicker.role}
          ownerColors={ownerColors}
          current={(() => {
            const sp = projects.find(p => p.id === raciPicker.spaceId);
            const tk = sp?.tasks.find(t => t.id === raciPicker.taskId);
            return tk?.raci?.[raciPicker.role] ?? null;
          })()}
          onSave={(val) => {
            const sp = projects.find(p => p.id === raciPicker.spaceId);
            const tk = sp?.tasks.find(t => t.id === raciPicker.taskId);
            const newRaci = { R: null, A: null, C: null, I: null, ...(tk?.raci || {}), [raciPicker.role]: val };
            updateTask(raciPicker.spaceId, raciPicker.taskId, { raci: newRaci });
          }}
          onClose={() => setRaciPicker(null)}
        />
      )}

      {/* ══════════════ Aviso de modo demo — nadie confunde esto con datos reales ══════════════ */}
      {IS_DEMO && (
        <div style={{ background: "#0f172a", color: "#fff", textAlign: "center", padding: "6px 14px", fontSize: 11.5, fontWeight: 700, letterSpacing: 0.3 }}>
          🧪 Caso de demostración — todos los datos son ficticios. Los cambios no se guardan.
        </div>
      )}

      {/* ══════════════ TAB BAR — pública vs privada ══════════════ */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", padding: "0 20px", gap: 4, overflowX: "auto" }}>
        {[
          { key: "client",     label: `Pendientes Proyecto Expansión ${IS_DEMO ? "Andesa" : "Gaveti"}`, icon: IcList },
          { key: "timeline",   label: "Línea del tiempo", icon: IcFlag },
          { key: "urgentes",   label: "Urgentes", icon: IcAlert },
          { key: "precios",    label: "Análisis de precios", icon: IcChart },
          ...(IS_DEMO ? [{ key: "categoria", label: "Análisis de Categoría", icon: IcScatter }] : []),
          { key: "ventas",     label: "Ventas", icon: IcTrend },
          { key: "resultados", label: "Repositorio de documentos", icon: IcAward },
          { key: "gantt",      label: "Gantt", icon: IcGantt, lock: guestMode && !IS_DEMO },
        ].map(t => {
          const TabIcon = t.icon;
          return (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding: "12px 14px", fontSize: 13, fontWeight: 700, border: "none", background: "transparent", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
              display: "inline-flex", alignItems: "center", gap: 6,
              color: activeTab === t.key ? "#1D4ED8" : "#94a3b8",
              borderBottom: activeTab === t.key ? "3px solid #1D4ED8" : "3px solid transparent" }}>
            <TabIcon size={14} />
            {t.label}
            {t.lock && <IcLock size={12} />}
          </button>
          );
        })}
      </div>

      {/* ══════════════ TAB: Pendientes Gaveti (pública) ══════════════ */}
      {activeTab === "client" && !clientUnlocked && (
        <div style={{ maxWidth: 380, margin: "80px auto", textAlign: "center", padding: 20 }}>
          <div style={{ marginBottom: 12 }}><IcKey size={34} color="#94a3b8" /></div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>Acceso al proyecto</div>
          <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 18 }}>Ingresa el código de acceso compartido por Parte CPG.</div>
          <input autoFocus type="text" value={accessCodeInput}
            onChange={e => { setAccessCodeInput(e.target.value); setAccessCodeError(false); }}
            onKeyDown={e => e.key === "Enter" && submitAccessCode()}
            placeholder="Código de acceso"
            style={{ width: "100%", fontSize: 14, textAlign: "center", padding: "10px 14px", borderRadius: 10, border: accessCodeError ? "1.5px solid #EF4444" : "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
          {accessCodeError && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 10 }}>Código incorrecto, intenta de nuevo.</div>}
          <button onClick={submitAccessCode}
            style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            Entrar
          </button>
        </div>
      )}

      {activeTab === "client" && clientUnlocked && (() => {
        const ESTATUS_META = {
          pendiente:    { label: "Pendiente",    color: "#DC2626", bg: "#FEF2F2" },
          en_progreso:  { label: "En progreso",  color: "#F59E0B", bg: "#FFFBEB" },
          esperando:    { label: `Esperando a ${BRAND_NAME}`, color: "#7C3AED", bg: "#F5F3FF" },
          completado:   { label: "Completado",   color: "#22C55E", bg: "#F0FDF4" },
        };
        const totalPendientes = clientAreas.reduce((a, area) => a + area.rows.filter(r => r.estatus === "pendiente").length, 0);
        const totalEnProgreso = clientAreas.reduce((a, area) => a + area.rows.filter(r => r.estatus === "en_progreso").length, 0);
        const todasFilas      = clientAreas.flatMap(a => a.rows.map(r => ({ ...r, _area: a.name })));
        const completados     = todasFilas.filter(r => r.estatus === "completado");
        const esperando       = todasFilas.filter(r => r.estatus === "esperando");
        // Cerrado en los últimos 7 días — es lo que corre la junta semanal
        const hace7 = (() => { const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10); })();
        const cerradoSemana = completados
          .filter(r => r.fechaCompletado && r.fechaCompletado >= hace7)
          .sort((a, b) => (b.fechaCompletado || "").localeCompare(a.fechaCompletado || ""));
        const completadosOrden = [...completados].sort((a, b) => (b.fechaCompletado || "").localeCompare(a.fechaCompletado || ""));

        const updateRow = (areaId, rowId, patch) => {
          const next = clientAreas.map(a => a.id !== areaId ? a : { ...a, rows: a.rows.map(r => {
            if (r.id !== rowId) return r;
            const merged = { ...r, ...patch };
            // Sella la fecha al cerrar; la conserva si se reabre y se vuelve a cerrar se resella.
            if (patch.estatus === "completado" && r.estatus !== "completado") {
              merged.fechaCompletado = new Date().toISOString().slice(0, 10);
            }
            return merged;
          }) });
          setClientAreas(next); saveClientAreas(next);
        };
        const addRow = (areaId) => {
          const maxPrio = Math.max(-1, ...clientAreas.flatMap(a => a.rows.map(r => Number.isFinite(r.prio) ? r.prio : -1)));
          const newRow = { id: "cr" + Date.now(), paso: "Nuevo pendiente", responsable: "", fecha: "", estatus: "pendiente", prio: maxPrio + 1 };
          const next = clientAreas.map(a => a.id !== areaId ? a : { ...a, rows: [...a.rows, newRow] });
          setClientAreas(next); saveClientAreas(next);
          setEditingClientCell({ areaId, rowId: newRow.id, field: "paso" });
        };
        const addArea = () => {
          const newArea = { id: "ca" + Date.now(), name: "Nueva área", rows: [] };
          const next = [...clientAreas, newArea];
          setClientAreas(next); saveClientAreas(next);
          setEditingAreaName(newArea.id);
        };
        const renameArea = (areaId, name) => {
          const next = clientAreas.map(a => a.id !== areaId ? a : { ...a, name });
          setClientAreas(next); saveClientAreas(next);
        };
        // ── Lista plana ordenada por prioridad, no por área ──────────────────
        // El área deja de ser contenedor y pasa a ser atributo de la fila.
        // El orden es compartido (Firestore) porque la prioridad es una decisión
        // del equipo, no una preferencia personal de quien mira.
        const PALETA_AREA = ["#2563EB","#7C3AED","#DB2777","#DC2626","#059669","#EA580C","#0891B2","#65758B"];
        const colorArea = (areaId) => PALETA_AREA[Math.max(0, clientAreas.findIndex(a => a.id === areaId)) % PALETA_AREA.length];

        const activas = clientAreas
          .flatMap((a, ai) => a.rows.map((r, ri) => ({ ...r, _areaId: a.id, _areaName: a.name, _orig: ai * 1000 + ri })))
          .filter(r => r.estatus !== "completado")
          .sort((x, y) => {
            const px = Number.isFinite(x.prio) ? x.prio : 1e9;
            const py = Number.isFinite(y.prio) ? y.prio : 1e9;
            return px !== py ? px - py : x._orig - y._orig;
          });
        const visiblesPend = filtroArea ? activas.filter(r => r._areaId === filtroArea) : activas;

        // Reescribe prio 0..n-1 sobre la secuencia global completa.
        const guardarPrioridad = (secuenciaIds) => {
          const pos = new Map(secuenciaIds.map((id, i) => [id, i]));
          const next = clientAreas.map(a => ({ ...a, rows: a.rows.map(r => pos.has(r.id) ? { ...r, prio: pos.get(r.id) } : r) }));
          setClientAreas(next); saveClientAreas(next);
        };
        // Mover dentro de una vista filtrada no debe alterar lo que no se ve:
        // se reacomoda la subsecuencia visible dentro del orden global.
        const moverPendiente = (idArrastrado, idDestino) => {
          if (!idArrastrado || idArrastrado === idDestino) return;
          const vis = visiblesPend.map(r => r.id);
          const i = vis.indexOf(idArrastrado), j = vis.indexOf(idDestino);
          if (i === -1 || j === -1) return;
          vis.splice(j, 0, vis.splice(i, 1)[0]);
          const enVista = new Set(vis);
          let k = 0;
          guardarPrioridad(activas.map(r => enVista.has(r.id) ? vis[k++] : r.id));
        };
        const moverUnaPos = (id, delta) => {
          const vis = visiblesPend.map(r => r.id);
          const i = vis.indexOf(id), j = i + delta;
          if (i === -1 || j < 0 || j >= vis.length) return;
          moverPendiente(id, vis[j]);
        };
        // Cambiar de área mueve la fila entre contenedores pero conserva su prioridad.
        const cambiarArea = (rowId, desdeId, haciaId) => {
          if (desdeId === haciaId) return;
          const fila = clientAreas.find(a => a.id === desdeId)?.rows.find(r => r.id === rowId);
          if (!fila) return;
          const next = clientAreas.map(a => {
            if (a.id === desdeId) return { ...a, rows: a.rows.filter(r => r.id !== rowId) };
            if (a.id === haciaId) return { ...a, rows: [...a.rows, fila] };
            return a;
          });
          setClientAreas(next); saveClientAreas(next);
        };
        const deleteRow = (areaId, rowId) => {
          const next = clientAreas.map(a => a.id !== areaId ? a : { ...a, rows: a.rows.filter(r => r.id !== rowId) });
          setClientAreas(next); saveClientAreas(next);
        };
        const canDeleteClient = !guestMode && user?.email?.endsWith("@partecpg.com");
        return (
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "24px 20px 60px" }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 11, color: "#1D4ED8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 4 }}>{companyName}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>Semáforo de pendientes y próximos pasos</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>Agrega y actualiza estatus. No se puede eliminar información.</div>
              <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {totalPendientes > 0 && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "#FEF2F2", border: "1px solid #FECACA" }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#DC2626", display: "inline-block" }} />
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: "#DC2626" }}>{totalPendientes} pendiente{totalPendientes !== 1 ? "s" : ""}</span>
                  </div>
                )}
                {totalEnProgreso > 0 && (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, background: "#FFFBEB", border: "1px solid #FDE68A" }}>
                    <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#F59E0B", display: "inline-block" }} />
                    <span style={{ fontSize: 12.5, fontWeight: 700, color: "#F59E0B" }}>{totalEnProgreso} en progreso</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Cerrado esta semana — esto corre la junta del jueves ── */}
            {cerradoSemana.length > 0 && (
              <div style={{ marginBottom: 18, background: "#F0FDF4", borderRadius: 12, border: "1px solid #BBF7D0", padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
                  <IcCheckCircle size={15} color="#16A34A" />
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#15803D" }}>Cerrado esta semana</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#16A34A", background: "#DCFCE7", padding: "2px 8px", borderRadius: 20 }}>{cerradoSemana.length}</div>
                </div>
                {cerradoSemana.slice(0, 8).map(r => (
                  <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0" }}>
                    <span style={{ color: "#16A34A", fontSize: 12, fontWeight: 900, lineHeight: 1.5, flexShrink: 0 }}>✓</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: "#166534", lineHeight: 1.45 }}>{r.paso}</div>
                      <div style={{ fontSize: 10.5, color: "#4ADE80", fontWeight: 700, marginTop: 1 }}>{r._area}{r.responsable ? ` · ${r.responsable}` : ""}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Esperando a Gaveti — hace visible el lado del cliente ── */}
            {esperando.length > 0 && (
              <div style={{ marginBottom: 18, background: "#F5F3FF", borderRadius: 12, border: "1px solid #DDD6FE", padding: "14px 16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                  <IcHourglass size={15} color="#7C3AED" />
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#6D28D9" }}>Esperando a {BRAND_NAME}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#7C3AED", background: "#EDE9FE", padding: "2px 8px", borderRadius: 20 }}>{esperando.length}</div>
                </div>
                <div style={{ fontSize: 11, color: "#A78BFA", marginBottom: 8 }}>Trabajo listo de nuestro lado, detenido por una definición pendiente.</div>
                {esperando.map(r => (
                  <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0" }}>
                    <span style={{ color: "#A78BFA", fontSize: 12, lineHeight: 1.5, flexShrink: 0 }}>◷</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, color: "#5B21B6", lineHeight: 1.45 }}>{r.paso}</div>
                      <div style={{ fontSize: 10.5, color: "#A78BFA", fontWeight: 700, marginTop: 1 }}>{r._area}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Filtro por área — el área ya no agrupa, sólo etiqueta ── */}
            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 14, alignItems: "center" }}>
              <button onClick={() => setFiltroArea(null)}
                style={{ fontSize: 11.5, fontWeight: 800, padding: "5px 12px", borderRadius: 20, cursor: "pointer",
                  border: filtroArea === null ? "1.5px solid #0f172a" : "1px solid #e2e8f0",
                  background: filtroArea === null ? "#0f172a" : "#fff", color: filtroArea === null ? "#fff" : "#64748b" }}>
                Todas ({activas.length})
              </button>
              {clientAreas.map(a => {
                const n = activas.filter(r => r._areaId === a.id).length;
                const on = filtroArea === a.id;
                const col = colorArea(a.id);
                if (editingAreaName === a.id) return (
                  <input key={a.id} autoFocus defaultValue={a.name}
                    onBlur={e => { renameArea(a.id, e.target.value.trim() || a.name); setEditingAreaName(null); }}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === "Escape") e.target.blur(); }}
                    style={{ fontSize: 11.5, fontWeight: 800, padding: "5px 10px", borderRadius: 20, border: `1.5px solid ${col}`, outline: "none", width: 150 }} />
                );
                return (
                  <button key={a.id} onClick={() => setFiltroArea(on ? null : a.id)}
                    onDoubleClick={() => canDeleteClient && setEditingAreaName(a.id)}
                    title={canDeleteClient ? "Doble click para renombrar" : ""}
                    style={{ fontSize: 11.5, fontWeight: 800, padding: "5px 12px", borderRadius: 20, cursor: "pointer",
                      border: on ? `1.5px solid ${col}` : "1px solid #e2e8f0",
                      background: on ? col : "#fff", color: on ? "#fff" : (n ? "#64748b" : "#cbd5e1") }}>
                    {a.name}{n > 0 ? ` (${n})` : ""}
                  </button>
                );
              })}
              {canDeleteClient && (
                <button onClick={addArea}
                  style={{ fontSize: 11.5, fontWeight: 800, padding: "5px 11px", borderRadius: 20, cursor: "pointer",
                    border: "1px dashed #cbd5e1", background: "#fff", color: "#94a3b8" }}>+ Área</button>
              )}
            </div>

            {/* ── Lista única, del 1 al N, ordenada por prioridad ── */}
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden", marginBottom: 14 }}>
              <div style={{ overflowX: "auto" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", borderBottom: "1px solid #f1f5f9", minWidth: 760, background: "#fcfdfe" }}>
                  <div style={{ width: 13, flexShrink: 0 }} />
                  <div style={{ width: 24, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textAlign: "center" }}>#</div>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>Pendiente</div>
                  <div style={{ width: 124, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>Área</div>
                  <div style={{ width: 88, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>Responsable</div>
                  <div style={{ width: 116, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>Fecha</div>
                  <div style={{ width: 112, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>Estatus</div>
                  <div style={{ width: 52, flexShrink: 0, fontSize: 9.5, fontWeight: 800, color: "#EF4444", textTransform: "uppercase", letterSpacing: 0.8, textAlign: "center" }}>Urgente</div>
                  {canDeleteClient && <div style={{ width: 20, flexShrink: 0 }} />}
                </div>

                {visiblesPend.length === 0 && (
                  <div style={{ padding: "34px 16px", textAlign: "center", fontSize: 12.5, color: "#cbd5e1" }}>
                    No hay pendientes abiertos{filtroArea ? " en esta área" : ""}.
                  </div>
                )}

                {visiblesPend.map((row, idx) => {
                  const meta = ESTATUS_META[row.estatus] || ESTATUS_META.pendiente;
                  const col = colorArea(row._areaId);
                  const esDestino = dragRowState?.sobre === row.id && dragRowState?.id !== row.id;
                  const seArrastra = dragRowState?.id === row.id;
                  const edit = (f) => editingClientCell?.rowId === row.id && editingClientCell?.field === f;
                  return (
                    <div key={row.id}
                      draggable
                      onDragStart={() => setDragRowState({ id: row.id, sobre: row.id })}
                      onDragOver={e => { e.preventDefault(); setDragRowState(p => p ? { ...p, sobre: row.id } : p); }}
                      onDrop={e => { e.preventDefault(); moverPendiente(dragRowState?.id, row.id); setDragRowState(null); }}
                      onDragEnd={() => setDragRowState(null)}
                      style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 12px",
                        borderBottom: idx < visiblesPend.length - 1 ? "1px solid #f1f5f9" : "none",
                        borderLeft: `3px solid ${col}`,
                        background: esDestino ? "#EFF6FF" : "transparent", opacity: seArrastra ? 0.4 : 1, minWidth: 760 }}>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: 1, gap: 1 }}>
                        <button onClick={() => moverUnaPos(row.id, -1)} title="Subir"
                          style={{ border: "none", background: "transparent", color: "#cbd5e1", fontSize: 9, lineHeight: 1, cursor: "pointer", padding: 0, fontWeight: 900 }}>▲</button>
                        <span style={{ cursor: "grab", color: "#cbd5e1", fontSize: 11, lineHeight: 1, userSelect: "none", letterSpacing: "-1px" }} title="Arrastra para repriorizar">⠿</span>
                        <button onClick={() => moverUnaPos(row.id, 1)} title="Bajar"
                          style={{ border: "none", background: "transparent", color: "#cbd5e1", fontSize: 9, lineHeight: 1, cursor: "pointer", padding: 0, fontWeight: 900 }}>▼</button>
                      </div>

                      <div style={{ width: 24, flexShrink: 0, textAlign: "center", fontSize: 12.5, fontWeight: 900, color: "#94a3b8", paddingTop: 1 }}>
                        {idx + 1}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        {edit("paso") ? (
                          <textarea autoFocus defaultValue={row.paso} rows={2}
                            onBlur={e => { updateRow(row._areaId, row.id, { paso: e.target.value }); setEditingClientCell(null); }}
                            onKeyDown={e => { if (e.key === "Escape") e.target.blur(); }}
                            style={{ width: "100%", fontSize: 12.5, color: "#334155", border: "1.5px solid #1D4ED8", borderRadius: 6, padding: "5px 8px", outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                        ) : (
                          <div onClick={() => setEditingClientCell({ areaId: row._areaId, rowId: row.id, field: "paso" })}
                            style={{ fontSize: 12.5, color: "#334155", lineHeight: 1.5, cursor: "text" }}>{row.paso}</div>
                        )}
                      </div>

                      {/* Área como atributo editable, no como contenedor */}
                      <div style={{ width: 124, flexShrink: 0 }}>
                        {canDeleteClient ? (
                          <select value={row._areaId} onChange={e => cambiarArea(row.id, row._areaId, e.target.value)}
                            style={{ width: "100%", fontSize: 10.5, fontWeight: 800, border: `1px solid ${col}44`, borderRadius: 20,
                              padding: "3px 5px", outline: "none", color: col, background: col + "10", cursor: "pointer" }}>
                            {clientAreas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                          </select>
                        ) : (
                          <span style={{ display: "inline-block", fontSize: 10.5, fontWeight: 800, color: col, background: col + "12",
                            border: `1px solid ${col}33`, padding: "2px 9px", borderRadius: 20 }}>{row._areaName}</span>
                        )}
                      </div>

                      <div style={{ width: 88, flexShrink: 0 }}>
                        {edit("responsable") ? (
                          <input autoFocus defaultValue={row.responsable}
                            onBlur={e => { updateRow(row._areaId, row.id, { responsable: e.target.value }); setEditingClientCell(null); }}
                            onKeyDown={e => { if (e.key === "Enter" || e.key === "Escape") e.target.blur(); }}
                            style={{ width: "100%", fontSize: 11.5, border: "1.5px solid #1D4ED8", borderRadius: 5, padding: "3px 6px", outline: "none", boxSizing: "border-box" }} />
                        ) : (
                          <div onClick={() => setEditingClientCell({ areaId: row._areaId, rowId: row.id, field: "responsable" })}
                            style={{ fontSize: 11.5, color: row.responsable ? "#1e293b" : "#cbd5e1", fontWeight: 600, cursor: "text", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.responsable || "— asignar"}</div>
                        )}
                      </div>

                      <div style={{ width: 116, flexShrink: 0 }}>
                        <DateField value={row.fecha || ""} onChange={v => updateRow(row._areaId, row.id, { fecha: v })}
                          inputStyle={{ fontSize: 11, borderRadius: 5, padding: "3px 26px 3px 5px" }} />
                      </div>

                      <div style={{ width: 112, flexShrink: 0 }}>
                        <select value={row.estatus} onChange={e => updateRow(row._areaId, row.id, { estatus: e.target.value })}
                          style={{ fontSize: 11, fontWeight: 700, border: `1px solid ${meta.color}55`, borderRadius: 6, padding: "4px 4px", outline: "none", color: meta.color, background: meta.bg, width: "100%", cursor: "pointer" }}>
                          <option value="pendiente">Pendiente</option>
                          <option value="en_progreso">En progreso</option>
                          <option value="esperando">{`Esperando a ${BRAND_NAME}`}</option>
                          <option value="completado">Completado</option>
                        </select>
                      </div>

                      <div style={{ width: 52, flexShrink: 0, display: "flex", justifyContent: "center", paddingTop: 3 }}>
                        <input type="checkbox" checked={!!row.urgente}
                          title={row.urgente ? "Quitar de urgentes" : "Marcar como urgente"}
                          onChange={() => {
                            const maxPos = Math.max(0, ...clientAreas.flatMap(a => a.rows.filter(r => r.urgente).map(r => r.urgentePos || 0)));
                            const next = clientAreas.map(a => a.id !== row._areaId ? a : { ...a, rows: a.rows.map(r => r.id !== row.id ? r : { ...r, urgente: !r.urgente, urgentePos: !r.urgente ? maxPos + 1 : null }) });
                            setClientAreas(next); saveClientAreas(next);
                          }}
                          style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#EF4444" }} />
                      </div>

                      {canDeleteClient && (
                        <button onClick={() => deleteRow(row._areaId, row.id)} title="Eliminar pendiente"
                          style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 11, fontWeight: 900, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>✕</button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div style={{ padding: "9px 16px", borderTop: "1px solid #f1f5f9" }}>
                <button onClick={() => addRow(filtroArea || clientAreas[0]?.id)}
                  disabled={!clientAreas.length}
                  style={{ fontSize: 11.5, fontWeight: 700, color: clientAreas.length ? "#1D4ED8" : "#cbd5e1", background: "none", border: "none", cursor: clientAreas.length ? "pointer" : "default", padding: 0 }}>
                  + Agregar pendiente{filtroArea ? ` en ${clientAreas.find(a => a.id === filtroArea)?.name}` : ""}
                </button>
              </div>
            </div>

            {/* ── Acervo de completados — ya no se borra nada ── */}
            {completadosOrden.length > 0 && (
              <div style={{ marginTop: 24, background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", overflow: "hidden" }}>
                <button onClick={() => setShowCompletados(v => !v)}
                  style={{ width: "100%", padding: "13px 16px", background: "#F8FAFC", border: "none", borderBottom: showCompletados ? "1px solid #e2e8f0" : "none",
                    display: "flex", alignItems: "center", gap: 9, cursor: "pointer", textAlign: "left" }}>
                  <IcArchive size={15} color="#64748b" />
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#334155", flex: 1 }}>Completados</div>
                  <div style={{ fontSize: 11.5, fontWeight: 800, color: "#16A34A", background: "#F0FDF4", border: "1px solid #BBF7D0", padding: "2px 9px", borderRadius: 20 }}>{completadosOrden.length}</div>
                  {showCompletados ? <IcChevronUp size={14} color="#94a3b8" /> : <IcChevronDown size={14} color="#94a3b8" />}
                </button>
                {showCompletados && (
                  <div>
                    <div style={{ padding: "9px 16px", fontSize: 11, color: "#94a3b8", borderBottom: "1px solid #f1f5f9" }}>
                      Todo lo que ya se cerró en el proyecto. Se conserva para consulta — nada se elimina.
                    </div>
                    {completadosOrden.map((r, i) => (
                      <div key={r.id} style={{ display: "flex", alignItems: "flex-start", gap: 9, padding: "9px 16px", borderBottom: i < completadosOrden.length - 1 ? "1px solid #f8fafc" : "none" }}>
                        <span style={{ color: "#22C55E", fontSize: 12, fontWeight: 900, lineHeight: 1.5, flexShrink: 0 }}>✓</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, color: "#475569", lineHeight: 1.45 }}>{r.paso}</div>
                          <div style={{ fontSize: 10.5, color: "#cbd5e1", fontWeight: 700, marginTop: 2 }}>
                            {r._area}{r.responsable ? ` · ${r.responsable}` : ""}{r.fechaCompletado ? ` · cerrado ${r.fechaCompletado}` : ""}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════════════ TAB: Urgentes ══════════════ */}
      {activeTab === "urgentes" && !clientUnlocked && (
        <div style={{ maxWidth: 380, margin: "80px auto", textAlign: "center", padding: 20 }}>
          <div style={{ marginBottom: 12 }}><IcKey size={34} color="#94a3b8" /></div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>Acceso al proyecto</div>
          <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 18 }}>Ingresa el código de acceso compartido por Parte CPG.</div>
          <input autoFocus type="text" value={accessCodeInput}
            onChange={e => { setAccessCodeInput(e.target.value); setAccessCodeError(false); }}
            onKeyDown={e => e.key === "Enter" && submitAccessCode()}
            placeholder="Código de acceso"
            style={{ width: "100%", fontSize: 14, textAlign: "center", padding: "10px 14px", borderRadius: 10, border: accessCodeError ? "1.5px solid #EF4444" : "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
          {accessCodeError && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 10 }}>Código incorrecto, intenta de nuevo.</div>}
          <button onClick={submitAccessCode} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#EF4444", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Entrar</button>
        </div>
      )}
      {activeTab === "urgentes" && clientUnlocked && (() => {
        const urgentRows = clientAreas
          .flatMap(a => a.rows.filter(r => r.urgente).map(r => ({ ...r, areaName: a.name, areaId: a.id })))
          .sort((x, y) => (x.urgentePos ?? 9999) - (y.urgentePos ?? 9999));
        const ESTATUS_META = { pendiente: { label:"Pendiente", color:"#DC2626", bg:"#FEF2F2" }, en_progreso: { label:"En progreso", color:"#F59E0B", bg:"#FFFBEB" }, completado: { label:"Completado", color:"#22C55E", bg:"#F0FDF4" } };
        // Reordena las urgentes: recibe la lista de ids en el nuevo orden y persiste urgentePos = 1..n
        const applyUrgentOrder = (orderedIds) => {
          const posById = Object.fromEntries(orderedIds.map((id, i) => [id, i + 1]));
          const next = clientAreas.map(a => ({ ...a, rows: a.rows.map(r => r.urgente && posById[r.id] ? { ...r, urgentePos: posById[r.id] } : r) }));
          setClientAreas(next); saveClientAreas(next);
        };
        const moveUrgent = (id, dir) => {
          const ids = urgentRows.map(r => r.id);
          const i = ids.indexOf(id);
          const j = i + dir;
          if (i < 0 || j < 0 || j >= ids.length) return;
          [ids[i], ids[j]] = [ids[j], ids[i]];
          applyUrgentOrder(ids);
        };
        const dropUrgent = (targetId) => {
          if (!urgentDragId || urgentDragId === targetId) { setUrgentDragId(null); return; }
          const ids = urgentRows.map(r => r.id);
          const from = ids.indexOf(urgentDragId);
          const to = ids.indexOf(targetId);
          if (from < 0 || to < 0) { setUrgentDragId(null); return; }
          ids.splice(from, 1);
          ids.splice(to, 0, urgentDragId);
          applyUrgentOrder(ids);
          setUrgentDragId(null);
        };
        const shareWhatsApp = () => {
          const lines = urgentRows.map((r, i) => `#${i + 1} ${r.paso}${r.responsable ? " — " + r.responsable : ""}${r.fecha ? " (" + new Date(r.fecha + "T00:00:00").toLocaleDateString("es-MX", { day: "numeric", month: "short" }) + ")" : ""}`);
          const text = `*Pendientes Urgentes — ${companyName}*\n\n${lines.join("\n")}\n\nVer detalle: ${window.location.origin}/#urgentes`;
          window.open("https://wa.me/?text=" + encodeURIComponent(text), "_blank");
        };
        return (
          <div style={{ maxWidth: 680, margin: "0 auto", padding: "28px 20px 60px" }}>
            {/* Header — optimized for screenshot share */}
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 11, color: "#EF4444", fontWeight: 800, textTransform: "uppercase", letterSpacing: 2, marginBottom: 6 }}>{companyName}</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#0f172a", marginBottom: 4 }}>Pendientes Urgentes</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>Actualizado {new Date().toLocaleDateString("es-MX", { day:"numeric", month:"long", year:"numeric" })}</div>
              {urgentRows.length > 0 && (
                <button onClick={shareWhatsApp}
                  style={{ marginTop: 12, display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 20, border: "none", background: "#25D366", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer" }}>
                  <IcWhatsApp size={14} color="#fff" />
                  Compartir por WhatsApp
                </button>
              )}
            </div>

            {urgentRows.length === 0 ? (
              <div style={{ textAlign: "center", padding: "60px 20px", color: "#94a3b8" }}>
                <div style={{ marginBottom: 10 }}><IcCheckCircle size={32} color="#22C55E" /></div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Sin urgentes por ahora</div>
                <div style={{ fontSize: 12, marginTop: 6 }}>Marca la casilla <b>Urgente</b> en la pestaña Pendientes para destacar algo aquí.</div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {urgentRows.map((row, i) => {
                  const meta = ESTATUS_META[row.estatus] || ESTATUS_META.pendiente;
                  return (
                    <div key={row.id}
                      draggable
                      onDragStart={() => setUrgentDragId(row.id)}
                      onDragOver={e => e.preventDefault()}
                      onDrop={e => { e.preventDefault(); dropUrgent(row.id); }}
                      onDragEnd={() => setUrgentDragId(null)}
                      style={{ background: "#fff", borderRadius: 12, border: urgentDragId === row.id ? "1.5px dashed #EF4444" : "1.5px solid #FECACA", overflow: "hidden", boxShadow: "0 2px 8px #EF444408", opacity: urgentDragId === row.id ? 0.5 : 1, cursor: "grab" }}>
                      <div style={{ background: "#FEF2F2", padding: "6px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "#EF4444" }}>#{i+1}</span>
                        <span style={{ fontSize: 10, color: "#EF4444", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, flex: 1 }}>{row.areaName}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: meta.color, background: meta.bg, padding: "2px 8px", borderRadius: 20, border: `1px solid ${meta.color}44` }}>{meta.label}</span>
                        {/* Flechas para reordenar (útil en móvil, donde no hay drag & drop) */}
                        <div style={{ display: "flex", gap: 2 }}>
                          <button onClick={() => moveUrgent(row.id, -1)} disabled={i === 0} title="Subir"
                            style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid #FECACA", background: "#fff", cursor: i === 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: i === 0 ? 0.3 : 1, padding: 0 }}>
                            <IcChevronUp size={13} color="#EF4444" />
                          </button>
                          <button onClick={() => moveUrgent(row.id, 1)} disabled={i === urgentRows.length - 1} title="Bajar"
                            style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid #FECACA", background: "#fff", cursor: i === urgentRows.length - 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", opacity: i === urgentRows.length - 1 ? 0.3 : 1, padding: 0 }}>
                            <IcChevronDown size={13} color="#EF4444" />
                          </button>
                        </div>
                      </div>
                      <div style={{ padding: "12px 14px" }}>
                        <div style={{ fontSize: 13.5, color: "#1e293b", lineHeight: 1.6, fontWeight: 500, marginBottom: 10 }}>{row.paso}</div>
                        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                          {row.responsable && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <IcUser size={12} color="#94a3b8" />
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{row.responsable}</span>
                            </div>
                          )}
                          {row.fecha && (
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <IcCal size={12} color="#94a3b8" />
                              <span style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>{new Date(row.fecha+"T00:00:00").toLocaleDateString("es-MX", { day:"numeric", month:"short", year:"numeric" })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ marginTop: 24, textAlign: "center", fontSize: 11, color: "#cbd5e1" }}>
              {IS_DEMO ? "demo.partecpg.com" : "gaveti.partecpg.com"} · Parte CPG
            </div>
          </div>
        );
      })()}

      {/* ══════════════ TAB: Ventas CRM ══════════════ */}
      {activeTab === "ventas" && !clientUnlocked && (
        <div style={{ maxWidth: 380, margin: "80px auto", textAlign: "center", padding: 20 }}>
          <div style={{ marginBottom: 12 }}><IcKey size={34} color="#94a3b8" /></div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>Acceso al proyecto</div>
          <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 18 }}>Ingresa el código de acceso compartido por Parte CPG.</div>
          <input autoFocus type="text" value={accessCodeInput}
            onChange={e => { setAccessCodeInput(e.target.value); setAccessCodeError(false); }}
            onKeyDown={e => e.key === "Enter" && submitAccessCode()}
            placeholder="Código de acceso"
            style={{ width: "100%", fontSize: 14, textAlign: "center", padding: "10px 14px", borderRadius: 10, border: accessCodeError ? "1.5px solid #EF4444" : "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
          {accessCodeError && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 10 }}>Código incorrecto.</div>}
          <button onClick={submitAccessCode} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Entrar</button>
        </div>
      )}
      {activeTab === "ventas" && clientUnlocked && (() => {
        const canDeleteCrm = !guestMode && user?.email?.endsWith("@partecpg.com");
        const STAGE_COLORS = { "Prospecto":"#94A3B8","Submission":"#84CC16","Contactado":"#3B82F6","Calificado":"#06B6D4","Cita agendada":"#8B5CF6","Muestra enviada":"#F97316","Propuesta / Cotización":"#F59E0B","Revisión interna":"#EC4899","Negociación":"#EF4444","Cerrado Ganado":"#22C55E","Cerrado / Retomar":"#64748B" };
        const fmtUSD = (n) => {
          if (!n || isNaN(n)) return null;
          const v = Number(n);
          if (v >= 1000000) return "$" + (v / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
          if (v >= 1000) return "$" + (v / 1000).toFixed(0) + "K";
          return "$" + v.toLocaleString("en-US");
        };
        const isOverdue = (deal) => deal.fecha && !deal.etapa.startsWith("Cerrado") && new Date(deal.fecha + "T23:59:59") < new Date();
        const isDueSoon = (deal) => {
          if (!deal.fecha || deal.etapa.startsWith("Cerrado") || isOverdue(deal)) return false;
          return new Date(deal.fecha + "T00:00:00") <= new Date(Date.now() + 7 * 86400000);
        };
        const openDeal = (deal) => { setCrmDraft({ ...deal }); setCrmModal({ deal }); };
        const openNew  = () => { setCrmDraft({ id: "crm"+Date.now(), nombre:"", responsable:"", contacto:"", email:"", tipo:"", estado:"", producto:"", ultimoContacto:"", fecha:"", accion:"", valor:"", prioridad:"", etapa:"Prospecto", notas:"" }); setCrmModal({ new: true }); };
        const saveDraft = () => {
          if (!crmDraft?.nombre?.trim()) return;
          const next = crmModal.new ? [...crmDeals, crmDraft] : crmDeals.map(d => d.id === crmDraft.id ? crmDraft : d);
          setCrmDeals(next); saveCrmDeals(next); setCrmModal(null); setCrmDraft(null);
        };
        const deleteDeal = (id) => { const next = crmDeals.filter(d => d.id !== id); setCrmDeals(next); saveCrmDeals(next); };
        const moveDealToStage = (dealId, stage) => {
          const deal = crmDeals.find(d => d.id === dealId);
          if (!deal || deal.etapa === stage) return;
          const next = crmDeals.map(d => d.id === dealId ? { ...d, etapa: stage } : d);
          setCrmDeals(next); saveCrmDeals(next);
        };
        return (
          <div style={{ padding: "20px 16px 60px", overflowX: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: "#1D4ED8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 2 }}>{companyName}</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Pipeline de Ventas</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <select value={crmFilterPrioridad} onChange={e => setCrmFilterPrioridad(e.target.value)}
                  style={{ fontSize: 11.5, fontWeight: 600, border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", outline: "none", color: "#475569", background: "#fff", cursor: "pointer" }}>
                  <option value="Todas">Prioridad: Todas</option>
                  {Object.keys(CRM_PRIORIDADES).map(p => <option key={p} value={p}>Prioridad: {p}</option>)}
                </select>
                <select value={crmFilterTipo} onChange={e => setCrmFilterTipo(e.target.value)}
                  style={{ fontSize: 11.5, fontWeight: 600, border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", outline: "none", color: "#475569", background: "#fff", cursor: "pointer" }}>
                  <option value="Todos">Tipo: Todos</option>
                  {CRM_TIPOS.map(t => <option key={t} value={t}>Tipo: {t}</option>)}
                </select>
                <select value={crmFilterResp} onChange={e => setCrmFilterResp(e.target.value)}
                  style={{ fontSize: 11.5, fontWeight: 600, border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 8px", outline: "none", color: "#475569", background: "#fff", cursor: "pointer" }}>
                  <option value="Todos">Responsable: Todos</option>
                  {[...new Set(crmDeals.map(d => (d.responsable || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)).map(r => <option key={r} value={r}>Responsable: {r}</option>)}
                </select>
                <button onClick={openNew} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Agregar cuenta</button>
              </div>
            </div>

            {/* Kanban */}
            <div style={{ display: "flex", gap: 10, minWidth: CRM_STAGES.length * 220 }}>
              {CRM_STAGES.map(stage => {
                const stageDeals = crmDeals.filter(d => d.etapa === stage
                  && (crmFilterPrioridad === "Todas" || d.prioridad === crmFilterPrioridad)
                  && (crmFilterTipo === "Todos" || d.tipo === crmFilterTipo)
                  && (crmFilterResp === "Todos" || (d.responsable || "").trim() === crmFilterResp));
                const color = STAGE_COLORS[stage] || "#94A3B8";
                const stageValue = stageDeals.reduce((a, d) => a + (Number(d.valor) || 0), 0);
                return (
                  <div key={stage}
                    onDragOver={e => { e.preventDefault(); if (crmDragId) setCrmDragOverStage(stage); }}
                    onDragLeave={() => { if (crmDragOverStage === stage) setCrmDragOverStage(null); }}
                    onDrop={e => { e.preventDefault(); if (crmDragId) moveDealToStage(crmDragId, stage); setCrmDragId(null); setCrmDragOverStage(null); }}
                    style={{ flex: 1, minWidth: 200, background: crmDragOverStage === stage && crmDragId ? color + "10" : "#F8FAFC", borderRadius: 10, border: crmDragOverStage === stage && crmDragId ? `1.5px dashed ${color}` : "1px solid #e2e8f0", overflow: "hidden", transition: "background 0.15s, border 0.15s" }}>
                    {/* Column header */}
                    <div style={{ padding: "10px 12px", borderBottom: "2px solid "+color, display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{stage}</div>
                        {stageValue > 0 && <div style={{ fontSize: 10, fontWeight: 700, color: color }}>{fmtUSD(stageValue)}</div>}
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: color, background: color+"18", padding: "1px 7px", borderRadius: 20 }}>{stageDeals.length}</div>
                    </div>
                    {/* Cards */}
                    <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 6, minHeight: 80 }}>
                      {stageDeals.map(deal => (
                        <div key={deal.id} onClick={() => openDeal(deal)}
                          draggable
                          onDragStart={e => { e.stopPropagation(); setCrmDragId(deal.id); }}
                          onDragEnd={() => { setCrmDragId(null); setCrmDragOverStage(null); }}
                          style={{ background: "#fff", borderRadius: 8, border: isDueSoon(deal) ? "3px solid #DC2626" : "1px solid #e2e8f0", padding: isDueSoon(deal) ? "8px 8px 6px" : "10px 10px 8px", cursor: "grab", boxShadow: "0 1px 3px #0001", transition: "box-shadow 0.15s", opacity: crmDragId === deal.id ? 0.4 : 1 }}
                          onMouseEnter={e => e.currentTarget.style.boxShadow="0 4px 12px #0002"}
                          onMouseLeave={e => e.currentTarget.style.boxShadow="0 1px 3px #0001"}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 4 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1e293b", flex: 1, minWidth: 0 }}>{deal.nombre}</div>
                            {deal.prioridad && CRM_PRIORIDADES[deal.prioridad] && (
                              <span style={{ fontSize: 8.5, fontWeight: 800, color: CRM_PRIORIDADES[deal.prioridad], background: CRM_PRIORIDADES[deal.prioridad] + "18", padding: "2px 6px", borderRadius: 10, textTransform: "uppercase", letterSpacing: 0.5, flexShrink: 0 }}>{deal.prioridad}</span>
                            )}
                          </div>
                          {(fmtUSD(deal.valor) || deal.tipo) && (
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, flexWrap: "wrap" }}>
                              {fmtUSD(deal.valor) && <span style={{ fontSize: 11, fontWeight: 800, color: "#16A34A" }}>{fmtUSD(deal.valor)}<span style={{ fontSize: 8.5, fontWeight: 600, color: "#94a3b8" }}> /año</span></span>}
                              {deal.tipo && <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", background: "#f1f5f9", padding: "1px 6px", borderRadius: 8 }}>{deal.tipo}</span>}
                            </div>
                          )}
                          {deal.responsable && <div style={{ fontSize: 10.5, color: "#475569", fontWeight: 600, marginBottom: 3 }}><span style={{ fontSize: 8.5, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>Resp.</span> {deal.responsable}</div>}
                          {deal.contacto && <div style={{ fontSize: 10.5, color: "#94a3b8", display: "flex", alignItems: "center", gap: 4 }}><IcUser size={11} color="#94a3b8" /> {deal.contacto}</div>}
                          {deal.fecha && (
                            <div style={{ fontSize: 10.5, color: isOverdue(deal) ? "#DC2626" : "#94a3b8", fontWeight: isOverdue(deal) ? 800 : 400, display: "flex", alignItems: "center", gap: 4 }}>
                              <IcCal size={11} color={isOverdue(deal) ? "#DC2626" : "#94a3b8"} />
                              {new Date(deal.fecha+"T00:00:00").toLocaleDateString("es-MX",{day:"numeric",month:"short"})}
                              {isOverdue(deal) && <span style={{ fontSize: 8.5, fontWeight: 800, background: "#FEE2E2", padding: "1px 5px", borderRadius: 8, textTransform: "uppercase" }}>Vencido</span>}
                            </div>
                          )}
                          {deal.accion && <div style={{ fontSize: 10.5, color: "#334155", fontWeight: 600, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>→ {deal.accion}</div>}
                          {deal.notas && <div style={{ fontSize: 10.5, color: "#64748b", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{deal.notas}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Deal modal */}
            {crmModal && crmDraft && (
              <div style={{ position: "fixed", inset: 0, background: "#00000055", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                onClick={e => { if (e.target === e.currentTarget) { setCrmModal(null); setCrmDraft(null); } }}>
                <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px #0003", maxHeight: "90vh", overflowY: "auto" }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 16 }}>
                    {crmModal.new ? "Nueva cuenta" : "Editar cuenta"}
                  </div>
                  <datalist id="crm-responsables">
                    {[...new Set(crmDeals.map(d => (d.responsable || "").trim()).filter(Boolean))].map(r => <option key={r} value={r} />)}
                  </datalist>
                  {[
                    [{ label: "Empresa / Retailer", field: "nombre", type: "text" }],
                    [{ label: "Responsable", field: "responsable", type: "text" }],
                    [{ label: "Contacto", field: "contacto", type: "text" }, { label: "Email", field: "email", type: "email" }],
                    [{ label: "Estado (EEUU)", field: "estado", type: "text" }, { label: "Producto de interés", field: "producto", type: "text" }],
                    [{ label: "Último contacto", field: "ultimoContacto", type: "date" }, { label: "Próximo seguimiento", field: "fecha", type: "date" }],
                    [{ label: "Próxima acción", field: "accion", type: "text" }],
                    [{ label: "Valor anual estimado (USD)", field: "valor", type: "number" }],
                  ].map((rowFields, ri) => (
                    <div key={ri} style={{ display: "flex", gap: 10, marginBottom: 12 }}>
                      {rowFields.map(({ label, field, type }) => (
                        <div key={field} style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                          {type === "date" ? (
                            <DateField value={crmDraft[field] || ""} onChange={v => setCrmDraft(p => ({ ...p, [field]: v }))} />
                          ) : (
                            <input type={type} value={crmDraft[field] || ""} onChange={e => setCrmDraft(p => ({ ...p, [field]: e.target.value }))}
                              placeholder={field === "valor" ? "ej. 50000" : ""}
                              list={field === "responsable" ? "crm-responsables" : undefined}
                              style={{ width: "100%", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", boxSizing: "border-box" }} />
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                  {/* Tipo de cliente */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Tipo de cliente</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {CRM_TIPOS.map(t => {
                        const active = crmDraft.tipo === t;
                        return <div key={t} onClick={() => setCrmDraft(p => ({ ...p, tipo: active ? "" : t }))}
                          style={{ padding: "5px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? "#1D4ED8" : "#e2e8f0"}`, background: active ? "#1D4ED818" : "#f8fafc", color: active ? "#1D4ED8" : "#94a3b8" }}>{t}</div>;
                      })}
                    </div>
                  </div>
                  {/* Prioridad */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Prioridad</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {Object.entries(CRM_PRIORIDADES).map(([p, c]) => {
                        const active = crmDraft.prioridad === p;
                        return <div key={p} onClick={() => setCrmDraft(prev => ({ ...prev, prioridad: active ? "" : p }))}
                          style={{ padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? c : "#e2e8f0"}`, background: active ? c + "18" : "#f8fafc", color: active ? c : "#94a3b8" }}>{p}</div>;
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Etapa</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {CRM_STAGES.map(s => {
                        const c = STAGE_COLORS[s];
                        const active = crmDraft.etapa === s;
                        return <div key={s} onClick={() => setCrmDraft(p => ({ ...p, etapa: s }))}
                          style={{ padding: "5px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? c : "#e2e8f0"}`, background: active ? c+"18" : "#f8fafc", color: active ? c : "#94a3b8" }}>{s}</div>;
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Notas</div>
                    <textarea value={crmDraft.notas || ""} rows={3} onChange={e => setCrmDraft(p => ({ ...p, notas: e.target.value }))}
                      style={{ width: "100%", fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "space-between", flexWrap: "wrap" }}>
                    <div>
                      {canDeleteCrm && !crmModal.new && (crmModal.confirmDelete ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#991B1B" }}>¿Eliminar esta cuenta?</span>
                          <button onClick={() => { deleteDeal(crmDraft.id); setCrmModal(null); setCrmDraft(null); }}
                            style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#DC2626", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Sí, eliminar</button>
                          <button onClick={() => setCrmModal(m => ({ ...m, confirmDelete: false }))}
                            style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer" }}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => setCrmModal(m => ({ ...m, confirmDelete: true }))}
                          style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Eliminar</button>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { setCrmModal(null); setCrmDraft(null); }}
                        style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                      <button onClick={saveDraft}
                        style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Guardar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════════════ TAB: Línea del tiempo (Hitos) ══════════════ */}
      {activeTab === "timeline" && !clientUnlocked && (
        <div style={{ maxWidth: 380, margin: "80px auto", textAlign: "center", padding: 20 }}>
          <div style={{ marginBottom: 12 }}><IcKey size={34} color="#94a3b8" /></div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>Acceso al proyecto</div>
          <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 18 }}>Ingresa el código de acceso compartido por Parte CPG.</div>
          <input autoFocus type="text" value={accessCodeInput}
            onChange={e => { setAccessCodeInput(e.target.value); setAccessCodeError(false); }}
            onKeyDown={e => e.key === "Enter" && submitAccessCode()}
            placeholder="Código de acceso"
            style={{ width: "100%", fontSize: 14, textAlign: "center", padding: "10px 14px", borderRadius: 10, border: accessCodeError ? "1.5px solid #EF4444" : "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
          {accessCodeError && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 10 }}>Código incorrecto.</div>}
          <button onClick={submitAccessCode} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Entrar</button>
        </div>
      )}
      {activeTab === "timeline" && clientUnlocked && (() => {
        const canEditTl = !guestMode && user?.email?.endsWith("@partecpg.com");
        const counts = Object.keys(TL_ESTADOS).reduce((a, k) => { a[k] = milestones.filter(m => m.estado === k).length; return a; }, {});
        const visible = milestones
          .filter(m => tlFilterEstado === "Todos" || m.estado === tlFilterEstado)
          .filter(m => !tlFilterFase || m.fase === tlFilterFase);
        const monthKeys = TL_MONTHS.map(mo => mo.key);
        const openNewTl = () => { setTlDraft({ id: "tl" + Date.now(), area: TL_AREAS[0], label: "", estado: "pendiente", mes: TL_MONTHS[0].key, notas: "" }); setTlModal({ new: true }); };
        const openEditTl = (m) => { setTlDraft({ ...m }); setTlModal({ edit: true }); };
        const saveTlDraft = () => {
          if (!tlDraft.label.trim()) return;
          const exists = milestones.some(m => m.id === tlDraft.id);
          const next = exists ? milestones.map(m => m.id === tlDraft.id ? tlDraft : m) : [...milestones, tlDraft];
          saveMilestones(next); setTlModal(null); setTlDraft(null);
        };
        const deleteTl = (id) => { saveMilestones(milestones.filter(m => m.id !== id)); setTlModal(null); setTlDraft(null); };
        const moveMonth = (id, dir) => {
          saveMilestones(milestones.map(m => {
            if (m.id !== id) return m;
            let idx = monthKeys.indexOf(m.mes); if (idx < 0) idx = 0;
            const ni = Math.max(0, Math.min(monthKeys.length - 1, idx + dir));
            return { ...m, mes: monthKeys[ni] };
          }));
        };
        // ── Links por hito (solo edición con login @partecpg.com) ──
        const openLinkEdit = (m) => { setTlLinkInput(m.link || ""); setTlLinkEditing(m.id); };
        const cancelLinkEdit = () => { setTlLinkEditing(null); setTlLinkInput(""); };
        const saveLink = (id) => {
          let url = tlLinkInput.trim();
          if (url && !/^https?:\/\//i.test(url)) url = "https://" + url; // acepta pegar sin http
          saveMilestones(milestones.map(m => m.id === id ? { ...m, link: url } : m));
          setTlLinkEditing(null); setTlLinkInput("");
        };
        const removeLink = (id) => {
          saveMilestones(milestones.map(m => m.id === id ? { ...m, link: "" } : m));
          setTlLinkEditing(null); setTlLinkInput("");
        };
        const areaColor = (area) => ["#6366F1", "#0EA5E9", "#14B8A6", "#F59E0B", "#EC4899"][TL_AREAS.indexOf(area)] || "#94A3B8";
        const SALMON = "#E07856";
        return (
          <div style={{ maxWidth: 880, margin: "0 auto", padding: "20px 16px 60px" }}>
            <div style={{ marginBottom: 6, fontSize: 18, fontWeight: 800, color: "#0f172a" }}>Línea del tiempo del proyecto</div>
            <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 16 }}>Avances y pendientes por mes — Expansión {BRAND_NAME} USA. Cada hito indica su área; muévelos de mes con las flechas.</div>

            {/* Resumen por estado */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {Object.entries(TL_ESTADOS).map(([k, meta]) => (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, background: meta.bg, border: `1px solid ${meta.color}33`, borderRadius: 20, padding: "4px 12px" }}>
                  <span style={{ width: 9, height: 9, borderRadius: "50%", background: meta.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, fontWeight: 800, color: meta.color }}>{counts[k]}</span>
                  <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600 }}>{meta.label}</span>
                </div>
              ))}
            </div>

            {/* Filtro por estado + añadir */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 22, alignItems: "center" }}>
              {["Todos", ...Object.keys(TL_ESTADOS)].map(k => {
                const active = tlFilterEstado === k;
                const meta = TL_ESTADOS[k];
                const color = meta ? meta.color : "#1D4ED8";
                const label = meta ? meta.label : "Todos";
                return <div key={k} onClick={() => setTlFilterEstado(k)}
                  style={{ padding: "5px 12px", borderRadius: 20, fontSize: 11.5, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? color : "#e2e8f0"}`, background: active ? color + "18" : "#f8fafc", color: active ? color : "#94a3b8" }}>{label}</div>;
              })}
              {canEditTl && (
                <button onClick={openNewTl} style={{ marginLeft: "auto", padding: "6px 14px", borderRadius: 8, border: "none", background: SALMON, color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>+ Añadir hito</button>
              )}
            </div>

            <TlFasesBar milestones={milestones} fase={tlFilterFase} setFase={setTlFilterFase} />

            {/* Timeline continua: una sola línea que atraviesa todos los meses */}
            <div style={{ position: "relative", paddingLeft: 30 }}>
              <div style={{ position: "absolute", left: 11, top: 14, bottom: 14, width: 2, background: "#e2e8f0" }} />
              {TL_MONTHS.map((mo, mi) => {
                const items = visible.filter(m => (m.mes || "2026-02") === mo.key);
                return (
                  <div key={mo.key} style={{ marginBottom: 6 }}>
                    {/* Marcador de mes sobre la misma línea */}
                    <div style={{ position: "relative", marginTop: mi === 0 ? 0 : 16, marginBottom: 10 }}>
                      <span style={{ position: "absolute", left: -27, top: 0, width: 18, height: 18, borderRadius: "50%", background: SALMON, border: "3px solid #fff", boxShadow: "0 0 0 2px " + SALMON }} />
                      <span style={{ display: "inline-block", fontSize: 11.5, fontWeight: 800, color: SALMON, background: SALMON + "14", border: `1px solid ${SALMON}33`, borderRadius: 20, padding: "3px 12px", textTransform: "uppercase", letterSpacing: 0.5 }}>{mo.label}</span>
                    </div>
                    {/* Hitos del mes */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {items.map(m => {
                        const meta = TL_ESTADOS[m.estado] || TL_ESTADOS.pendiente;
                        const idx = monthKeys.indexOf(m.mes);
                        const ac = areaColor(m.area);
                        return (
                          <div key={m.id} onClick={() => canEditTl && openEditTl(m)}
                            style={{ position: "relative", background: "#fff", border: "1px solid #e2e8f0", borderLeft: `3px solid ${meta.color}`, borderRadius: 10, padding: "10px 12px", boxShadow: "0 1px 4px #0f172a08", cursor: canEditTl ? "pointer" : "default" }}>
                            <span style={{ position: "absolute", left: -24, top: 15, width: 12, height: 12, borderRadius: "50%", background: meta.color, border: "2px solid #fff", boxShadow: "0 0 0 1.5px " + meta.color }} />
                            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: "inline-block", fontSize: 9.5, fontWeight: 800, color: ac, background: ac + "14", border: `1px solid ${ac}33`, borderRadius: 6, padding: "1px 7px", marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.3 }}>{m.area}</span>
                                {m.fase && TL_FASE_IDX[m.fase] !== undefined && (() => {
                                  const f = TL_FASES[TL_FASE_IDX[m.fase]];
                                  return <span style={{ display: "inline-block", fontSize: 9.5, fontWeight: 800, color: f.color, background: f.color + "14", border: `1px solid ${f.color}33`, borderRadius: 6, padding: "1px 7px", marginLeft: 5, marginBottom: 5, textTransform: "uppercase", letterSpacing: 0.3 }}>{f.label}</span>;
                                })()}
                                {(m.version || 1) > 1 && (
                                  <span title="Este hito iteró: volvió a una fase anterior y se rehízo"
                                    style={{ display: "inline-block", fontSize: 9.5, fontWeight: 800, color: SALMON, background: SALMON + "18", border: `1px solid ${SALMON}55`, borderRadius: 6, padding: "1px 7px", marginLeft: 5, marginBottom: 5, letterSpacing: 0.3 }}>
                                    &#8617; v{m.version}
                                  </span>
                                )}
                                <div style={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b", lineHeight: 1.35 }}>{m.label}</div>
                                {m.notas && <div style={{ fontSize: 11.5, color: "#94a3b8", marginTop: 3, lineHeight: 1.4 }}>{m.notas}</div>}
                                {(m.iteraciones || []).length > 0 && (() => {
                                  const ult = m.iteraciones[m.iteraciones.length - 1];
                                  return (
                                    <div style={{ fontSize: 11, color: SALMON, marginTop: 5, lineHeight: 1.4, background: SALMON + "0F", borderRadius: 6, padding: "4px 8px" }}>
                                      <b>&#8617; v{ult.version}</b> &middot; {ult.motivo}{ult.fecha ? " · " + isoToDisplay(ult.fecha) : ""}
                                    </div>
                                  );
                                })()}
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                  {m.link ? (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                      <a href={m.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, fontWeight: 800, color: "#1D4ED8", background: "#EFF6FF", padding: "3px 8px", borderRadius: 20, border: "1px solid #1D4ED833", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap", textDecoration: "none", cursor: "pointer" }}>
                                        <IcLink size={11} color="#1D4ED8" /> Ir al link
                                      </a>
                                      {canEditTl && (
                                        <button onClick={e => { e.stopPropagation(); openLinkEdit(m); }} title="Editar link"
                                          style={{ width: 22, height: 22, borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
                                          <IcPencil size={11} color="#64748b" />
                                        </button>
                                      )}
                                    </span>
                                  ) : canEditTl ? (
                                    <button onClick={e => { e.stopPropagation(); openLinkEdit(m); }}
                                      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, fontWeight: 800, color: "#94a3b8", background: "#f8fafc", padding: "3px 8px", borderRadius: 20, border: "1px solid #e2e8f0", textTransform: "uppercase", letterSpacing: 0.4, whiteSpace: "nowrap", cursor: "pointer" }}>
                                      <IcLink size={11} color="#94a3b8" /> Agregar link
                                    </button>
                                  ) : null}
                                  <span style={{ fontSize: 9.5, fontWeight: 800, color: meta.color, background: meta.bg, padding: "3px 8px", borderRadius: 20, textTransform: "uppercase", letterSpacing: 0.4, border: `1px solid ${meta.color}33`, whiteSpace: "nowrap" }}>{meta.label}</span>
                                </div>
                                {canEditTl && (
                                  <div style={{ display: "flex", gap: 4 }} onClick={e => e.stopPropagation()}>
                                    <button onClick={() => moveMonth(m.id, -1)} disabled={idx <= 0} title="Mes anterior"
                                      style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid #e2e8f0", background: idx <= 0 ? "#f8fafc" : "#fff", cursor: idx <= 0 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, opacity: idx <= 0 ? 0.4 : 1 }}>
                                      <IcChevronUp size={13} color="#64748b" />
                                    </button>
                                    <button onClick={() => moveMonth(m.id, 1)} disabled={idx >= monthKeys.length - 1} title="Mes siguiente"
                                      style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid #e2e8f0", background: idx >= monthKeys.length - 1 ? "#f8fafc" : "#fff", cursor: idx >= monthKeys.length - 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, opacity: idx >= monthKeys.length - 1 ? 0.4 : 1 }}>
                                      <IcChevronDown size={13} color="#64748b" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                            {canEditTl && tlLinkEditing === m.id && (
                              <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                                <input autoFocus type="text" value={tlLinkInput}
                                  onChange={e => setTlLinkInput(e.target.value)}
                                  onKeyDown={e => { if (e.key === "Enter") saveLink(m.id); if (e.key === "Escape") cancelLinkEdit(); }}
                                  placeholder="Pega la URL (Drive, doc, etc.)"
                                  style={{ flex: 1, minWidth: 160, fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 8, padding: "6px 9px", outline: "none", boxSizing: "border-box" }} />
                                <button onClick={() => saveLink(m.id)}
                                  style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Guardar</button>
                                {m.link && (
                                  <button onClick={() => removeLink(m.id)}
                                    style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Quitar</button>
                                )}
                                <button onClick={cancelLinkEdit}
                                  style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 11.5, fontWeight: 700, cursor: "pointer" }}>Cancelar</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal de edición */}
            {tlModal && tlDraft && (
              <div style={{ position: "fixed", inset: 0, background: "#00000055", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
                onClick={e => { if (e.target === e.currentTarget) { setTlModal(null); setTlDraft(null); setTlIterMotivo(""); setTlIterFase(null); } }}>
                <div style={{ background: "#fff", borderRadius: 16, padding: 24, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px #0003", maxHeight: "90vh", overflowY: "auto" }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 16 }}>{tlModal.new ? "Nuevo hito" : "Editar hito"}</div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Hito</div>
                    <input type="text" value={tlDraft.label} onChange={e => setTlDraft(p => ({ ...p, label: e.target.value }))}
                      style={{ width: "100%", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Área</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {TL_AREAS.map(a => {
                        const active = tlDraft.area === a;
                        return <div key={a} onClick={() => setTlDraft(p => ({ ...p, area: a }))}
                          style={{ padding: "5px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? "#1D4ED8" : "#e2e8f0"}`, background: active ? "#1D4ED818" : "#f8fafc", color: active ? "#1D4ED8" : "#94a3b8" }}>{a}</div>;
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Fase</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {TL_FASES.map(f => {
                        const active = tlDraft.fase === f.key;
                        return <div key={f.key} onClick={() => setTlDraft(p => ({ ...p, fase: active ? null : f.key }))}
                          style={{ padding: "5px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? f.color : "#e2e8f0"}`, background: active ? f.color + "18" : "#f8fafc", color: active ? f.color : "#94a3b8" }}>{f.label}</div>;
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Estado</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {Object.entries(TL_ESTADOS).map(([k, meta]) => {
                        const active = tlDraft.estado === k;
                        return <div key={k} onClick={() => setTlDraft(p => ({ ...p, estado: k }))}
                          style={{ padding: "5px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? meta.color : "#e2e8f0"}`, background: active ? meta.color + "18" : "#f8fafc", color: active ? meta.color : "#94a3b8" }}>{meta.label}</div>;
                      })}
                    </div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Mes</div>
                    <select value={tlDraft.mes} onChange={e => setTlDraft(p => ({ ...p, mes: e.target.value }))}
                      style={{ width: "100%", fontSize: 13, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", boxSizing: "border-box", background: "#fff", color: "#334155" }}>
                      {TL_MONTHS.map(mo => <option key={mo.key} value={mo.key}>{mo.label}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: 14, background: SALMON + "0C", border: `1px solid ${SALMON}33`, borderRadius: 10, padding: "11px 12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: SALMON, textTransform: "uppercase", letterSpacing: 0.5 }}>Iteraciones</div>
                      <div style={{ fontSize: 11.5, fontWeight: 800, color: SALMON }}>versión v{tlDraft.version || 1}</div>
                    </div>
                    <div style={{ fontSize: 11, color: "#94a3b8", lineHeight: 1.45, marginBottom: 8 }}>
                      Volver a una fase anterior no es un retraso: es el proceso funcionando. Regístralo aquí para que quede explicado y no parezca incumplimiento.
                    </div>
                    {(tlDraft.iteraciones || []).map(it => (
                      <div key={it.id} style={{ display: "flex", gap: 7, alignItems: "flex-start", padding: "5px 0", borderTop: "1px solid " + SALMON + "22" }}>
                        <span style={{ fontSize: 10, fontWeight: 800, color: SALMON, background: "#fff", border: `1px solid ${SALMON}44`, borderRadius: 5, padding: "1px 6px", flexShrink: 0, marginTop: 1 }}>v{it.version}</span>
                        <span style={{ fontSize: 11.5, color: "#334155", lineHeight: 1.4, flex: 1 }}>
                          {it.motivo}
                          {it.de && it.a && TL_FASE_IDX[it.de] !== undefined && TL_FASE_IDX[it.a] !== undefined && (
                            <span style={{ display: "block", fontSize: 10.5, color: "#94a3b8", fontWeight: 700, marginTop: 2 }}>
                              {TL_FASES[TL_FASE_IDX[it.de]].label} &rarr; {TL_FASES[TL_FASE_IDX[it.a]].label}
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: 10.5, color: "#94a3b8", flexShrink: 0 }}>{isoToDisplay(it.fecha)}</span>
                      </div>
                    ))}
                    <div style={{ marginTop: 9, marginBottom: 2 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 800, color: "#94a3b8", marginBottom: 5, letterSpacing: 0.3 }}>REGRESA A LA FASE</div>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {TL_FASES.map(f => {
                          const active = tlIterFase === f.key;
                          return <div key={f.key} onClick={() => setTlIterFase(active ? null : f.key)}
                            style={{ padding: "4px 9px", borderRadius: 20, fontSize: 10.5, fontWeight: 700, cursor: "pointer", border: `1.5px solid ${active ? f.color : "#e2e8f0"}`, background: active ? f.color + "18" : "#fff", color: active ? f.color : "#94a3b8" }}>{f.label}</div>;
                        })}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                      <input type="text" value={tlIterMotivo} placeholder="Motivo de la iteración (ej.: hallazgo del estudio de mercado)"
                        onChange={e => setTlIterMotivo(e.target.value)}
                        onKeyDown={e => { if (e.key !== "Enter") return; const mo = tlIterMotivo.trim(); if (!mo) return; setTlDraft(p => { const nv = (p.version || 1) + 1; const de = p.fase || null; const a = tlIterFase || p.fase || null; return { ...p, version: nv, fase: a, iteraciones: [...(p.iteraciones || []), { id: "it" + Date.now(), version: nv, motivo: mo, fecha: new Date().toISOString().slice(0, 10), de, a }] }; }); setTlIterMotivo(""); setTlIterFase(null); }}
                        style={{ flex: 1, minWidth: 170, fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 9px", outline: "none", boxSizing: "border-box", background: "#fff" }} />
                      <button onClick={() => { const mo = tlIterMotivo.trim(); if (!mo) return; setTlDraft(p => { const nv = (p.version || 1) + 1; const de = p.fase || null; const a = tlIterFase || p.fase || null; return { ...p, version: nv, fase: a, iteraciones: [...(p.iteraciones || []), { id: "it" + Date.now(), version: nv, motivo: mo, fecha: new Date().toISOString().slice(0, 10), de, a }] }; }); setTlIterMotivo(""); setTlIterFase(null); }}
                        style={{ padding: "7px 13px", borderRadius: 8, border: "none", background: SALMON, color: "#fff", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                        Registrar v{(tlDraft.version || 1) + 1}
                      </button>
                    </div>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Notas (opcional)</div>
                    <textarea value={tlDraft.notas || ""} rows={2} onChange={e => setTlDraft(p => ({ ...p, notas: e.target.value }))}
                      style={{ width: "100%", fontSize: 12.5, border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", outline: "none", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ display: "flex", gap: 8, justifyContent: "space-between" }}>
                    <div>
                      {!tlModal.new && (
                        <button onClick={() => deleteTl(tlDraft.id)}
                          style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Eliminar</button>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => { setTlModal(null); setTlDraft(null); setTlIterMotivo(""); setTlIterFase(null); }}
                        style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", fontSize: 12, cursor: "pointer" }}>Cancelar</button>
                      <button onClick={saveTlDraft}
                        style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>Guardar</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ══════════════ TAB: Análisis de precios (mismos permisos que Línea del tiempo) ══════════════ */}
      {activeTab === "precios" && !clientUnlocked && (
        <div style={{ maxWidth: 380, margin: "80px auto", textAlign: "center", padding: 20 }}>
          <div style={{ marginBottom: 12 }}><IcKey size={34} color="#94a3b8" /></div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>Acceso al proyecto</div>
          <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 18 }}>Ingresa el código de acceso compartido por Parte CPG.</div>
          <input autoFocus type="text" value={accessCodeInput}
            onChange={e => { setAccessCodeInput(e.target.value); setAccessCodeError(false); }}
            onKeyDown={e => e.key === "Enter" && submitAccessCode()}
            placeholder="Código de acceso"
            style={{ width: "100%", fontSize: 14, textAlign: "center", padding: "10px 14px", borderRadius: 10, border: accessCodeError ? "1.5px solid #EF4444" : "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
          {accessCodeError && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 10 }}>Código incorrecto.</div>}
          <button onClick={submitAccessCode} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Entrar</button>
        </div>
      )}
      {activeTab === "precios" && clientUnlocked && <PriceAnalysisTab user={user} />}
      {activeTab === "categoria" && IS_DEMO && <CategoryAnalysisTab />}

      {/* ══════════════ TAB: Bloqueos (mismos permisos que Línea del tiempo) ══════════════ */}
      {activeTab === "resultados" && !clientUnlocked && (
        <div style={{ maxWidth: 380, margin: "80px auto", textAlign: "center", padding: 20 }}>
          <div style={{ marginBottom: 12 }}><IcKey size={34} color="#94a3b8" /></div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>Acceso al proyecto</div>
          <div style={{ fontSize: 12.5, color: "#94a3b8", marginBottom: 18 }}>Ingresa el código de acceso compartido por Parte CPG.</div>
          <input autoFocus type="text" value={accessCodeInput}
            onChange={e => { setAccessCodeInput(e.target.value); setAccessCodeError(false); }}
            onKeyDown={e => e.key === "Enter" && submitAccessCode()}
            placeholder="Código de acceso"
            style={{ width: "100%", fontSize: 14, textAlign: "center", padding: "10px 14px", borderRadius: 10, border: accessCodeError ? "1.5px solid #EF4444" : "1.5px solid #e2e8f0", outline: "none", boxSizing: "border-box", marginBottom: 10 }} />
          {accessCodeError && <div style={{ fontSize: 12, color: "#EF4444", marginBottom: 10 }}>Código incorrecto.</div>}
          <button onClick={submitAccessCode} style={{ width: "100%", padding: "10px", borderRadius: 10, border: "none", background: "#1D4ED8", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>Entrar</button>
        </div>
      )}
      {activeTab === "resultados" && clientUnlocked && (
        <ResultadosTab
          fichas={resultados}
          onSave={saveResultados}
          canEdit={!guestMode && !!user?.email?.endsWith("@partecpg.com")}
          frente={resFrente} setFrente={setResFrente}
          verId={resVer} setVerId={setResVer}
          draft={resDraft} setDraft={setResDraft}
          goTab={setActiveTab}
          companyName={companyName}
        />
      )}

      {/* ══════════════ TAB: Gantt completo (privada — requiere login, salvo en demo) ══════════════ */}
      {activeTab === "gantt" && ((guestMode && !IS_DEMO) ? (
        <div style={{ maxWidth: 480, margin: "80px auto", textAlign: "center", padding: 20 }}>
          <div style={{ marginBottom: 12 }}><IcLock size={38} color="#94a3b8" /></div>
          <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b", marginBottom: 6 }}>Esta sección requiere acceso con login</div>
          <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 18 }}></div>
          <button onClick={signIn}
            style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 20px", borderRadius: 20, border: "2px solid #e2e8f0", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, color: "#334155" }}>
            <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-21 0-1.3-.2-2.7-.5-4z"/></svg>
            Iniciar sesión
          </button>
        </div>
      ) : (
        <>
      {/* ══════════════ HEADER: 2 columnas ══════════════
          Col izq: [Login | Título | Cards] + [Pizarra debajo]
          Col der: [Objetivos] [Time Tracker] (sin tocar)
      ══════════════════════════════════════════════════ */}
      <div style={{ background:"#fff", borderBottom:"1px solid #e2e8f0", display:"flex", alignItems:"stretch" }}>

        {/* ── Columna izquierda ── */}
        <div style={{ display:"flex", flexDirection:"column", flex:1, minWidth:0 }}>

          {/* Fila 1: Login | Título | Cards */}
          <div style={{ display:"flex", alignItems:"center", gap:12, padding:"6px 16px", borderBottom:"1px solid #FDE68A" }}>

            {/* Auth */}
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, flexShrink:0, minWidth:56 }}>
              {authLoading ? (
                <div style={{ fontSize:10, color:"#94a3b8" }}>...</div>
              ) : unauthorized ? (
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                  <div style={{ fontSize:10, color:"#EF4444", fontWeight:600 }}>🚫</div>
                  <button onClick={()=>{setUnauthorized(false);signIn();}}
                    style={{ fontSize:9, padding:"2px 6px", borderRadius:5, border:"1px solid #e2e8f0", background:"#f8fafc", cursor:"pointer" }}>Otra cuenta</button>
                </div>
              ) : user ? (<>
                <img src={user.photoURL} alt="" style={{ width:30, height:30, borderRadius:"50%", border:"2px solid #6366F1" }} />
                <div style={{ fontSize:10, fontWeight:700, color:"#1e293b", lineHeight:1 }}>{user.displayName?.split(" ")[0]}</div>
                <button onClick={signOut_} style={{ fontSize:9, color:"#94a3b8", background:"none", border:"none", cursor:"pointer", padding:0 }}>Salir</button>
              </>) : (
                <button onClick={signIn} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, padding:"4px 8px", borderRadius:10, border:"1.5px solid #e2e8f0", background:"#fff", cursor:"pointer" }}>
                  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-21 0-1.3-.2-2.7-.5-4z"/></svg>
                  <span style={{ fontSize:9, fontWeight:700, color:"#334155" }}>Entrar</span>
                </button>
              )}
            </div>

            <div style={{ width:1, height:40, background:"#e2e8f0", flexShrink:0 }} />

            {/* Empresa + Título */}
            <div style={{ flexShrink:0, minWidth:130 }}>
              {editingHeader==="company" ? (
                <input autoFocus value={companyName} onChange={e=>setCompanyName(e.target.value)}
                  onBlur={()=>{setEditingHeader(null);saveMeta({companyName});}}
                  onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape"){setEditingHeader(null);saveMeta({companyName});}}}
                  style={{ fontSize:9, letterSpacing:2, color:"#6C63FF", textTransform:"uppercase", fontWeight:700, border:"none", borderBottom:"2px solid #6C63FF", outline:"none", background:"transparent", width:"100%", padding:"1px 0" }} />
              ) : (
                <div onDoubleClick={()=>!guestMode&&setEditingHeader("company")}
                  style={{ fontSize:9, letterSpacing:2, color:companyName?"#6C63FF":"#c4b5fd", textTransform:"uppercase", fontWeight:700, cursor:guestMode?"default":"text" }}>
                  {companyName||"Empresa · Proyecto"}
                </div>
              )}
              {editingHeader==="project" ? (
                <input autoFocus value={projectName} onChange={e=>setProjectName(e.target.value)}
                  onBlur={()=>{setEditingHeader(null);saveMeta({projectName});}}
                  onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape"){setEditingHeader(null);saveMeta({projectName});}}}
                  style={{ fontSize:15, fontWeight:800, color:"#0f172a", border:"none", borderBottom:"2px solid #0f172a", outline:"none", background:"transparent", width:"100%", padding:"1px 0" }} />
              ) : (
                <div onDoubleClick={()=>!guestMode&&setEditingHeader("project")}
                  style={{ fontSize:15, fontWeight:800, color:projectName?"#0f172a":"#94a3b8", cursor:guestMode?"default":"text", lineHeight:1.2 }}>
                  {projectName||"Nombre del proyecto"}
                </div>
              )}
            </div>

            <div style={{ width:1, height:40, background:"#e2e8f0", flexShrink:0 }} />

            {/* Stats cards */}
            <div style={{ display:"flex", gap:6, flexShrink:0 }}>
              {[
                { label:"Hoy", value:TODAY.toLocaleDateString("es-MX",{day:"numeric",month:"short"}), color:"#F59E0B", bg:"#FFFBEB", border:"#FDE68A" },
                { label:"Atrasadas", value:totalDelayed, color:"#EF4444", bg:"#FEF2F2", border:"#FECACA" },
                { label:"En progreso", value:totalInProgress, color:"#3B82F6", bg:"#EFF6FF", border:"#BFDBFE" },
              ].map(({label,value,color,bg,border})=>(
                <div key={label} style={{ textAlign:"center", padding:"4px 10px", background:bg, borderRadius:8, border:`1px solid ${border}`, minWidth:52 }}>
                  <div style={{ fontSize:8, color, fontWeight:700, textTransform:"uppercase", letterSpacing:0.8 }}>{label}</div>
                  <div style={{ fontSize:14, fontWeight:900, color, lineHeight:1.2 }}>{value}</div>
                </div>
              ))}
            </div>

            {isMetricsViewer && (
              <button onClick={openMetrics} title="Solo visible para ti"
                style={{ display:"flex", alignItems:"center", gap:5, padding:"5px 11px", borderRadius:8, border:"1px solid #e2e8f0", background:"#fff", color:"#64748b", fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0 }}>
                📊 Métricas de uso
              </button>
            )}
          </div>

          {/* Fila 2: Pizarra de Avisos */}
          <div style={{ background:"#FFFBEB", padding:"5px 16px", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", flex:1 }}>
            <div style={{ fontSize:9, fontWeight:800, color:"#92400e", textTransform:"uppercase", letterSpacing:1.2, flexShrink:0, display:"flex", alignItems:"center", gap:4 }}><IcMegaphone size={11} color="#92400e" /> Pizarra de avisos</div>
            <div style={{ width:1, height:18, background:"#FDE68A", flexShrink:0 }} />

            {avisos.map(aviso=>(
              <div key={aviso.id} style={{ display:"flex", alignItems:"center", gap:5, background:aviso.done?"#f1f5f9":"#fff", border:`1px solid ${aviso.done?"#e2e8f0":"#FDE68A"}`, borderRadius:20, padding:"2px 10px 2px 5px", flexShrink:0 }}>
                <div onClick={()=>{if(guestMode)return;const next=avisos.map(a=>a.id===aviso.id?{...a,done:!a.done}:a);setAvisos(next);saveAvisos(next);}}
                  style={{ width:14,height:14,borderRadius:"50%",border:`2px solid ${aviso.done?"#22C55E":"#F59E0B"}`,background:aviso.done?"#22C55E":"transparent",cursor:guestMode?"default":"pointer",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center" }}>
                  {aviso.done&&<span style={{ fontSize:8,color:"#fff",fontWeight:900 }}>✓</span>}
                </div>
                {editingAviso===aviso.id ? (
                  <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                    <input autoFocus defaultValue={aviso.texto}
                      onBlur={e=>{const next=avisos.map(a=>a.id===aviso.id?{...a,texto:e.target.value}:a);setAvisos(next);saveAvisos(next);setEditingAviso(null);}}
                      onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape")e.target.blur();}}
                      style={{ fontSize:11,border:"none",borderBottom:"1px solid #F59E0B",outline:"none",background:"transparent",color:"#1e293b",minWidth:100 }} />
                    <input type="date" defaultValue={aviso.fecha}
                      onBlur={e=>{const next=avisos.map(a=>a.id===aviso.id?{...a,fecha:e.target.value}:a);setAvisos(next);saveAvisos(next);}}
                      style={{ fontSize:10,border:"none",borderBottom:"1px solid #F59E0B",outline:"none",background:"transparent",color:"#F59E0B" }} />
                  </div>
                ) : (
                  <div onDoubleClick={()=>!guestMode&&setEditingAviso(aviso.id)} style={{ display:"flex",alignItems:"center",gap:5,cursor:guestMode?"default":"text" }}>
                    <span style={{ fontSize:11,color:aviso.done?"#94a3b8":"#1e293b",textDecoration:aviso.done?"line-through":"none",whiteSpace:"nowrap" }}>{aviso.texto}</span>
                    {aviso.fecha&&<span style={{ fontSize:9,color:aviso.done?"#cbd5e1":"#F59E0B",fontWeight:700,whiteSpace:"nowrap" }}>{aviso.fecha}</span>}
                  </div>
                )}
                {!guestMode&&(
                  <button onClick={()=>{const next=avisos.filter(a=>a.id!==aviso.id);setAvisos(next);saveAvisos(next);}}
                    style={{ fontSize:12,color:"#fcd34d",background:"none",border:"none",cursor:"pointer",padding:0,marginLeft:2,lineHeight:1 }}>×</button>
                )}
              </div>
            ))}

            {!guestMode && (
              <button onClick={()=>{
                const newA={id:"av"+Date.now(),texto:"Nuevo aviso",fecha:new Date().toISOString().slice(0,10),done:false};
                const next=[...avisos,newA];setAvisos(next);saveAvisos(next);
                setTimeout(()=>setEditingAviso(newA.id),50);
              }} style={{ width:22,height:22,borderRadius:"50%",border:"1.5px dashed #F59E0B",background:"transparent",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",color:"#F59E0B",flexShrink:0 }}>+</button>
            )}
            {avisos.length===0&&(
              <div style={{ fontSize:10,color:"#d97706",fontStyle:"italic" }}>{guestMode?"Sin avisos pendientes":"Usa + para agregar avisos"}</div>
            )}
          </div>
        </div>

        {/* ── Columna derecha: Objetivos + Time Tracker ── */}
        <div style={{ display:"flex", alignItems:"center", gap:0, borderLeft:"1px solid #e2e8f0", flexShrink:0 }}>

          {/* Objectives */}
          <div style={{ background:"#fafafa", padding:"6px 14px", minWidth:320, maxWidth:440, display:"flex", flexDirection:"column", gap:3, alignSelf:"stretch", justifyContent:"center" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:1 }}>
              <div style={{ fontSize:8, color:"#94a3b8", fontWeight:700, textTransform:"uppercase", letterSpacing:1.2 }}>Objetivos o Hitos a alcanzar</div>
              {!guestMode && objectives.length < 6 && (
                <button onClick={()=>{
                  const newId="o"+Date.now();
                  const usedColors=objectives.map(o=>o.color);
                  const nextColor=OBJECTIVE_COLORS.find(c=>!usedColors.includes(c))||OBJECTIVE_COLORS[objectives.length%OBJECTIVE_COLORS.length];
                  const next=[...objectives,{id:newId,label:"Nuevo objetivo",color:nextColor,progress:0,date:""}];
                  setObjectives(next);saveObjectives(next);setEditingObjective({id:newId,field:"label"});
                }} style={{ width:16,height:16,borderRadius:"50%",border:"1.5px solid #cbd5e1",background:"#fff",cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",justifyContent:"center",color:"#94a3b8",lineHeight:1 }}>+</button>
              )}
            </div>
            {objectives.map(obj=>(
              <div key={obj.id} style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ width:3, height:18, borderRadius:2, background:obj.color, flexShrink:0 }} />
                {/* Label + progress + date (right) */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                    {editingObjective?.id===obj.id && editingObjective.field==="label" ? (
                      <input autoFocus value={obj.label}
                        onChange={e=>setObjectives(prev=>prev.map(o=>o.id===obj.id?{...o,label:e.target.value}:o))}
                        onBlur={()=>{saveObjectives(objectives);setEditingObjective(null);}}
                        onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape"){saveObjectives(objectives);setEditingObjective(null);}}}
                        style={{ flex:1,fontSize:10,fontWeight:600,border:"none",borderBottom:`1.5px solid ${obj.color}`,outline:"none",background:"transparent",color:"#1e293b",padding:"0 0 1px" }} />
                    ) : (
                      <div onDoubleClick={()=>!guestMode&&setEditingObjective({id:obj.id,field:"label"})}
                        style={{ flex:1,fontSize:10,fontWeight:600,color:"#1e293b",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",cursor:guestMode?"default":"text",lineHeight:1.2 }}>{obj.label}</div>
                    )}
                    {/* Progress */}
                    {editingObjective?.id===obj.id && editingObjective.field==="progress" ? (
                      <input autoFocus type="number" min="0" max="100" value={obj.progress}
                        onChange={e=>setObjectives(prev=>prev.map(o=>o.id===obj.id?{...o,progress:Math.min(100,Math.max(0,Number(e.target.value)))}:o))}
                        onBlur={()=>{saveObjectives(objectives);setEditingObjective(null);}}
                        onKeyDown={e=>{if(e.key==="Enter"||e.key==="Escape"){saveObjectives(objectives);setEditingObjective(null);}}}
                        style={{ width:28,fontSize:9,border:`1px solid ${obj.color}`,borderRadius:3,outline:"none",textAlign:"center",color:obj.color,fontWeight:700,padding:"1px 2px",flexShrink:0 }} />
                    ) : (
                      <div onClick={()=>!guestMode&&setEditingObjective({id:obj.id,field:"progress"})}
                        style={{ fontSize:9,fontWeight:700,color:obj.color,minWidth:22,textAlign:"right",cursor:guestMode?"default":"pointer",flexShrink:0 }}>{obj.progress}%</div>
                    )}
                    {/* Date — right side */}
                    <input type="date" value={obj.date||""} onChange={e=>{const next=objectives.map(o=>o.id===obj.id?{...o,date:e.target.value}:o);setObjectives(next);saveObjectives(next);}}
                      disabled={!!guestMode} title="Fecha hito"
                      style={{ fontSize:8, border:"none", borderBottom:`1px dashed ${obj.color}`, outline:"none", background:"transparent", color:obj.date?obj.color:"#cbd5e1", width:76, flexShrink:0, cursor:guestMode?"default":"text", opacity:guestMode?0.6:1, padding:"0 0 1px" }} />
                    {!guestMode && (
                      <button onClick={()=>{const next=objectives.filter(o=>o.id!==obj.id);setObjectives(next);saveObjectives(next);}}
                        style={{ width:12,height:12,border:"none",background:"transparent",cursor:"pointer",color:"#d1d5db",fontSize:11,display:"flex",alignItems:"center",justifyContent:"center",padding:0,lineHeight:1,flexShrink:0 }}>×</button>
                    )}
                  </div>
                  <div style={{ height:3, background:"#e2e8f0", borderRadius:3, overflow:"hidden", marginTop:2 }}>
                    <div style={{ width:`${obj.progress}%`, height:"100%", background:obj.color, borderRadius:3, transition:"width 0.4s" }} />
                  </div>
                </div>
              </div>
            ))}
            {objectives.length===0 && <div style={{ fontSize:9,color:"#cbd5e1",textAlign:"center" }}>Sin objetivos — usa + para agregar</div>}
          </div>

          <div style={{ width:1, height:"100%", background:"#e2e8f0", flexShrink:0 }} />

          {/* Time Tracker */}
          {!guestMode ? (
          <div style={{ background:"#fafafa", padding:"7px 12px", minWidth:290, maxWidth:390, display:"flex", flexDirection:"column", gap:4, flexShrink:0, alignSelf:"stretch", justifyContent:"center" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ fontSize:8, color:"#94a3b8", fontWeight:700, textTransform:"uppercase", letterSpacing:1.2, display:"flex", alignItems:"center", gap:4 }}><IcClock size={10} color="#94a3b8" /> Time Tracker</div>
              <button onClick={()=>setShowTimeHistory(h=>!h)} style={{ fontSize:9,color:"#6366F1",background:"none",border:"none",cursor:"pointer",fontWeight:700,padding:0 }}>
                {showTimeHistory?"Ocultar":"Historial"}
              </button>
            </div>
            {isTimeAdmin && (
              <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
                <div style={{ fontSize:7,color:"#F59E0B",fontWeight:800,textTransform:"uppercase",letterSpacing:1 }}>👁 Vista admin</div>
                <select value={watchedUid||""} onChange={e=>{const uid=e.target.value;setWatchedUid(uid||null);const f=timeUserList.find(u=>u.uid===uid);setWatchedName(f?f.name||f.email:"");}}
                  style={{ fontSize:10,border:"1px solid #FDE68A",borderRadius:5,padding:"3px 6px",background:"#FFFBEB",color:"#92400e",outline:"none",cursor:"pointer" }}>
                  <option value="">— Mi tiempo —</option>
                  {timeUserList.filter(u=>u.uid!==user?.uid).map(u=>(<option key={u.uid} value={u.uid}>{u.name||u.email}</option>))}
                </select>
                {watchedUid && <div style={{ fontSize:8,color:"#92400e",background:"#FEF3C7",borderRadius:4,padding:"2px 5px",fontWeight:600 }}>Viendo: <strong>{watchedName}</strong></div>}
              </div>
            )}
            {(()=>{
              const dToday=watchedUid?watchedToday:todaySeconds;
              const dTotal=watchedUid?watchedTotal:totalSeconds;
              return (
                <div style={{ display:"flex",gap:6 }}>
                  <div style={{ flex:1,textAlign:"center",background:"#EFF6FF",borderRadius:5,padding:"3px 4px" }}>
                    <div style={{ fontSize:7,color:"#3B82F6",fontWeight:700,textTransform:"uppercase" }}>Hoy</div>
                    <div style={{ fontSize:13,fontWeight:900,color:"#3B82F6",lineHeight:1.2 }}>{fmt(dToday)}</div>
                  </div>
                  <div style={{ flex:1,textAlign:"center",background:"#F5F3FF",borderRadius:5,padding:"3px 4px" }}>
                    <div style={{ fontSize:7,color:"#6366F1",fontWeight:700,textTransform:"uppercase" }}>Total</div>
                    <div style={{ fontSize:13,fontWeight:900,color:"#6366F1",lineHeight:1.2 }}>{fmt(dTotal)}</div>
                  </div>
                </div>
              );
            })()}
            {!watchedUid && (<>
              {renderTimerControls(false)}
              {pipWin && createPortal(renderTimerControls(true), pipWin.document.body)}
              {!timerPendingSave && (
                <div style={{ display:"flex",flexDirection:"column",gap:3,background:"#f8fafc",border:"1px solid #e2e8f0",borderRadius:6,padding:"4px 8px" }}>
                  <div style={{ display:"flex",gap:3,alignItems:"center" }}>
                    <input type="number" min="0" max="23" placeholder="h" value={manualHours} onChange={e=>setManualHours(e.target.value)}
                      style={{ width:30,fontSize:10,border:"1px solid #e2e8f0",borderRadius:4,padding:"2px 4px",outline:"none",textAlign:"center" }} />
                    <span style={{ fontSize:8,color:"#94a3b8" }}>h</span>
                    <input type="number" min="0" max="59" placeholder="m" value={manualMins} onChange={e=>setManualMins(e.target.value)}
                      style={{ width:30,fontSize:10,border:"1px solid #e2e8f0",borderRadius:4,padding:"2px 4px",outline:"none",textAlign:"center" }} />
                    <span style={{ fontSize:8,color:"#94a3b8" }}>m</span>
                    <input type="date" value={manualDate} onChange={e=>setManualDate(e.target.value)}
                      style={{ flex:1,fontSize:9,border:"1px solid #e2e8f0",borderRadius:4,padding:"2px 4px",outline:"none",color:"#334155" }} />
                    <button onClick={()=>{
                      const s=(parseInt(manualHours||0)*3600)+(parseInt(manualMins||0)*60);
                      if(s<1) return;
                      const entryMonthPrefix = manualDate.slice(0,7); // e.g. "2026-04"
                      const isArchived = timeArchives.some(a => a.monthKey === entryMonthPrefix);
                      if(isArchived) {
                        const [yr,mo] = entryMonthPrefix.split("-");
                        const monthName = new Date(parseInt(yr),parseInt(mo)-1,1).toLocaleString("es-MX",{month:"long"});
                        const label = `${monthName.charAt(0).toUpperCase()+monthName.slice(1)} ${yr}`;
                        setConfirm({ message: `Esta fecha pertenece a ${label}, que ya está archivado. ¿Agregar las horas ahí?`, onConfirm: async () => {
                          await saveToArchive(entryMonthPrefix, s, manualNote, manualDate);
                          setManualHours(""); setManualMins(""); setManualNote(""); setManualDate(new Date().toISOString().slice(0,10));
                          setConfirm(null); setExpandedArchive(entryMonthPrefix); setShowTimeHistory(true);
                        }});
                      } else {
                        saveTimeEntry(s,manualNote,manualDate);
                        setManualHours(""); setManualMins(""); setManualNote(""); setManualDate(new Date().toISOString().slice(0,10));
                      }
                    }}
                      style={{ fontSize:9,fontWeight:700,background:"#6366F1",color:"#fff",border:"none",borderRadius:4,padding:"3px 7px",cursor:"pointer",flexShrink:0 }}>+ Agregar</button>
                  </div>
                  <input placeholder="Describe en qué se usó el tiempo" maxLength={180} value={manualNote} onChange={e=>setManualNote(e.target.value)}
                    style={{ fontSize:9,border:"1px solid #e2e8f0",borderRadius:4,padding:"3px 6px",outline:"none",color:"#334155",background:"#fff" }} />
                </div>
              )}
            </>)}
            {showTimeHistory && (()=>{
              const entries=watchedUid?watchedEntries:timeEntries;
              const canEdit=!watchedUid;
              return (
                <div style={{ maxHeight:160,overflowY:"auto",display:"flex",flexDirection:"column",gap:2 }}>
                  {entries.length===0 && <div style={{ fontSize:8,color:"#cbd5e1",textAlign:"center",padding:3 }}>Sin registros aún</div>}
                  {[...entries].sort((a,b)=>(b.date||"").localeCompare(a.date||"")).slice(0,30).map(entry=>{
                    const isEditing=editingEntry?.id===entry.id;
                    if(isEditing){
                      const eH=Math.floor(editingEntry.seconds/3600),eM=Math.floor((editingEntry.seconds%3600)/60);
                      const commit=()=>{updateTimeEntry(entry.id,{seconds:editingEntry.seconds,note:editingEntry.note,date:editingEntry.date});setEditingEntry(null);};
                      return (
                        <div key={entry.id} style={{ display:"flex",flexDirection:"column",gap:3,padding:"4px 6px",background:"#F5F3FF",borderRadius:6,border:"1px solid #a5b4fc" }}>
                          <div style={{ display:"flex",gap:3,alignItems:"center" }}>
                            <input type="number" min="0" max="23" value={eH} onChange={e=>setEditingEntry(p=>({...p,seconds:(parseInt(e.target.value)||0)*3600+(p.seconds%3600)}))}
                              style={{ width:28,fontSize:9,border:"1px solid #a5b4fc",borderRadius:3,padding:"2px 3px",outline:"none",textAlign:"center" }} />
                            <span style={{ fontSize:8,color:"#94a3b8" }}>h</span>
                            <input type="number" min="0" max="59" value={eM} onChange={e=>setEditingEntry(p=>({...p,seconds:Math.floor(p.seconds/3600)*3600+(parseInt(e.target.value)||0)*60}))}
                              style={{ width:28,fontSize:9,border:"1px solid #a5b4fc",borderRadius:3,padding:"2px 3px",outline:"none",textAlign:"center" }} />
                            <span style={{ fontSize:8,color:"#94a3b8" }}>m</span>
                            <input type="date" value={editingEntry.date||""} onChange={e=>setEditingEntry(p=>({...p,date:e.target.value}))}
                              style={{ flex:1,fontSize:8,border:"1px solid #a5b4fc",borderRadius:3,padding:"2px 3px",outline:"none" }} />
                            <button onClick={commit} style={{ fontSize:8,fontWeight:700,background:"#6366F1",color:"#fff",border:"none",borderRadius:3,padding:"2px 5px",cursor:"pointer" }}>✓</button>
                            <button onClick={()=>setEditingEntry(null)} style={{ fontSize:9,color:"#94a3b8",background:"none",border:"none",cursor:"pointer" }}>✕</button>
                          </div>
                          <input maxLength={180} value={editingEntry.note} placeholder="Describe en qué se usó el tiempo"
                            onChange={e=>setEditingEntry(p=>({...p,note:e.target.value}))}
                            onKeyDown={e=>{if(e.key==="Enter")commit();if(e.key==="Escape")setEditingEntry(null);}}
                            style={{ fontSize:8,border:"1px solid #a5b4fc",borderRadius:3,padding:"2px 5px",outline:"none",color:"#334155" }} />
                        </div>
                      );
                    }
                    return (
                      <div key={entry.id} onDoubleClick={()=>canEdit&&setEditingEntry({id:entry.id,seconds:entry.seconds,note:entry.note||"",date:entry.date||""})}
                        style={{ display:"flex",alignItems:"flex-start",gap:4,padding:"2px 5px",background:"#fff",borderRadius:4,border:"1px solid #f1f5f9" }}>
                        <div style={{ fontSize:7,color:"#94a3b8",flexShrink:0 }}>{entry.date}</div>
                        <div style={{ fontSize:8,fontWeight:700,color:"#6366F1",flexShrink:0 }}>{fmt(entry.seconds)}</div>
                        <div style={{ fontSize:8,color:entry.note?"#334155":"#cbd5e1",flex:1,whiteSpace:"pre-wrap",wordBreak:"break-word",fontStyle:entry.note?"normal":"italic" }}>{entry.note||"sin descripción"}</div>
                        {canEdit && <button onClick={()=>deleteTimeEntry(entry.id)} style={{ fontSize:8,color:"#cbd5e1",background:"none",border:"none",cursor:"pointer",padding:0 }}>×</button>}
                      </div>
                    );
                  })}

                  {/* ── Archived months ── */}
                  {[...timeArchives].sort((a,b)=>b.monthKey.localeCompare(a.monthKey)).map(archive => {
                    const myArchive = watchedUid
                      ? archive.users.find(u => u.uid === watchedUid)
                      : archive.users.find(u => u.uid === user?.uid);
                    const myEntries = myArchive?.entries || [];
                    const totalSecs = myEntries.reduce((a,e) => a + e.seconds, 0);
                    const isOpen = expandedArchive === archive.monthKey;
                    const isAdding = addingToArchive?.monthKey === archive.monthKey;
                    const [yr, mo] = archive.monthKey.split("-");
                    const monthName = new Date(parseInt(yr), parseInt(mo)-1, 1).toLocaleString("es-MX", { month: "long" });
                    const label = `${monthName.charAt(0).toUpperCase()+monthName.slice(1)} ${yr}`;
                    const defaultDate = `${yr}-${mo}-${new Date(parseInt(yr), parseInt(mo), 0).getDate()}`;
                    return (
                      <div key={archive.monthKey}>
                        <div style={{ display:"flex",alignItems:"center",gap:4 }}>
                          <div onClick={()=>setExpandedArchive(isOpen ? null : archive.monthKey)}
                            style={{ display:"flex",alignItems:"center",gap:5,padding:"4px 7px",background:"#F0FDF4",borderRadius:6,border:"1px solid #86EFAC",cursor:"pointer",flex:1 }}>
                            <div style={{ fontSize:8,color:"#16A34A",flexShrink:0 }}>{isOpen ? "▾" : "▸"}</div>
                            <div style={{ fontSize:9,fontWeight:800,color:"#15803D",flex:1 }}>📁 {label}</div>
                            <div style={{ fontSize:9,fontWeight:700,color:"#16A34A",flexShrink:0 }}>{fmt(totalSecs)}</div>
                          </div>
                          {!watchedUid && (
                            <button
                              onClick={()=>setAddingToArchive(isAdding ? null : { monthKey:archive.monthKey, h:"", m:"", date:defaultDate, note:"" })}
                              title="Agregar horas a este mes"
                              style={{ fontSize:13,width:20,height:20,borderRadius:"50%",border:"1.5px dashed #16A34A",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:"#16A34A",flexShrink:0,lineHeight:1 }}>+</button>
                          )}
                        </div>
                        {/* Add-to-archive form */}
                        {isAdding && !watchedUid && (
                          <div style={{ marginLeft:8,marginTop:3,display:"flex",flexDirection:"column",gap:3,background:"#F0FDF4",border:"1px solid #86EFAC",borderRadius:6,padding:"5px 8px" }}>
                            <div style={{ fontSize:8,color:"#15803D",fontWeight:700 }}>Agregar a {label}</div>
                            <div style={{ display:"flex",gap:3,alignItems:"center" }}>
                              <input type="number" min="0" max="23" placeholder="h" value={addingToArchive.h}
                                onChange={e=>setAddingToArchive(p=>({...p,h:e.target.value}))}
                                style={{ width:28,fontSize:9,border:"1px solid #86EFAC",borderRadius:4,padding:"2px 4px",outline:"none",textAlign:"center" }} />
                              <span style={{ fontSize:8,color:"#86EFAC" }}>h</span>
                              <input type="number" min="0" max="59" placeholder="m" value={addingToArchive.m}
                                onChange={e=>setAddingToArchive(p=>({...p,m:e.target.value}))}
                                style={{ width:28,fontSize:9,border:"1px solid #86EFAC",borderRadius:4,padding:"2px 4px",outline:"none",textAlign:"center" }} />
                              <span style={{ fontSize:8,color:"#86EFAC" }}>m</span>
                              <input type="date" value={addingToArchive.date}
                                onChange={e=>setAddingToArchive(p=>({...p,date:e.target.value}))}
                                style={{ flex:1,fontSize:8,border:"1px solid #86EFAC",borderRadius:4,padding:"2px 3px",outline:"none",color:"#15803D" }} />
                              <button onClick={async()=>{
                                const s=(parseInt(addingToArchive.h||0)*3600)+(parseInt(addingToArchive.m||0)*60);
                                if(s>0){ await saveToArchive(archive.monthKey,s,addingToArchive.note,addingToArchive.date); setAddingToArchive(null); setExpandedArchive(archive.monthKey); }
                              }} style={{ fontSize:8,fontWeight:700,background:"#16A34A",color:"#fff",border:"none",borderRadius:4,padding:"3px 7px",cursor:"pointer",flexShrink:0 }}>+ Agregar</button>
                            </div>
                            <input placeholder="Descripción (opcional)" maxLength={180} value={addingToArchive.note}
                              onChange={e=>setAddingToArchive(p=>({...p,note:e.target.value}))}
                              style={{ fontSize:8,border:"1px solid #86EFAC",borderRadius:4,padding:"2px 5px",outline:"none",color:"#334155",background:"#fff" }} />
                          </div>
                        )}
                        {/* Expanded entries */}
                        {isOpen && myEntries.length > 0 && (
                          <div style={{ marginLeft:8,display:"flex",flexDirection:"column",gap:2,marginTop:2 }}>
                            {[...myEntries].sort((a,b)=>(b.date||"").localeCompare(a.date||"")).map(entry=>(
                              <div key={entry.id} style={{ display:"flex",alignItems:"flex-start",gap:4,padding:"2px 5px",background:"#F7FEF9",borderRadius:4,border:"1px solid #BBF7D0" }}>
                                <div style={{ fontSize:7,color:"#86EFAC",flexShrink:0 }}>{entry.date}</div>
                                <div style={{ fontSize:8,fontWeight:700,color:"#16A34A",flexShrink:0 }}>{fmt(entry.seconds)}</div>
                                <div style={{ fontSize:8,color:entry.note?"#334155":"#cbd5e1",flex:1,whiteSpace:"pre-wrap",wordBreak:"break-word",fontStyle:entry.note?"normal":"italic" }}>{entry.note||"sin descripción"}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          ) : (
            <div style={{ fontSize:9,color:"#cbd5e1",padding:"7px 10px",background:"#fafafa",flexShrink:0,alignSelf:"stretch",display:"flex",alignItems:"center" }}>
              <IcLock size={11} color="#cbd5e1" style={{ marginRight: 4 }} /> Inicia sesión para<br/>usar el Time Tracker
            </div>
          )}
        </div>
      </div>
      {/* ── Legend + Owner Filter ── */}
      <div style={{ background: "#fff", borderBottom: "1px solid #f1f5f9", padding: "8px 24px", display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
        {/* Zoom controls */}
        <div style={{ display: "flex", gap: 4, alignItems: "center", background: "#f1f5f9", borderRadius: 8, padding: "2px 4px" }}>
          {[3, 6, 12, 24].map(m => (
            <div key={m} onClick={() => setZoomMonths(m)}
              style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", userSelect: "none",
                background: zoomMonths === m ? "#fff" : "transparent",
                color: zoomMonths === m ? "#1e293b" : "#94a3b8",
                boxShadow: zoomMonths === m ? "0 1px 4px #0001" : "none",
                transition: "all 0.15s" }}>
              {m === 12 ? "12m" : m === 6 ? "6m" : m === 3 ? "3m" : "24m"}
            </div>
          ))}
        </div>
        <div style={{ width: 1, height: 16, background: "#e2e8f0" }} />
        {[
          { color: "#22C55E", label: "Completada" },
          { color: "#F59E0B", label: "En progreso" },
          { color: "#EF4444", label: "Atrasada" },
          { color: "#94A3B8", label: "Por iniciar" },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#64748b" }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />{label}
          </div>
        ))}

        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Filtrar por responsable:</span>
          {/* "All" button */}
          <div onClick={() => setFilterOwner(null)}
            style={{ padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, cursor: "pointer", userSelect: "none",
              background: filterOwner === null ? "#0f172a" : "#f1f5f9",
              color: filterOwner === null ? "#fff" : "#475569",
              border: `2px solid ${filterOwner === null ? "#0f172a" : "#e2e8f0"}` }}>
            Todos
          </div>
          {Object.entries(ownerColors).map(([o, c]) => (
            <div key={o} style={{ position: "relative", display: "inline-flex" }}
              onMouseEnter={e => { if (!guestMode) e.currentTarget.querySelector(".del-user").style.display = "flex"; }}
              onMouseLeave={e => { const d = e.currentTarget.querySelector(".del-user"); if(d) d.style.display = "none"; }}>
              <div onClick={() => setFilterOwner(prev => prev === o ? null : o)}
                title={`${c.label} — click para filtrar`}
                style={{ width: 30, height: 30, borderRadius: "50%", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, fontWeight: 800, color: "#fff", cursor: "pointer", userSelect: "none",
                  opacity: filterOwner && filterOwner !== o ? 0.3 : 1,
                  boxShadow: filterOwner === o ? `0 0 0 3px ${c.bg}55, 0 0 0 5px ${c.bg}` : "none",
                  transition: "all 0.15s" }}>
                {o.slice(0,2)}
              </div>
              {!guestMode && (
                <div className="del-user"
                  onClick={e => { e.stopPropagation(); setConfirm({ message: `¿Eliminar al usuario ${o} (${c.label})?`, onConfirm: () => { deleteUser(o); setConfirm(null); } }); }}
                  style={{ display: "none", position: "absolute", top: -4, right: -4, width: 14, height: 14, borderRadius: "50%",
                    background: "#EF4444", color: "#fff", fontSize: 9, fontWeight: 900,
                    alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 10, border: "1.5px solid #fff" }}>
                  ✕
                </div>
              )}
            </div>
          ))}
          {filterOwner && (
            <span style={{ fontSize: 11, color: "#1D4ED8", fontWeight: 600 }}>
              Mostrando: {filterOwner} ({ownerColors[filterOwner]?.label})
            </span>
          )}
          {/* Add user button */}
          <div onClick={() => setAddUserOpen(true)}
            title="Agregar usuario"
            style={{ width: 28, height: 28, borderRadius: "50%", background: "#f1f5f9", border: "2px dashed #cbd5e1",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, lineHeight: 1, color: "#94a3b8",
              cursor: "pointer", transition: "all 0.15s", marginLeft: 4 }}
            onMouseEnter={e => { e.currentTarget.style.background="#EEF2FF"; e.currentTarget.style.borderColor="#1D4ED8"; e.currentTarget.style.color="#1D4ED8"; }}
            onMouseLeave={e => { e.currentTarget.style.background="#f1f5f9"; e.currentTarget.style.borderColor="#cbd5e1"; e.currentTarget.style.color="#94a3b8"; }}>
            +
          </div>
        </div>
      </div>

      {/* ── Gantt body ── */}
      <div ref={ganttScrollRef} style={{ overflowX: "auto" }}>
        {/* Inner div width = totalMonths/zoomMonths × visible area, so each zoom shows exactly N months */}
        <div ref={ganttBodyRef} style={{ width: `${(totalMonths / zoomMonths) * 100}%`, minWidth: LABEL_W + zoomMonths * 80, position: "relative" }}>

          {/* Month header */}
          <div style={{ display: "flex", borderBottom: "2px solid #e2e8f0", background: "#fff" }}>
            {/* STICKY: label + RACI header */}
            <div style={{ display: "flex", flexShrink: 0, position: "sticky", left: 0, zIndex: 30, background: "#fff" }}>
            <div style={{ width: LABEL_W, flexShrink: 0, padding: "8px 16px", fontSize: 11, fontWeight: 700, color: "#64748b", borderRight: "none", position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>Área / Tarea / Subtarea</span>
              {!guestMode && (
                <div onClick={addSpace} title="Agregar nueva área"
                  style={{ width: 20, height: 20, borderRadius: "50%", background: "#6366F1", color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, lineHeight: 1, fontWeight: 400,
                    cursor: "pointer", flexShrink: 0, marginRight: 8 }}>+</div>
              )}
              {/* Column resize handle */}
              <div onMouseDown={startLabelResize}
                title="Arrastrar para redimensionar columna"
                style={{ position: "absolute", right: -3, top: 0, bottom: 0, width: 6, cursor: "ew-resize", zIndex: 10,
                  display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div style={{ width: 2, height: "60%", background: "#cbd5e1", borderRadius: 2 }} />
              </div>
            </div>
            {/* RACI column headers */}
            {RACI_ROLES.map((role, i) => (
              <div key={role} style={{ width: RACI_W, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, fontWeight: 900, color: Object.values(RACI_META)[i].color,
                borderRight: role === "I" ? "1px solid #e2e8f0" : "none",
                borderLeft: role === "R" ? "1px solid #e2e8f0" : "none",
                background: "#fafafa", letterSpacing: 0.5 }}>
                {role}
              </div>
            ))}
            </div>{/* end sticky */}
            <div ref={chartRef} style={{ flex: 1, position: "relative", height: 32 }}>
              {visibleMonths.map((m, i) => (
                <div key={i} style={{ position: "absolute", left: `${vpct(m)}%`, top: 0, bottom: 0, borderLeft: "1px solid #e2e8f0", paddingLeft: 4, paddingTop: 8, fontSize: 9, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap" }}>
                  {m.toLocaleString("es-MX", { month: "short" })} '{m.getFullYear().toString().slice(2)}
                </div>
              ))}
            </div>
          </div>

          {/* Rows */}
          {projects.map(space => {
            const visibleTasks = space.tasks.filter(taskMatchesFilter);
            if (filterOwner && visibleTasks.length === 0) return null;
            const spaceProgress = Math.round(space.tasks.reduce((a,t) => a + t.progress, 0) / Math.max(1, space.tasks.length));
            const hasDelayed = space.tasks.some(t => TODAY > t.end && t.progress < 100);

            const spaceIdx = projects.indexOf(space);
            const isSpaceDragTarget = dragOver?.spaceId === space.id && !dragOver?.taskId;
            return (
              <div key={space.id}>
                {/* Space drop indicator BEFORE */}
                {isSpaceDragTarget && dragOver?.position === "before" && (
                  <div style={{ height: 4, background: "#6366F1", borderRadius: 2, margin: "0 4px" }} />
                )}
                {/* Space header */}
                <div
                  data-drag-id={`space:${space.id}`}
                  onClick={() => setExpanded(ex => ({ ...ex, [space.id]: !ex[space.id] }))}
                  style={{ display: "flex", cursor: !guestMode ? "grab" : "pointer", background: dragItem?.spaceId === space.id && !dragItem?.taskId ? "#EEF2FF" : "#f8fafc", borderBottom: "1px solid #e2e8f0", opacity: dragItem?.spaceId === space.id && !dragItem?.taskId ? 0.5 : 1 }}>
                  {/* STICKY: label + RACI */}
                  <div style={{ display: "flex", flexShrink: 0, position: "sticky", left: 0, zIndex: 25, background: dragItem?.spaceId === space.id && !dragItem?.taskId ? "#EEF2FF" : "#f8fafc", borderLeft: `4px solid ${space.color}` }}>
                  <div style={{ width: LABEL_W - 4, flexShrink: 0, padding: "9px 8px", display: "flex", alignItems: "center", gap: 5, borderRight: "none" }}>
                    <div onMouseDown={e => { e.stopPropagation(); startRowDrag(e, { spaceId: space.id }); }} style={{ cursor: guestMode ? "default" : "grab", color: "#b0bec5", fontSize: 11, flexShrink: 0, lineHeight: 1, userSelect: "none", letterSpacing: "-1px" }} onClick={e => e.stopPropagation()}>⠿</div>
                    <span onClick={e => { e.stopPropagation(); setExpanded(ex => ({ ...ex, [space.id]: !ex[space.id] })); }} style={{ fontSize: 10, color: space.color, transform: expanded[space.id] ? "rotate(90deg)" : "rotate(0)", display: "inline-block", transition: "transform 0.2s" }}>▶</span>
                    <div style={{ flex: 1 }} onDoubleClick={e => { e.stopPropagation(); if (!guestMode) setEditingSpace(space.id); else showLoginToast(); }}>
                      {editingSpace === space.id ? (
                        <input
                          autoFocus
                          defaultValue={space.space}
                          onBlur={e => renameSpace(space.id, e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") renameSpace(space.id, e.target.value); if (e.key === "Escape") setEditingSpace(null); }}
                          onClick={e => e.stopPropagation()}
                          style={{ fontSize: 11, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5, border: "none", borderBottom: `2px solid ${space.color}`, outline: "none", background: "transparent", width: "100%", padding: "1px 0" }}
                        />
                      ) : (
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: 0.5 }} title="Doble click para renombrar">{space.space}</div>
                      )}
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 3 }}>
                        <div style={{ flex: 1, height: 3, background: "#e2e8f0", borderRadius: 2 }}>
                          <div style={{ width: `${spaceProgress}%`, height: "100%", background: space.color, borderRadius: 2 }} />
                        </div>
                        <span style={{ fontSize: 9, color: "#94a3b8" }}>{spaceProgress}%</span>
                      </div>
                    </div>
                    {hasDelayed && <span style={{ fontSize: 9, background: "#FEE2E2", color: "#EF4444", padding: "2px 5px", borderRadius: 4, fontWeight: 800, flexShrink: 0 }}>ATRASO</span>}
                    {!guestMode && <button onClick={e => { e.stopPropagation(); setModal({ spaceId: space.id, type: "task", spaceColor: space.color }); }}
                      title="Agregar tarea"
                      style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: space.color, color: "#fff", fontSize: 15, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>+</button>}
                    {!guestMode && <button onClick={e => { e.stopPropagation(); deleteSpace(space.id); }}
                      title="Eliminar área"
                      style={{ width: 20, height: 20, borderRadius: "50%", border: "none", background: "#FEE2E2", color: "#EF4444", fontSize: 12, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, opacity: 0.7 }}>✕</button>}
                  </div>
                  {/* RACI header cells for this space */}
                  {RACI_ROLES.map((role, i) => (
                    <div key={role} style={{ width: RACI_W, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                      borderLeft: role === "R" ? `1px solid ${space.color}33` : "none",
                      borderRight: role === "I" ? "1px solid #e2e8f0" : "none",
                      background: `${space.color}08` }}>
                      <div style={{ fontSize: 8, fontWeight: 900, color: `${space.color}99`, letterSpacing: 0.5 }}>{role}</div>
                    </div>
                  ))}
                  </div>{/* end sticky */}
                  <div style={{ flex: 1, position: "relative" }}>
                    {visibleMonths.map((m,i) => <div key={i} style={{ position: "absolute", left: `${vpct(m)}%`, top: 0, bottom: 0, borderLeft: "1px solid #f1f5f9" }} />)}
                  </div>
                </div>

                {/* Space drop indicator AFTER */}
                {isSpaceDragTarget && dragOver?.position === "after" && (
                  <div style={{ height: 4, background: "#6366F1", borderRadius: 2, margin: "0 4px" }} />
                )}
                {/* Task rows */}
                {expanded[space.id] && visibleTasks.map(task => {
                  const tc = taskColor(task);
                  const hasSubtasks = task.subtasks.length > 0;
                  const isExpT = expandedTasks[task.id];
                  const isEditingThisName = editingName?.taskId === task.id && !editingName?.subtaskId;

                  const taskIdx = visibleTasks.indexOf(task);
                  const isDragTarget = dragOver?.taskId === task.id && !dragOver?.subtaskId;
                  return (
                    <React.Fragment key={task.id}>
                      {isDragTarget && dragOver?.position === "before" && (
                        <div style={{ height: 3, background: "#6366F1", borderRadius: 2, marginLeft: 4 }} />
                      )}
                      <div
                        data-drag-id={`task:${space.id}:${task.id}`}
                        style={{
                          display: "flex",
                          background: dragItem?.taskId === task.id && !dragItem?.subtaskId ? "#EEF2FF" : "#fff",
                          borderBottom: "1px solid #f1f5f9",
                          opacity: dragItem?.taskId === task.id && !dragItem?.subtaskId ? 0.45 : 1,
                          transition: "opacity 0.15s",
                        }}>
                        {/* STICKY: label + RACI */}
                        <div style={{ display: "flex", flexShrink: 0, position: "sticky", left: 0, zIndex: 25, background: dragItem?.taskId === task.id && !dragItem?.subtaskId ? "#EEF2FF" : "#fff" }}>
                        <div style={{ width: LABEL_W, flexShrink: 0, display: "flex", alignItems: "center", gap: 4, padding: "0 6px 0 10px", borderRight: "none", minHeight: ROW_H }}>
                          {/* Drag handle — visible ⠿ grip dots */}
                          <div
                            onMouseDown={e => { e.stopPropagation(); startRowDrag(e, { spaceId: space.id, taskId: task.id }); }}
                            style={{ cursor: guestMode ? "default" : "grab", color: "#b0bec5", fontSize: 13, padding: "2px 3px", flexShrink: 0, lineHeight: 1, userSelect: "none", letterSpacing: "-1px" }}
                            title={guestMode ? "" : "Arrastra para reordenar"}>⠿</div>
                          {hasSubtasks
                            ? <span onClick={() => setExpandedTasks(e => ({ ...e, [task.id]: !e[task.id] }))}
                                style={{ fontSize: 9, color: "#94a3b8", cursor: "pointer", transform: isExpT ? "rotate(90deg)" : "rotate(0)", display: "inline-block", transition: "transform 0.2s", flexShrink: 0 }}>▶</span>
                            : <span style={{ width: 9 }} />}

                          <div style={{ flex: 1, minWidth: 0 }}>
                            {isEditingThisName ? (
                              <input autoFocus value={editNameVal}
                                onChange={e => setEditNameVal(e.target.value)}
                                onBlur={commitName}
                                onKeyDown={e => e.key === "Enter" && commitName()}
                                style={{ width: "100%", fontSize: 12, fontWeight: 600, border: "1.5px solid #6C63FF", borderRadius: 5, padding: "2px 5px", outline: "none", color: "#334155" }} />
                            ) : (
                              <div onDoubleClick={() => guestMode ? showLoginToast() : startEditName(space.id, task.id, task.name, null)}
                                style={{ fontSize: 12, fontWeight: 600, color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text" }}
                                title="Doble click para editar nombre">{task.name}</div>
                            )}
                            <div style={{ fontSize: 10, color: "#94a3b8" }}>
                              {task.start.toLocaleDateString("es-MX", { day: "numeric", month: "short" })} → {task.end.toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
                            </div>
                          </div>

                          {/* Progress */}
                          {editingProgress === task.id ? (
                            <input autoFocus type="number" min="0" max="100" value={task.progress}
                              style={{ width: 38, borderRadius: 6, border: `1.5px solid ${tc}`, fontSize: 11, fontWeight: 700, padding: "2px 3px", textAlign: "center", color: tc, outline: "none" }}
                              onBlur={() => setEditingProgress(null)}
                              onChange={e => updateTask(space.id, task.id, { progress: Math.min(100, Math.max(0, Number(e.target.value))) })}
                              onClick={e => e.stopPropagation()} />
                          ) : (
                            <div onClick={() => guestMode ? showLoginToast() : setEditingProgress(task.id)} title={guestMode ? "Inicia sesión para editar" : "Click para editar %"}
                              style={{ fontSize: 11, fontWeight: 800, color: tc, minWidth: 28, textAlign: "right", cursor: "text", flexShrink: 0 }}>{task.progress}%</div>
                          )}
                          {/* 3-dot menu */}
                          {!guestMode && <button
                            onClick={e => { e.stopPropagation(); setMenu({ spaceId: space.id, taskId: task.id, spaceColor: space.color, x: e.clientX, y: e.clientY }); }}
                            title="Más opciones"
                            style={{ width: 22, height: 22, border: "none", background: "transparent", color: "#94a3b8", borderRadius: 5, fontSize: 14, cursor: "pointer", fontWeight: 900, display:"flex", alignItems:"center", justifyContent:"center", flexShrink: 0, letterSpacing: 1 }}>⋯</button>}
                        </div>
                        {/* RACI cells for task */}
                        {RACI_ROLES.map((role, ri) => {
                          const raciVal = task.raci?.[role] ?? null;
                          const c = raciVal ? (ownerColors[raciVal] || { bg: "#94A3B8" }) : null;
                          const roleMeta = Object.values(RACI_META)[ri];
                          return (
                            <div key={role}
                              onClick={() => guestMode ? showLoginToast() : setRaciPicker({ spaceId: space.id, taskId: task.id, role })}
                              title={raciVal ? `${role}: ${ownerColors[raciVal]?.label || raciVal}` : `Asignar ${role}`}
                              style={{ width: RACI_W, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                                minHeight: ROW_H, cursor: guestMode ? "default" : "pointer",
                                borderLeft: role === "R" ? "1px solid #f1f5f9" : "none",
                                borderRight: role === "I" ? "1px solid #e2e8f0" : "none",
                                background: raciVal ? `${roleMeta.color}08` : "transparent",
                                transition: "background 0.15s" }}
                              onMouseEnter={e => { if (!guestMode) e.currentTarget.style.background = `${roleMeta.color}18`; }}
                              onMouseLeave={e => { e.currentTarget.style.background = raciVal ? `${roleMeta.color}08` : "transparent"; }}>
                              {raciVal ? (
                                <div style={{ width: 22, height: 22, borderRadius: "50%", background: c.bg,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 7, fontWeight: 900, color: "#fff", flexShrink: 0 }}>
                                  {raciVal.slice(0,2)}
                                </div>
                              ) : (
                                <div style={{ width: 16, height: 16, borderRadius: "50%", border: `1.5px dashed ${roleMeta.color}55`,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 10, color: `${roleMeta.color}55` }}>+</div>
                              )}
                            </div>
                          );
                        })}
                        </div>{/* end sticky */}
                        <div style={{ flex: 1, position: "relative", minHeight: ROW_H }}>
                          {visibleMonths.map((m,i) => <div key={i} style={{ position: "absolute", left: `${vpct(m)}%`, top: 0, bottom: 0, borderLeft: "1px solid #f8fafc" }} />)}
                          <GanttBar bar={task} spaceId={space.id} taskId={task.id} subtaskId={null} taskName={task.name} />
                        </div>
                      </div>

                      {/* Subtask rows */}
                      {isExpT && task.subtasks
                        .filter(s => !filterOwner || s.owners.includes(filterOwner))
                        .map(sub => {
                          const sc = taskColor(sub);
                          const isEditingSubName = editingName?.subtaskId === sub.id;
                          const subIdx = task.subtasks.indexOf(sub);
                          const isSubDragTarget = dragOver?.subtaskId === sub.id;
                          return (
                            <>
                            {isSubDragTarget && dragOver.position === "before" && (
                              <div style={{ height: 2, background: "#6C63FF", margin: "0 0 0 36px", borderRadius: 2 }} />
                            )}
                            <div key={sub.id}
                              data-drag-id={`sub:${space.id}:${task.id}:${sub.id}`}
                              style={{ display: "flex", background: dragItem?.subtaskId === sub.id ? "#f0f4ff" : "#fafbfc", borderBottom: "1px solid #f1f5f9", opacity: dragItem?.subtaskId === sub.id ? 0.5 : 1 }}>
                              {/* STICKY: label + RACI */}
                              <div style={{ display: "flex", flexShrink: 0, position: "sticky", left: 0, zIndex: 25, background: dragItem?.subtaskId === sub.id ? "#f0f4ff" : "#fafbfc" }}>
                              <div style={{ width: LABEL_W, flexShrink: 0, display: "flex", alignItems: "center", gap: 5, padding: "0 6px 0 28px", borderRight: "none", minHeight: 30 }}>
                                <div onMouseDown={e => { e.stopPropagation(); startRowDrag(e, { spaceId: space.id, taskId: task.id, subtaskId: sub.id }); }} style={{ cursor: "grab", color: "#b0bec5", fontSize: 12, flexShrink: 0, lineHeight: 1, padding: "0 3px", userSelect: "none" }} title="Arrastra para reordenar">⠿</div>
                                <div style={{ width: 6, height: 6, borderRadius: "50%", background: sc, flexShrink: 0 }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  {isEditingSubName ? (
                                    <input autoFocus value={editNameVal}
                                      onChange={e => setEditNameVal(e.target.value)}
                                      onBlur={commitName}
                                      onKeyDown={e => e.key === "Enter" && commitName()}
                                      style={{ width: "100%", fontSize: 11, border: "1.5px solid #6C63FF", borderRadius: 5, padding: "2px 4px", outline: "none" }} />
                                  ) : (
                                    <div onDoubleClick={() => guestMode ? showLoginToast() : startEditName(space.id, task.id, sub.name, sub.id)}
                                      style={{ fontSize: 11, color: "#475569", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "text" }}
                                      title="Doble click para editar">{sub.name}</div>
                                  )}
                                </div>
                                {editingProgress === sub.id ? (
                                  <input autoFocus type="number" min="0" max="100" value={sub.progress}
                                    style={{ width: 36, borderRadius: 6, border: `1.5px solid ${sc}`, fontSize: 10, padding: "1px 3px", textAlign: "center", color: sc, outline: "none" }}
                                    onBlur={() => setEditingProgress(null)}
                                    onChange={e => updateTask(space.id, task.id, { progress: Math.min(100, Math.max(0, Number(e.target.value))) }, sub.id)}
                                    onClick={e => e.stopPropagation()} />
                                ) : (
                                  <div onClick={() => !guestMode && setEditingProgress(sub.id)}
                                    style={{ fontSize: 10, fontWeight: 700, color: sc, minWidth: 26, textAlign: "right", cursor: "text", flexShrink: 0 }}>{sub.progress}%</div>
                                )}
                                {!guestMode && <button
                                  onClick={e => { e.stopPropagation(); setMenu({ spaceId: space.id, taskId: task.id, subtaskId: sub.id, spaceColor: space.color, x: e.clientX, y: e.clientY }); }}
                                  title="Más opciones"
                                  style={{ width: 20, height: 20, border: "none", background: "transparent", color: "#94a3b8", borderRadius: 5, fontSize: 14, cursor: "pointer", fontWeight: 900, display:"flex", alignItems:"center", justifyContent:"center", flexShrink: 0 }}>⋯</button>}
                              </div>
                              {/* Subtask: R column shows owner avatar, A/C/I are empty spacers */}
                              <div onClick={() => guestMode ? showLoginToast() : setOwnerPicker({ spaceId: space.id, taskId: task.id, subtaskId: sub.id })}
                                title={guestMode ? "Inicia sesión para editar" : "Click para editar responsable"}
                                style={{ width: RACI_W, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                                  minHeight: 30, cursor: guestMode ? "default" : "pointer",
                                  borderLeft: "1px solid #f1f5f9", background: sub.owners.length > 0 ? "#6366F108" : "transparent",
                                  transition: "background 0.15s" }}
                                onMouseEnter={e => { if (!guestMode) e.currentTarget.style.background = "#6366F118"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = sub.owners.length > 0 ? "#6366F108" : "transparent"; }}>
                                {sub.owners.length === 0
                                  ? <div style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px dashed #6366F155", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#6366F155" }}>+</div>
                                  : (() => { const c = ownerColors[sub.owners[0]] || { bg: "#94A3B8" };
                                      return <div style={{ width: 20, height: 20, borderRadius: "50%", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 900, color: "#fff" }}>{sub.owners[0].slice(0,2)}</div>; })()
                                }
                              </div>
                              {/* A, C, I spacers */}
                              {["A","C","I"].map(role => (
                                <div key={role} style={{ width: RACI_W, flexShrink: 0,
                                  borderRight: role === "I" ? "1px solid #e2e8f0" : "none",
                                  background: "#fafbfc" }} />
                              ))}
                              </div>{/* end sticky */}
                              <div style={{ flex: 1, position: "relative", minHeight: 30 }}>
                                {visibleMonths.map((m,i) => <div key={i} style={{ position: "absolute", left: `${vpct(m)}%`, top: 0, bottom: 0, borderLeft: "1px solid #f8fafc" }} />)}
                                <GanttBar bar={sub} spaceId={space.id} taskId={task.id} subtaskId={sub.id} taskName={sub.name} />
                              </div>
                            </div>
                            {isSubDragTarget && dragOver.position === "after" && (
                              <div style={{ height: 2, background: "#6C63FF", margin: "0 0 0 36px", borderRadius: 2 }} />
                            )}
                            </>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}

          {/* Today footer label */}
          <div style={{ position: "relative", height: 20, background: "#f8fafc" }} />

          {/* Objective milestone lines — SINGLE block, very tenue */}
          {[...new Map(objectives.map(o => [o.id, o])).values()].filter(obj => obj.date).map(obj => {
            const objDate = new Date(obj.date + "T00:00:00");
            const objPct = vpct(objDate);
            if (objPct < 0 || objPct > 100) return null;
            const hex = obj.color.replace("#","");
            const r = parseInt(hex.slice(0,2),16), g = parseInt(hex.slice(2,4),16), b = parseInt(hex.slice(4,6),16);
            return (
              <div key={"objline-"+obj.id} style={{
                position: "absolute",
                left: `calc(${LABEL_W + RACI_ROLES.length * RACI_W}px + (100% - ${LABEL_W + RACI_ROLES.length * RACI_W}px) * ${objPct / 100})`,
                top: 0, bottom: 0, width: 0,
                borderLeft: `1px dashed rgba(${r},${g},${b},0.25)`,
                zIndex: 18,
                pointerEvents: "none",
              }}>
                <div style={{ position: "absolute", top: 32, left: 3, fontSize: 7, fontWeight: 600, color: `rgba(${r},${g},${b},0.4)`, whiteSpace: "nowrap", lineHeight: 1.2 }}>
                  ◆ {obj.label.length > 14 ? obj.label.slice(0,14)+"…" : obj.label}
                </div>
              </div>
            );
          })}

          {/* Single today overlay line */}
          <div style={{
            position: "absolute",
            left: `calc(${LABEL_W + RACI_ROLES.length * RACI_W}px + (100% - ${LABEL_W + RACI_ROLES.length * RACI_W}px) * ${vpct(TODAY) / 100})`,
            top: 0, bottom: 0, width: 2,
            background: "#F59E0B",
            zIndex: 20,
            pointerEvents: "none",
          }}>
            <div style={{ position: "sticky", top: 40, fontSize: 9, fontWeight: 800, color: "#F59E0B", whiteSpace: "nowrap", marginLeft: -12 }}>▲ HOY</div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center", padding: "10px", fontSize: 10, color: "#94a3b8" }}>
        <b>Doble click</b> en nombre para editar · <b>Click en %</b> para avance · <b>Arrastra bordes</b> para cambiar duración · <b>⠿ Arrastra filas</b> para reordenar · <b>⋯</b> para agregar subtarea o eliminar
      </div>
        </>
      ))}

      {showMetrics && (
        <div onClick={() => setShowMetrics(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: "#fff", borderRadius: 14, padding: 22, maxWidth: 640, width: "100%", maxHeight: "80vh", overflowY: "auto" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>Métricas de uso — Análisis de precios</div>
              <button onClick={() => setShowMetrics(false)} style={{ border: "none", background: "none", fontSize: 16, color: "#94a3b8", cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 16 }}>
              Solo pestaña Precios/FX. "Ver" = abrió la pestaña. El resto son acciones reales. No cuenta nada mientras alguien tiene Modo presentación activo.
            </div>
            {metricsLoading && <div style={{ color: "#94a3b8", fontSize: 13 }}>Cargando…</div>}
            {!metricsLoading && metricsMonths?.length === 0 && <div style={{ color: "#94a3b8", fontSize: 13 }}>No se pudo cargar.</div>}
            {!metricsLoading && metricsMonths?.map(({ key, label, data }) => (
              <div key={key} style={{ marginBottom: 18, border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: "8px 12px", background: "#f8fafc", fontSize: 12.5, fontWeight: 800, color: "#334155", textTransform: "capitalize" }}>{label}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ color: "#94a3b8", fontSize: 10, textTransform: "uppercase" }}>
                      <td style={{ padding: "6px 12px" }}>Acción</td>
                      <td style={{ padding: "6px 12px", textAlign: "right" }}>Cliente</td>
                      <td style={{ padding: "6px 12px", textAlign: "right" }}>Equipo</td>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(METRIC_LABELS).map(action => (
                      <tr key={action} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "6px 12px", color: "#475569" }}>{METRIC_LABELS[action]}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>{data?.client?.[action] || 0}</td>
                        <td style={{ padding: "6px 12px", textAlign: "right", fontWeight: 700, color: "#0f172a" }}>{data?.team?.[action] || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
